import React, { useState, useEffect, useRef, useMemo } from 'react';
// import CallPopupDemo from './working_call_popup';
import ExtendedAgenda from './ExtendedAgenda';
import OpenForConnectionsList from './OpenForConnectionsList';
import TimerActionButtons from './connection-ui/TimerActionButtons';
import NextStepModal from './connection-ui/NextStepModal';
import PhaseIndicator from './connection-ui/PhaseIndicator';
import ConnectionSidebar from './connection-ui/ConnectionSidebar';
import PhaseProgressPopup from './connection-ui/PhaseProgressPopup';
import ChatPhase from './connection-phases/ChatPhase';
import CallPhase from './connection-phases/CallPhase';
import VideoPhase from './connection-phases/VideoPhase';
// import PhysicalMeetingPhase from './connection-phases/PhysicalMeetingPhase'; // OLD VERSION - DISABLED
import PhysicalMeetingPhaseV2 from './connection-phases/PhysicalMeetingPhaseV2';
import ConversationAnalysisTrigger from './ConversationAnalysisTrigger';
// import CallInterfaceDebug from '../Superkonnected losse onderdelen/call-interface-debug';
import { Icon } from './icons';
import PlanningCard from '../Connectieflow_onderdelen/PlanningCard';
import NextStepActions from './NextStepActions';
import AIRelationManagerCard from './connection-cards/AIRelationManagerCard';
import AutoplanCard from './connection-cards/AutoplanCard';
import IncomingProposalsCard from './connection-cards/IncomingProposalsCard';
import { Search, Clock, X, Calendar, Flag, Phone, Video, AlertTriangle, ArrowRight, Edit, User, Send, Bot } from './icons';
import { db } from '../firebase';
import { collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot, query, orderBy, where, getDocs, getDoc, setDoc, serverTimestamp, writeBatch, runTransaction, arrayRemove } from 'firebase/firestore';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../firebase';
import { onlineStatusService } from '../services/onlineStatusService';
import { meetService } from '../services/meetService';
import { ensureConnectionJourneyFields, setConnectionPhase, setConnectionPhaseStatus, linkActiveEvent, ConnectionPhase, ConnectionPhaseStatus, completeCurrentPhase, cancelActiveAppointment, setPlanningLock, acquirePlanningLock, releasePlanningLock, PlanningLockResult, transitionPhaseGuarded, ALLOWED_TRANSITIONS } from '../services/connectionJourneyService';
import { handleKnownConnectiesCompletion } from './ConnectieFlowKnownConnecties';
import { intersectSlots, pickTopSlots, Slot } from '../utils/autoplan';
import { createProposal, listenProposals, acceptProposal, rejectProposal, counterProposal, MeetingProposal } from '../services/proposalService';
import { hasCompletedRequiredOnboardingFields } from '../lib/profileCompletion';
import { openInboxTab } from '../utils/navigationHelper';
import { connectGoogleCalendar } from '../utils/googleCalendar';
import { useWebRTCAudioCall } from '../hooks/useWebRTCAudioCall';
import { webrtcService } from '../services/webrtcService';
import { useToast, ToastContainer } from './ui/Toast';
import { ErrorBoundary } from './ui/ErrorBoundary';
import PresenceIndicator from './ui/PresenceIndicator';
import { updatePresence, listenToPresence, UserPresence } from '../services/presenceService';

// ==========================================
// ✅ HYBRID CALL SYSTEM
// ==========================================
// Audio calls: WebRTC (peer-to-peer, embedded in app)
// Video calls: Google Meet (external popup window)
//
// Audio (Bellen fase):
// - WebRTC peer-to-peer via webrtcService.ts
// - Firebase signaling via webrtcSignalingService.ts
// - useWebRTCAudioCall hook manages the full lifecycle
// - Remote audio plays through hidden <audio> element
// - MUST fully release microphone before Google Meet video
//
// Video (Beeldbellen fase):
// - Google Meet external popup (500x700, right side of screen)
// - meetService.ts handles URL generation and popup opening
// - User can see timer + notes while in video call
//
// Architectuur:
// - WebRTC Service: src/services/webrtcService.ts (audio only)
// - WebRTC Signaling: src/services/webrtcSignalingService.ts (Firebase)
// - Meet Service: src/services/meetService.ts (video only)
// - Audio Call Hook: src/hooks/useWebRTCAudioCall.ts
// - Flow Orchestrator: ConnectieFlow.tsx (timers, state machine)
//
// ==========================================

// Jitsi als backup (optioneel)
import JitsiCall from './JitsiCall';

// Legacy WebRTCCall interface voor backwards compatibility met state
interface WebRTCCall {
  id: string;
  type: 'audio' | 'video';
  participants: string[];
  status: 'initiating' | 'connecting' | 'connected' | 'ended';
  startTime?: Date;
  endTime?: Date;
  duration?: number;
  userName?: string;
}

// 🔴 DEPRECATED: deleteVideoCallDocument is niet meer nodig
// Google Meet popups worden automatisch gesloten door de gebruiker

interface ChatMessage {
  id: string;
  chatRoomId: string;
  senderId: string;
  senderName: string;
  senderPhoto?: string;
  message: string;
  timestamp: Date;
  messageType: 'text' | 'system' | 'call_invite' | 'video_call_link';
  videoCallUrl?: string; // Optional: for video_call_link type
}

interface ChatRoom {
  id: string;
  participants: string[];
  participantNames: string[];
  participantPhotos: string[];
  lastMessage?: string;
  lastMessageAt?: Date;
  messageCount: number;
  unreadCount: { [userId: string]: number };
  createdAt: Date;
}

// Base step interface
interface BaseStep {
  status: 'pending' | 'proposed' | 'active' | 'completed';
  proposedBy?: string;
  proposedAt?: Date;
  acceptances: { [userId: string]: boolean };
  scheduledTime?: Date;
  startTime?: Date;
}

// Chat step (no meetUrl)
interface ChatStep extends BaseStep {
  status: 'pending' | 'active' | 'completed';
  duration: number; // in seconds
}

// Call step (WebRTC audio - no meetUrl)
interface CallStep extends BaseStep {
  duration: number; // in seconds
}

// Video step (Google Meet - has meetUrl)
interface VideoStep extends BaseStep {
  duration: number; // in seconds
  meetUrl?: string; // Google Meet URL for video calls
}

// Physical step (location-based)
interface PhysicalStep extends BaseStep {
  location?: string;
}

interface ConnectionFlow {
  id: string;
  chatRoomId: string;
  participants: string[];
  user1Id?: string;
  user2Id?: string;
  status: 'pending' | 'active' | 'completed' | 'terminated';
  currentStep: 'chat' | 'call' | 'video' | 'physical';
  
  // Step tracking with proper types
  chatStep: ChatStep;
  callStep: CallStep;
  videoStep: VideoStep;
  physicalStep: PhysicalStep;
  
  // Physical meeting data
  physicalMeeting?: {
    locationProposals?: {
      user1?: Array<{id: string; name: string; address: string; lat: number; lng: number}>;
      user2?: Array<{id: string; name: string; address: string; lat: number; lng: number}>;
    };
    navigationStatus?: {
      user1?: { started?: boolean; arrived?: boolean };
      user2?: { started?: boolean; arrived?: boolean };
    };
    meetingStartTime?: any;
    meetingEndTime?: any;
  };
  
  // Legacy/fallback Meet URL (kept for backwards compatibility)
  googleMeetUrl?: string;
  
  createdAt: Date;
  updatedAt: Date;
}

type FlowPhaseId = 'chat' | 'call' | 'video' | 'physical';

interface FlowNote {
  id: string;
  text: string;
  phase: FlowPhaseId;
  createdAt: Date;
  createdBy: string;
}

interface ConnectieFlowProps {
  // No props needed
}

const DEMO_PHASES: { key: 'chat' | 'bellen' | 'video' | 'ontmoeten'; label: string }[] = [
  { key: 'chat',      label: 'Chat' },
  { key: 'bellen',    label: 'Bellen' },
  { key: 'video',     label: 'Video' },
  { key: 'ontmoeten', label: 'Ontmoeten' },
];

const ConnectieFlow: React.FC<ConnectieFlowProps> = () => {
  // Auth state
  const [user] = useAuthState(auth);
  
  // ✅ UX: Toast notifications
  const { toasts, success, error: showError, warning, info, removeToast } = useToast();
  
  // ✅ UX: Presence tracking - what is other user doing?
  const [otherUserPresence, setOtherUserPresence] = useState<UserPresence | null>(null);
  
  // ✅ UX: Loading states (prevents flash and improves feedback)
  const [isLoadingConnection, setIsLoadingConnection] = useState(false);
  const [loadingStates, setLoadingStates] = useState({
    sendingRequest: false,
    acceptingRequest: false,
    savingSettings: false,
    connectingCalendar: false,
    sendingMessage: false,
    completingPhase: false,
    startingCall: false,
    endingCall: false,
  });
  
  const setLoading = (key: keyof typeof loadingStates, value: boolean) => {
    setLoadingStates(prev => ({ ...prev, [key]: value }));
  };
  
  // All state declarations
  const [activeTab, setActiveTab] = useState('active'); // Start altijd op Verbinden tab
  const [showRegionOptions, setShowRegionOptions] = useState(true);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [selectedConnection, setSelectedConnection] = useState('');
  const [currentPhase, setCurrentPhase] = useState<ConnectionPhase>('chat');
  // UI-only: which phase card is currently expanded.
  // Intentionally NOT synced to currentPhase automatically — phase changes must
  // not auto-expand a card; the user (or an explicit action) must open it.
  const [activeCard, setActiveCard] = useState<ConnectionPhase | null>(null);
  const [connectionStatus, setConnectionStatus] = useState('accepted');
  // Timer in seconden - standaard 8 minuten (480s), wordt bij starten fase opnieuw gezet
  const [timer, setTimer] = useState(480);
  const [isTimerActive, setIsTimerActive] = useState(false); // Timer start pas na acceptatie
  const [showStartChatPopup, setShowStartChatPopup] = useState(false);
  const [onlineConnectionsForPopup, setOnlineConnectionsForPopup] = useState<any[]>([]);
  
  // Open for connections states
  const [isOpenForConnections, setIsOpenForConnections] = useState(false);
  const [showConnectionMenu, setShowConnectionMenu] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [expandedRegionSection, setExpandedRegionSection] = useState<'nederland' | 'internationaal'>('nederland');
  const [showSettingsPopup, setShowSettingsPopup] = useState(false);
  // Removed unused popup states
  
  // Agenda states
  const [showAgenda, setShowAgenda] = useState(false);
  // Navigation suppressor: set to true the moment openConnectionFlowForConnection
  // fires so that any Firestore first-snapshot setShowAgenda(true) calls that arrive
  // before the view is stable are invisible to the DOM. Cleared after 2500ms.
  // This guarantees zero flash of ExtendedAgenda when navigating to a connection.
  const [agendaNavSuppressed, setAgendaNavSuppressed] = useState(false);
  const agendaNavSuppressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showAgendaCloseConfirm, setShowAgendaCloseConfirm] = useState(false);
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(new Date());
  const [calendarView] = useState('week');
  const [showAppointmentForm, setShowAppointmentForm] = useState(false);
  const [selectedAppointmentDate, setSelectedAppointmentDate] = useState<string | null>(null);
  const [showAppointmentList, setShowAppointmentList] = useState(false);
  const [appointments, setAppointments] = useState<any[]>([]);
  const [postAcceptAction, setPostAcceptAction] = useState<'none' | 'chat' | 'agenda'>('none');
  
  // Conversation Analysis Trigger - NIEUW
  const [showAnalysisTrigger, setShowAnalysisTrigger] = useState(false);
  const [completedConnectionId, setCompletedConnectionId] = useState<string | null>(null);
  const [completedPhase, setCompletedPhase] = useState<'chat' | 'call' | 'video' | 'physical' | null>(null);
  const [pendingOpen, setPendingOpen] = useState<{ connectionId: string; mode?: 'chat' | 'agenda' } | null>(null);
  const [pendingAutoAcceptChat, setPendingAutoAcceptChat] = useState(false);
  // Prevent brief "both online" popup flash when we intentionally navigate straight into chat
  const suppressStartChatPopupUntilRef = useRef<number>(0);
  const [nowTick, setNowTick] = useState(Date.now());
  
  // Planning lock state
  const [planningLockResult, setPlanningLockResult] = useState<PlanningLockResult | null>(null);
  type FlowCalendarEvent = {
    id: string;
    title?: string;
    startTime: Date;
    endTime: Date;
    participants?: string[];
    type?: string;
    status?: string;
    connectionId?: string;
    phase?: 'call' | 'video' | 'physical';
    lockedToConnectionFlow?: boolean;
  };
  const [flowCalendarEvents, setFlowCalendarEvents] = useState<FlowCalendarEvent[]>([]);
  const SHOW_DEV_JOURNEY_CONTROLS =
    (typeof window !== 'undefined' && localStorage.getItem('devJourney') === '1');
  const [autoplanOpen, setAutoplanOpen] = useState(false);
  const [autoplanResults, setAutoplanResults] = useState<Slot[]>([]);
  const [autoplanError, setAutoplanError] = useState<string | null>(null);
  const [autoplanLoading, setAutoplanLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
  const [completingPhase, setCompletingPhase] = useState(false);
  const [completionNotice, setCompletionNotice] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelNotice, setCancelNotice] = useState<string | null>(null);
  const hasActiveScheduled = (conn: any) =>
    !!conn?.activeEventId &&
    ['scheduled', 'ready', 'in_progress'].includes(conn?.phaseStatus ?? '');
  const [proposalOpen, setProposalOpen] = useState(false);
  const [proposalSlots, setProposalSlots] = useState<{ start: string; end: string }[]>([{ start: '', end: '' }]);
  const [proposalMessage, setProposalMessage] = useState('');
  const [incomingProposals, setIncomingProposals] = useState<MeetingProposal[]>([]);
  const [relationManagerPrompted, setRelationManagerPrompted] = useState(false);
  const [nextStepChoiceOpen, setNextStepChoiceOpen] = useState(false);
  const [nextPhaseCandidate, setNextPhaseCandidate] = useState<ConnectionPhase | null>(null);
  const [pendingFinishSource, setPendingFinishSource] = useState<string | null>(null);
  const [pendingPostReflectionAction, setPendingPostReflectionAction] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [planningBanner, setPlanningBanner] = useState<{ userName: string; locationName: string } | null>(null);
  const [autoplanBanner, setAutoplanBanner] = useState<{ userName: string; type: 'match' | 'no_match'; slotLabel?: string; matchCount?: number } | null>(null);
  const [confirmedMeetingLocation, setConfirmedMeetingLocation] = useState<{ name: string; address: string } | null>(null);
  const [appointmentConfirmCard, setAppointmentConfirmCard] = useState<{
    start: Date; end: Date; locationName: string; otherUserName: string; myName: string;
  } | null>(null);
  
  // Connection request modal states
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [selectedUserForRequest, setSelectedUserForRequest] = useState<{ userId: string; userName: string; userPhoto?: string } | null>(null);
  const [requestMessage, setRequestMessage] = useState('');
  const [isKnownContact, setIsKnownContact] = useState(false);
  const [sendingRequest, setSendingRequest] = useState(false);
  const [currentUserHasPhone, setCurrentUserHasPhone] = useState(false); // ✅ FIX: default FALSE = block until confirmed
  
  const [relationManagerText, setRelationManagerText] = useState({
    title: 'AI Relation Manager',
    body: 'Zet dit aan om context, beloftes en next steps automatisch te onthouden, zodat je follow up strak blijft.',
    helper: 'Inschakelen voor deze connectie',
    toggleLabel: 'Inschakelen voor deze connectie',
  });

  const openFullAgendaPage = () => {
    if (!selectedConnectionData?.id) {
      window.location.hash = '#agenda';
      return;
    }
    const otherUserId =
      selectedConnectionData.user1Id === user?.uid
        ? selectedConnectionData.user2Id
        : selectedConnectionData.user1Id;
    window.dispatchEvent(
      new CustomEvent('openAgendaForConnection', {
        detail: {
      connectionId: selectedConnectionData.id,
      name: selectedConnectionData?.name,
      otherUserId,
        },
      })
    );
    window.location.hash = '#agenda';
  };

  // Toast auto-hide
  useEffect(() => {
    if (!toastMessage) return;
    const t = setTimeout(() => setToastMessage(null), 6000);
    return () => clearTimeout(t);
  }, [toastMessage]);

  // Block body scroll when modals are open
  useEffect(() => {
    if (showSettingsPopup || showAgenda) {
      // Save current overflow style
      const originalOverflow = document.body.style.overflow;
      // Block scroll
      document.body.style.overflow = 'hidden';
      
      // Restore on cleanup
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [showSettingsPopup, showAgenda]);

  const openProposalComposer = () => {
    setProposalOpen(true);
    setAutoplanError('Geen overlap gevonden. Stuur een voorstel.');
    if (!proposalSlots.length) {
      setProposalSlots([{ start: '', end: '' }]);
    }
  };
  const [appointmentForm, setAppointmentForm] = useState({
    title: '',
    time: '09:00',
    duration: '60',
    alarm: '15',
    description: ''
  });
  
  // Additional states from other files
  const [showEventDetails, setShowEventDetails] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [showPhaseSelector, setShowPhaseSelector] = useState(false);
  const [showTimerExpiredPopup, setShowTimerExpiredPopup] = useState(false);
  // Timer can only expire during active communication phases — never during 'completed'.
  const [timerExpiredPhase, setTimerExpiredPhase] = useState<'chat' | 'call' | 'video' | 'physical'>('chat');
  const timerExpiredDismissedKeyRef = useRef<string>(''); // Voorkomt heropening popup na dismiss
  // Tracks the last time a navigation/connection-selection happened.
  // The timer-expired popup is suppressed for 1500ms after navigation to prevent
  // transient phase changes (caused by event-driven connection loading) from
  // falsely triggering the popup.
  const lastNavigationTimeRef = useRef<number>(0);
  const [showCustomTimePopup, setShowCustomTimePopup] = useState(false);
  const [otherUserEnded, setOtherUserEnded] = useState(false);
  const [resyncAttempts, setResyncAttempts] = useState(0);
  const [customTimeForm, setCustomTimeForm] = useState({
    date: '',
    time: '',
    message: ''
  });
  
  // Chat states
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isInitialChatLoad, setIsInitialChatLoad] = useState(true);
  const [showChat] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentChatRoom, setCurrentChatRoom] = useState<ChatRoom | null>(null);
  const [selectedConnectionData, setSelectedConnectionData] = useState<any>(null);
  const [mobileFlowView, setMobileFlowView] = useState<'list' | 'detail'>('list');
  const [showMobileFlowDemo, setShowMobileFlowDemo] = useState(false);
  const [selectedMobileFlowDemo, setSelectedMobileFlowDemo] = useState<{
    id: string;
    name: string;
    initials: string;
    online: boolean;
    phase: string;
    status: 'open' | 'verbonden' | 'afgerond';
    progress: 'chat' | 'bellen' | 'video' | 'ontmoeten';
  } | null>(null);
  const [mobileFlowDemoFilter, setMobileFlowDemoFilter] = useState<'open' | 'verbonden' | 'afgerond'>('verbonden');
  const [mobileFlowDemoProgressFilter, setMobileFlowDemoProgressFilter] = useState<'alle' | 'chat' | 'bellen' | 'video' | 'ontmoeten'>('alle');
  const [mobileFlowDemoSearch, setMobileFlowDemoSearch] = useState('');
  const [demoChatInput, setDemoChatInput] = useState('');
  const [demoChatMessages, setDemoChatMessages] = useState<{ self: boolean; text: string }[]>([
    { self: false, text: 'Hoi, leuk dat we gekoppeld zijn.' },
    { self: true,  text: 'Ja zeker, zullen we kort kennismaken?' },
    { self: false, text: 'Goed idee. Ik kan vanmiddag even bellen.' },
  ]);
  const [demoNoteText, setDemoNoteText] = useState('');
  const [demoNoteSaved, setDemoNoteSaved] = useState(false);
  const [demoTimerSecs, setDemoTimerSecs] = useState(0);
  const [demoPhaseIdx, setDemoPhaseIdx] = useState(0);
  const [demoAiEnabled, setDemoAiEnabled] = useState(false);
  // Google Meet URL voor de huidige connectionFlow (uit Firestore)
  const [googleMeetUrl, setGoogleMeetUrl] = useState<string | null>(null);
  const [connectionFlow, setConnectionFlow] = useState<ConnectionFlow | null>(null);
  // Notes per fase
  const [notes, setNotes] = useState<FlowNote[]>([]);
  const [newNoteText, setNewNoteText] = useState('');
  const [isAiRelationManagerEnabled, setIsAiRelationManagerEnabled] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('aiRelationManagerEnabled') === 'true';
  });
  const [showAiRelationManagerCard, setShowAiRelationManagerCard] = useState<boolean>(false);
  const [showPhaseNotesCard, setShowPhaseNotesCard] = useState<boolean>(false);
  const hasChatMessages = chatMessages.length > 0;
  const mobileFlowDemoContacts = useMemo(() => ([
    { id: 'demo-fatima', name: 'Fatima Jansen', initials: 'FJ', online: true, phase: 'Chatfase', status: 'verbonden' as const, progress: 'chat' as const },
    { id: 'demo-mark', name: 'Mark de Vries', initials: 'MV', online: false, phase: 'Bellen gepland', status: 'verbonden' as const, progress: 'bellen' as const },
    { id: 'demo-lina', name: 'Lina Smit', initials: 'LS', online: true, phase: 'Videogesprek', status: 'verbonden' as const, progress: 'video' as const },
    { id: 'demo-samira', name: 'Samira Bakker', initials: 'SB', online: true, phase: 'Open voor kennismaking', status: 'open' as const, progress: 'chat' as const },
    { id: 'demo-noah', name: 'Noah Vermeer', initials: 'NV', online: false, phase: 'Afgeronde flow', status: 'afgerond' as const, progress: 'ontmoeten' as const },
  ]), []);
  const visibleMobileFlowDemoContacts = useMemo(
    () => mobileFlowDemoContacts.filter(contact =>
      contact.status === mobileFlowDemoFilter &&
      (mobileFlowDemoProgressFilter === 'alle' || contact.progress === mobileFlowDemoProgressFilter) &&
      contact.name.toLowerCase().includes(mobileFlowDemoSearch.trim().toLowerCase())
    ),
    [mobileFlowDemoContacts, mobileFlowDemoFilter, mobileFlowDemoProgressFilter, mobileFlowDemoSearch]
  );

  useEffect(() => {
    if (selectedConnectionData?.id) {
      setMobileFlowView('detail');
    }
  }, [selectedConnectionData?.id]);

  const toggleAiRelationManager = (value?: boolean) => {
    setIsAiRelationManagerEnabled(prev => {
      const next = typeof value === 'boolean' ? value : !prev;
      try {
        if (typeof window !== 'undefined') {
          localStorage.setItem('aiRelationManagerEnabled', next ? 'true' : 'false');
        }
      } catch (e) {
        console.warn('Could not persist AI relation manager toggle', e);
      }
      return next;
    });
  };
  
  useEffect(() => {
    const phase: ConnectionPhase = selectedConnectionData?.phase ?? 'chat';
    if (phase === 'physical') {
      setRelationManagerText({
        title: 'Samenvatting nodig voor AI follow up',
        body: 'Vul na de meeting kort in wat er is besproken, dan maakt AI een next step plan en conceptbericht.',
        helper: 'Minimaal 30 tekens',
        toggleLabel: 'Inschakelen',
      });
      return;
    }
    if (phase === 'video') {
      setRelationManagerText({
        title: 'AI Relation Manager tijdens je video',
        body: 'Leg besluiten en actiepunten vast, AI maakt er een duidelijke follow up van.',
        helper: 'Alleen wat jij invult wordt gebruikt',
        toggleLabel: 'Inschakelen',
      });
      return;
    }
    if (phase === 'call') {
      setRelationManagerText({
        title: 'AI Relation Manager tijdens je call',
        body: 'AI noteert kernpunten en actiepunten. Na afloop krijg je een korte follow up suggestie.',
        helper: 'Je kunt dit altijd uitzetten',
        toggleLabel: 'Inschakelen',
      });
      return;
    }
    setRelationManagerText({
      title: 'AI Relation Manager',
      body: 'Zet dit aan om context, beloftes en next steps automatisch te onthouden, zodat je follow up strak blijft.',
      helper: 'Inschakelen voor deze connectie',
      toggleLabel: 'Inschakelen voor deze connectie',
    });
  }, [selectedConnectionData]);
  const [showMeetupReflection, setShowMeetupReflection] = useState(false);
  const [meetupStartedAt, setMeetupStartedAt] = useState<Date | null>(null);
  const [showAiRelationInfo, setShowAiRelationInfo] = useState(false);
  
  type ConnectionCTA = 'chat' | 'agenda' | 'later';

  const handleConnectionCTA = async (cta: ConnectionCTA) => {
    if (cta === 'agenda') {
      setPostAcceptAction('agenda');
      
      // Acquire planning lock before opening agenda
      if (selectedConnectionData?.id && user?.uid && user?.displayName) {
        try {
          const lockResult = await acquirePlanningLock(
            selectedConnectionData.id,
            user.uid,
            user.displayName,
            selectedConnectionData.phase as ConnectionPhase || 'call'
          );
          
          setPlanningLockResult(lockResult);
          
          // Open agenda regardless (observer mode will be handled in ExtendedAgenda)
          setShowAgenda(true);
        } catch (error) {
          console.error('[planning-lock] Failed to acquire lock:', error);
          // Fallback: open agenda anyway
          setShowAgenda(true);
        }
      } else {
        // No lock needed if no connection selected
        setShowAgenda(true);
      }
      return;
    }
    if (cta === 'chat') {
      setPostAcceptAction('chat');
      setShowAgenda(false);
      // ✅ Update presence: back to idle
      if (connectionFlow?.id && user?.uid) {
        updatePresence(
          connectionFlow.id,
          user.uid,
          user.displayName || user.email || 'User',
          'idle'
        );
      }
      return;
    }
    // later/none
    setPostAcceptAction('none');
  };

  // Cleanup planning lock when agenda closes - MOVED AFTER activeEvent DECLARATION

  // Lightweight timer for ready-window checks
  useEffect(() => {
    const interval = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(interval);
  }, []);

  // Reusable hook helpers
  const useNow = (intervalMs = 30000) => {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
      const t = setInterval(() => setNow(Date.now()), intervalMs);
      return () => clearInterval(t);
    }, [intervalMs]);
    return now;
  };

  const isWithinReadyWindow = (
    start: Date,
    end: Date,
    nowMs: number,
    leadMin = 10,
    graceMin = 15
  ) => {
    const lead = leadMin * 60_000;
    const grace = graceMin * 60_000;
    const now = nowMs;
    return now >= (start.getTime() - lead) && now <= (end.getTime() + grace);
  };

  const nowMs = useNow(30000);

  const findActiveEvent = (conn: any, events: FlowCalendarEvent[]): FlowCalendarEvent | null => {
    if (!conn) return null;
    if (conn.activeEventId) {
      const byId = events.find(e => e.id === conn.activeEventId);
      if (byId) return byId;
    }
    const a = conn.user1Id;
    const b = conn.user2Id;
    const now = Date.now();
    const candidates = events
      .filter(e =>
        e.status !== 'cancelled' &&
        Array.isArray(e.participants) &&
        e.participants.includes(a) &&
        e.participants.includes(b) &&
        (e.type === 'call' || e.type === 'meeting' || e.type === 'networking') &&
        e.startTime?.getTime?.() >= now - 60 * 60_000
      )
      .sort((x, y) => x.startTime.getTime() - y.startTime.getTime());
    return candidates[0] ?? null;
  };

  const activeEvent = React.useMemo(() => {
    if (!selectedConnectionData) return null;
    return findActiveEvent(selectedConnectionData, flowCalendarEvents);
  }, [selectedConnectionData, flowCalendarEvents]);

  // Cleanup planning lock when agenda closes
  useEffect(() => {
    // Release lock when agenda is closed and user was the owner
    if (!showAgenda && planningLockResult?.granted && selectedConnectionData?.id && user?.uid) {
      // Check of er daadwerkelijk een planning is gemaakt
      // Als niet, dan is het een abort - maak notificaties aan
      const checkAndNotifyAbort = async () => {
        // Check of er een actieve planning event is
        // Als er geen activeEvent is, dan abort notificaties aanmaken
        const hasActivePlanning = activeEvent && activeEvent.status !== 'cancelled';
        
        if (!hasActivePlanning) {
          const otherUserId = selectedConnectionData.user1Id === user.uid
            ? selectedConnectionData.user2Id
            : selectedConnectionData.user1Id;

          if (otherUserId) {
            try {
              // Maak abort notificatie aan voor beide gebruikers
              await Promise.all([
                addDoc(collection(db, 'inboxItems'), {
                  userId: user.uid,
                  type: 'planning_aborted',
                  category: 'planning',
                  title: 'Planning niet afgerond',
                  message: `Planning met ${selectedConnectionData.name || 'de andere gebruiker'} is niet afgerond. Afronding nodig.`,
                  connectionId: selectedConnectionData.id,
                  isRead: false,
                  createdAt: serverTimestamp(),
                }),
                addDoc(collection(db, 'inboxItems'), {
                  userId: otherUserId,
                  type: 'planning_aborted',
                  category: 'planning',
                  title: 'Planning niet afgerond',
                  message: `Planning met ${user.displayName || 'jou'} is niet afgerond. Afronding nodig.`,
                  connectionId: selectedConnectionData.id,
                  isRead: false,
                  createdAt: serverTimestamp(),
                }),
              ]);
            } catch (error) {
              console.error('[planning-lock] Failed to create abort notifications:', error);
            }
          }
        }
      };

      checkAndNotifyAbort();
      releasePlanningLock(selectedConnectionData.id, user.uid).catch(console.error);
      setPlanningLockResult(null);
    }
  }, [showAgenda, planningLockResult?.granted, selectedConnectionData?.id, selectedConnectionData?.name, selectedConnectionData?.user1Id, selectedConnectionData?.user2Id, user?.uid, user?.displayName, activeEvent]);

  type CtaAction = 'open_planning' | 'start_call' | 'join_call' | 'start_chat';

  const getPrimaryCta = (conn: any, event: any, nowTs: number): { label: string; action: CtaAction } => {
    const phase: ConnectionPhase = conn?.phase ?? 'chat';
    const status: ConnectionPhaseStatus = conn?.phaseStatus ?? 'not_scheduled';

    if (phase === 'chat') {
      return { label: 'Start chat', action: 'start_chat' };
    }

    // For call/video/physical
    if (status === 'not_scheduled' || !conn?.activeEventId) {
      if (phase === 'video') return { label: 'Plan videogesprek', action: 'open_planning' };
      if (phase === 'physical') return { label: 'Plan fysieke afspraak', action: 'open_planning' };
      return { label: 'Plan afspraak', action: 'open_planning' };
    }

    if (status === 'in_progress') {
      return { label: phase === 'call' ? 'Join call' : 'Join', action: 'join_call' };
    }

    if (status === 'scheduled' && event) {
      const ready = isWithinReadyWindow(event.startTime, event.endTime, nowTs, 10, 15);
      if (ready) return { label: phase === 'call' ? 'Bel starten' : 'Starten', action: 'start_call' };
      return { label: 'Bekijk/Verplaats afspraak', action: 'open_planning' };
    }

    return { label: 'Open planning', action: 'open_planning' };
  };

  const primaryCta = React.useMemo(
    () => getPrimaryCta(selectedConnectionData, activeEvent, nowMs),
    [selectedConnectionData, activeEvent, nowMs]
  );

  useEffect(() => {
    const newId = selectedConnectionData?.id;
    // Guard 1: no ID means a temporary reload — do not reset.
    if (!newId) return;
    // Guard 2: same connection (e.g. Firestore snapshot refresh) — do not reset.
    // This prevents collapsing the physical card when the connections array reloads
    // with fresh data for the connection that is already open.
    if (prevSelectedConnectionIdRef.current === newId) return;
    prevSelectedConnectionIdRef.current = newId;
    setCompletionNotice(null);
    setCancelNotice(null);
    setActiveCard(null);
  }, [selectedConnectionData?.id]);

  const hasMismatchedActiveEvent = React.useMemo(() => {
    if (!activeEvent || !selectedConnectionData?.id) return false;
    if (!activeEvent.connectionId) return false;
    return activeEvent.connectionId !== selectedConnectionData.id;
  }, [activeEvent, selectedConnectionData?.id]);

  const canCompletePhase = React.useMemo(() => {
    const phase: ConnectionPhase = selectedConnectionData?.phase ?? 'chat';
    const status: ConnectionPhaseStatus = selectedConnectionData?.phaseStatus ?? 'not_scheduled';
    if (!['call', 'video', 'physical'].includes(phase)) return false;
    if (hasMismatchedActiveEvent) return false;
    if (status === 'in_progress') return true;
    if (status === 'scheduled' && activeEvent?.endTime?.getTime) {
      return nowMs > activeEvent.endTime.getTime();
    }
    return false;
  }, [selectedConnectionData, hasMismatchedActiveEvent, activeEvent, nowMs]);

  const canCancelAppointment = React.useMemo(() => {
    const status: ConnectionPhaseStatus = selectedConnectionData?.phaseStatus ?? 'not_scheduled';
    return !!selectedConnectionData?.activeEventId && ['scheduled', 'ready'].includes(status);
  }, [selectedConnectionData]);

  // Proposals listener for current connection
  useEffect(() => {
    if (!selectedConnectionData?.id || !user?.uid) return;
    const unsub = listenProposals(selectedConnectionData.id, user.uid, setIncomingProposals);
    return () => {
      if (typeof unsub === 'function') unsub();
    };
  }, [selectedConnectionData?.id, user?.uid]);

  const handlePrimaryCta = async () => {
    if (!selectedConnectionData?.id) {
      handleConnectionCTA('agenda');
      return;
    }
    const connectionId = selectedConnectionData.id;
    const action = primaryCta.action;
    if (action === 'open_planning') {
      setShowStartChatPopup(false);
      setOnlineConnectionsForPopup([]);
      handleConnectionCTA('agenda');
      setAutoplanOpen(true);
      const hasSuggestions = await runAutoplan(true);
      if (!hasSuggestions) {
        // Fallback: alleen agenda openen, geen extra UI
        setAutoplanOpen(false);
        setShowAgenda(true);
        setAutoplanError(null);
      }
      // Use the actual current phase so we never accidentally reset a physical (or video) phase back to 'call'.
      setConnectionPhase(connectionId, (selectedConnectionData.phase ?? 'call') as ConnectionPhase, selectedConnectionData.phaseStatus ?? 'not_scheduled').catch(console.warn);
      return;
    }
    if (action === 'start_chat') {
      setShowStartChatPopup(false);
      setOnlineConnectionsForPopup([]);
      handleConnectionCTA('chat');
      return;
    }
    if (action === 'start_call' || action === 'join_call') {
      setShowStartChatPopup(false);
      setOnlineConnectionsForPopup([]);
      await setConnectionPhaseStatus(connectionId, 'in_progress');
      info('Call gestart - fase status staat nu op in_progress');
      return;
    }
  };

  // Single source of truth: derive next phase from ALLOWED_TRANSITIONS so this
  // function stays automatically consistent when the transition map is updated.
  const getNextPhase = (phase: ConnectionPhase): ConnectionPhase | null => {
    const targets = ALLOWED_TRANSITIONS[phase];
    return (targets && targets.length > 0) ? targets[0] : null;
  };

  const openNextStepChoice = (nextPhase: ConnectionPhase | null, source?: string) => {
    setNextPhaseCandidate(nextPhase);
    setPendingFinishSource(source || null);
    setNextStepChoiceOpen(true);
  };

  const handleChooseNow = async () => {
    if (!nextPhaseCandidate || !selectedConnectionData?.id) {
      setNextStepChoiceOpen(false);
      return;
    }
    const connId = selectedConnectionData.id;

    // Use transaction-guarded transition instead of plain updateDoc.
    // The snapshot listener will update currentPhase once Firestore confirms.
    const expectedPhase = (Object.entries(ALLOWED_TRANSITIONS) as [ConnectionPhase, ConnectionPhase[]][])
      .find(([, targets]) => targets.includes(nextPhaseCandidate))?.[0] ?? 'chat';
    await transitionPhaseGuarded(connId, expectedPhase, nextPhaseCandidate, { phaseStatus: 'ready' });

    if (!isMountedRef.current) return;
    setSelectedConnectionData((prev: any) =>
      prev
        ? {
            ...prev,
            phase: nextPhaseCandidate,
            phaseStatus: 'ready',
            activeEventId: null,
          }
        : prev
    );
    setShowAgenda(false);
    setAutoplanOpen(false);
    setNextStepChoiceOpen(false);
    setToastMessage('Volgende fase geopend');
  };

  const handleChoosePlanLater = async () => {
    if (!nextPhaseCandidate || !selectedConnectionData?.id) {
      setNextStepChoiceOpen(false);
      return;
    }
    const connId = selectedConnectionData.id;
    const otherUserId =
      selectedConnectionData.user1Id === user?.uid
        ? selectedConnectionData.user2Id
        : selectedConnectionData.user1Id;
    const notifyDeferred = async () => {
      try {
        if (!otherUserId) return;
        await addDoc(collection(db, 'inboxItems'), {
          userId: otherUserId,
          type: 'reminder',
          category: 'connection',
          title: 'Gesprek gepland voor later',
          message:
            nextPhaseCandidate === 'call'
              ? 'De andere gebruiker plant de belafspraak later in. Open ConnectieFlow om een tijd te kiezen.'
              : 'De andere gebruiker pakt de volgende fase later op. Open ConnectieFlow om een tijd te kiezen.',
          actionData: {
            actionType: 'accept_connection',
            connectionId: connId,
            connectionName: selectedConnectionData?.name,
            otherUserId: user?.uid
          },
          isRead: false,
          createdAt: serverTimestamp()
        });
      } catch (err) {
        console.warn('Kon uitstel-notificatie niet verzenden:', err);
      }
    };
    await setConnectionPhase(connId, nextPhaseCandidate, 'not_scheduled');
    // Zet planning lock zodat de andere user ziet dat er wordt gepland
    try {
      await setPlanningLock(connId, {
        status: 'planning',
        lockedByUid: user?.uid || null,
        lockedByUsername: user?.displayName || 'Connectie',
        lockReason: 'later_clicked',
        expiresAt: new Date(Date.now() + 15 * 60 * 1000),
      });
    } catch (err) {
      console.warn('Kon planning lock niet zetten', err);
    }
    setSelectedConnectionData((prev: any) =>
      prev
        ? {
            ...prev,
            phase: nextPhaseCandidate,
            phaseStatus: 'not_scheduled',
            activeEventId: null,
          }
        : prev
    );
    setShowAgenda(true);
    setAutoplanOpen(true);
    const hasSuggestions = await runAutoplan(true);
    if (!hasSuggestions) {
      setAutoplanOpen(false);
      setShowAgenda(true);
      setAutoplanError(null);
    }
    // Laat andere user weten dat er wordt gepland
    await notifyDeferred();
    setToastMessage('Planning geopend, kies een moment');
    setNextStepChoiceOpen(false);
  };

  // 📞 NEW: Direct bellen - Triggert de call fase meteen
  const handleDirectCall = async (overridePhase?: 'call' | 'video' | 'physical') => {
    const inferredPhaseFromSelection =
      selectedConnectionData?.phase && selectedConnectionData.phase !== 'physical'
        ? getNextPhase(selectedConnectionData.phase as ConnectionPhase)
        : null;
    const targetPhase = overridePhase ?? nextPhaseCandidate ?? inferredPhaseFromSelection;
    if (!targetPhase || !selectedConnectionData?.id || !user?.uid) {
      setNextStepChoiceOpen(false);
      return;
    }

    // Single-flight guard: prevent double-click race
    if (callProposalInFlightRef.current) {
      console.warn('[handleDirectCall] already in flight, ignoring duplicate call');
      return;
    }
    callProposalInFlightRef.current = true;

    // ── Physical: switch UI BEFORE any async Firestore write ─────────────
    // Firestore snapshots from the writes below arrive async. If currentPhase
    // is still 'video' when they land, the phase-sync has no valid guard and
    // can reset to 'chat' → visible flicker. Setting the phase here first
    // means the guard (isInitiatorWithPendingPhysical && currentPhase==='physical')
    // is already active before any snapshot arrives.
    if (targetPhase === 'physical') {
      setShowVideoCallInterface(false);
      setShowCallInterface(false);
      setShowJitsiCall(false);
      setCurrentPhase('physical');
      setActiveCard('physical');
    }
    // ─────────────────────────────────────────────────────────────────────

    const connId = selectedConnectionData.id;
    // connectionFlow might not be loaded yet when the NextStep modal is shown.
    // Derive the deterministic flow/chatRoom ID and ensure the flow doc exists.
    // We need the other participant UID to derive the deterministic flow ID.
    // Some UI states store only a minimal connection object, so fall back to
    // connectionFlow/currentChatRoom or a Firestore fetch when needed.
    let otherUserIdFromConn: string | undefined =
      selectedConnectionData.user1Id && selectedConnectionData.user2Id
        ? (selectedConnectionData.user1Id === user.uid
            ? selectedConnectionData.user2Id
            : selectedConnectionData.user1Id)
        : undefined;

    if (!otherUserIdFromConn) {
      otherUserIdFromConn =
        connectionFlow?.participants?.find((p) => p !== user.uid) ||
        currentChatRoom?.participants?.find((p) => p !== user.uid) ||
        undefined;
    }

    if (!otherUserIdFromConn) {
      try {
        const connSnap = await getDoc(doc(db, 'connections', connId));
        const connData: any = connSnap.exists() ? connSnap.data() : null;
        if (connData?.user1Id && connData?.user2Id) {
          otherUserIdFromConn =
            connData.user1Id === user.uid ? connData.user2Id : connData.user1Id;
        }
      } catch (e) {
        console.warn('[handleDirectCall] Could not load connection doc for participants:', e);
      }
    }

    if (!otherUserIdFromConn) {
      setToastMessage('❌ Kon de andere gebruiker niet bepalen. Open de connectie opnieuw en probeer nogmaals.');
      setNextStepChoiceOpen(false);
      callProposalInFlightRef.current = false;
      return;
    }
    const participantIds = [user.uid, otherUserIdFromConn].filter(Boolean).sort();

    // Resolve the REAL shared room/flow id first to avoid cross-user mismatches.
    // Priority:
    // 1) currently loaded connectionFlow/currentChatRoom
    // 2) existing chatRoom document for these 2 participants
    // 3) deterministic fallback (legacy safety net)
    let resolvedFlowId: string | null =
      connectionFlow?.id ||
      currentChatRoom?.id ||
      null;

    if (!resolvedFlowId) {
      try {
        const roomsSnap = await getDocs(
          query(collection(db, 'chatRooms'), where('participants', 'array-contains', user.uid))
        );
        const existingRoom = roomsSnap.docs.find((d) => {
          const data: any = d.data();
          const roomParticipants = Array.isArray(data?.participants) ? data.participants : [];
          return roomParticipants.length === 2 && roomParticipants.includes(otherUserIdFromConn);
        });
        if (existingRoom) {
          resolvedFlowId = existingRoom.id;
        }
      } catch (e) {
        console.warn('[handleDirectCall] Could not resolve existing chatRoom id:', e);
      }
    }

    if (!resolvedFlowId && participantIds.length >= 2) {
      resolvedFlowId = `chat_${participantIds[0]}_${participantIds[1]}`;
    }

    if (resolvedFlowId && participantIds.length >= 2) {
      try {
        await findOrCreateConnectionFlow(resolvedFlowId, participantIds as string[]);
      } catch (e) {
        console.warn('[handleDirectCall] Could not ensure connectionFlow exists:', e);
      }
    }

    const flowId = resolvedFlowId;
    if (!flowId) {
      setToastMessage('❌ Kon chat/flow niet bepalen. Open de connectie opnieuw en probeer nogmaals.');
      setNextStepChoiceOpen(false);
      callProposalInFlightRef.current = false;
      return;
    }
    
    
    try {
      // 1. Atomically guard the phase transition — prevents phase skipping and
      //    duplicate writes when both users click simultaneously.
      //    Derive the expected current phase from ALLOWED_TRANSITIONS (reverse lookup).
      const expectedPhase = (Object.entries(ALLOWED_TRANSITIONS) as [ConnectionPhase, ConnectionPhase[]][])
        .find(([, targets]) => targets.includes(targetPhase))?.[0] ?? 'chat';
      const transitionResult = await transitionPhaseGuarded(connId, expectedPhase, targetPhase, { phaseStatus: 'ready' });
      if (!transitionResult.success) {
        // Phase already at target (idempotent) or race — still proceed with flow doc update
        if (process.env.NODE_ENV === 'development') {
          console.warn('[handleDirectCall] transitionPhaseGuarded non-success:', transitionResult.reason);
        }
      }
      
      // 2. Determine step key (callStep, videoStep, or physicalStep)
      const stepKey =
        targetPhase === 'call' ? 'callStep' :
        targetPhase === 'video' ? 'videoStep' :
        targetPhase === 'physical' ? 'physicalStep' :
        null;
      
      if (!stepKey) {
        console.error('❌ Invalid phase for direct call:', targetPhase);
        return;
      }
      
      // 3. Update connectionFlow - set as PENDING (waiting for other user)
      const flowRef = doc(db, 'connectionFlows', flowId);
      const acceptances: { [key: string]: boolean } = {};
      // ✅ FIX: Only mark initiator as accepted, so other user can still accept
      acceptances[user.uid] = true;
      
      // ✅ FIX: Generate meetUrl for video calls (was missing!)
      let videoMeetUrl: string | null = null;
      if (targetPhase === 'video') {
        try {
          videoMeetUrl = await meetService.createMeetUrlForFlow(
            flowId,
            'video',
            user?.email || undefined,
            user?.uid,
            user?.displayName || user?.email || 'Gebruiker'
          );
        } catch (err) {
          console.error('❌ [Direct Call] Failed to generate video URL:', err);
        }
      }

      const updatePayload: any = {
        [`${stepKey}.status`]: 'pending',
        [`${stepKey}.proposedBy`]: user.uid, // Current user is initiator
        [`${stepKey}.acceptances`]: acceptances,
        [`${stepKey}.duration`]: 480, // 8 minuten
        // ✅ FIX: Set currentStep zodat de realtime listener bij BEIDE users de juiste fase toont
        currentStep: targetPhase,
        // Overall flow status is used for tab filtering (pending/active/completed/terminated)
        status: 'pending',
        updatedAt: serverTimestamp()
      };

      // ✅ Include meetUrl in same atomic write for video calls
      if (targetPhase === 'video' && videoMeetUrl) {
        updatePayload[`${stepKey}.meetUrl`] = videoMeetUrl;
      }

      await updateDoc(flowRef, updatePayload);
      
      if (targetPhase === 'physical') {
        // Notify the other user that physical planning has started.
        try {
          const otherUserIdForPhysical = selectedConnectionData.user1Id === user.uid
            ? selectedConnectionData.user2Id
            : selectedConnectionData.user1Id;
          if (otherUserIdForPhysical) {
            await addDoc(collection(db, 'planningNotifications'), {
              toUserId: otherUserIdForPhysical,
              fromUserId: user.uid,
              fromUserName: user.displayName || user.email?.split('@')[0] || 'Je connectie',
              connectionId: connId,
              type: 'physical_phase_started',
              message: `${user.displayName || 'Je connectie'} is de fysieke afspraak aan het plannen.`,
              read: false,
              createdAt: serverTimestamp()
            });
          }
        } catch (e) {
          console.warn('[Physical] Could not write physical_phase_started notification:', e);
        }
      }
      
      // 4. Update local selectedConnectionData (non-phase fields only — phase comes from snapshot)
      if (isMountedRef.current) {
        setSelectedConnectionData((prev: any) =>
          prev
            ? {
                ...prev,
                phase: targetPhase,
                phaseStatus: 'ready',
                activeEventId: null,
              }
            : prev
        );
      }
      
      // Guard: bail out if component unmounted during await
      if (!isMountedRef.current) return;

      // 5. Start outgoing call tone and show interface for audio calls
      if (targetPhase === 'call') {
        try {
          if (outgoingToneRef.current) {
            try {
              outgoingToneRef.current.pause();
              outgoingToneRef.current.currentTime = 0;
            } catch {}
          }
          
          if (!outgoingToneRef.current) {
            const audio = new Audio('/sounds/outgoing-call.mp3');
            audio.loop = true;
            outgoingToneRef.current = audio;
          }
          outgoingToneRef.current.currentTime = 0;
          const playPromise = outgoingToneRef.current.play();
          if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(err => {
              console.warn('Outgoing-call sound blocked by browser:', err);
            });
          }
        } catch (toneErr) {
          console.warn('Kon outgoing-call geluid niet afspelen:', toneErr);
        }
        
        setCallStatus('initiating');
        setShowCallInterface(true); // Show interface for initiator — phase sync useEffect will follow
        
        // Set currentCall for UI data
        const otherUserName = selectedConnectionData?.name || 'Onbekend';
        const otherUserId = otherUserIdFromConn || '';
        setCurrentCall({
          id: flowId,
          type: 'audio',
          participants: [user.uid, otherUserId],
          userName: otherUserName,
          startTime: new Date(),
          status: 'initiating'
        });
        
      }
      
      // 5b. Show video interface immediately for video call initiator
      if (targetPhase === 'video') {
        // Stop WebRTC audio call first (if any)
        if (webrtcAudioCall && webrtcAudioCall.call) {
          webrtcAudioCall.endCall();
        }
        
        // Ensure phase is set immediately so VideoPhase can render (it also needs currentPhase === 'video')
        setCurrentPhase('video');
        setShowVideoCallInterface(true);
        setShowCallInterface(false);
        setShowJitsiCall(false);
      }

      // 5c. Physical phase UI was already switched at the top of handleDirectCall
      //     (before the first async write) so that phase-sync guards are active
      //     during all Firestore operations. Nothing to do here.
      
      setNextStepChoiceOpen(false);
      setToastMessage(`🎉 ${targetPhase === 'call' ? 'Bellen' : targetPhase === 'video' ? 'Videobellen' : 'Afspraak'} gestart!`);
      
      // 6. Optimistic local connectionFlow update (snapshot will confirm shortly)
      if (connectionFlow) {
        const existingStepData = connectionFlow[stepKey as keyof ConnectionFlow] || {};
        const stepUpdate: any = {
          ...(typeof existingStepData === 'object' && existingStepData !== null ? existingStepData : {}),
          status: 'pending',
          proposedBy: user.uid,
          acceptances: acceptances,
          duration: 480
        };
        if (targetPhase === 'video' && videoMeetUrl) {
          stepUpdate.meetUrl = videoMeetUrl;
        }
        const updatedFlow = {
          ...connectionFlow,
          [stepKey]: stepUpdate,
          status: 'pending'
        };
        setConnectionFlow(updatedFlow as ConnectionFlow);
      }
      
      // 7. WebRTC hook will automatically start when it detects callStep.status === 'active'
      // The other user will see incoming popup via real-time listener
      
    } catch (error) {
      console.error('❌ Error starting direct call:', error);
      if (isMountedRef.current) setToastMessage('❌ Fout bij starten call');
    } finally {
      callProposalInFlightRef.current = false;
    }
  };

  // ⏸️ NEW: Afbreken - Breekt de flow af + stuurt notificaties naar beide users
  const handleAbortFlow = async () => {
    if (!selectedConnectionData?.id) {
      setNextStepChoiceOpen(false);
      return;
    }
    
    const connId = selectedConnectionData.id;
    const otherUserId =
      selectedConnectionData.user1Id === user?.uid
        ? selectedConnectionData.user2Id
        : selectedConnectionData.user1Id;
    
    try {
      
      // ✅ FIX: Mark connectionFlow as terminated so other user gets notified
      if (connectionFlow?.id) {
        const flowRef = doc(db, 'connectionFlows', connectionFlow.id);
        await updateDoc(flowRef, {
          status: 'terminated',
          endedBy: user?.uid,
          endedAt: serverTimestamp(),
          endReason: 'aborted',
          // Reset currentStep so both users exit the active phase
          currentStep: 'chat',
          updatedAt: serverTimestamp()
        });
      }
      
      // Reset phase status to 'not_scheduled'. Read the actual phase from Firestore
      // rather than trusting local state — prevents using a stale phase if the
      // connection document was already updated by the other user concurrently.
      try {
        const freshSnap = await getDoc(doc(db, 'connections', connId));
        const freshPhase: ConnectionPhase =
          (freshSnap.exists() && freshSnap.data()?.phase) || selectedConnectionData.phase || 'chat';
        await setConnectionPhase(connId, freshPhase, 'not_scheduled');
      } catch (phaseErr) {
        console.warn('[handleAbortFlow] Could not reset phase status (non-critical):', phaseErr);
      }
      
      // Stuur notificatie naar BEIDE users (de ander + jezelf voor later)
      const notificationTitle = 'Connectie flow afgebroken';
      const notificationMessage = `${user?.displayName || 'De ander'} heeft de connectie flow tijdelijk afgebroken. Je kunt deze later weer oppakken via Netwerk > Connecties.`;
      
      // Notificatie voor de andere user
      if (otherUserId) {
        await addDoc(collection(db, 'inboxItems'), {
          userId: otherUserId,
          type: 'reminder',
          category: 'connection',
          title: notificationTitle,
          message: notificationMessage,
          actionData: {
            actionType: 'accept_connection',
            connectionId: connId,
            connectionName: selectedConnectionData?.name,
            otherUserId: user?.uid
          },
          read: false,
          createdAt: serverTimestamp()
        });
      }
      
      // Notificatie voor jezelf (herinnering)
      await addDoc(collection(db, 'inboxItems'), {
        userId: user?.uid || '',
        type: 'reminder',
        category: 'connection',
        title: 'Connectie flow onderbroken',
        message: `Je hebt de connectie flow met ${selectedConnectionData?.name || 'deze persoon'} onderbroken. Pak deze later weer op via Netwerk > Connecties.`,
        actionData: {
          actionType: 'accept_connection',
          connectionId: connId,
          connectionName: selectedConnectionData?.name,
          otherUserId: otherUserId
        },
        read: false,
        createdAt: serverTimestamp()
      });
      
      setToastMessage('⏸️ Connectie flow afgebroken. Beide partijen ontvangen een herinnering.');
      setNextStepChoiceOpen(false);
      
      // Sluit de connectie flow UI
      setTimeout(() => {
        setSelectedConnection('');
        setSelectedConnectionData(null);
      }, 1500);
      
    } catch (error) {
      console.error('[abort-flow] Error aborting flow:', error);
      setToastMessage('❌ Fout bij afbreken. Probeer het opnieuw.');
    }
  };

  // 🔚 NEW: Beëindigen - Beëindigt de connectie + stuurt notificatie naar andere user
  const handleEndConnection = async (reason?: string) => {
    if (!selectedConnectionData?.id || !user?.uid) return;
    
    const connId = selectedConnectionData.id;
    const otherUserId = selectedConnectionData.user1Id === user.uid 
      ? selectedConnectionData.user2Id 
      : selectedConnectionData.user1Id;
    
    try {
      // Update connection status
      await updateConnectionStatus(connId, 'completed');
      
      // Stuur notificatie naar andere user
      if (otherUserId) {
        await addDoc(collection(db, 'inboxItems'), {
          userId: otherUserId,
          type: 'reminder',
          category: 'connection',
          title: 'Connectie beëindigd',
          message: `${user?.displayName || 'De andere gebruiker'} heeft de connectie beëindigd.`,
          actionData: {
            actionType: 'connection_ended',
            connectionId: connId,
            connectionName: selectedConnectionData?.name,
            otherUserId: user?.uid,
            reason: reason || null
          },
          read: false,
          createdAt: serverTimestamp()
        });
      }
      
      // Update connectionFlow om andere user te laten weten
      if (connectionFlow?.id) {
        const flowRef = doc(db, 'connectionFlows', connectionFlow.id);
        await updateDoc(flowRef, {
          endedBy: user.uid,
          endedAt: serverTimestamp(),
          endReason: reason || null,
          status: 'terminated'
        });
      }
      
      setToastMessage('✅ Connectie beëindigd. De andere gebruiker is op de hoogte gesteld.');
      timerExpiredDismissedKeyRef.current = 'connection_ended'; // Voorkom heropening
      setShowTimerExpiredPopup(false);
      
      // Sluit de connectie flow UI
      setTimeout(() => {
        setSelectedConnection('');
        setSelectedConnectionData(null);
      }, 1500);
      
    } catch (error) {
      console.error('[end-connection] Error ending connection:', error);
      setToastMessage('❌ Fout bij beëindigen. Probeer het opnieuw.');
    }
  };

  // 📅 NEW: Plannen via agenda - Opent ExtendedAgenda met planning lock systeem
  // useCallback keeps the reference stable so PhysicalMeetingPhaseV2 (React.memo) doesn't re-render.
  const handlePlanViaAgenda = React.useCallback(async () => {
    if (!selectedConnectionData?.id) {
      setNextStepChoiceOpen(false);
      return;
    }
    
    const connId = selectedConnectionData.id;
    
    // Determine the phase to use for planning lock
    // If called from NextStepChoice modal: use nextPhaseCandidate
    // If called directly from "Plannen" button: use current phase
    const phaseForLock = (nextPhaseCandidate || selectedConnectionData.phase || 'call') as ConnectionPhase;
    
    // Acquire planning lock BEFORE opening agenda
    // Note: displayName can be empty string ("") which is falsy — use fallback
    const displayNameForLock = user?.displayName || user?.email?.split('@')[0] || 'Gebruiker';
    if (user?.uid) {
      try {
        const lockResult = await acquirePlanningLock(
          connId,
          user.uid,
          displayNameForLock,
          phaseForLock
        );
        setPlanningLockResult(lockResult);
        
        // Open agenda regardless (observer mode will be handled in ExtendedAgenda)
        setShowAgenda(true);
        setNextStepChoiceOpen(false);
        setToastMessage(lockResult.granted ? 'Agenda geopend - jij plant nu' : 'Agenda geopend - observeer modus');

        // Load confirmed meeting location for this connection (if any)
        try {
          const connSnap = await getDoc(doc(db, 'connections', connId));
          const connData = connSnap.data();
          const selLoc = connData?.physicalMeeting?.selectedLocation;
          if (selLoc?.location) {
            setConfirmedMeetingLocation({
              name: selLoc.location.name || selLoc.location.address || 'Ontmoetingslocatie',
              address: selLoc.location.address || '',
            });
          }
        } catch (e) {
          console.warn('[handlePlanViaAgenda] Could not load confirmed location:', e);
        }

        // Notify the other user via Firestore so they get a toast + auto-open agenda
        if (lockResult.granted) {
          const otherUserId = selectedConnectionData.user1Id === user.uid
            ? selectedConnectionData.user2Id
            : selectedConnectionData.user1Id;
          if (otherUserId) {
            try {
              await addDoc(collection(db, 'planningNotifications'), {
                toUserId: otherUserId,
                fromUserId: user.uid,
                fromUserName: displayNameForLock,
                connectionId: connId,
                type: 'planning_started',
                createdAt: serverTimestamp(),
                read: false,
              });
            } catch (e) {
              console.warn('[planning-lock] Could not write planningNotification:', e);
            }
          }
      }
    } catch (error) {
      console.error('[planning-lock] Failed to acquire lock:', error);
      setToastMessage('❌ Fout bij openen agenda: ' + (error as Error).message);
    }
  } else {
    // Fallback if no user info
    setShowAgenda(true);
    setNextStepChoiceOpen(false);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConnectionData?.id, selectedConnectionData?.user1Id, selectedConnectionData?.user2Id, selectedConnectionData?.phase, user?.uid, user?.displayName, user?.email, nextPhaseCandidate, activeEvent]);

  // Auto-acquire planning lock whenever the agenda opens without an existing lock.
  // This is a safety net for all the places that call setShowAgenda(true) directly
  // without going through handlePlanViaAgenda(). When the initiating user opens the
  // agenda, the lock must be acquired so the other user gets notified automatically.
  const agendaAutoLockRef = useRef<string | null>(null);
  useEffect(() => {
    if (!showAgenda) {
      agendaAutoLockRef.current = null;
      return;
    }
    if (!selectedConnectionData?.id || !user?.uid) return;
    // Skip if a lock result is already known (handlePlanViaAgenda already ran, or observer mode)
    if (planningLockResult !== null) return;
    // Skip if we already ran auto-lock for this connection in this open session
    if (agendaAutoLockRef.current === selectedConnectionData.id) return;

    agendaAutoLockRef.current = selectedConnectionData.id;
    const connId = selectedConnectionData.id;
    // displayName can be empty string "" (falsy) — always use a fallback
    const displayName = user.displayName || user.email?.split('@')[0] || 'Gebruiker';
    const phaseForLock = (selectedConnectionData.phase || 'physical') as ConnectionPhase;

    acquirePlanningLock(connId, user.uid, displayName, phaseForLock)
      .then((lockResult) => {
        setPlanningLockResult(lockResult);
        if (lockResult.granted) {
          const otherUserId = selectedConnectionData.user1Id === user.uid
            ? selectedConnectionData.user2Id
            : selectedConnectionData.user1Id;
          if (otherUserId) {
            addDoc(collection(db, 'planningNotifications'), {
              toUserId: otherUserId,
              fromUserId: user.uid,
              fromUserName: displayName,
              connectionId: connId,
              type: 'planning_started',
              createdAt: serverTimestamp(),
              read: false,
            }).catch((e) => console.warn('[planning-lock] Auto-lock notification failed:', e));
          }
        }
      })
      .catch((err) => console.warn('[planning-lock] Auto-acquire failed:', err));
  }, [showAgenda, planningLockResult, selectedConnectionData?.id, user?.uid]);

  // Handle Esc/Enter for the choice modal
  useEffect(() => {
    if (!nextStepChoiceOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setNextStepChoiceOpen(false);
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        handleChooseNow();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [nextStepChoiceOpen, handleChooseNow]);

  const finishAndChooseNext = async (source: string, skipReflectionCheck = false) => {
    if (!selectedConnectionData?.id) return;
    
    // NOTE: hasMismatchedActiveEvent check removed here
    // "Volgende" button should always show NextStepChoice modal
    // Mismatch is handled elsewhere (at scheduling time, not during phase transition)

    const currentPhase: ConnectionPhase = selectedConnectionData?.phase ?? 'chat';

    const handledKnown = await handleKnownConnectiesCompletion({
      currentPhase,
      connectionId: selectedConnectionData.id,
      selectedConnectionData,
      setSelectedConnectionData,
      setCompletionNotice,
      setToastMessage,
      setNextStepChoiceOpen
    });
    if (handledKnown) return;

    // Fysieke fase vereist samenvatting voor afronden
    if (currentPhase === 'physical' && !skipReflectionCheck) {
      setPendingPostReflectionAction(source);
      setShowMeetupReflection(true);
      return;
    }

    setCompletionNotice(null);
    setCompletingPhase(true);
    try {
      // ✅ Haal de volgende fase rechtstreeks uit Firestore via completeCurrentPhase (voorkomt race op local state)
      let nextPhase = await completeCurrentPhase(selectedConnectionData.id);

      // Trigger AI analysis popup only for physical phase.
      // For call/video this popup interrupts the direct next-step flow.
      if (currentPhase === 'physical') {
        setCompletedConnectionId(selectedConnectionData.id);
        setCompletedPhase(currentPhase);
        setShowAnalysisTrigger(true);
      }

      // 🔄 Fallback: check direct in Firestore of fase al is bijgewerkt (bijv. door andere client)
      if (!nextPhase) {
        try {
          const connSnap = await getDoc(doc(db, 'connections', selectedConnectionData.id));
          const remotePhase = connSnap.exists() ? (connSnap.data()?.phase as ConnectionPhase | undefined) : undefined;
          if (remotePhase && remotePhase !== currentPhase) {
            nextPhase = remotePhase;
          }
        } catch (err) {
          console.warn('[finishAndChooseNext] Failed to read connection after completion', err);
        }
      }

      // 🔄 Laatste fallback: bereken lokaal volgende fase (voorkomt no-op)
      if (!nextPhase) {
        nextPhase = getNextPhase(currentPhase);
      }

      // Geen volgende fase beschikbaar (bijv. chat of flow is al afgerond)
      if (!nextPhase) {
        setCompletingPhase(false);
        return;
      }

      // Guard: bail out if component unmounted during the async completeCurrentPhase call
      if (!isMountedRef.current) return;

      // ── Completed phase: show the connection-complete screen ─────────────
      if (nextPhase === 'completed') {
        setCurrentPhase('completed');
        setSelectedConnectionData((prev: any) =>
          prev ? { ...prev, phase: 'completed', phaseStatus: 'done' } : prev
        );
        setCompletingPhase(false);
        return;
      }

      setAutoplanError(null);
      setCompletionNotice('Fase afgerond — kies hoe je de volgende stap wil oppakken.');

      // Update local selectedConnectionData to keep UI responsive while snapshot propagates
      setSelectedConnectionData((prev: any) =>
        prev
          ? {
              ...prev,
              phase: nextPhase,
              phaseStatus: 'not_scheduled',
              activeEventId: null,
            }
          : prev
      );

      // For chat → call: skip the modal and directly propose the call
      if (currentPhase === 'chat' && nextPhase === 'call') {
        setNextPhaseCandidate('call');
        await handleDirectCall('call');
      } else {
        // ✅ FIX: Do NOT open NextStepModal for User 2 when the other user has
        // already started a pending step. User 2 should see the IncomingCallPopup /
        // IncomingVideoCallPopup instead — the realtime listener handles that.
        // Only suppress the NextStep modal when there is a *real* actionable
        // incoming pending step for me (proposed by the other user AND I have
        // not accepted it yet). This prevents false negatives where "Volgende"
        // appears to do nothing due to stale pending data.
        const hasIncomingPendingStep =
          (connectionFlow?.videoStep?.status === 'pending' &&
            !!connectionFlow?.videoStep?.proposedBy &&
            connectionFlow?.videoStep?.proposedBy !== user?.uid &&
            !connectionFlow?.videoStep?.acceptances?.[user?.uid || '']) ||
          (connectionFlow?.callStep?.status === 'pending' &&
            !!connectionFlow?.callStep?.proposedBy &&
            connectionFlow?.callStep?.proposedBy !== user?.uid &&
            !connectionFlow?.callStep?.acceptances?.[user?.uid || '']) ||
          (connectionFlow?.physicalStep?.status === 'pending' &&
            !!connectionFlow?.physicalStep?.proposedBy &&
            connectionFlow?.physicalStep?.proposedBy !== user?.uid &&
            !connectionFlow?.physicalStep?.acceptances?.[user?.uid || '']);

        if (!hasIncomingPendingStep) {
          openNextStepChoice(nextPhase, source);
        } else {
        }
      }
      if (isMountedRef.current) setRelationManagerPrompted(true);
    } catch (err) {
      console.error('completeCurrentPhase error', err);
      if (isMountedRef.current) setAutoplanError('Kon fase niet afronden. Probeer opnieuw of open planning.');
    } finally {
      if (isMountedRef.current) setCompletingPhase(false);
    }
  };

  const handleCancelAppointment = async () => {
    if (!selectedConnectionData?.id) return;
    setCancelNotice(null);
    setAutoplanError(null);
    setCancelling(true);
    try {
      await cancelActiveAppointment(selectedConnectionData.id);
      setCancelNotice('Afspraak geannuleerd. Plan een nieuwe tijd.');
    } catch (err: any) {
      console.error('cancelActiveAppointment error', err);
      if (err?.message === 'MISMATCH_EVENT') {
        setAutoplanError('Deze afspraak hoort niet bij deze connectie. Open planning om te verplaatsen.');
        handleConnectionCTA('agenda');
      } else {
        setAutoplanError('Kon de afspraak niet annuleren. Probeer opnieuw of open planning.');
      }
    } finally {
      setCancelling(false);
    }
  };

  const getAvailabilitySlotsForUser = (userId: string): Slot[] => {
    const networking = flowCalendarEvents.filter(
      (e) =>
        e.type === 'networking' &&
        Array.isArray(e.participants) &&
        e.participants.includes(userId)
    );
    return networking.map((e) => ({
      start: e.startTime,
      end: e.endTime,
    }));
  };

  const runAutoplan = async (suppressErrors = false): Promise<boolean> => {
    if (!user || !selectedConnectionData) return false;
    if (hasActiveScheduled(selectedConnectionData)) {
      if (!suppressErrors) setAutoplanError('Er staat al een afspraak gepland. Open planning om te verplaatsen.');
      setAutoplanResults([]);
      setSelectedSlot(null);
      return false;
    }
    setAutoplanLoading(true);
    if (!suppressErrors) setAutoplanError(null);
    setAutoplanResults([]);
    setSelectedSlot(null);

    try {
      if (selectedConnectionData.phase !== 'call') {
        await setConnectionPhase(selectedConnectionData.id, 'call', selectedConnectionData.phaseStatus ?? 'not_scheduled');
      }

      const otherUserId = selectedConnectionData.user1Id === user.uid
        ? selectedConnectionData.user2Id
        : selectedConnectionData.user1Id;

      if (!otherUserId) {
        if (!suppressErrors) setAutoplanError('Geen andere gebruiker gevonden voor deze connectie.');
        return false;
      }

      const slotsA = getAvailabilitySlotsForUser(user.uid);
      const slotsB = getAvailabilitySlotsForUser(otherUserId);

      const overlaps = intersectSlots(slotsA, slotsB, 20); // 20 min minimum for call
      const top = pickTopSlots(overlaps, 5);

      if (!top.length) {
        if (!suppressErrors) setAutoplanError('Geen overlap gevonden. Stuur een voorstel.');
        setAutoplanResults([]);
        setSelectedSlot(null);
        return false;
      }

      setAutoplanResults(top);
      setAutoplanError(null);
      setSelectedSlot(top[0] || null);
      return true;
    } catch (err: any) {
      console.error('Autoplan error', err);
      if (!suppressErrors) setAutoplanError('Autoplan mislukt. Probeer opnieuw.');
      setAutoplanResults([]);
      setSelectedSlot(null);
      return false;
    } finally {
      setAutoplanLoading(false);
    }
  };

  const createEventFromSlot = async (slot: Slot) => {
    if (!user || !selectedConnectionData?.id) return;
    if (hasActiveScheduled(selectedConnectionData)) {
      setAutoplanError('Er staat al een afspraak gepland. Open planning om te verplaatsen.');
      return;
    }
    const connectionId = selectedConnectionData.id;
    const otherUserId = selectedConnectionData.user1Id === user.uid
      ? selectedConnectionData.user2Id
      : selectedConnectionData.user1Id;
    if (!otherUserId) {
      setAutoplanError('Geen andere gebruiker gevonden.');
      return;
    }
    try {
      const { addDoc, collection, serverTimestamp } = await import('firebase/firestore');
      const eventData = {
        title: `Call met ${selectedConnectionData?.name || 'Connectie'}`,
        description: 'Automatisch geplande call via Autoplan',
        startTime: slot.start,
        endTime: slot.end,
        type: 'call',
        category: 'work',
        source: 'superkonnected',
        participants: [user.uid, otherUserId],
        participantNames: [user.displayName || '', selectedConnectionData?.name || ''],
        status: 'confirmed',
        createdBy: user.uid,
        createdAt: serverTimestamp(),
        reminderEnabled: true,
        reminderMinutesBefore: 10,
        reminderSent: false,
        connectionId,
        phase: 'call',
        lockedToConnectionFlow: true
      };
      const docRef = await addDoc(collection(db, 'calendarEvents'), eventData);
      await linkActiveEvent(connectionId, docRef.id);
      await setConnectionPhaseStatus(connectionId, 'scheduled');
      await setConnectionPhase(connectionId, 'call', 'scheduled');
      setSelectedSlot(slot);
      setAutoplanError(null);
      setToastMessage(
        `Call ingepland op ${slot.start.toLocaleString('nl-NL', {
          weekday: 'short',
          day: 'numeric',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit'
        })}`
      );
      // Notify other user via inbox
      try {
        const otherUserIdSafe = otherUserId;
        if (otherUserIdSafe) {
          await addDoc(collection(db, 'inboxItems'), {
            userId: otherUserIdSafe,
            type: 'reminder',
            category: 'connection',
            title: 'Nieuwe belafspraak gepland',
            message: `Er is een call ingepland voor ${slot.start.toLocaleString('nl-NL', {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
              hour: '2-digit',
              minute: '2-digit'
            })}.`,
            actionData: {
              actionType: 'accept_connection',
              connectionId,
              connectionName: selectedConnectionData?.name,
              otherUserId: user.uid
            },
            isRead: false,
            createdAt: serverTimestamp()
          });
        }
      } catch (err) {
        console.warn('Kon inbox-melding voor ingeplande call niet sturen:', err);
      }
      // Fallback toast for Autoplan result
    } catch (err) {
      console.error('Error creating event from slot', err);
      setAutoplanError('Kon event niet aanmaken.');
    }
  };

  const submitProposal = async () => {
    if (!user || !selectedConnectionData?.id) return;
    const connectionId = selectedConnectionData.id;
    const otherUserId = selectedConnectionData.user1Id === user.uid
      ? selectedConnectionData.user2Id
      : selectedConnectionData.user1Id;
    if (!otherUserId) {
      setAutoplanError('Geen andere gebruiker gevonden.');
      return;
    }

    const slots = proposalSlots
      .map((s) => {
        if (!s.start || !s.end) return null;
        const start = new Date(s.start);
        const end = new Date(s.end);
        if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
        return { startTime: start, endTime: end };
      })
      .filter(Boolean) as { startTime: Date; endTime: Date }[];

    if (slots.length === 0) {
      setAutoplanError('Voeg minstens 1 geldige tijdslot toe.');
      return;
    }

    try {
      await createProposal(connectionId, user.uid, otherUserId, 'call', slots, proposalMessage);
      setProposalOpen(false);
      setProposalSlots([{ start: '', end: '' }]);
      setProposalMessage('');
      setAutoplanError(null);
    } catch (err) {
      console.error('proposal create error', err);
      setAutoplanError('Kon voorstel niet versturen.');
    }
  };

  // Dev-only helper: advance the journey to the next phase via completeCurrentPhase.
  // Uses the same transactional path as the real flow so it stays consistent.
  // Only visible in the dev panel (rendered only in NODE_ENV === 'development').
  const advanceJourney = async (conn: any) => {
    if (!conn?.id) return;
    if (process.env.NODE_ENV !== 'development') return; // guard: never runs in production
    try {
      const nextPhase = await completeCurrentPhase(conn.id);
      if (nextPhase) {
        setSelectedConnectionData((prev: any) =>
          prev ? { ...prev, phase: nextPhase, phaseStatus: 'not_scheduled' } : prev
        );
        if (nextPhase === 'completed') {
          setCurrentPhase('completed');
        }
      }
    } catch (err) {
      console.warn('advanceJourney failed', err);
    }
  };
  
  // Google account check states
  const [hasGoogleProvider, setHasGoogleProvider] = useState(false);
  const [showGoogleAccountRequired, setShowGoogleAccountRequired] = useState(false);
  const [isLinkingGoogle, setIsLinkingGoogle] = useState(false);

  // Check if user has Google provider
  useEffect(() => {
    if (user) {
      const hasGoogle = user.providerData.some(provider => provider.providerId === 'google.com');
      setHasGoogleProvider(hasGoogle);
      
      // Show warning if user doesn't have Google provider (but only once, not every time)
      if (!hasGoogle && !localStorage.getItem('googleAccountWarningShown')) {
        setShowGoogleAccountRequired(true);
      }
    } else {
      setHasGoogleProvider(false);
      setShowGoogleAccountRequired(false);
    }
  }, [user]);

  // Listen for deep link to region settings
  useEffect(() => {
    const handleOpenRegionSettings = () => {
      // Only show the region selection, NOT the WorkScope modal
      setShowRegionOptions(true);
      // Scroll to the region section
      setTimeout(() => {
        const regionSection = document.querySelector('[data-region-selection]');
        if (regionSection) {
          regionSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 200);
    };

    window.addEventListener('openRegionSettings', handleOpenRegionSettings);
    return () => {
      window.removeEventListener('openRegionSettings', handleOpenRegionSettings);
    };
  }, []);

  const handleRegionToggle = (region: string) => {
    setSelectedRegions(prev => {
      const newRegions = prev.includes(region) 
        ? prev.filter(r => r !== region)
        : [...prev, region];
      
      // Mark as unsaved
      setSettingsSaved(false);
      
      return newRegions;
    });
  };

  const saveRegionSettings = async () => {
    if (!user?.uid) return;
    
    setSavingSettings(true);
    setSettingsSaved(false);
    
    try {
      await saveRegionPreferences(selectedRegions);
      
      // Show success message
      setSettingsSaved(true);
      setTimeout(() => {
        setSettingsSaved(false);
      }, 3000);
    } catch (error) {
      console.error('Error saving region settings:', error);
      showError('Er is een fout opgetreden bij het opslaan.');
    } finally {
      setLoading('savingSettings', false);
    }
  };

  // Link Google account to current user
  // Note: Firebase doesn't support linking accounts via popup easily
  // User needs to log out and log back in with Google to link accounts
  const handleLinkGoogleAccount = async () => {
    if (!user) return;
    
    const confirmMessage = 'Om je Google account te koppelen, moet je uitloggen en opnieuw inloggen met Google.\n\nWil je nu uitloggen en opnieuw inloggen met Google?';
    
    if (window.confirm(confirmMessage)) {
      try {
        const { signOut, signInWithPopup } = await import('firebase/auth');
        await signOut(auth);
        // After sign out, redirect to login and show Google sign-in option
        // The user will need to sign in with Google on the login page
        window.location.reload();
      } catch (error: any) {
        console.error('❌ Error during logout:', error);
        showError('Er is een fout opgetreden. Probeer handmatig uit te loggen en opnieuw in te loggen met Google.');
      }
    }
  };

  // Connect Google Calendar only (without linking account)
  const handleConnectGoogleCalendar = async () => {
    setIsLinkingGoogle(true);
    try {
      await connectGoogleCalendar();
      setShowGoogleAccountRequired(false);
      localStorage.setItem('googleAccountWarningShown', 'true');
      success('Google Calendar succesvol gekoppeld!');
    } catch (error: any) {
      console.error('❌ Error connecting Google Calendar:', error);
      showError('Fout bij koppelen Google Calendar: ' + (error.message || 'Onbekende fout'));
    } finally {
      setIsLinkingGoogle(false);
    }
  };

  const handleSetOpenForConnections = async () => {
    if (!user?.uid) {
      warning('Je moet ingelogd zijn om je open te stellen voor connecties.');
      return;
    }
    
    setSavingSettings(true);
    
    try {
      const { default: openForConnectionsService } = await import('../services/openForConnectionsService');
      
      // Get user profile data
      const userData = {
        userName: user.displayName || 'Onbekend',
        userEmail: user.email || '',
        userPhoto: user.photoURL || '',
        userRegion: selectedRegions.join(', ') || 'Nederland',
        userCompany: 'Onbekend', // TODO: Get from user profile
        userBio: 'Ondernemer op SuperKonnected', // TODO: Get from user profile
        subscriptionTier: 'free' as const // TODO: Get from user subscription
      };
      
      await openForConnectionsService.setOpenForConnections(user.uid, userData);
      setIsOpenForConnections(true);
      
      // Show success
      setSettingsSaved(true);
      setTimeout(() => {
        setSettingsSaved(false);
      }, 3000);
    } catch (error) {
      console.error('Error setting open for connections:', error);
      showError('Er is een fout opgetreden. Probeer het opnieuw.');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleCloseConnectionFlow = async () => {
    if (!user?.uid) return;
    
    setSavingSettings(true);
    
    try {
      const { default: openForConnectionsService } = await import('../services/openForConnectionsService');
      await openForConnectionsService.closeForConnections(user.uid);
      setIsOpenForConnections(false);
      setShowConnectionMenu(false);
      
      success('Je staat niet meer open voor nieuwe connecties.');
    } catch (error) {
      console.error('Error closing connection flow:', error);
      showError('Er is een fout opgetreden.');
    } finally {
      setSavingSettings(false);
    }
  };

  // Save selected regions to user profile - gekoppeld aan onboarding data
  const saveRegionPreferences = async (regions: string[]) => {
    if (!user?.uid) return;
    
    try {
      const { doc, updateDoc, serverTimestamp } = await import('firebase/firestore');
      const userRef = doc(db, 'userProfiles', user.uid);
      
      // Update both selectedRegions (for quick access) and onboardingData.eventPrefs.regions
      await updateDoc(userRef, {
        selectedRegions: regions,
        'onboardingData.eventPrefs.regions': regions,
        'onboardingData.region': regions.join(', '), // Primary region as string
        updatedAt: serverTimestamp()
      });
      
    } catch (error) {
      console.error('❌ Fout bij opslaan regio voorkeuren:', error);
      throw error;
    }
  };

  // ✅ FIX: Real-time listener for userProfiles (bidirectional sync with ProfileSettings)
  useEffect(() => {
    if (!user?.uid) return;
    
    const userProfilesRef = doc(db, 'userProfiles', user.uid);
    
    // Real-time listener for userProfiles changes (from ProfileSettings)
    const unsubscribe = onSnapshot(userProfilesRef, (snapshot) => {
      if (snapshot.exists()) {
        const userData = snapshot.data();
        
        // Prioriteit: onboardingData.eventPrefs.regions > selectedRegions
        const regions = userData.onboardingData?.eventPrefs?.regions || 
                       userData.selectedRegions || 
                       [];
        
        if (regions.length > 0) {
          setSelectedRegions(regions);
        }
      }
    }, (error) => {
      console.error('❌ Error in ConnectieFlow real-time listener:', error);
    });
    
    return () => {
      unsubscribe();
    };
  }, [user?.uid]);
  
  // Load initial region preferences from user profile - vanuit onboarding data
  const loadRegionPreferences = async () => {
    if (!user?.uid) return;
    
    try {
      const { doc, getDoc } = await import('firebase/firestore');
      const userRef = doc(db, 'userProfiles', user.uid);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const userData = userSnap.data();
        
        // Prioriteit: onboardingData.eventPrefs.regions > selectedRegions
        const regions = userData.onboardingData?.eventPrefs?.regions || 
                       userData.selectedRegions || 
                       [];
        
        if (regions.length > 0) {
          setSelectedRegions(regions);
        }
      }
    } catch (error) {
      console.error('❌ Fout bij laden regio voorkeuren:', error);
    }
  };
  const chatEndRef = useRef<HTMLDivElement>(null);
  
  // Popup states
  const [showIncomingCall, setShowIncomingCall] = useState(false);
  const [showIncomingVideo, setShowIncomingVideo] = useState(false);
  const [showIncomingMeeting, setShowIncomingMeeting] = useState(false);
  // Prevent immediate re-open of incoming popups due to realtime race conditions.
  const suppressIncomingPopupUntilRef = useRef<{ call: number; video: number; physical: number }>({
    call: 0,
    video: 0,
    physical: 0
  });
  const [showBreakPopup, setShowBreakPopup] = useState(false);
  const [showSchedulePopup, setShowSchedulePopup] = useState(false);

  // Call system state
  const [callStatus, setCallStatus] = useState<'idle' | 'initiating' | 'ringing' | 'in-call' | 'ended'>('idle');
  const [showCallInterface, setShowCallInterface] = useState(false);
  // BACKUP - WebRTC call state preserved (still used in backup code paths)
  const [currentCall, setCurrentCall] = useState<WebRTCCall | null>(null);
  
  // NEW - Jitsi call state
  const [showJitsiCall, setShowJitsiCall] = useState(false);
  const [jitsiCallType, setJitsiCallType] = useState<'audio' | 'video'>('audio');
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  const callAnswerUnsubscribeRef = useRef<(() => void) | null>(null);
  const outgoingToneRef = useRef<HTMLAudioElement | null>(null);
  const candidateUnsubscribeRef = useRef<(() => void) | null>(null);
  // ✅ Stream refs voor cleanup en track management
  const localVideoStreamRef = useRef<MediaStream | null>(null);
  const remoteVideoStreamRef = useRef<MediaStream | null>(null);
  const webrtcSetupInProgressRef = useRef<boolean>(false);
  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const iceCandidateQueueRef = useRef<RTCIceCandidateInit[]>([]);
  const candidateTimerRef = useRef<NodeJS.Timeout | null>(null);
  const pendingRemoteCandidatesRef = useRef<RTCIceCandidateInit[]>([]); // Buffer voor ICE candidates die binnenkomen voor remote description

  // ── State-sync reliability refs ──────────────────────────────────────────
  /** Tracks whether this component instance is still mounted.
   *  Set to false in the top-level cleanup effect; checked in all async
   *  functions before calling any setState to prevent memory leaks. */
  const isMountedRef = useRef(true);
  /** Single-flight guard for call/video proposal — prevents the "Volgende"
   *  button double-click from firing two Firestore writes. */
  const callProposalInFlightRef = useRef(false);
  /** Always-current mirror of selectedConnection for use inside long-lived
   *  Firestore listeners. Those listeners close over the value at mount time
   *  (stale closure). Reading from this ref instead gives the live value
   *  without adding selectedConnection to the listener's dependency array
   *  (which would cause the listener to be torn down and recreated on every
   *  connection-name change). */
  const selectedConnectionRef = useRef(selectedConnection);
  // Keep refs in sync on every render — direct assignment, no useEffect needed.
  // Refs are mutable and don't trigger re-renders, making them safe for this pattern.
  selectedConnectionRef.current = selectedConnection;

  /** Always-current mirror of currentPhase for use inside long-lived Firestore
   *  listeners that would otherwise close over a stale value. Same pattern as
   *  selectedConnectionRef above. */
  const currentPhaseRef = useRef<ConnectionPhase>(currentPhase);
  currentPhaseRef.current = currentPhase;

  /** Always-current mirror of selectedConnectionData for use inside long-lived
   *  Firestore listeners (display names, phase fallbacks, etc.). */
  const selectedConnectionDataRef = useRef(selectedConnectionData);
  selectedConnectionDataRef.current = selectedConnectionData;

  /** Tracks the connectionFlow.id for which the notification listener (the
   *  first onSnapshot at line ~3763) is already active.  In React StrictMode
   *  the effect runs twice; this prevents a duplicate subscription. */
  const notifListenerFlowIdRef = useRef<string | null>(null);

  /** Tracks the last connection ID that triggered the UI-reset effect (line ~684).
   *  Prevents collapsing the active card when selectedConnectionData updates with
   *  the same ID (e.g. a Firestore reload of the same connection). */
  const prevSelectedConnectionIdRef = useRef<string | undefined>(undefined);

  // ==========================================
  // ✅ WEBRTC AUDIO CALL HOOK
  // ==========================================
  // This hook manages the full lifecycle of WebRTC audio calls
  const otherUserId = connectionFlow?.participants?.find(p => p !== user?.uid) || '';
  const isCallInitiator = connectionFlow?.callStep?.proposedBy === user?.uid;
  // Hard gate: only start WebRTC when the call is truly active (after accept).
  const isCallActive = connectionFlow?.callStep?.status === 'active';

  const webrtcAudioCall = useWebRTCAudioCall({
    flowId: connectionFlow?.id || '',
    userId: user?.uid || '',
    otherUserId: otherUserId,
    otherUserName: selectedConnectionData?.name || 'Onbekend',
    enabled: isCallActive && !!connectionFlow && !!user,
    isInitiator: isCallInitiator
  });

  // Sync WebRTC call state with UI state
  useEffect(() => {
    if (webrtcAudioCall.call) {
      setCurrentCall(webrtcAudioCall.call);
      setCallStatus(webrtcAudioCall.isConnected ? 'in-call' : 'initiating');
      setShowCallInterface(true);
    } else {
      // Don't clear UI state if:
      // 1. We're in initiating status (WebRTC is setting up)
      // 2. callStep is still pending/active in Firestore (other party hasn't responded yet)
      const callStepBusy = 
        connectionFlow?.callStep?.status === 'pending' || 
        connectionFlow?.callStep?.status === 'active';
      
      if (callStatus !== 'initiating' && !callStepBusy) {
        setCurrentCall(null);
        setCallStatus('idle');
        setShowCallInterface(false);
      } else {
      }
    }
  }, [webrtcAudioCall.call, webrtcAudioCall.isConnected, connectionFlow?.callStep?.status, callStatus]);

  // ==========================================
  // ✅ INCOMING CALL/VIDEO/MEETING POPUP TRIGGERS
  // ==========================================
  // Detecteer wanneer de andere user een call/video/meeting start
  // en toon de juiste "Inkomende [X]" popup
  useEffect(() => {
    if (!connectionFlow || !user?.uid) return;
    const now = Date.now();

    // Only trigger popups when the flow/chatStep is already active (i.e. the chat phase
    // has been properly started). This prevents stale callStep/videoStep data from a
    // previous session triggering an incoming popup right after loading the flow.
    const chatHasStarted =
      connectionFlow.chatStep?.status === 'active' ||
      connectionFlow.chatStep?.status === 'completed' ||
      connectionFlow.callStep?.status === 'active' ||
      connectionFlow.callStep?.status === 'completed' ||
      connectionFlow.videoStep?.status === 'active' ||
      connectionFlow.videoStep?.status === 'completed';

    // Check for incoming CALL — only when chat has started or call is genuinely new
    if (
      chatHasStarted &&
      connectionFlow.callStep?.status === 'pending' &&
      connectionFlow.callStep?.proposedBy &&
      connectionFlow.callStep.proposedBy !== user.uid &&
      !connectionFlow.callStep.acceptances?.[user.uid] &&
      now >= suppressIncomingPopupUntilRef.current.call &&
      !showIncomingCall
    ) {
      setShowIncomingCall(true);
      setShowTimerExpiredPopup(false);
    }

    // Check for incoming VIDEO — only when the other user proposes (pending)
    if (
      chatHasStarted &&
      connectionFlow.videoStep?.status === 'pending' &&
      connectionFlow.videoStep?.proposedBy &&
      connectionFlow.videoStep.proposedBy !== user.uid &&
      !connectionFlow.videoStep.acceptances?.[user.uid] &&
      now >= suppressIncomingPopupUntilRef.current.video &&
      !showIncomingVideo
    ) {
      setShowIncomingVideo(true);
      setShowTimerExpiredPopup(false);
    }

    // Check for incoming MEETING — only on pending (pending = not yet accepted by receiver)
    if (
      chatHasStarted &&
      connectionFlow.physicalStep?.status === 'pending' &&
      connectionFlow.physicalStep?.proposedBy &&
      connectionFlow.physicalStep.proposedBy !== user.uid &&
      !connectionFlow.physicalStep.acceptances?.[user.uid] &&
      now >= suppressIncomingPopupUntilRef.current.physical &&
      !showIncomingMeeting
    ) {
      setShowIncomingMeeting(true);
      setShowTimerExpiredPopup(false);
    }
  }, [
    connectionFlow?.callStep?.status,
    connectionFlow?.callStep?.proposedBy,
    connectionFlow?.videoStep?.status,
    connectionFlow?.videoStep?.proposedBy,
    connectionFlow?.physicalStep?.status,
    connectionFlow?.physicalStep?.proposedBy,
    user?.uid,
    showIncomingCall,
    showIncomingVideo,
    showIncomingMeeting
  ]);

  // ==========================================
  // ✅ CLEANUP - UI state en audio elementen
  // ==========================================
  // Google Meet draait extern (popup), hier alleen UI cleanup
  // WebRTC audio cleanup wordt gedaan door useWebRTCAudioCall hook
  const cleanupMediaAndConnection = async () => {
    
    // WebRTC cleanup is handled by the hook
    // Google Meet runs externally and closes via user action
    
    // Reset UI state
    setShowVideoCallInterface(false);
    setCurrentVideoCall(null);
    setCallStatus('idle');
    
    // Clear legacy stream refs (not used with Meet, but keep for compatibility)
    if (localVideoStreamRef.current) {
        localVideoStreamRef.current = null;
    }
    if (remoteVideoStreamRef.current) {
        remoteVideoStreamRef.current = null;
    }
    setLocalVideoStream(null);
    setRemoteVideoStream(null);
    
    // Stop remote audio element (legacy)
    if (remoteAudioRef.current) {
      try {
        remoteAudioRef.current.pause();
        remoteAudioRef.current.srcObject = null;
      } catch {}
    }
    
  };

  // 🔴 DEPRECATED: addRemoteIceCandidateSafe is not needed with Google Meet
  // Google Meet handles all connection logic internally

  // ==========================================
  // ✅ UPGRADE TO VIDEO - Google Meet
  // ==========================================
  // Upgrade from audio (WebRTC) to video (Google Meet)
  // This is handled via proposeNextStep('video') which opens Meet popup
  // The same Meet window can be used, or a new one with video enabled.
  const upgradeToVideo = async () => {
    
    try {
      // No camera management needed - Meet handles it
      
      // Update UI state
      setShowVideoCallInterface(true);
      
    } catch (error: any) {
      console.error("❌ [VideoCall] VIDEO UPGRADE ERROR", error);
      throw error;
    }
  };

  // ==========================================
  // ✅ STOP ALL CALLS (WebRTC + UI cleanup)
  // ==========================================
  const stopAllAudioAndStreams = async () => {
    
    // ✅ CRITICAL: End WebRTC audio call first (releases microphone!)
    if (webrtcAudioCall && webrtcAudioCall.call) {
      webrtcAudioCall.endCall();
    }
    
    // UI cleanup
    await cleanupMediaAndConnection();
    
    // Clear timers
    if (candidateTimerRef.current) {
      clearTimeout(candidateTimerRef.current);
      candidateTimerRef.current = null;
    }
    iceCandidateQueueRef.current = [];
    
    // Reset call status
    setCallStatus('ended');
    
    setShowCallInterface(false);
    setShowVideoCallInterface(false);
    setShowJitsiCall(false);
    setCurrentCall(null);
    setCurrentVideoCall(null);
    
    // Legacy peer connection cleanup (voor backwards compatibility)
      if (peerConnectionRef.current) {
      try {
        peerConnectionRef.current.close();
        peerConnectionRef.current = null;
      } catch {}
    }
  };

  // Zorg dat de uitgaande beltoon ALTĲD stopt zodra de call niet meer in "initiating/ringing" status is
  useEffect(() => {
    if ((callStatus === 'in-call' || callStatus === 'ended') && outgoingToneRef.current) {
      try {
        outgoingToneRef.current.pause();
        outgoingToneRef.current.currentTime = 0;
      } catch {}
    }
  }, [callStatus]);

  // ==========================================
  // ✅ SEND CONNECTION REQUEST
  // ==========================================
  const sendConnectionRequest = async (toUserId: string) => {
    if (!user?.uid) {
      console.error('❌ No user!');
      showError('Je bent niet ingelogd. Probeer opnieuw.');
      return;
    }
    
    // ✅ BLOCK: Cannot send without phone number
    if (!currentUserHasPhone) {
                    warning('Telefoonnummer vereist. Je kunt geen connectieverzoek versturen zonder telefoonnummer. Ga naar Instellingen → Profiel om je telefoonnummer toe te voegen.');
      setShowRequestModal(false);
      setSelectedUserForRequest(null);
      return;
    }
    
    if (sendingRequest) return;
    setSendingRequest(true);

    try {
      // Get receiver's profile
      const toUserDoc = await getDoc(doc(db, 'userProfiles', toUserId));
      let toUserData = toUserDoc.exists() ? toUserDoc.data() : {};
      
      // If no displayName in userProfiles, try users collection
      if (!toUserData.displayName) {
        const usersDoc = await getDoc(doc(db, 'users', toUserId));
        if (usersDoc.exists()) {
          toUserData = { ...toUserData, ...usersDoc.data() };
        }
      }
      
      const toUserName = toUserData.displayName || toUserData.email?.split('@')[0] || 'Onbekend';
      const toUserPhoto = toUserData.photoURL || '';
      
      // Prevent duplicate pending requests between the same two users
      const pendingChecks = await Promise.all([
        getDocs(query(collection(db, 'connectionRequests'), where('fromUserId', '==', user.uid), where('toUserId', '==', toUserId), where('status', '==', 'pending'))),
        getDocs(query(collection(db, 'connectionRequests'), where('fromUserId', '==', toUserId), where('toUserId', '==', user.uid), where('status', '==', 'pending')))
      ]);
      const hasPending = pendingChecks.some(s => s.size > 0);
      
      if (hasPending) {
        warning('Er bestaat al een open connectieverzoek tussen jullie.');
        setSendingRequest(false);
        return;
      }

      // Get current user profile
      const currentUserDoc = await getDoc(doc(db, 'userProfiles', user.uid));
      let currentUserData = currentUserDoc.exists() ? currentUserDoc.data() : {};
      
      if (!currentUserData.displayName) {
        const usersDoc = await getDoc(doc(db, 'users', user.uid));
        if (usersDoc.exists()) {
          currentUserData = { ...currentUserData, ...usersDoc.data() };
        }
      }

      const requestData: any = {
        fromUserId: user.uid,
        toUserId: toUserId,
        fromUserName: currentUserData.displayName || user.displayName || user.email?.split('@')[0] || 'Onbekend',
        toUserName: toUserName,
        fromUserPhoto: currentUserData.photoURL || user.photoURL || '',
        toUserPhoto: toUserPhoto,
        message: requestMessage || '',
        status: 'pending',
        createdAt: new Date(),
        updatedAt: new Date()
      };

      // Add knownContact if checkbox is checked
      if (isKnownContact) {
        requestData.knownContact = {
          markedBy: user.uid,
          markedAt: new Date(),
          status: 'pending'
        };
      }

      await addDoc(collection(db, 'connectionRequests'), requestData);

      setRequestMessage('');
      setIsKnownContact(false);
      setShowRequestModal(false);
      setSelectedUserForRequest(null);
      
      // Reload connections to show the new pending request
      await loadConnectionsAndRequests();
      
      // Switch to pending tab
      setActiveTab('pending');
      
      // Check if user has phone number and show warning if not
      const hasPhone = !!(currentUserData.phone || currentUserData.phoneNumber);
      if (!hasPhone) {
        success('Connectie verzoek verzonden!');
        warning('BELANGRIJK: Je hebt nog geen telefoonnummer ingevuld! Zonder telefoonnummer ben je NIET zichtbaar in "Open voor connecties". Ga naar Instellingen → Profiel.');
      } else {
        success('Connectie verzoek verzonden!');
      }
    } catch (error) {
      console.error('❌ Error sending connection request:', error);
      showError('Er ging iets mis bij het verzenden van het verzoek: ' + (error as Error).message);
    } finally {
      setSendingRequest(false);
    }
  };

  // 🔴 DEPRECATED - Legacy test handler (niet meer gebruikt)
  const handleJoinLivekit = () => {
    if (!user) {
      showError('Je moet ingelogd zijn om te testen');
      return;
    }

    // Use current user's display name or fallback to email
    const participantName = user.displayName || user.email || 'Test User';
    
    // For testing: use selected connection or create test room
    let roomName = 'test-room-' + user.uid.substring(0, 8);
    
    if (selectedConnectionData && currentChatRoom) {
      roomName = currentChatRoom.id;
    }
    // 🔴 DEPRECATED: Audio calls via WebRTC ("Bellen"), Video via Google Meet ("Beeldbellen")
  };

  // Initialiseer remote audio element correct
  useEffect(() => {
    if (remoteAudioRef.current) {
      const audioElement = remoteAudioRef.current;
      audioElement.muted = false;
      audioElement.volume = 1.0;
    }
  }, []);


  // AI and summary state
  const [showSummaryPopup, setShowSummaryPopup] = useState(false);
  const [conversationSummary, setConversationSummary] = useState<string>('');

  // ==========================================
  // ✅ CALL STATE MANAGEMENT
  // ==========================================
  // Audio calls: WebRTC (peer-to-peer, embedded in app)
  // Video calls: Google Meet (external popup window)
  // Our app orchestrates: timers, status, notes, UI state
  
  // Video call interface state (UI only, no media streams)
  const [showVideoCallInterface, setShowVideoCallInterface] = useState(false);
  const [currentVideoCall, setCurrentVideoCall] = useState<{
    userName: string;
    startTime: number;
  } | null>(null);
  
  // Legacy stream state (kept for backwards compatibility)
  const [localVideoStream, setLocalVideoStream] = useState<MediaStream | null>(null);
  const [remoteVideoStream, setRemoteVideoStream] = useState<MediaStream | null>(null);

  // Real connections will be loaded from Firestore
  const [connections, setConnections] = useState<any[]>([]);
  const [loadingConnections, setLoadingConnections] = useState(true);
  const [isFullyOnboarded, setIsFullyOnboarded] = useState<boolean | null>(null);
  const [missingOnboardingFields, setMissingOnboardingFields] = useState<string[]>([]);
  const [connectionFlowsMap, setConnectionFlowsMap] = useState<Record<string, any>>({});

  // Check if user has completed all required onboarding fields
  useEffect(() => {
    if (!user?.uid) {
      setIsFullyOnboarded(null);
      setLoadingConnections(false);
      return;
    }

    const checkOnboardingStatus = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'userProfiles', user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const check = hasCompletedRequiredOnboardingFields(userData);
          setIsFullyOnboarded(check.isComplete);
          setMissingOnboardingFields(check.missingFields);
          
          if (!check.isComplete) {
            console.warn('⚠️ User not fully onboarded. Missing fields:', check.missingFields);
            setLoadingConnections(false);
          }
        } else {
          console.warn('⚠️ User profile not found');
          setIsFullyOnboarded(false);
          setMissingOnboardingFields(['Profiel niet gevonden']);
          setLoadingConnections(false);
        }
      } catch (error) {
        console.error('❌ Error checking onboarding status:', error);
        setIsFullyOnboarded(false);
        setLoadingConnections(false);
      }
    };

    checkOnboardingStatus();
  }, [user?.uid]);

  // ✅ Check if current user has phone number
  useEffect(() => {
    if (!user?.uid) return;
    const checkPhone = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'userProfiles', user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          const hasPhone = !!(data.phone || data.phoneNumber);
          setCurrentUserHasPhone(hasPhone);
        }
      } catch (err) {
        console.error('Error checking phone number:', err);
      }
    };
    checkPhone();
    
    // Real-time listener for phone number changes
    const unsubPhone = onSnapshot(doc(db, 'userProfiles', user.uid), (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        setCurrentUserHasPhone(!!(data.phone || data.phoneNumber));
      }
    });
    return () => unsubPhone();
  }, [user?.uid]);

  // Load real connections AND connection requests from Firestore
  useEffect(() => {
      if (!user?.uid) {
        setLoadingConnections(false);
        return;
      }
      
    // Block loading connections if user is not fully onboarded
    if (isFullyOnboarded === false) {
      setLoadingConnections(false);
      return;
    }
    
    // Wait for onboarding check to complete (null means still checking)
    if (isFullyOnboarded === null) return;

    // Use the function defined outside useEffect
    const loadConnections = async () => {
      try {
        setLoadingConnections(true);
        
        // Import connection request service
        const { connectionRequestService } = await import('../services/connectionRequestService');
        
        // Load accepted connections (where I am user1)
        const connectionsQuery1 = query(
          collection(db, 'connections'),
          where('user1Id', '==', user.uid)
        );
        const connectionsSnapshot1 = await getDocs(connectionsQuery1);
        
        // Load accepted connections (where I am user2)
        const connectionsQuery2 = query(
          collection(db, 'connections'),
          where('user2Id', '==', user.uid)
        );
        const connectionsSnapshot2 = await getDocs(connectionsQuery2);
        
        // Format accepted connections with user data
        const acceptedConnectionsData = await Promise.all([
          ...connectionsSnapshot1.docs.map(async (docSnap) => {
            const data = docSnap.data();
            // Get the other user (user2) - try users collection first
            const otherUserDoc = await getDoc(doc(db, 'users', data.user2Id));
            let otherUserData = otherUserDoc.exists() ? otherUserDoc.data() : {};
            
            // If no displayName in users, try userProfiles
            if (!otherUserData.displayName) {
              const profileDoc = await getDoc(doc(db, 'userProfiles', data.user2Id));
              if (profileDoc.exists()) {
                otherUserData = { ...otherUserData, ...profileDoc.data() };
              }
            }
            
            // Use email as fallback if still no displayName
            const displayName = otherUserData.displayName || otherUserData.email?.split('@')[0] || 'Onbekend';
            
            const base = {
              id: docSnap.id,
              ...data,
              name: displayName,
              avatar: otherUserData.photoURL || null,
              online: false,
              lastActivity: 'Klik om te chatten',
              time: new Date(data.acceptedAt?.toDate?.() || data.createdAt?.toDate?.()).toLocaleDateString('nl-NL'),
              status: 'accepted',
              phase: (data.phase as ConnectionPhase) || 'chat',
              phaseStatus: data.phaseStatus || 'not_scheduled',
            };
            
            // Markeer nieuwe acceptatie voor deze gebruiker
            const hasNewAcceptance = Array.isArray((data as any).newForUserIds) && (data as any).newForUserIds.includes(user.uid);
            
            return {
              ...base,
              hasNewAcceptance
            };
          }),
          ...connectionsSnapshot2.docs.map(async (docSnap) => {
            const data = docSnap.data();
            // Get the other user (user1) - try users collection first
            const otherUserDoc = await getDoc(doc(db, 'users', data.user1Id));
            let otherUserData = otherUserDoc.exists() ? otherUserDoc.data() : {};
            
            // If no displayName in users, try userProfiles
            if (!otherUserData.displayName) {
              const profileDoc = await getDoc(doc(db, 'userProfiles', data.user1Id));
              if (profileDoc.exists()) {
                otherUserData = { ...otherUserData, ...profileDoc.data() };
              }
            }
            
            // Use email as fallback if still no displayName
            const displayName = otherUserData.displayName || otherUserData.email?.split('@')[0] || 'Onbekend';
            
            const base = {
              id: docSnap.id,
              ...data,
              name: displayName,
              avatar: otherUserData.photoURL || null,
              online: false,
              lastActivity: 'Klik om te chatten',
              time: new Date(data.acceptedAt?.toDate?.() || data.createdAt?.toDate?.()).toLocaleDateString('nl-NL'),
              status: 'accepted',
              phase: (data.phase as ConnectionPhase) || 'chat',
              phaseStatus: data.phaseStatus || 'not_scheduled',
            };
            
            const hasNewAcceptance = Array.isArray((data as any).newForUserIds) && (data as any).newForUserIds.includes(user.uid);
            
            return {
              ...base,
              hasNewAcceptance
            };
          })
        ]);
        
        // Filter out "Onbekend" connections - these users haven't completed onboarding
        const acceptedConnections = acceptedConnectionsData.filter(conn => conn.name !== 'Onbekend');
        
        // Load received connection requests (people who sent ME a request)
        const receivedRequests = await connectionRequestService.getReceivedConnectionRequests(user.uid);
        const receivedConnectionsData = await Promise.all(
          receivedRequests
            .filter(req => req.status === 'pending')
            .map(async (req) => {
              // Get sender's profile - try users collection first, then userProfiles
              const senderDoc = await getDoc(doc(db, 'users', req.fromUserId));
              let senderData = senderDoc.exists() ? senderDoc.data() : {};
              
              // If no displayName in users, try userProfiles
              if (!senderData.displayName) {
                const profileDoc = await getDoc(doc(db, 'userProfiles', req.fromUserId));
                if (profileDoc.exists()) {
                  senderData = { ...senderData, ...profileDoc.data() };
                }
              }
              
              // Use email as fallback if still no displayName
              const displayName = senderData.displayName || senderData.email?.split('@')[0] || 'Onbekend';
              
              return {
                id: req.id,
                name: displayName,
                avatar: senderData.photoURL || null,
                online: false,
                lastActivity: 'Wacht op jouw acceptatie',
                time: new Date(req.createdAt.toDate()).toLocaleDateString('nl-NL'),
                phase: 'pending',
                status: 'pending',
                unread: true,
                user1Id: req.fromUserId,
                user2Id: user.uid,
                requestType: 'received', // Important: this person sent ME a request
                requestId: req.id,
                requestData: req,
                hasOnboarding: !!senderData.displayName // Track if user has completed onboarding
              };
            })
        );
        
        // Filter out "Onbekend" from received requests - they need to complete onboarding first
        const filteredReceivedConnections = receivedConnectionsData.filter(conn => conn.name !== 'Onbekend');
        
        // Load sent connection requests (people I sent a request TO)
        const sentRequests = await connectionRequestService.getSentConnectionRequests(user.uid);
        const sentConnectionsData = await Promise.all(
          sentRequests
            .filter(req => req.status === 'pending')
            .map(async (req) => {
              // Get receiver's profile - try users collection first, then userProfiles
              const receiverDoc = await getDoc(doc(db, 'users', req.toUserId));
              let receiverData = receiverDoc.exists() ? receiverDoc.data() : {};
              
              // If no displayName in users, try userProfiles
              if (!receiverData.displayName) {
                const profileDoc = await getDoc(doc(db, 'userProfiles', req.toUserId));
                if (profileDoc.exists()) {
                  receiverData = { ...receiverData, ...profileDoc.data() };
                }
              }
              
              // Use email as fallback if still no displayName
              const displayName = receiverData.displayName || receiverData.email?.split('@')[0] || 'Onbekend';
              
              return {
                id: req.id,
                name: displayName,
                avatar: receiverData.photoURL || null,
                online: false,
                lastActivity: 'Wacht op acceptatie',
                time: new Date(req.createdAt.toDate()).toLocaleDateString('nl-NL'),
                phase: 'pending',
                status: 'pending',
                unread: false,
                user1Id: user.uid,
                user2Id: req.toUserId,
                requestType: 'sent', // Important: I sent THIS person a request
                requestId: req.id,
                requestData: req,
                hasOnboarding: !!receiverData.displayName // Track if user has completed onboarding
              };
            })
        );
        
        // Filter out "Onbekend" from sent requests too
        const filteredSentConnections = sentConnectionsData.filter(conn => conn.name !== 'Onbekend');
        
        // Load connectionFlows for accepted connections to determine status
        const flowsMap: Record<string, any> = {};
        for (const conn of acceptedConnections) {
          try {
            // Try to get the chatroom for this connection
            const chatRoomQuery = query(
              collection(db, 'chatRooms'),
              where('participants', 'array-contains', user.uid)
            );
            const chatRoomSnap = await getDocs(chatRoomQuery);
            
            // Find the chatroom for this specific connection
            const chatRoom = chatRoomSnap.docs.find(doc => {
              const data = doc.data();
              const participants = data.participants || [];
              const otherUserId = (conn as any).user1Id === user.uid ? (conn as any).user2Id : (conn as any).user1Id;
              return participants.includes(otherUserId);
            });
            
            if (chatRoom) {
              // Check if a connectionFlow exists for this chatroom
              const flowRef = doc(db, 'connectionFlows', chatRoom.id);
              const flowSnap = await getDoc(flowRef);
              
              if (flowSnap.exists()) {
                const flowData = flowSnap.data();
                flowsMap[conn.id] = {
                  ...flowData,
                  chatRoomId: chatRoom.id
                };
              }
            }
          } catch (error) {
            console.warn('Error loading flow for connection', conn.id, error);
          }
        }
        
        setConnectionFlowsMap(flowsMap);
        
        // Combine all connections (all filtered to exclude "Onbekend")
        const allConnections = [
          ...acceptedConnections,
          ...filteredReceivedConnections,
          ...filteredSentConnections
        ];
        

        setConnections(allConnections);
      } catch (error) {
        console.error('Error loading connections and requests:', error);
      } finally {
        setLoadingConnections(false);
      }
    };

    loadConnections();
    loadRegionPreferences();
    
    // Set up real-time listener for connection requests
    const setupRealtimeListener = async () => {
      const { connectionRequestService } = await import('../services/connectionRequestService');
      
      // Listen for received requests
      const receivedQuery = query(
        collection(db, 'connectionRequests'),
        where('toUserId', '==', user.uid),
        where('status', '==', 'pending')
      );
      
      const unsubscribeReceived = onSnapshot(receivedQuery, () => {
        loadConnections();
      });
      
      // Listen for sent requests status changes
      const sentQuery = query(
        collection(db, 'connectionRequests'),
        where('fromUserId', '==', user.uid),
        where('status', '==', 'pending')
      );
      
      const unsubscribeSent = onSnapshot(sentQuery, () => {
        loadConnections();
      });
      
      return () => {
        unsubscribeReceived();
        unsubscribeSent();
      };
    };
    
    const unsubscribePromise = setupRealtimeListener();
    
    return () => {
      unsubscribePromise.then(unsub => unsub());
    };
  }, [user?.uid, isFullyOnboarded]);

  // Helper function to check online status (reads from userProfiles where onlineStatusService writes)
  const checkUserOnlineStatus = async (userId: string): Promise<boolean> => {
    try {
      const onlineUserRef = doc(db, 'userProfiles', userId);
      const onlineUserSnap = await getDoc(onlineUserRef);
      if (onlineUserSnap.exists()) {
        const onlineData = onlineUserSnap.data();
        if (onlineData.isOnline === true) {
          return true;
        }
        // Also check lastSeen (within last 5 minutes = online)
        if (onlineData.lastSeen) {
          const lastSeen = onlineData.lastSeen.toMillis?.() || 0;
          const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);
          return lastSeen > fiveMinutesAgo;
        }
      }
      return false;
    } catch (error) {
      console.warn('Error checking online status for', userId, error);
      return false;
    }
  };

  // ✅ FIX: Make loadConnectionsAndRequests available outside useEffect
  const loadConnectionsAndRequests = async () => {
    if (!user?.uid || isFullyOnboarded === false) return;
    
    try {
      setLoadingConnections(true);
      
      // Import connection request service
      const { connectionRequestService } = await import('../services/connectionRequestService');
      
      // Load accepted connections (where I am user1)
      const connectionsQuery1 = query(
        collection(db, 'connections'),
        where('user1Id', '==', user.uid)
      );
      const connectionsSnapshot1 = await getDocs(connectionsQuery1);
      
      // Load accepted connections (where I am user2)
      const connectionsQuery2 = query(
        collection(db, 'connections'),
        where('user2Id', '==', user.uid)
      );
      const connectionsSnapshot2 = await getDocs(connectionsQuery2);
      
      // Format accepted connections with user data
      const acceptedConnectionsData = await Promise.all([
        ...connectionsSnapshot1.docs.map(async (docSnap) => {
          const data = docSnap.data();
          const otherUserDoc = await getDoc(doc(db, 'userProfiles', data.user2Id));
          let otherUserData = otherUserDoc.exists() ? otherUserDoc.data() : {};
          
          if (!otherUserData.displayName) {
            const usersDoc = await getDoc(doc(db, 'users', data.user2Id));
            if (usersDoc.exists()) {
              otherUserData = { ...otherUserData, ...usersDoc.data() };
            }
          }
          
          const displayName = otherUserData.displayName || otherUserData.email?.split('@')[0] || 'Onbekend';
          
          // Check online status
          const isOnline = await checkUserOnlineStatus(data.user2Id);
          
          return {
            id: docSnap.id,
            ...data,
            name: displayName,
            // Gebruik echte avatar-URL indien aanwezig, anders geen avatar (UI toont dan fallback)
            avatar: otherUserData.photoURL || null,
            photoURL: otherUserData.photoURL || null,
            photoPosition: otherUserData.onboardingData?.photoPosition || otherUserData.photoPosition || { x: 50, y: 50 },
            online: isOnline,
            lastActivity: 'Klik om te chatten',
            time: new Date(data.acceptedAt?.toDate?.() || data.createdAt?.toDate?.()).toLocaleDateString('nl-NL'),
            status: 'accepted',
            phase: (data.phase as ConnectionPhase) || 'chat',
            phaseStatus: data.phaseStatus || 'not_scheduled',
            user1Id: data.user1Id,
            user2Id: data.user2Id
          };
        }),
        ...connectionsSnapshot2.docs.map(async (docSnap) => {
          const data = docSnap.data();
          const otherUserDoc = await getDoc(doc(db, 'userProfiles', data.user1Id));
          let otherUserData = otherUserDoc.exists() ? otherUserDoc.data() : {};
          
          if (!otherUserData.displayName) {
            const usersDoc = await getDoc(doc(db, 'users', data.user1Id));
            if (usersDoc.exists()) {
              otherUserData = { ...otherUserData, ...usersDoc.data() };
            }
          }
          
          const displayName = otherUserData.displayName || otherUserData.email?.split('@')[0] || 'Onbekend';
          
          // Check online status
          const isOnline = await checkUserOnlineStatus(data.user1Id);
          
          return {
            id: docSnap.id,
            ...data,
            name: displayName,
            // Gebruik echte avatar-URL indien aanwezig, anders geen avatar (UI toont dan fallback)
            avatar: otherUserData.photoURL || null,
            photoURL: otherUserData.photoURL || null,
            photoPosition: otherUserData.onboardingData?.photoPosition || otherUserData.photoPosition || { x: 50, y: 50 },
            online: isOnline,
            lastActivity: 'Klik om te chatten',
            time: new Date(data.acceptedAt?.toDate?.() || data.createdAt?.toDate?.()).toLocaleDateString('nl-NL'),
            status: 'accepted',
            phase: (data.phase as ConnectionPhase) || 'chat',
            phaseStatus: data.phaseStatus || 'not_scheduled',
            user1Id: data.user1Id,
            user2Id: data.user2Id
          };
        })
      ]);
      
      const acceptedConnections = acceptedConnectionsData.filter(conn => conn.name !== 'Onbekend');
      
      // Load received connection requests
      const receivedRequests = await connectionRequestService.getReceivedConnectionRequests(user.uid);
      const receivedConnectionsData = await Promise.all(
        receivedRequests
          .filter(req => req.status === 'pending')
          .map(async (req) => {
            const senderDoc = await getDoc(doc(db, 'userProfiles', req.fromUserId));
            let senderData = senderDoc.exists() ? senderDoc.data() : {};
            
            if (!senderData.displayName) {
              const usersDoc = await getDoc(doc(db, 'users', req.fromUserId));
              if (usersDoc.exists()) {
                senderData = { ...senderData, ...usersDoc.data() };
              }
            }
            
            const displayName = senderData.displayName || senderData.email?.split('@')[0] || 'Onbekend';
            
            // Check online status
            const isOnline = await checkUserOnlineStatus(req.fromUserId);
            
            return {
              id: req.id,
              name: displayName,
              avatar: senderData.photoURL ? '👤' : '👤',
              online: isOnline,
              lastActivity: 'Wacht op jouw acceptatie',
              time: new Date(req.createdAt.toDate()).toLocaleDateString('nl-NL'),
              phase: 'pending',
              status: 'pending',
              unread: true,
              user1Id: req.fromUserId,
              user2Id: user.uid,
              requestType: 'received',
              requestId: req.id,
              requestData: req
            };
          })
      );
      
      const filteredReceivedConnections = receivedConnectionsData.filter(conn => conn.name !== 'Onbekend');
      
      // Load sent connection requests
      const sentRequests = await connectionRequestService.getSentConnectionRequests(user.uid);
      const sentConnectionsData = await Promise.all(
        sentRequests
          .filter(req => req.status === 'pending')
          .map(async (req) => {
            const receiverDoc = await getDoc(doc(db, 'userProfiles', req.toUserId));
            let receiverData = receiverDoc.exists() ? receiverDoc.data() : {};
            
            if (!receiverData.displayName) {
              const usersDoc = await getDoc(doc(db, 'users', req.toUserId));
              if (usersDoc.exists()) {
                receiverData = { ...receiverData, ...usersDoc.data() };
              }
            }
            
            const displayName = receiverData.displayName || receiverData.email?.split('@')[0] || 'Onbekend';
            
            // Check online status
            const isOnline = await checkUserOnlineStatus(req.toUserId);
            
            return {
              id: req.id,
              name: displayName,
              avatar: receiverData.photoURL ? '👤' : '👤',
              online: isOnline,
              lastActivity: 'Wacht op acceptatie',
              time: new Date(req.createdAt.toDate()).toLocaleDateString('nl-NL'),
              phase: 'pending',
              status: 'pending',
              unread: false,
              user1Id: user.uid,
              user2Id: req.toUserId,
              requestType: 'sent',
              requestId: req.id,
              requestData: req
            };
          })
      );
      
      const filteredSentConnections = sentConnectionsData.filter(conn => conn.name !== 'Onbekend');
      
      // Combine all connections
      const allConnections = [
        ...acceptedConnections,
        ...filteredReceivedConnections,
        ...filteredSentConnections
      ];

      setConnections(allConnections);

      // Immediately sync selectedConnectionData with the fresh data from this reload.
      // The effect at line ~6849 will also run, but it fires asynchronously after React
      // processes the state update. By updating here synchronously (in the same batch),
      // we avoid a tick where selectedConnectionData is stale or null.
      // This prevents the physical phase card from collapsing when the other user's
      // Firestore write triggers a connections reload.
      if (selectedConnection) {
        const freshSelected = allConnections.find(c => c.name === selectedConnection);
        if (freshSelected) {
          setSelectedConnectionData(freshSelected);
        }
        // If not found: Fix A in the effect handles this — keeps existing data.
      }

      setLoadingConnections(false);
    } catch (error) {
      console.error('Error reloading connections:', error);
      setLoadingConnections(false);
    }
  };
  
  // Real-time listener for connections - auto-select when connection is accepted
  useEffect(() => {
    if (!user?.uid || isFullyOnboarded === false) return;
    
    
    // Per-connection processing guard: Map<connectionId, boolean>
    // A single boolean flag would block ALL connection changes while one is
    // being processed. Using a Map allows changes to different connections to
    // be processed concurrently without interfering with each other.
    const processingMap = new Map<string, boolean>();

    // Listen to connections where I am user1
    const connectionsQuery1 = query(
      collection(db, 'connections'),
      where('user1Id', '==', user.uid),
      where('status', '==', 'accepted')
    );
    
    // Listen to connections where I am user2
    const connectionsQuery2 = query(
      collection(db, 'connections'),
      where('user2Id', '==', user.uid),
      where('status', '==', 'accepted')
    );
    
    const handleConnectionChange = async (change: any, otherUserId: string) => {
      const connectionId: string = change.doc.id;
      if (processingMap.get(connectionId)) return;
      if (change.type !== 'added' && change.type !== 'modified') return;

      processingMap.set(connectionId, true);
      try {
        const data = change.doc.data();

        const validConnectionPhases: ConnectionPhase[] = ['chat', 'call', 'video', 'physical', 'completed'];
        const rawPhase = data.phase as string | undefined;
        let resolvedPhase: ConnectionPhase = 'chat';
        if (rawPhase && validConnectionPhases.includes(rawPhase as ConnectionPhase)) {
          resolvedPhase = rawPhase as ConnectionPhase;
        }

        // ── MODIFIED: targeted in-place update — no full reload ──────────────
        // A 'modified' event fires for every Firestore write to any accepted
        // connection (location proposals, planning lock, navigation status, …).
        // Calling loadConnectionsAndRequests() here would trigger a multi-query
        // storm on every such write. Instead, patch only the affected connection
        // in local state — no Firestore reads needed.
        if (change.type === 'modified') {
          setConnections((prev: any[]) =>
            prev.map((c) =>
              c.id === connectionId
                ? { ...c, phase: resolvedPhase, phaseStatus: data.phaseStatus ?? c.phaseStatus }
                : c
            )
          );
          // Keep selectedConnectionData in sync if this is the open connection.
          if (selectedConnectionDataRef.current?.id === connectionId) {
            setSelectedConnectionData((prev: any) =>
              prev ? { ...prev, phase: resolvedPhase, phaseStatus: data.phaseStatus ?? prev.phaseStatus } : prev
            );
          }
          return;
        }

        // ── ADDED: new accepted connection — fetch profile + auto-select ─────
        // Guard: skip if user is already viewing this exact connection (by ID).
        if (selectedConnectionDataRef.current?.id === connectionId) {
          return;
        }

        // Get other user's profile (only needed for 'added' to build the connection object)
        const otherUserDoc = await getDoc(doc(db, 'userProfiles', otherUserId));
        let otherUserData = otherUserDoc.exists() ? otherUserDoc.data() : {};

        if (!otherUserData.displayName) {
          const usersDoc = await getDoc(doc(db, 'users', otherUserId));
          if (usersDoc.exists()) {
            otherUserData = { ...otherUserData, ...usersDoc.data() };
          }
        }

        const displayName = otherUserData.displayName || otherUserData.email?.split('@')[0] || 'Onbekend';
        if (displayName === 'Onbekend') return;

        const acceptedConnection = {
          id: connectionId,
          name: displayName,
          avatar: '👤',
          online: false,
          lastActivity: 'Klik om te chatten',
          time: new Date().toLocaleDateString('nl-NL'),
          status: 'accepted',
          phase: resolvedPhase,
          user1Id: data.user1Id,
          user2Id: data.user2Id,
        };

        setSelectedConnection(acceptedConnection.name);
        setSelectedConnectionData(acceptedConnection);
        setActiveTab('active');
        setCurrentPhase(resolvedPhase);

        // Full reload only for genuinely new connections (not modifications)
        // to pull in avatar, phaseStatus, etc. from the complete profile.
        await loadConnectionsAndRequests();
      } catch (error) {
        console.error('Error in connection change handler:', error);
      } finally {
        processingMap.delete(connectionId);
      }
    };
    
    const unsubscribe1 = onSnapshot(connectionsQuery1, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        const data = change.doc.data();
        handleConnectionChange(change, data.user2Id);
      });
    });
    
    const unsubscribe2 = onSnapshot(connectionsQuery2, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        const data = change.doc.data();
        handleConnectionChange(change, data.user1Id);
      });
    });
    
    return () => {
      unsubscribe1();
      unsubscribe2();
    };
  }, [user?.uid, isFullyOnboarded]); // Removed connections and selectedConnection from deps to prevent loops

  // Fase-configuratie (alle tijden in seconden)
  // ALLE fases zijn 8 minuten (480s) voor synchrone timers
  const phases = {
    pending: { name: 'Connectieverzoek', icon: '🤝', color: 'gray', duration: null },
    chat: { name: 'Chat', icon: '💬', color: 'amber', duration: 480 },      // 8 minuten
    call: { name: 'Bellen', icon: '📞', color: 'amber', duration: 480 },  // 8 minuten
    video: { name: 'Beeldbellen', icon: '📹', color: 'amber', duration: 480 },  // 8 minuten
    physical: { name: 'Fysieke Afspraak', icon: '🏢', color: 'amber', duration: null },
    completed: { name: 'Voltooid', icon: '✅', color: 'green', duration: null }
  };

  // Volgorde van de flow-fases – gebruikt voor tabs en logica
  const phaseOrder: (keyof typeof phases)[] = [
    'pending',
    'chat',
    'call',
    'video',
    'physical',
    'completed'
  ];

  const tabs = [
    { key: 'active', label: 'Verbinden' },
    { key: 'inprogress', label: 'Lopend' },
    { key: 'pending', label: 'Wachtend' },
    { key: 'completed', label: 'Afgerond' }
  ];

  // ==========================================
  // ✅ UNIFIED TIMER FOR ALL STEPS
  // ==========================================
  // Timer werkt voor chat, call, en video steps.
  // We rekenen client-side het resterende aantal seconden uit op basis van
  // `{step}.startTime` en `{step}.duration` uit Firestore.
  // BELANGRIJK: Beide laptops gebruiken exact dezelfde startTime (via Firestore) voor synchronisatie.
  
  // ✅ FIX: Serialiseer startTime naar number voor betere dependency detection
  // React detecteert Firestore Timestamp objecten niet altijd als veranderd,
  // dus serialiseren we ze naar numbers (milliseconds) zodat React de wijzigingen wel detecteert
  const chatStartTimeMs = useMemo(() => {
    if (!connectionFlow?.chatStep?.startTime) return null;
    const startTime = connectionFlow.chatStep.startTime;
    if (typeof (startTime as any)?.toMillis === 'function') {
      return (startTime as any).toMillis();
    } else if (startTime instanceof Date) {
      return startTime.getTime();
    } else if (typeof startTime === 'object' && (startTime as any).seconds) {
      const ts = startTime as any;
      return (ts.seconds * 1000) + ((ts.nanoseconds || 0) / 1000000);
    }
    return new Date(startTime as any).getTime();
  }, [connectionFlow?.chatStep?.startTime]);

  const callStartTimeMs = useMemo(() => {
    if (!connectionFlow?.callStep?.startTime) return null;
    const startTime = connectionFlow.callStep.startTime;
    if (typeof (startTime as any)?.toMillis === 'function') {
      return (startTime as any).toMillis();
    } else if (startTime instanceof Date) {
      return startTime.getTime();
    } else if (typeof startTime === 'object' && (startTime as any).seconds) {
      const ts = startTime as any;
      return (ts.seconds * 1000) + ((ts.nanoseconds || 0) / 1000000);
    }
    return new Date(startTime as any).getTime();
  }, [connectionFlow?.callStep?.startTime]);

  const videoStartTimeMs = useMemo(() => {
    if (!connectionFlow?.videoStep?.startTime) return null;
    const startTime = connectionFlow.videoStep.startTime;
    if (typeof (startTime as any)?.toMillis === 'function') {
      return (startTime as any).toMillis();
    } else if (startTime instanceof Date) {
      return startTime.getTime();
    } else if (typeof startTime === 'object' && (startTime as any).seconds) {
      const ts = startTime as any;
      return (ts.seconds * 1000) + ((ts.nanoseconds || 0) / 1000000);
    }
    return new Date(startTime as any).getTime();
  }, [connectionFlow?.videoStep?.startTime]);

  useEffect(() => {
    if (!connectionFlow) {
      setIsTimerActive(false);
      return;
    }

    // Determine which step is currently active
    let activeStep: any = null;
    // stepName is always one of the active communication phases — never 'completed'
    let stepName: 'chat' | 'call' | 'video' | 'physical' = 'chat';
    let startTimeMs: number | null = null;
    
    if (currentPhase === 'chat' && connectionFlow.chatStep?.status === 'active') {
      activeStep = connectionFlow.chatStep;
      stepName = 'chat';
      startTimeMs = chatStartTimeMs;
    } else if (currentPhase === 'call' && connectionFlow.callStep?.status === 'active') {
      activeStep = connectionFlow.callStep;
      stepName = 'call';
      startTimeMs = callStartTimeMs;
    } else if (currentPhase === 'video' && connectionFlow.videoStep?.status === 'active') {
      activeStep = connectionFlow.videoStep;
      stepName = 'video';
      startTimeMs = videoStartTimeMs;
    }

    if (!activeStep || !startTimeMs) {
      setIsTimerActive(false);
      return;
    }
    
    const duration = activeStep.duration || 480; // default 8 minutes

    // ✅ FIX: Unieke key voor deze stap, zodat dismiss niet herhaald kan worden
    const stepKey = `${stepName}_${startTimeMs}`;

    const updateTimer = () => {
      const now = Date.now();
      const elapsed = now - startTimeMs!;
      const remainingMs = Math.max(0, duration * 1000 - elapsed);
      const remainingSeconds = Math.ceil(remainingMs / 1000);

      setTimer(remainingSeconds);
      setIsTimerActive(remainingSeconds > 0);

      // ✅ FIX: Toon popup alleen als:
      // 1. Timer op 0 staat
      // 2. Popup niet al eerder gedismist is voor DEZE specifieke stap
      // 3. Niet in fysieke fase (fysieke fase heeft eigen timer/flow)
      // 4. Niet binnen 1500ms na een navigatie/connectie-selectie —
      //    transiente fase-wisselingen door event-driven loading mogen de
      //    popup niet triggeren.
      const mssinceNavigation = Date.now() - lastNavigationTimeRef.current;
      if (
        remainingSeconds === 0 &&
        timerExpiredDismissedKeyRef.current !== stepKey &&
        currentPhase !== 'physical' &&
        currentPhase !== 'completed' &&
        mssinceNavigation > 2500
      ) {
        setTimerExpiredPhase(stepName); // Sla de correcte fase op
        setShowTimerExpiredPopup(true);
      }
    };

    // ✅ Update immediately
    updateTimer();
    
    // ✅ Update every second
    const intervalId = setInterval(updateTimer, 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, [
    currentPhase,
    connectionFlow?.id,
    connectionFlow?.chatStep?.status,
    chatStartTimeMs, // Serialized to number - React can detect changes
    connectionFlow?.chatStep?.duration,
    connectionFlow?.callStep?.status,
    callStartTimeMs, // Serialized to number
    connectionFlow?.callStep?.duration,
    connectionFlow?.videoStep?.status,
    videoStartTimeMs, // Serialized to number
    connectionFlow?.videoStep?.duration,
    // ✅ FIX: showTimerExpiredPopup VERWIJDERD uit dependencies om oneindige lus te voorkomen
  ]);

  // ==========================================
  // ⌨️ KEYBOARD SHORTCUT: Focus notes textarea
  // ==========================================
  // Ctrl+N (Windows) or Cmd+N (Mac) to quickly focus notes field
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check for Ctrl+N (Windows/Linux) or Cmd+N (Mac)
      if ((e.ctrlKey || e.metaKey) && e.key === 'n') {
        e.preventDefault(); // Prevent browser's "New Window"
        const notesTextarea = document.getElementById('notes-textarea') as HTMLTextAreaElement;
        if (notesTextarea) {
          notesTextarea.focus();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Update online status service when in active chat flow
  useEffect(() => {
    const isInActiveFlow = selectedConnection !== '';
    onlineStatusService.setInActiveChatFlow(isInActiveFlow);
    
    return () => {
      // Reset when component unmounts
      onlineStatusService.setInActiveChatFlow(false);
    };
  }, [currentPhase, selectedConnection]);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getEventsForDate = (date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    const calendarEventsForDate = flowCalendarEvents.filter((event: FlowCalendarEvent) => {
      const startStr = event.startTime?.toISOString?.().split('T')[0];
      return startStr === dateStr;
    });
    const appointmentsForDate = appointments.filter((app: any) => app.date === dateStr);
    return [...calendarEventsForDate, ...appointmentsForDate];
  };

  // Verleng de huidige fase met +1 minuut (maximaal 12 minuten totaal)
  const handlePlusOneMinute = async () => {
    try {
      if (!connectionFlow) return;

      // Bepaal welke step bij de huidige fase hoort
      let stepKey: 'chatStep' | 'callStep' | 'videoStep' | 'physicalStep' | null = null;
      if (currentPhase === 'chat') stepKey = 'chatStep';
      else if (currentPhase === 'call') stepKey = 'callStep';
      else if (currentPhase === 'video') stepKey = 'videoStep';
      else if (currentPhase === 'physical') stepKey = 'physicalStep';

      if (!stepKey) return;

      const stepData: any = (connectionFlow as any)[stepKey];
      const currentDuration: number =
        typeof stepData?.duration === 'number'
          ? stepData.duration
          : stepKey === 'chatStep'
            ? 480
            : 1800;

      const maxSeconds = 12 * 60; // 12 minuten
      const newDuration = Math.min(currentDuration + 60, maxSeconds);

      if (newDuration === currentDuration) return;

      const flowRef = doc(db, 'connectionFlows', connectionFlow.id);
      await updateDoc(flowRef, {
        [`${stepKey}.duration`]: newDuration,
        updatedAt: serverTimestamp()
      });

      // Voor niet-chat fases gebruiken we dezelfde lokale timer
      if (currentPhase !== 'chat') {
        setTimer(newDuration);
        setIsTimerActive(true);
      }
    } catch (error) {
      console.error('❌ Error while adding +1 minute:', error);
    }
  };

  const updateConnectionStatus = async (connectionId: string, status: string) => {
    try {
      const connectionRef = doc(db, 'connections', connectionId);
      await updateDoc(connectionRef, {
        status: status,
        lastActivity: new Date(),
        completedAt: status === 'completed' ? new Date() : null
      });
    } catch (error) {
      console.error('Error updating connection status:', error);
    }
  };

  const hasAppointments = (date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    return appointments.some((app: any) => app.date === dateStr);
  };

  const getWeekDays = (date: Date) => {
    const week = [];
    const startOfWeek = new Date(date);
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
    startOfWeek.setDate(diff);

    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      week.push(day);
    }
    return week;
  };

  const getMonthDays = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Add previous month's trailing days
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      const prevDay = new Date(year, month, -i);
      days.push(prevDay);
    }
    
    // Add current month's days
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    // Add next month's leading days to fill the grid
    const remainingDays = 42 - days.length; // 6 rows × 7 days
    for (let day = 1; day <= remainingDays; day++) {
      days.push(new Date(year, month + 1, day));
    }
    
    return days;
  };

  const handleDateClick = (date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    setSelectedAppointmentDate(dateStr);
    
    if (hasAppointments(date)) {
      setShowAppointmentList(true);
    } else {
      setShowAppointmentForm(true);
      setAppointmentForm({
        title: '',
        time: '09:00',
        duration: '60',
        alarm: '15',
        description: ''
      });
    }
  };

  const saveAppointment = () => {
    if (!appointmentForm.title.trim()) return;
    
    const newAppointment = {
      id: Date.now(),
      ...appointmentForm,
      date: selectedAppointmentDate,
      type: 'custom'
    };
    
    setAppointments([...appointments, newAppointment]);
    setShowAppointmentForm(false);
  };

  const deleteAppointment = (id: number) => {
    setAppointments(appointments.filter((app: any) => app.id !== id));
  };

  const editAppointment = (appointment: any) => {
    setAppointmentForm(appointment);
    setShowAppointmentList(false);
    setShowAppointmentForm(true);
  };

  const handleEventClick = (event: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedEvent(event);
    if (event.type === 'connectflow') {
      setShowPhaseSelector(true);
    } else {
      setShowEventDetails(true);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !currentChatRoom || !user) return;

    try {
      const messageData = {
        chatRoomId: currentChatRoom.id,
        senderId: user.uid,
        senderName: user.displayName || 'Onbekend',
        senderPhoto: user.photoURL || '',
        message: newMessage.trim(),
        timestamp: serverTimestamp(),
        messageType: 'text' as const
      };

      // Clear input immediately for better UX
      setNewMessage('');

      // Add message to Firebase
      await addDoc(collection(db, 'chatMessages'), messageData);

      // Update room with last message
      await updateDoc(doc(db, 'chatRooms', currentChatRoom.id), {
        lastMessage: messageData.message,
        lastMessageAt: serverTimestamp(),
        messageCount: currentChatRoom.messageCount + 1
      });

    } catch (error) {
      console.error('❌ Error sending message:', error);
      // Restore message in input on error
      setNewMessage(newMessage);
    }
  };

  // NEW: Send video call link message to chat
  const sendVideoCallLinkMessage = async (videoCallUrl: string) => {
    if (!currentChatRoom || !user) {
      console.error('❌ [VideoCall] Cannot send video link: no chat room or user');
      return;
    }

    try {
      const provider = videoCallUrl.includes('meet.jit.si') ? 'Jitsi' : 'Google Meet';
      const messageData = {
        chatRoomId: currentChatRoom.id,
        senderId: user.uid,
        senderName: user.displayName || 'Onbekend',
        senderPhoto: user.photoURL || '',
        message: `🎥 ${provider} video call gestart! Klik op de knop om mee te doen.`,
        timestamp: serverTimestamp(),
        messageType: 'video_call_link' as const,
        videoCallUrl: videoCallUrl
      };

      // Add message to Firebase
      await addDoc(collection(db, 'chatMessages'), messageData);

      // Update room with last message
      await updateDoc(doc(db, 'chatRooms', currentChatRoom.id), {
        lastMessage: `🎥 Video call link gedeeld`,
        lastMessageAt: serverTimestamp(),
        messageCount: currentChatRoom.messageCount + 1
      });

    } catch (error) {
      console.error('❌ [VideoCall] Error sending video link message:', error);
    }
  };

  const findOrCreateChatRoom = async (connectionUserId: string, connectionName: string, connectionPhoto?: string) => {
    if (!user) return;

    try {

      // Create sorted participant IDs for consistent lookup (both directions)
      const participantIds = [user.uid, connectionUserId].sort();
      
      // Look for existing chat room - check both possible participant orders
      // Query 1: current user is in participants
      const existingRoomQuery1 = query(
        collection(db, 'chatRooms'),
        where('participants', 'array-contains', user.uid)
      );
      
      // Query 2: other user is in participants (in case we're the second user)
      const existingRoomQuery2 = query(
        collection(db, 'chatRooms'),
        where('participants', 'array-contains', connectionUserId)
      );
      
      const [snapshot1, snapshot2] = await Promise.all([
        getDocs(existingRoomQuery1),
        getDocs(existingRoomQuery2)
      ]);
      
      // Combine results and find room with both participants
      const allRooms = [...snapshot1.docs, ...snapshot2.docs];
      const existingRoom = allRooms.find(doc => {
        const data = doc.data();
        const roomParticipants = Array.isArray(data.participants) ? data.participants : [];
        // Check if both users are in this room (order doesn't matter)
        const hasUser1 = roomParticipants.includes(user.uid);
        const hasUser2 = roomParticipants.includes(connectionUserId);
        return hasUser1 && hasUser2;
      });

      if (existingRoom) {
        // Use existing room
        const roomData = {
          id: existingRoom.id,
          ...existingRoom.data(),
          createdAt: existingRoom.data().createdAt?.toDate() || new Date(),
          lastMessageAt: existingRoom.data().lastMessageAt?.toDate()
        } as ChatRoom;
        setCurrentChatRoom(roomData);
        
        // Also find or create connectionFlow
        await findOrCreateConnectionFlow(existingRoom.id, [user.uid, connectionUserId]);
      } else {
        // ✅ FIX RACE CONDITION: Use deterministic chatRoom ID based on sorted userIds
        // This ensures both users always create/use the SAME chatRoom
        const deterministicRoomId = `chat_${participantIds[0]}_${participantIds[1]}`;
        const newRoomData = {
          participants: participantIds, // Sorted for consistency
          participantNames: participantIds.map(id => 
            id === user.uid ? (user.displayName || 'Onbekend') : connectionName
          ),
          participantPhotos: participantIds.map(id => 
            id === user.uid ? (user.photoURL || '') : (connectionPhoto || '')
          ),
          messageCount: 0,
          unreadCount: { [user.uid]: 0, [connectionUserId]: 0 },
          createdAt: serverTimestamp()
        };

        // ✅ Use setDoc with deterministic ID instead of addDoc (prevents race condition)
        const roomRef = doc(db, 'chatRooms', deterministicRoomId);
        await setDoc(roomRef, newRoomData, { merge: true }); // merge prevents overwriting if created simultaneously
        setCurrentChatRoom({
          id: deterministicRoomId,
          ...newRoomData,
          createdAt: new Date() // Use Date for local state, serverTimestamp is for Firestore
        });
        
        // ✅ Send system welcome message with both names
        const myName = user.displayName || 'Jij';
        const otherName = connectionName || 'Connectie';
        await addDoc(collection(db, 'chatMessages'), {
          chatRoomId: deterministicRoomId,
          senderId: 'system',
          senderName: 'SuperKonnected',
          senderPhoto: '',
          message: `🎉 ${myName} en ${otherName} zijn nu verbonden! Start het gesprek.`,
          timestamp: new Date(),
          messageType: 'system'
        });
        
        // Create connectionFlow for new room
        await findOrCreateConnectionFlow(deterministicRoomId, participantIds);
      }
    } catch (error) {
      console.error('❌ Error finding/creating chat room:', error);
    }
  };

  const findOrCreateConnectionFlow = async (chatRoomId: string, participants: string[]) => {
    if (!user) return;

    try {

      const flowRef = doc(db, 'connectionFlows', chatRoomId);
      const flowSnap = await getDoc(flowRef);

      if (flowSnap.exists()) {
        const flowData = { id: flowSnap.id, ...flowSnap.data() } as ConnectionFlow;
        setConnectionFlow(flowData);
      } else {
        const newFlowData: Omit<ConnectionFlow, 'id'> = {
          chatRoomId,
          participants,
          status: 'pending',
          currentStep: 'chat',
          chatStep: {
            status: 'pending',
            acceptances: {},
            duration: 480 // 8 minuten – in lijn met auto-timer
          },
          callStep: {
            status: 'pending',
            acceptances: {},
            duration: 1800 // 30 minutes
          },
          videoStep: {
            status: 'pending',
            acceptances: {},
            duration: 1800 // 30 minutes
          },
          physicalStep: {
            status: 'pending',
            acceptances: {}
          },
          createdAt: new Date(),
          updatedAt: new Date()
        };

        // ✅ Use merge to prevent overwriting if both users create simultaneously
        await setDoc(flowRef, newFlowData, { merge: true });
        setConnectionFlow({ id: chatRoomId, ...newFlowData });
      }
    } catch (error) {
      console.error('❌ Error finding/creating connection flow:', error);
    }
  };

  // ==========================================
  // 🔥 REALTIME LISTENER FOR CONNECTION FLOW
  // ==========================================
  // ✅ CRITICAL: This listener ensures BOTH users see timer updates in real-time!
  // Without this, timers won't be synchronized between users.
  useEffect(() => {
    if (!connectionFlow?.id) return;

    // StrictMode guard: React 18 runs effects twice in dev; skip if already
    // subscribed to this exact flow document.
    if (notifListenerFlowIdRef.current === connectionFlow.id) return;
    notifListenerFlowIdRef.current = connectionFlow.id;


    const flowRef = doc(db, 'connectionFlows', connectionFlow.id);
    
    // Keep track of previous state for change detection.
    // isFirstSnapshot guards against stale-data toasts on remount/refresh:
    // prevFlowData starts null, so without this guard every "!prev && new" check
    // would fire for data that was already written in a previous session.
    let prevFlowData: ConnectionFlow | null = null;
    let isFirstFlowSnapshot = true;
    
    const unsubscribe = onSnapshot(flowRef, (snapshot) => {
      if (snapshot.exists()) {
        const flowData = { id: snapshot.id, ...snapshot.data() } as ConnectionFlow;
        
        // ✅ UX: Real-time notifications for other user's actions.
        // Skip all change-detection on the first snapshot — prevFlowData is null
        // and the data reflects a previous session, not a live event.
        if (!isFirstFlowSnapshot && prevFlowData && user) {
          const otherUserName = selectedConnectionDataRef.current?.name || 'De andere gebruiker';
          
          // ==========================================
          // 🔔 CHAT STEP NOTIFICATIONS
          // ==========================================
          
          // Chat accepted by other user
          const _connData = selectedConnectionDataRef.current;
          if (!prevFlowData.chatStep?.acceptances?.[_connData?.id || ''] && 
              flowData.chatStep?.acceptances?.[_connData?.id || '']) {
            const otherUserId = _connData?.id;
            if (otherUserId && otherUserId !== user.uid) {
              success(`✅ ${otherUserName} heeft de chat geaccepteerd!`);
            }
          }
          
          // ==========================================
          // 🔔 CALL STEP NOTIFICATIONS
          // ==========================================
          
          // Call proposed by other user
          if (!prevFlowData.callStep?.proposedBy && flowData.callStep?.proposedBy) {
            if (flowData.callStep.proposedBy !== user.uid) {
              info(`📞 ${otherUserName} wil bellen`);
            }
          }
          
          // Call accepted (notify proposer)
          if (prevFlowData.callStep?.status === 'pending' && 
              flowData.callStep?.status === 'active') {
            if (flowData.callStep.proposedBy === user.uid) {
              success(`✅ ${otherUserName} heeft geaccepteerd!`);
            }
          }
          
          // Call ended by other user
          if (prevFlowData.callStep?.status === 'active' && 
              flowData.callStep?.status === 'completed') {
            info(`📴 Gesprek beëindigd`);
          }
          
          // ==========================================
          // 🔔 VIDEO STEP NOTIFICATIONS
          // ==========================================
          
          // Video proposed by other user
          if (!prevFlowData.videoStep?.proposedBy && flowData.videoStep?.proposedBy) {
            if (flowData.videoStep.proposedBy !== user.uid) {
              info(`📹 ${otherUserName} wil videobellen`);
            }
          }
          
          // Video accepted (notify proposer)
          if (prevFlowData.videoStep?.status === 'pending' && 
              flowData.videoStep?.status === 'active') {
            if (flowData.videoStep.proposedBy === user.uid) {
              success(`✅ ${otherUserName} heeft geaccepteerd!`);
            }
          }
          
          // Video ended by other user
          if (prevFlowData.videoStep?.status === 'active' && 
              flowData.videoStep?.status === 'completed') {
            info(`📴 Videogesprek beëindigd`);
          }
          
          // ==========================================
          // 🔔 TIMER +1 MIN NOTIFICATIONS
          // ==========================================
          
          // Detect timer extension in any phase
          const chatDurationChanged = prevFlowData.chatStep?.duration !== flowData.chatStep?.duration;
          const callDurationChanged = prevFlowData.callStep?.duration !== flowData.callStep?.duration;
          const videoDurationChanged = prevFlowData.videoStep?.duration !== flowData.videoStep?.duration;
          
          if (chatDurationChanged || callDurationChanged || videoDurationChanged) {
            // Only notify if duration increased (not initial set)
            const prevDuration = prevFlowData.chatStep?.duration || prevFlowData.callStep?.duration || prevFlowData.videoStep?.duration || 0;
            const newDuration = flowData.chatStep?.duration || flowData.callStep?.duration || flowData.videoStep?.duration || 0;
            
            if (newDuration > prevDuration && prevDuration > 0) {
              info(`⏱️ ${otherUserName} heeft +1 minuut toegevoegd`);
            }
          }
          
          // ==========================================
          // 🔔 PHYSICAL MEETING NOTIFICATIONS
          // ==========================================
          
          // Location proposed
          const prevUser1Proposals = prevFlowData.physicalMeeting?.locationProposals?.user1?.length || 0;
          const prevUser2Proposals = prevFlowData.physicalMeeting?.locationProposals?.user2?.length || 0;
          const newUser1Proposals = flowData.physicalMeeting?.locationProposals?.user1?.length || 0;
          const newUser2Proposals = flowData.physicalMeeting?.locationProposals?.user2?.length || 0;
          
          // Check if other user added a location
          const otherUserAddedLocation = (
            (flowData.user1Id === user.uid && newUser2Proposals > prevUser2Proposals) ||
            (flowData.user2Id === user.uid && newUser1Proposals > prevUser1Proposals)
          );
          
          if (otherUserAddedLocation) {
            info(`📍 ${otherUserName} heeft een locatie voorgesteld`);
          }
          
          // On the way status changed
          const prevOnTheWay = prevFlowData.physicalMeeting?.navigationStatus?.user1?.started || 
                               prevFlowData.physicalMeeting?.navigationStatus?.user2?.started;
          const newOnTheWay = flowData.physicalMeeting?.navigationStatus?.user1?.started || 
                              flowData.physicalMeeting?.navigationStatus?.user2?.started;
          
          if (!prevOnTheWay && newOnTheWay) {
            // Check which user started
            const user1Started = flowData.physicalMeeting?.navigationStatus?.user1?.started;
            const user2Started = flowData.physicalMeeting?.navigationStatus?.user2?.started;
            
            if ((user1Started && flowData.user1Id !== user.uid) || 
                (user2Started && flowData.user2Id !== user.uid)) {
              info(`🚗 ${otherUserName} is onderweg naar de locatie`);
            }
          }
          
          // Arrived status changed
          const prevArrived = prevFlowData.physicalMeeting?.navigationStatus?.user1?.arrived || 
                              prevFlowData.physicalMeeting?.navigationStatus?.user2?.arrived;
          const newArrived = flowData.physicalMeeting?.navigationStatus?.user1?.arrived || 
                             flowData.physicalMeeting?.navigationStatus?.user2?.arrived;
          
          if (!prevArrived && newArrived) {
            // Check which user arrived
            const user1Arrived = flowData.physicalMeeting?.navigationStatus?.user1?.arrived;
            const user2Arrived = flowData.physicalMeeting?.navigationStatus?.user2?.arrived;
            
            if ((user1Arrived && flowData.user1Id !== user.uid) || 
                (user2Arrived && flowData.user2Id !== user.uid)) {
              success(`✅ ${otherUserName} is aangekomen op de locatie`);
            }
          }
          
          // Meeting started
          if (!prevFlowData.physicalMeeting?.meetingStartTime && 
              flowData.physicalMeeting?.meetingStartTime) {
            info(`🤝 Meeting gestart`);
          }
          
          // Meeting ended
          if (!prevFlowData.physicalMeeting?.meetingEndTime && 
              flowData.physicalMeeting?.meetingEndTime) {
            info(`👋 Meeting beëindigd`);
          }
          
          
        }
        
        isFirstFlowSnapshot = false;
        prevFlowData = flowData;
        // Keep Google Meet URL in sync — also done in Listener B for bootstrap,
        // but Listener A is the source of truth once it is running.
        setGoogleMeetUrl((flowData as any).googleMeetUrl || null);
        
        // Check if other user has ended/aborted the connection
        if ((flowData as any).endedBy && (flowData as any).endedBy !== user?.uid) {
          setOtherUserEnded(true);
          // ✅ Auto-close timer popup when other user ends — prevents stale popup
          timerExpiredDismissedKeyRef.current = 'other_user_ended';
          setShowTimerExpiredPopup(false);
        } else {
          setOtherUserEnded(false);
        }
        
        // ✅ FIX: If flow is terminated (aborted), reset UI for this user
        if (flowData.status === 'terminated') {
          
          // Close all active interfaces
          setShowCallInterface(false);
          setShowVideoCallInterface(false);
          setShowAgenda(false);
          setShowBreakPopup(false);
          setNextStepChoiceOpen(false);
          
          // Reset phase to chat
          setCurrentPhase('chat');
          
          // Stop all audio/video
          stopAllAudioAndStreams();
          
          // Show notification if we weren't the one who terminated
          if ((flowData as any).endedBy !== user?.uid) {
            setToastMessage('⚠️ De andere gebruiker heeft het gesprek afgebroken');
          }
        }
        
        setConnectionFlow(flowData);
      } else {
        console.warn('⚠️ [Realtime] ConnectionFlow deleted');
        setConnectionFlow(null);
      }
    }, (error) => {
      console.error('❌ [Realtime] Error listening to connectionFlow:', error);
    });

    return () => {
      notifListenerFlowIdRef.current = null; // allow re-subscription on next mount
      unsubscribe();
    };
  }, [connectionFlow?.id]); // Only re-subscribe if flow ID changes

  // ==========================================
  // 📡 PRESENCE LISTENER - Track other user's actions
  // ==========================================
  useEffect(() => {
    if (!connectionFlow?.id || !selectedConnectionData?.id || !user?.uid) return;
    
    // Determine other user ID
    const otherUserId = selectedConnectionData.user1Id === user.uid
      ? selectedConnectionData.user2Id
      : selectedConnectionData.user1Id;
    
    if (!otherUserId) return;
    
    
    const unsubscribe = listenToPresence(connectionFlow.id, otherUserId, (presence) => {
      setOtherUserPresence(presence);
    });
    
    return () => {
      unsubscribe();
    };
  }, [connectionFlow?.id, selectedConnectionData?.id, user?.uid]);

  // ==========================================
  // PLANNING LOCK + LOCATION AGREEMENT LISTENER
  // ==========================================
  // Listen for planning lock changes and location agreements.
  // When the other user starts planning, auto-open the Extended Agenda
  // for the current user so both users see the same planning view.
  // Uses selectedConnectionData.id (the real Firestore document ID).
  useEffect(() => {
    const connDocId = selectedConnectionData?.id;
    if (!connDocId || !user?.uid) return;
    
    const connectionRef = doc(db, 'connections', connDocId);
    let prevPlanningOwner: string | null = null;
    let prevSelectedLocationId: string | null = null;
    // planningOwnerStartedAt is updated every time acquirePlanningLock runs (even re-acquisition)
    let prevOwnerStartedAt: string | null = null;
    let isFirstSnapshot = true;
    
    
    const unsubscribe = onSnapshot(connectionRef, (snapshot) => {
      if (!snapshot.exists()) return;
      
      const data = snapshot.data();
      const newPlanningOwner = data.planningOwnerId || null;
      const planningOwnerName = data.planningOwnerName || null;
      // planningOwnerStartedAt is a serverTimestamp — convert to ms string for comparison
      const newOwnerStartedAt = data.planningOwnerStartedAt?.toMillis?.()?.toString() || null;
      
      // --- Planning lock changes ---
      if (newPlanningOwner && newPlanningOwner !== user.uid && planningOwnerName) {
        const hasSelectedPhysicalLocation = !!data?.physicalMeeting?.selectedLocation?.locationId;
        const isPhysicalContext =
          data?.phase === 'physical' ||
          selectedConnectionDataRef.current?.phase === 'physical' ||
          !!data?.physicalMeeting;
        const shouldAutoOpenAgenda = isPhysicalContext ? hasSelectedPhysicalLocation : true;
        // lockSessionChanged: owner re-acquired the lock (new session, same or different owner)
        const lockSessionChanged = !isFirstSnapshot && newOwnerStartedAt && newOwnerStartedAt !== prevOwnerStartedAt;
        const ownerJustStarted = !isFirstSnapshot && !prevPlanningOwner;

        if (isFirstSnapshot) {
          // First load: silently open in observer mode (no toast — agenda might not be wanted)
          setPlanningLockResult({
            granted: false,
            ownerId: newPlanningOwner,
            ownerName: planningOwnerName,
            phase: (data.planningOwnerPhase as ConnectionPhase) || null,
            expiresAt: data.planningLockExpiresAt || null,
          });
          // Guard: do NOT auto-open the agenda on first snapshot if:
          // 1. The user just navigated here (within 1500ms) — the planningOwnerId in
          //    Firestore is stale leftover from a previous planning session and must
          //    not hijack the view the user explicitly navigated to.
          // 2. An appointment is already scheduled — planning is done, no need to
          //    re-open the agenda automatically.
          const msSinceNavFirstSnap = Date.now() - lastNavigationTimeRef.current;
          const appointmentAlreadyScheduled = !!data.activeEventId &&
            ['scheduled', 'ready', 'in_progress', 'completed'].includes(data.phaseStatus || '');
          if (shouldAutoOpenAgenda && msSinceNavFirstSnap > 2500 && !appointmentAlreadyScheduled) {
            setShowAgenda(true);
          }
        } else if (ownerJustStarted || lockSessionChanged) {
          // Real-time: other user started or re-opened planning (ownerStartedAt changed = new session).
          // Register in dedup set so the planningNotifications backup channel skips this event.
          shownPlanningNotifRef.current.add(`${connDocId}:planning_started`);
          setToastMessage(`${planningOwnerName} is een afspraak aan het plannen. Je agenda wordt geopend.`);
          setPlanningLockResult({
            granted: false,
            ownerId: newPlanningOwner,
            ownerName: planningOwnerName,
            phase: (data.planningOwnerPhase as ConnectionPhase) || null,
            expiresAt: data.planningLockExpiresAt || null,
          });
          if (shouldAutoOpenAgenda) {
            setShowAgenda(true);
          }
        }
      }

      if (!isFirstSnapshot && prevPlanningOwner && !newPlanningOwner) {
      }

      prevPlanningOwner = newPlanningOwner;

      // --- Location agreement detection ---
      const selectedLoc = data.physicalMeeting?.selectedLocation;
      const newLocationId = selectedLoc?.locationId || null;
      const selectedByOther = selectedLoc?.selectedBy !== user.uid;
      const locationName = selectedLoc?.location?.name || selectedLoc?.location?.address || 'een locatie';

      // Always keep confirmedMeetingLocation in sync (regardless of who selected it)
      if (selectedLoc?.location) {
        setConfirmedMeetingLocation({
          name: selectedLoc.location.name || selectedLoc.location.address || 'Ontmoetingslocatie',
          address: selectedLoc.location.address || '',
        });
      } else if (!newLocationId) {
        setConfirmedMeetingLocation(null);
      }

      if (newLocationId && selectedByOther) {
        if (isFirstSnapshot) {
          // First load: location was already agreed.
          // Guard: skip auto-open if the user just navigated here (stale Firestore state
          // must not override the view the user explicitly navigated to) or if an
          // appointment is already scheduled (planning is complete).
          const msSinceNavLocSnap = Date.now() - lastNavigationTimeRef.current;
          const apptScheduled = !!data.activeEventId &&
            ['scheduled', 'ready', 'in_progress', 'completed'].includes(data.phaseStatus || '');
          if (msSinceNavLocSnap > 2500 && !apptScheduled) {
            setToastMessage(`Locatie "${locationName}" is al gekozen. Je agenda wordt geopend.`);
            setShowAgenda(true);
          }
        } else if (prevSelectedLocationId !== newLocationId) {
          // Real-time change: other user just confirmed a location
          setToastMessage(`Jullie zijn "${locationName}" overeengekomen! Nu kunnen jullie samen de datum vastleggen.`);
          setShowAgenda(true);
        }
      }

      prevSelectedLocationId = newLocationId;
      prevOwnerStartedAt = newOwnerStartedAt;

      // ── Completion detection for user 2 ──────────────────────────────────
      // This listener watches connections/{id} directly (no status filter), so
      // it still fires after completeCurrentPhase writes {phase: 'completed',
      // status: 'active'} — even though the connections query drops the document.
      // User 2 would otherwise never see the completion screen without a refresh.
      const connPhase = data.phase as ConnectionPhase | undefined;
      if (connPhase === 'completed' && currentPhaseRef.current !== 'completed') {
        setCurrentPhase('completed');
        setSelectedConnectionData((prev: any) =>
          prev ? { ...prev, phase: 'completed', phaseStatus: 'done' } : prev
        );
      }

      // After first snapshot, all subsequent changes are real-time
      isFirstSnapshot = false;
    }, (error) => {
      console.error('[Realtime] Error listening to planning lock:', error);
    });
    
    return () => {
      unsubscribe();
    };
  }, [selectedConnectionData?.id, user?.uid]);

  // ==========================================
  // PLANNING NOTIFICATIONS LISTENER
  // ==========================================
  // Backup channel: listen for planningNotifications documents
  // so User 2 always gets notified when User 1 starts planning,
  // even if the connection document listener misses it.
  // Use a ref so the listener can always read the latest selectedConnectionData without re-subscribing
  const selectedConnectionIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    selectedConnectionIdRef.current = selectedConnectionData?.id;
  }, [selectedConnectionData?.id]);

  // Deduplication guard: tracks which `${connectionId}:${type}` notifications have
  // already been shown in this session via the connection-document listener channel.
  // The planningNotifications backup channel checks this set before showing a toast,
  // preventing the user from seeing the same planning event twice.
  const shownPlanningNotifRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!user?.uid) return;

    const q = query(
      collection(db, 'planningNotifications'),
      where('toUserId', '==', user.uid),
      where('read', '==', false)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      snapshot.docChanges().forEach(async (change) => {
        if (change.type !== 'added') return;
        const data = change.doc.data();

        // Dedup: skip if this connectionId+type was already handled by the
        // connection-document listener in this session.
        const dedupKey = `${data.connectionId}:${data.type}`;
        if (shownPlanningNotifRef.current.has(dedupKey)) {
          // Still mark as read so it doesn't accumulate in Firestore
          updateDoc(doc(db, 'planningNotifications', change.doc.id), { read: true }).catch(() => {});
          return;
        }
        shownPlanningNotifRef.current.add(dedupKey);

        const isCurrentConn = data.connectionId === selectedConnectionIdRef.current;
        const name = data.fromUserName || 'Je connectie';

        // --- Type: planning_started ---
        if (data.type === 'planning_started') {
          setToastMessage(`${name} is de planning gestart.`);
          setPlanningLockResult({
            granted: false,
            ownerId: data.fromUserId,
            ownerName: name,
            phase: null,
            expiresAt: null,
          });
          // Only open the agenda when:
          // 1. This notification is for the connection the user is currently viewing.
          //    Stale notifications for other connections must never hijack the UI.
          // 2. The user did not just navigate here (within 1500ms) — navigation can
          //    arrive before selectedConnectionIdRef is populated, and we must not
          //    open the agenda on a plain "view connection" navigation.
          // 3. Not in the physical location-selection flow (confirmed location is the gate).
          const msSinceNav = Date.now() - lastNavigationTimeRef.current;
          const isPhysicalFlow = selectedConnectionData?.phase === 'physical' || currentPhase === 'physical';
          if (
            isCurrentConn &&
            msSinceNav > 2500 &&
            (!isPhysicalFlow || !!confirmedMeetingLocation)
          ) {
            setShowAgenda(true);
          }
        }

        // --- Type: location_selected_planning (vanuit PhysicalMeetingPhaseV2) ---
        if (data.type === 'location_selected_planning') {
          const locName = data.locationName || 'een locatie';
          setPlanningBanner({ userName: name, locationName: locName });
          // Also set the confirmed location so the planning_started guard passes,
          // and open the ExtendedAgenda immediately for User 2 as well.
          // Guard: only open for the active connection and not right after navigation.
          setConfirmedMeetingLocation({ name: locName, address: '' });
          if (isCurrentConn && Date.now() - lastNavigationTimeRef.current > 2500) {
            setShowAgenda(true);
          }
        }

        // --- Type: physical_phase_started ---
        if (data.type === 'physical_phase_started') {
          setToastMessage(`${name} is de fysieke afspraak aan het plannen.`);
          setPlanningLockResult({
            granted: false,
            ownerId: data.fromUserId || null,
            ownerName: name,
            phase: 'physical',
            expiresAt: null,
          });
        }

        // --- Type: autoplan_match_found ---
        if (data.type === 'autoplan_match_found') {
          setAutoplanBanner({ userName: name, type: 'match', slotLabel: data.locationName, matchCount: data.matchCount || 1 });
        }

        // --- Type: autoplan_no_match ---
        if (data.type === 'autoplan_no_match') {
          setAutoplanBanner({ userName: name, type: 'no_match' });
        }

        // Mark as read so it doesn't re-fire on reconnect
        try {
          await updateDoc(doc(db, 'planningNotifications', change.doc.id), { read: true });
        } catch (e) {
          console.warn('[planningNotif] Could not mark as read:', e);
        }
      });
    }, (error) => {
      console.warn('[planningNotif] listener error:', error);
    });

    return () => {
      unsubscribe();
    };
  // Only re-subscribe when the user changes. All other values are read via refs
  // or are irrelevant to the Firestore query, so listing them here would cause
  // hundreds of unnecessary re-subscriptions during the planning flow.
  }, [user?.uid]);

  // Note: agenda auto-open is blocked at source during physical planning
  // (see shouldAutoOpenAgenda logic in connections onSnapshot).

  // ==========================================
  // 📅 APPOINTMENT CONFIRMATION CARD LISTENER
  // ==========================================
  // For the user who plans: listen via custom event from ExtendedAgenda
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (!detail?.slot) return;
      const slot = detail.slot as { start: Date | string; end: Date | string };
      const start = slot.start instanceof Date ? slot.start : new Date(slot.start);
      const end = slot.end instanceof Date ? slot.end : new Date(slot.end);
      setAppointmentConfirmCard({
        start,
        end,
        locationName: confirmedMeetingLocation?.name || confirmedMeetingLocation?.address || '',
        otherUserName: selectedConnectionData?.name || 'Je connectie',
        myName: user?.displayName || 'Jij',
      });
    };
    window.addEventListener('appointment-planned', handler);
    return () => window.removeEventListener('appointment-planned', handler);
  }, [confirmedMeetingLocation, selectedConnectionData?.name, user?.displayName]);

  // For the OTHER user: listen for meeting_confirmed notification in Firestore
  useEffect(() => {
    if (!user?.uid) return;
    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', user.uid),
      where('type', '==', 'meeting_confirmed'),
      where('read', '==', false)
    );
    const unsub = onSnapshot(q, (snap) => {
      snap.docChanges().forEach(async (change) => {
        if (change.type !== 'added') return;
        const data = change.doc.data();
        const startRaw = data.data?.start;
        const endRaw = data.data?.end;
        if (!startRaw || !endRaw) return;
        const start = new Date(startRaw);
        const end = new Date(endRaw);
        setAppointmentConfirmCard({
          start,
          end,
          locationName: confirmedMeetingLocation?.name || confirmedMeetingLocation?.address || '',
          otherUserName: data.data?.otherUserName || selectedConnectionData?.name || 'Je connectie',
          myName: user?.displayName || 'Jij',
        });
        // Mark as read
        updateDoc(doc(db, 'notifications', change.doc.id), { read: true }).catch(() => {});
      });
    });
    return () => unsub();
  }, [user?.uid, confirmedMeetingLocation, selectedConnectionData?.name, user?.displayName]);

  // ==========================================
  // 🎯 SYNC CURRENT PHASE WITH CONNECTION FLOW
  // ==========================================
  // ✅ This ensures the phase indicator at the top stays in sync with the active step
  // ⚠️ IMPORTANT: Only sync when steps are ACTIVE or recently PENDING
  // Don't override currentPhase with old/stale data
  useEffect(() => {
    if (!connectionFlow || !user?.uid) return;
    
    const now = Date.now();
    const RECENT_THRESHOLD = 30000; // 30 seconds - only react to recent changes
    
    // Als we al in de call-UI zitten, niet terugschakelen naar chat
    if (showCallInterface) {
      if (currentPhase !== 'call') {
        setCurrentPhase('call');
      }
      return;
    }
    
    // ✅ FIX: Als we al in de video-UI zitten, niet terugschakelen naar chat
    if (showVideoCallInterface) {
      if (currentPhase !== 'video') {
        setCurrentPhase('video');
      }
      return;
    }

    // ✅ FIX: Als handleDirectCall('physical') nog bezig is, niet terugschakelen.
    //
    // TIMING RACE: setCurrentPhase('physical') wordt synchroon gezet in handleDirectCall
    // (vóór de eerste await). React herrendert en de phase-sync effect draait opnieuw.
    // Op dat moment is physicalStep.proposedBy nog null in de Firestore-snapshot
    // (de write is nog niet klaar), dus isInitiatorWithPendingPhysical = false en de
    // guard hieronder valt weg. chatStep.active pakt dan de sync over en reset naar
    // 'chat' — zichtbaar als een flicker.
    //
    // callProposalInFlightRef.current is true voor de gehele duur van handleDirectCall,
    // inclusief alle awaits. Dit beschermt de INITIATOR tegen race conditions.
    // De ONTVANGER (User 2) heeft geen inFlight-ref maar wordt beschermd via de
    // connectionFlows Firestore-listener: zodra physicalStep.status 'pending' of 'active'
    // wordt, mapt de listener direct naar 'physical' en roept setActiveCard('physical') aan.
    if (callProposalInFlightRef.current && currentPhase === 'physical') {
      return;
    }
    
    // Extra guard: als wij de initiator zijn en callStep pending is, blijf in call
    // ⚠️ MAAR: Alleen als we AL in de call fase zitten! Anders bypass we chat fase
    const isInitiatorWithPendingCall = 
      connectionFlow.callStep?.status === 'pending' && 
      connectionFlow.callStep?.proposedBy === user.uid;

    if (isInitiatorWithPendingCall && currentPhase === 'call') {
      // Keep in call phase (don't switch back to chat)
      return;
    }
    
    // ✅ FIX: Als wij de initiator zijn en videoStep pending is, blijf in video
    // ⚠️ MAAR: Alleen als we AL in de video fase zitten! Anders bypass we chat/call fases
    const isInitiatorWithPendingVideo = 
      connectionFlow.videoStep?.status === 'pending' && 
      connectionFlow.videoStep?.proposedBy === user.uid;

    if (isInitiatorWithPendingVideo && currentPhase === 'video') {
      // Keep in video phase (don't switch back to chat)
      return;
    }

    // ✅ FIX: Als wij de initiator zijn en physicalStep pending is, blijf in physical.
    // Zonder deze guard schakelt de fase-sync terug naar 'video' omdat videoStep.status
    // nog 'active' staat in Firestore terwijl wij al naar physical zijn gegaan.
    // Identiek patroon als de call- en video-guard hierboven.
    const isInitiatorWithPendingPhysical =
      connectionFlow.physicalStep?.status === 'pending' &&
      connectionFlow.physicalStep?.proposedBy === user.uid;

    if (isInitiatorWithPendingPhysical && currentPhase === 'physical') {
      // Keep in physical phase (don't let videoStep.active revert us)
      return;
    }
    
    // Helper to check if a step is recent OR has no startTime yet (just initiated)
    const isRecent = (step: any) => {
      // If step is pending/active but has no startTime yet, consider it recent
      // (This happens when a call is just initiated but Firestore hasn't synced startTime)
      if (step?.status === 'pending' || step?.status === 'active') {
        if (!step.startTime) {
          return true; // Just started, no timestamp yet
        }
      }
      if (!step?.startTime) return false;
      const startTimeMs = step.startTime.toDate ? step.startTime.toDate().getTime() : new Date(step.startTime).getTime();
      return (now - startTimeMs) < RECENT_THRESHOLD;
    };
    
    // Priority order: Most recently active/pending step wins
    // ONLY check ACTIVE steps, don't auto-switch on PENDING
    // (PENDING switches are handled explicitly in handleDirectCall, etc.)
    
    // Physical meeting when active (recent) or proposed — single consolidated check.
    // NOTE: 'pending' alone must NOT trigger a phase switch (it is the default initial
    // state of every step). Only 'active' (with recency guard) and 'proposed' count.
    if (
      (connectionFlow.physicalStep?.status === 'active' && isRecent(connectionFlow.physicalStep)) ||
      connectionFlow.physicalStep?.status === 'proposed'
    ) {
      setCurrentPhase('physical');
      setActiveCard('physical');
      return;
    }
    
    // Video call when active
    // Guard: if connections.phase is already 'physical', a race condition may still
    // show videoStep as 'active' in connectionFlows. Don't revert to video in that case.
    if (connectionFlow.videoStep?.status === 'active' && isRecent(connectionFlow.videoStep)) {
      if (selectedConnectionData?.phase === 'physical') {
        // connections.phase already advanced to physical — respect that, don't revert.
        return;
      }
      setCurrentPhase('video');
      return;
    }
    
    // Audio call when active OR pending (for the initiator)
    if (connectionFlow.callStep?.status === 'active' && isRecent(connectionFlow.callStep)) {
      setCurrentPhase('call');
      return;
    }
    
    // Handle PENDING call for initiator only (show "call" screen while waiting)
    // BUT: Don't override if currently in an active chat
    if (
      connectionFlow.callStep?.status === 'pending' && 
      connectionFlow.callStep?.proposedBy === user.uid &&
      isRecent(connectionFlow.callStep) &&
      connectionFlow.chatStep?.status !== 'active' // Don't override active chat!
    ) {
      setCurrentPhase('call');
      return;
    }
    
    // Chat when active (HIGH PRIORITY - don't let pending calls override this!)
    if (connectionFlow.chatStep?.status === 'active' && isRecent(connectionFlow.chatStep)) {
      setCurrentPhase('chat');
      return;
    }
    
    // Chat when pending but I have accepted (waiting for other user)
    // This ensures we stay in chat phase while waiting for other user to join
    if (
      connectionFlow.chatStep?.status === 'pending' &&
      connectionFlow.chatStep?.acceptances?.[user.uid] === true
    ) {
      setCurrentPhase('chat');
      return;
    }

    // ✅ FIX: Als call/video stap 'completed' is en currentPhase nog op die stap staat,
    // forceer terug naar chat. Dit voorkomt dat User 2 vastloopt op een voltooide stap.
    if (
      (currentPhase === 'video' && connectionFlow.videoStep?.status === 'completed') ||
      (currentPhase === 'call' && connectionFlow.callStep?.status === 'completed')
    ) {
      setCurrentPhase('chat');
      return;
    }
    
    // If nothing recent is active, don't change currentPhase
    // (Let manual setCurrentPhase calls handle it)
    
  }, [connectionFlow?.callStep?.status, connectionFlow?.callStep?.proposedBy, connectionFlow?.videoStep?.status, connectionFlow?.videoStep?.proposedBy, connectionFlow?.physicalStep?.status, connectionFlow?.physicalStep?.proposedBy, connectionFlow?.chatStep?.status, user?.uid, currentPhase, showCallInterface, showVideoCallInterface]);

  // Extra sync: forceer chat terug wanneer chatStep actief is en geen andere stap bezig is.
  // Guards: call-UI actief, video-UI actief, én physical step bezig —
  // chatStep.status wordt nooit op 'completed' gezet bij de overgang naar latere fases,
  // dus zonder de physicalBusy guard zou deze effect de physical fase altijd naar chat resetten.
  useEffect(() => {
    if (!connectionFlow) return;

    const callBusy =
      connectionFlow.callStep?.status === 'active' ||
      connectionFlow.callStep?.status === 'pending';
    const videoBusy =
      connectionFlow.videoStep?.status === 'active' ||
      connectionFlow.videoStep?.status === 'pending';
    const physicalBusy =
      connectionFlow.physicalStep?.status === 'active' ||
      connectionFlow.physicalStep?.status === 'pending' ||
      connectionFlow.physicalStep?.status === 'proposed';

    if (showCallInterface || showVideoCallInterface) {
      return;
    }

    if (!callBusy && !videoBusy && !physicalBusy && connectionFlow.chatStep?.status === 'active' && currentPhase !== 'chat') {
      setCurrentPhase('chat');
    }
  }, [
    connectionFlow?.chatStep?.status,
    connectionFlow?.callStep?.status,
    connectionFlow?.videoStep?.status,
    connectionFlow?.physicalStep?.status,
    currentPhase,
    showCallInterface,
    showVideoCallInterface
  ]);

  // ==========================================
  // 📹 JITSI INLINE IFRAME (NO POPUP)
  // ==========================================
  // Jitsi is now shown inline via iframe, no popup needed!
  // This useEffect just stops WebRTC audio when video starts
  useEffect(() => {
    if (!connectionFlow || !user) return;

    const videoStep = connectionFlow.videoStep;
    if (!videoStep?.meetUrl) return;
    if (videoStep.status !== 'active' && videoStep.status !== 'pending') return;

    // Use functional setter to read the CURRENT phase without stale closure issues.
    // We do not change the phase here — only toggle the video UI when we are
    // already confirmed to be in the video phase.
    setCurrentPhase((prev) => {
      if (prev !== 'video') return prev; // not yet in video phase — skip side effects

      // Stop WebRTC audio first (if active)
      if (webrtcAudioCall && webrtcAudioCall.call) {
        webrtcAudioCall.endCall();
      }

      setShowVideoCallInterface(true);
      setShowCallInterface(false);
      setShowJitsiCall(false);
      return prev; // phase stays 'video', unchanged
    });
  }, [connectionFlow?.videoStep?.status, connectionFlow?.videoStep?.meetUrl, connectionFlow?.id, user, webrtcAudioCall]);

  const acceptChatStep = async () => {
    if (!user) {
      console.error('❌ User not found');
                          showError('Je bent niet ingelogd. Log opnieuw in.');
      return;
    }

    if (!connectionFlow) {
      console.error('❌ ConnectionFlow not found');
      // Try to create it if we have a chat room
      if (currentChatRoom) {
        await findOrCreateConnectionFlow(currentChatRoom.id, currentChatRoom.participants);
        // Wait a moment for state to update, then try again
        setTimeout(async () => {
          const flowRef = doc(db, 'connectionFlows', currentChatRoom.id);
          const flowSnap = await getDoc(flowRef);
          if (flowSnap.exists()) {
            const flowData = { id: flowSnap.id, ...flowSnap.data() } as ConnectionFlow;
            setConnectionFlow(flowData);
            await acceptChatStep(); // Retry
          } else {
            showError('Kon connection flow niet maken. Probeer opnieuw.');
          }
        }, 500);
        return;
      } else {
        showError('Chat room niet gevonden. Selecteer eerst een connectie.');
        return;
      }
    }

    try {

      const flowRef = doc(db, 'connectionFlows', connectionFlow.id);

      // Use a transaction so two simultaneous acceptances never lose each other.
      // The transaction reads the LIVE Firestore state (not local component state)
      // before merging the new acceptance, making this race-condition-safe.
      await runTransaction(db, async (transaction) => {
        const snap = await transaction.get(flowRef);
        if (!snap.exists()) throw new Error('ConnectionFlow document not found');

        const flowData = snap.data() as any;
        const liveAcceptances: Record<string, boolean> = flowData?.chatStep?.acceptances || {};

        // Idempotent: if this user already accepted, skip the write
        if (liveAcceptances[user.uid] === true) return;

        const updatedAcceptances = { ...liveAcceptances, [user.uid]: true };
        const duration: number = flowData?.chatStep?.duration || 480;

        transaction.update(flowRef, {
          'chatStep.acceptances': updatedAcceptances,
          'chatStep.status': 'active',
          'chatStep.startTime': serverTimestamp(),
          'chatStep.duration': duration,
          status: 'active',
          updatedAt: serverTimestamp(),
        });
      });

      setCurrentPhase('chat');

      // Reload to sync local connectionFlow state.
      // Wrapped separately: a reload failure must not mask the successful transaction.
      // If this getDoc fails the real-time onSnapshot listener will eventually
      // deliver the same update, so no user-visible action is required.
      try {
        const updatedFlowSnap = await getDoc(flowRef);
        if (updatedFlowSnap.exists()) {
          setConnectionFlow({ id: updatedFlowSnap.id, ...updatedFlowSnap.data() } as ConnectionFlow);
        }
      } catch (reloadErr) {
        console.warn('[acceptChatStep] State reload after transaction failed (non-critical):', reloadErr);
        // The onSnapshot listener will deliver the update asynchronously — no user action needed.
      }
    } catch (error: any) {
      console.error('❌ Error accepting chat step:', error);
      showError(`Fout bij accepteren: ${error.message || 'Onbekende fout'}`);
    }
  };

  /**
   * Beëindig de VIDEO stap handmatig.
   *
   * Dit zet `videoStep.status` op 'completed' in Firestore.
   * De realtime listener (flowData.videoStep.status === 'completed')
   * zorgt er vervolgens voor dat de inline Jitsi iframe wordt verborgen
   * door `setShowVideoCallInterface(false)` aan te roepen.
   */
  const endVideoStep = async () => {
    if (!connectionFlow || !user) {
      console.warn('⚠️ [VideoCall] Kan video niet beëindigen – geen connectionFlow of user');
      return;
    }

    try {

      const flowRef = doc(db, 'connectionFlows', connectionFlow.id);

      await updateDoc(flowRef, {
        'videoStep.status': 'completed',
        'videoStep.endedBy': user.uid,
        'videoStep.endTime': serverTimestamp(),
        // ✅ FIX: Set currentStep to 'physical' so BOTH users transition to the physical phase.
        // Previously this was 'chat' which caused the connectionFlows listener to override
        // connections.phase='physical' back to 'chat', preventing the Video→Physical transition.
        currentStep: 'physical',
        status: 'active',
        updatedAt: serverTimestamp()
      });

      // Hide video interface — phase will be set to 'physical' by finishAndChooseNext.
      setShowVideoCallInterface(false);

    } catch (error: any) {
      console.error('❌ [VideoCall] Error ending video step:', error);
      showError(`Er ging iets mis bij het afsluiten van het videogesprek: ${error.message || 'Onbekende fout'}`);
    }
  };

  /**
   * Beëindig de CALL (audio) stap.
   * Zet `callStep.status` op 'completed' en reset `currentStep` naar 'chat'.
   * Dit zorgt ervoor dat BEIDE users correct teruggaan naar de chat fase.
   */
  const endCallStep = async () => {
    if (!connectionFlow || !user) {
      console.warn('⚠️ [Call] Kan call niet beëindigen – geen connectionFlow of user');
      return;
    }

    try {

      const flowRef = doc(db, 'connectionFlows', connectionFlow.id);

      await updateDoc(flowRef, {
        'callStep.status': 'completed',
        'callStep.endedBy': user.uid,
        'callStep.endTime': serverTimestamp(),
        // ✅ FIX: Reset currentStep zodat BEIDE users terug naar chat gaan
        currentStep: 'chat',
        status: 'active',
        updatedAt: serverTimestamp()
      });

      // Direct UI-feedback
      setShowCallInterface(false);
      setCurrentPhase('chat');

    } catch (error: any) {
      console.error('❌ [Call] Error ending call step:', error);
    }
  };

  const proposeNextStep = async (step: 'call' | 'video' | 'physical') => {
    if (!connectionFlow || !user) return;

    try {

      const flowRef = doc(db, 'connectionFlows', connectionFlow.id);
      const stepKey = step === 'call' ? 'callStep' : step === 'video' ? 'videoStep' : 'physicalStep';

      // ✅ GENERATE MEET URL for VIDEO ONLY (call uses WebRTC)
      let meetUrl: string | null = null;
      if (step === 'video') {
        try {
          // ✅ This creates Google Meet (if user has Calendar) or Jitsi (fallback)
          meetUrl = await meetService.createMeetUrlForFlow(
            connectionFlow.id, 
            step, 
            user?.email || undefined,
            user?.uid,  // Pass user ID to check Google Calendar status
            user?.displayName || user?.email || 'Gebruiker'  // Pass display name for Jitsi
          );
          const provider = meetUrl.includes('meet.jit.si') ? 'Jitsi' : 'Google Meet';
          // 🆕 SEND VIDEO LINK TO CHAT
          if (meetUrl) {
            await sendVideoCallLinkMessage(meetUrl);
          }
        } catch (err) {
          console.error(`❌ [VideoCall] Failed to generate video call URL:`, err);
          // Continue anyway - URL can be set manually later
        }
      }

      // ✅ ATOMIC UPDATE: Include meetUrl in the same write (avoid race condition)
      const updateData: any = {
        // Use one canonical "awaiting acceptance" status across flows
        [`${stepKey}.status`]: 'pending',
        [`${stepKey}.proposedBy`]: user.uid,
        [`${stepKey}.proposedAt`]: serverTimestamp(),
        [`${stepKey}.acceptances`]: { [user.uid]: true },
        [`${stepKey}.duration`]: 480,  // 8 minuten voor alle fases
        currentStep: step,
        status: 'pending',
        updatedAt: serverTimestamp()
      };

      // Add meetUrl if this is a video step
      if (step === 'video' && meetUrl) {
        updateData[`${stepKey}.meetUrl`] = meetUrl;
      }

      await updateDoc(flowRef, updateData);

      // ==========================================
      // ✅ WEBRTC AUDIO CALL SETUP
      // ==========================================
      // WebRTC call will be automatically started by useWebRTCAudioCall hook
      // when callStep.status becomes 'active' (after both accept)
      if (step === 'call') {
        try {
          const otherUserId = (connectionFlow.participants || []).find(pId => pId !== user.uid);
          if (!otherUserId) {
            console.warn('⚠️ Geen andere participant gevonden voor call');
            return;
          }

          // Start uitgaande beltoon (keep this, it's UX)
          try {
            if (outgoingToneRef.current) {
              try {
                outgoingToneRef.current.pause();
                outgoingToneRef.current.currentTime = 0;
              } catch {}
            }
            
            if (!outgoingToneRef.current) {
              const audio = new Audio('/sounds/outgoing-call.mp3');
              audio.loop = true;
              outgoingToneRef.current = audio;
            }
            outgoingToneRef.current.currentTime = 0;
            const playPromise = outgoingToneRef.current.play();
            if (playPromise && typeof playPromise.catch === 'function') {
              playPromise.catch(err => {
                console.warn('Outgoing-call sound blocked by browser:', err);
              });
            }
          } catch (toneErr) {
            console.warn('Kon outgoing-call geluid niet afspelen:', toneErr);
          }

          // Set UI to show we're proposing the call
          setCallStatus('initiating');


          // Note: The actual WebRTC call setup will happen automatically
          // via useWebRTCAudioCall hook when status becomes 'active'

        } catch (err) {
          console.error('❌ Fout bij voorstellen call:', err);
        }
      }
      
      // Show incoming popup for the other user (they will see it via real-time listener)
      if (step === 'call') {
        // Current user proposed, don't show popup to them
      } else if (step === 'video') {
        // ✅ CRITICAL: Stop WebRTC audio call BEFORE starting video
        if (webrtcAudioCall && webrtcAudioCall.call) {
          webrtcAudioCall.endCall();
        }

        setShowVideoCallInterface(true);
        setShowCallInterface(false);
        setShowJitsiCall(false);
           
      } else if (step === 'physical') {
        // Physical meeting step - no video call needed
      }
    } catch (error) {
      console.error(`❌ Error proposing ${step} step:`, error);
    }
  };

  // ==========================================
  // 🔴 LEGACY WEBRTC CODE REMOVED
  // ==========================================
  // The following functions are no longer needed:
  // - All RTCPeerConnection setup
  // - Firebase signaling (offers/answers/ICE candidates)
  // - Manual track management
  // 
  // Now using:
  // - WebRTC audio calls (via useWebRTCAudioCall hook)
  // - Google Meet for video calls (external popup)
  // ==========================================


  // ==========================================
  // ✅ ACCEPT STEP - WEBRTC AUDIO / GOOGLE MEET VIDEO
  // ==========================================
  // When other user accepts:
  // - Audio call: WebRTC peer-to-peer via useWebRTCAudioCall hook
  // - Video call: Google Meet popup opens automatically via useEffect

  const acceptStep = async (step: 'call' | 'video' | 'physical') => {
    if (!user) return;
    const suppressMs = Date.now() + 6000;

    // Close the corresponding incoming popup immediately on accept click.
    // This avoids stale modal overlays while async acceptance logic runs.
    if (step === 'call') {
      suppressIncomingPopupUntilRef.current.call = suppressMs;
      setShowIncomingCall(false);
    } else if (step === 'video') {
      suppressIncomingPopupUntilRef.current.video = suppressMs;
      setShowIncomingVideo(false);
    } else if (step === 'physical') {
      suppressIncomingPopupUntilRef.current.physical = suppressMs;
      setShowIncomingMeeting(false);
    }

    // connectionFlow might not be in state yet — resolve it deterministically from
    // Firestore rather than relying on a setTimeout for React state propagation.
    let activeFlow: ConnectionFlow | null = connectionFlow;
    if (!activeFlow && currentChatRoom?.id) {
      try {
        await findOrCreateConnectionFlow(currentChatRoom.id, currentChatRoom.participants || []);
        // Read the flow document directly — do not wait for React state to update.
        const flowSnap = await getDoc(doc(db, 'connectionFlows', currentChatRoom.id));
        if (flowSnap.exists()) {
          activeFlow = { id: flowSnap.id, ...flowSnap.data() } as ConnectionFlow;
          setConnectionFlow(activeFlow);
        }
      } catch (e) {
        console.warn('[acceptStep] Could not load connectionFlow:', e);
      }
    }
    if (!activeFlow) {
      setToastMessage('⚠️ Verbinding nog niet geladen. Probeer opnieuw.');
      return;
    }

    try {

      const flowRef = doc(db, 'connectionFlows', activeFlow.id);
      const stepKey = step === 'call' ? 'callStep' : step === 'video' ? 'videoStep' : 'physicalStep';

      // Use a transaction so we never get stuck in "pending" due to stale local state.
      const txResult = await runTransaction(db, async (tx) => {
        const snap = await tx.get(flowRef);
        if (!snap.exists()) {
          throw new Error('FLOW_NOT_FOUND');
        }
        const flowData = snap.data() as any;
        const stepData = flowData?.[stepKey] || {};
        const participants: string[] = Array.isArray(flowData?.participants) ? flowData.participants : [];
        const prevAcceptances = stepData.acceptances || {};

        const nextAcceptances = { ...prevAcceptances, [user.uid]: true };
        const allAcceptedNow =
          participants.length >= 2 && participants.every((pId) => nextAcceptances[pId] === true);

        // Always persist my acceptance
        const baseUpdate: any = {
          [`${stepKey}.acceptances`]: nextAcceptances,
          updatedAt: serverTimestamp(),
        };

        if (allAcceptedNow) {
          baseUpdate[`${stepKey}.status`] = 'active';
          baseUpdate[`${stepKey}.startTime`] = serverTimestamp();
          if (step === 'call' || step === 'video') {
            baseUpdate[`${stepKey}.duration`] = 480;
          }
          baseUpdate.currentStep = step;
          baseUpdate.status = 'active';
        }

        tx.update(flowRef, baseUpdate);
        return { allAcceptedNow, nextAcceptances, participants };
      });

      const { allAcceptedNow, nextAcceptances, participants } = txResult;

      if (allAcceptedNow) {
        
        // ✅ FIX: For physical step, switch UI BEFORE the next Firestore write
        // so the phase-sync guard is active during all snapshot deliveries.
        if (step === 'physical' && selectedConnectionData?.id) {
          // Switch immediately — before updateDoc below — so any snapshot that
          // arrives during the write already finds currentPhase==='physical' and
          // the guard (isInitiatorWithPendingPhysical / physicalStep.active) works.
          setShowVideoCallInterface(false);
          setShowCallInterface(false);
          setShowJitsiCall(false);
          setCurrentPhase('physical');
          setActiveCard('physical');
        }
        
        // Start local timer if applicable
        if (step === 'call' || step === 'video') {
          setIsTimerActive(true);
          setTimer(480);
        }

        // Close popup and show interface
        setShowIncomingCall(false);
        setShowIncomingVideo(false);
        setShowIncomingMeeting(false);

        // ✅ Video call popup will be automatically opened by the useEffect
        // (watches videoStep.status === 'active' for BOTH proposer and acceptor)
        
        if (step === 'video') {
          // Show video interface immediately for acceptor (don't wait for useEffect)
          setCurrentPhase('video');        // VideoPhase rendert alleen als currentPhase === 'video'
          setShowVideoCallInterface(true);
          setShowCallInterface(false);
          setShowJitsiCall(false);
        }

        if (step === 'call') {
          // Stop uitgaande beltoon zodra call geaccepteerd is
          if (outgoingToneRef.current) {
            try {
              outgoingToneRef.current.pause();
              outgoingToneRef.current.currentTime = 0;
            } catch {}
          }
          
          // ==========================================
          // ✅ WEBRTC: Callee accepts call
          // ==========================================
          // The useWebRTCAudioCall hook will automatically setup the call
          // when it detects callStep.status === 'active'

          // Note: No manual call setup needed here.
          // The hook will handle: getUserMedia, peer connection, signaling, ICE candidates
          
      } else if (step === 'video') {
        }
      } else {
      }
    } catch (error) {
      console.error(`❌ Error accepting ${step} step:`, error);
          }
  };

  // ==========================================
  // 🔴 LEGACY CODE BLOCK REMOVED
  // ==========================================
  // All native WebRTC video call code has been removed
  //
  // The following were removed:
  // - RTCPeerConnection setup for video calls
  // - ICE candidate handling for video
  // - Firebase signaling (offers/answers) for video
  // - Manual video stream management
  //
  // Current implementation:
  // - Audio calls: WebRTC via useWebRTCAudioCall hook
  // - Video calls: Google Meet (external popup)
  // ==========================================

  /* LEGACY VIDEO CODE REMOVED - approximately 400 lines
     Original code created peer connections, handled ICE candidates,
     and managed Firebase signaling. Now replaced by Google Meet.
                autoGainControl: true
              }
            });
            
            console.log('✅ Video en audio toestemming verkregen!');
            
            // Sla de stream op voor gebruik in de video call interface
            // (Je kunt dit later gebruiken in de VideoCallInterface component)
            if (localVideoStreamRef.current) {
              // Stop oude stream als die er is
              localVideoStreamRef.current.getTracks().forEach((track: MediaStreamTrack) => track.stop());
            }
            localVideoStreamRef.current = stream;
            setLocalVideoStream(stream); // Update state for reactive re-render
            
            // Toon de video call interface
          setShowVideoCallInterface(true);
            
            // Start WebRTC video call setup (vergelijkbaar met audio call)
            try {
              const otherUserId = (connectionFlow.participants || []).find(pId => pId !== user.uid);
              if (!otherUserId) {
                console.warn('⚠️ Geen andere participant gevonden voor WebRTC video call');
                return;
              }

              // Check of er al een offer is (callee) of dat we een nieuwe call starten (caller)
              const callInfo = await getCallById(connectionFlow.id);
              
              if (callInfo && callInfo.offer) {
                // 📹 Callee: Er is al een offer, maak een answer met bestaande stream
                console.log('📹 Joining existing video call (callee)...');
                
                // Maak peer connection direct met bestaande stream
                // Cleanup oude peer connection eerst
                if (peerConnectionRef.current) {
                  peerConnectionRef.current.close();
                }
                const peerConnection = new RTCPeerConnection({
                  iceServers: [
                    { urls: 'stun:stun.l.google.com:19302' },
                    { urls: 'stun:stun1.l.google.com:19302' },
                    { urls: 'stun:stun2.l.google.com:19302' }
                  ]
                });
                peerConnectionRef.current = peerConnection; // Sla op in ref

                // ✅ FIX: Connection State Monitoring met error handling
                peerConnection.onconnectionstatechange = () => {
                  const state = peerConnection.connectionState;
                  console.log('🔌 Connection State (callee):', state);
                  if (state === 'failed') {
                    console.error('❌ Connection failed! State:', state);
                    showError('Er ging iets mis met de audioverbinding. Probeer opnieuw.');
                  } else if (state === 'connected') {
                    console.log('✅ Connection established (callee)');
                  } else if (state === 'disconnected') {
                    console.log('⚠️ Connection disconnected (tijdelijk - kan herstellen)');
                    // "disconnected" is een normale tijdelijke state, geen error
                  }
                };

                peerConnection.oniceconnectionstatechange = () => {
                  const iceState = peerConnection.iceConnectionState;
                  console.log('🧊 ICE Connection State (callee):', iceState);
                  if (iceState === 'failed') {
                    console.error('❌ ICE connection failed! State:', iceState);
                    showError('Er ging iets mis met de audioverbinding. Probeer opnieuw.');
                  } else if (iceState === 'connected') {
                    console.log('✅ ICE connection established (callee)');
                  } else if (iceState === 'disconnected') {
                    console.log('⚠️ ICE connection disconnected (tijdelijk - kan herstellen)');
                    // "disconnected" is een normale tijdelijke state, geen error
                  }
                };

                peerConnection.onicegatheringstatechange = () => {
                  console.log('📊 ICE Gathering State (callee):', peerConnection.iceGatheringState);
                };

                // ✅ FIX: Betere track handling met transceivers
                console.log('📹 Adding tracks to peer connection (callee)');
                stream.getTracks().forEach((track: MediaStreamTrack) => {
                  console.log(`  ➕ Adding ${track.kind} track:`, {
                    id: track.id,
                    label: track.label,
                    enabled: track.enabled,
                    readyState: track.readyState
                  });
                  
                  const transceiver = peerConnection.addTransceiver(track, {
                    direction: 'sendrecv',
                    streams: [stream]
                  });
                  
                  console.log('  ✅ Transceiver created:', {
                    mid: transceiver.mid,
                    direction: transceiver.direction
                  });
                });

                // ✅ FIX: Verbeterde ontrack handler
                let remoteStreamReceived = false;
                peerConnection.ontrack = (event) => {
                  console.log('📹 ========== ONTRACK EVENT (CALLEE) ==========');
                  console.log('Event details:', {
                    streams: event.streams.length,
                    track: {
                      kind: event.track.kind,
                      id: event.track.id,
                      label: event.track.label,
                      readyState: event.track.readyState,
                      enabled: event.track.enabled
                    },
                    transceiver: {
                      mid: event.transceiver.mid,
                      direction: event.transceiver.direction,
                      currentDirection: event.transceiver.currentDirection
                    }
                  });

                  const remoteStream = event.streams[0];
                  if (!remoteStream) {
                    console.error('❌ Geen remote stream in ontrack event!');
                    return;
                  }

                  // Voorkom dubbele verwerking
                  if (remoteStreamReceived && remoteVideoStreamRef.current?.id === remoteStream.id) {
                    console.log('⚠️ Remote stream al ontvangen, skip duplicate');
                    return;
                  }

                  console.log('📹 Remote stream details:', {
                    id: remoteStream.id,
                    active: remoteStream.active,
                    videoTracks: remoteStream.getVideoTracks().length,
                    audioTracks: remoteStream.getAudioTracks().length
                  });

                  remoteStream.getVideoTracks().forEach((track, idx) => {
                    console.log(`  📹 Video track ${idx}:`, {
                      id: track.id,
                      label: track.label,
                      enabled: track.enabled,
                      readyState: track.readyState,
                      muted: track.muted
                    });
                  });

                  // Stop oude stream
                  if (remoteVideoStreamRef.current) {
                    console.log('🧹 Stopping old remote stream');
                    remoteVideoStreamRef.current.getTracks().forEach((track: MediaStreamTrack) => track.stop());
                  }

                  // Update ref en state
                  remoteVideoStreamRef.current = remoteStream;
                  setRemoteVideoStream(remoteStream);
                  remoteStreamReceived = true;
                  
                  console.log('✅ Remote stream state geupdate (callee)');
                  console.log('==========================================');
                };

                // ✅ FIX: ICE candidate handling
                const iceCandidateQueue: RTCIceCandidateInit[] = [];
                let candidateTimer: NodeJS.Timeout | null = null;

                peerConnection.onicecandidate = async (event) => {
                  if (event.candidate) {
                    console.log('🧊 New ICE candidate (callee):', {
                      type: event.candidate.type,
                      protocol: event.candidate.protocol
                    });
                    
                    iceCandidateQueue.push(event.candidate.toJSON());
                    
                    if (candidateTimer) clearTimeout(candidateTimer);
                    candidateTimer = setTimeout(async () => {
                      if (iceCandidateQueue.length > 0) {
                        console.log(`📤 Verzenden ${iceCandidateQueue.length} ICE candidates (callee)`);
                        for (const candidate of iceCandidateQueue) {
                          try {
                            await addIceCandidateToCall(connectionFlow.id, user.uid, candidate);
                          } catch (err) {
                            console.error('❌ Fout bij opslaan ICE-candidate:', err);
                          }
                        }
                        iceCandidateQueue.length = 0;
                      }
                    }, 100);
                  } else {
                    console.log('✅ ICE gathering complete (callee)');
                  }
                };

                // ✅ FIX: Create answer and send function voor onnegotiationneeded
                const createAnswerAndSend = async () => {
                  try {
                    console.log('📤 Creating answer and sending...');
                    
                    // Maak answer
                    const answer = await peerConnection.createAnswer();
                    await peerConnection.setLocalDescription(answer);

                    // Wacht op ICE gathering
                    await new Promise<void>((resolve) => {
                      const checkInterval = setInterval(() => {
                        if (peerConnection.iceGatheringState === 'complete') {
                          clearInterval(checkInterval);
                          console.log('✅ ICE gathering complete (callee), verzenden answer');
                          resolve();
                        }
                      }, 100);
                      
                      setTimeout(() => {
                        clearInterval(checkInterval);
                        console.log('⏰ ICE gathering timeout (callee), verzenden answer anyway');
                        resolve();
                      }, 3000);
                    });

                    // Sla answer op
                    const finalAnswer = peerConnection.localDescription;
                    if (finalAnswer) {
                      await setAnswerOnCall(connectionFlow.id, finalAnswer);
                      console.log('📤 Answer verzonden naar Firestore');
                    }
                  } catch (err) {
                    console.error('❌ Fout bij createAnswerAndSend:', err);
                  }
                };

                // ✅ FIX: onnegotiationneeded handler - automatisch answer creëren en versturen
                peerConnection.onnegotiationneeded = async () => {
                  console.log('🔄 Negotiation needed - creating answer...');
                  await createAnswerAndSend();
                };

                // Set remote description (offer) eerst
                console.log('📥 Setting remote description (offer)...');
                await peerConnection.setRemoteDescription(callInfo.offer);
                console.log('✅ Remote description set (callee)');

                // ✅ Na succesvol setRemoteDescription: buffered ICE-candidates toevoegen
                const pc = peerConnectionRef.current || webrtcService.getPeerConnection();
                if (pc && pendingRemoteCandidatesRef.current.length > 0) {
                  console.log("❄️ Flushing pending ICE candidates:", pendingRemoteCandidatesRef.current.length);
                  const candidatesToProcess = [...pendingRemoteCandidatesRef.current];
                  pendingRemoteCandidatesRef.current = []; // Clear before processing
                  for (const c of candidatesToProcess) {
                    await addRemoteIceCandidateSafe(c);
                  }
                  console.log('✅ [REMOTE CANDIDATE] All queued candidates processed');
                }

                // ✅ Initial answer creation (voor eerste setup)
                await createAnswerAndSend();
                
                // Probeer de naam uit connectionFlow te halen als selectedConnectionData niet beschikbaar is
                const otherUserId = (connectionFlow.participants || []).find(pId => pId !== user.uid);
                const userName = selectedConnectionData?.name || 
                               (otherUserId ? 'Deelnemer' : 'Onbekend');
                const callWithName = {
                  userName: userName,
                  startTime: Date.now(),
                };
                setCurrentVideoCall(callWithName);

                // Luister naar ICE-candidates
                if (candidateUnsubscribeRef.current) {
                  candidateUnsubscribeRef.current();
                }
          candidateUnsubscribeRef.current = listenForRemoteCandidates(
            connectionFlow.id,
            user.uid,
            async (remoteCandidate) => {
              await addRemoteIceCandidateSafe(remoteCandidate);
            }
          );
              } else {
                // 📹 Caller: Start nieuwe video call en maak offer
                console.log('📹 Starting new video call (caller)...');
                const videoCall = await webrtcService.initiateCall('video', [user.uid, otherUserId], {
                  audio: true,
                  video: true
                });
                
                // ✅ FIX: Sla local stream op
                const localStream = webrtcService.getLocalStream();
                if (localStream) {
                  localVideoStreamRef.current = localStream;
                  setLocalVideoStream(localStream);
                  // ✅ CRITICAL FIX: Update ook de hook's localStream
                  if (setLocalStreamInHook) {
                    setLocalStreamInHook(localStream);
                  }
                  console.log('✅ Local video stream opgeslagen (webrtcService path)');
                }
                
                const callWithName = {
                  userName: selectedConnectionData?.name || 'Onbekend',
                  startTime: videoCall.startTime ? videoCall.startTime.getTime() : Date.now(),
                };
                setCurrentVideoCall(callWithName);
                
                // ✅ FIX: Toon video call interface
                setShowVideoCallInterface(true);
                setShowCallInterface(false);
                setShowJitsiCall(false);

                // Setup handlers voor remote video stream
                webrtcService.setCallHandlers({
                  onRemoteStream: (remoteStream) => {
                    console.log('📹 Remote video stream ontvangen (caller via webrtcService)', remoteStream);
                    if (remoteVideoStreamRef.current) {
                      remoteVideoStreamRef.current.getTracks().forEach((track: MediaStreamTrack) => track.stop());
                    }
                    remoteVideoStreamRef.current = remoteStream;
                    setRemoteVideoStream(remoteStream); // ✅ FIX: Update state zodat VideoCallInterface de stream krijgt
                  },
                  onIceCandidate: async (candidate) => {
                    try {
                      await addIceCandidateToCall(connectionFlow.id, user.uid, candidate.toJSON());
                    } catch (err) {
                      console.error('❌ Fout bij opslaan ICE-candidate (video caller):', err);
                    }
                  },
                  onCallStart: (c) => {
                    console.log('📹 WebRTC video call gestart (caller):', c);
                  },
                  onCallEnd: () => {
                    console.log('📹 WebRTC video call beëindigd (caller)');
                    setShowJitsiCall(false);
            // BACKUP - setShowVideoCallInterface(false);
                    setCurrentVideoCall(null);
                  },
                  onCallError: (err) => {
                    console.error('❌ WebRTC video call fout (caller):', err);
                    showError('Er ging iets mis met de video verbinding.');
                  }
                });

                // Maak offer en sla op in Firestore
                const offer = await webrtcService.createOffer();
                await createOrUpdateCallWithOffer(connectionFlow.id, user.uid, otherUserId, offer);

                // Luister naar answer
                if (callAnswerUnsubscribeRef.current) {
                  callAnswerUnsubscribeRef.current();
                }
                callAnswerUnsubscribeRef.current = listenForAnswer(
                  connectionFlow.id,
                  async (answer) => {
                    console.log('📨 Answer ontvangen voor WebRTC video call (caller)');
                    await webrtcService.setRemoteDescription(answer);
                    
                    // ✅ Na succesvol setRemoteDescription: buffered ICE-candidates toevoegen
                    const pc = peerConnectionRef.current || webrtcService.getPeerConnection();
                    if (pc && pendingRemoteCandidatesRef.current.length > 0) {
                      console.log("❄️ Flushing pending ICE candidates:", pendingRemoteCandidatesRef.current.length);
                      const candidatesToProcess = [...pendingRemoteCandidatesRef.current];
                      pendingRemoteCandidatesRef.current = []; // Clear before processing
                      for (const c of candidatesToProcess) {
                        await addRemoteIceCandidateSafe(c);
                      }
                      console.log('✅ [REMOTE CANDIDATE] All queued candidates processed');
                    }
                  }
                );

                // Luister naar ICE-candidates
                if (candidateUnsubscribeRef.current) {
                  candidateUnsubscribeRef.current();
                }
          candidateUnsubscribeRef.current = listenForRemoteCandidates(
            connectionFlow.id,
            user.uid,
            async (remoteCandidate) => {
              await addRemoteIceCandidateSafe(remoteCandidate);
            }
          );
              }
            } catch (webrtcErr) {
              console.error('❌ Fout bij opzetten WebRTC video call:', webrtcErr);
              // ✅ FIX: Geen Jitsi fallback meer - toon error message
              showError('Kon video call niet starten. Probeer opnieuw.');
              setShowVideoCallInterface(false);
              setShowJitsiCall(false);
            }
          } catch (mediaErr: any) {
            console.error('❌ Fout bij verkrijgen video toestemming:', mediaErr);
            const errorName = mediaErr.name || 'UnknownError';
            let errorMessage = 'Kon geen toegang krijgen tot camera en/of microfoon.';
            
            if (errorName === 'NotAllowedError' || errorName === 'PermissionDeniedError') {
              errorMessage = 'Je camera staat uit, druk op toestamen.';
            } else if (errorName === 'NotFoundError' || errorName === 'DevicesNotFoundError') {
              errorMessage = 'Geen camera en/of microfoon gevonden op dit apparaat.';
            } else if (errorName === 'NotReadableError' || errorName === 'TrackStartError') {
              errorMessage = 'Camera en/of microfoon wordt al gebruikt door een andere applicatie.';
            }
            
            showError(errorMessage);
            // Don't show video interface if permission was denied - user needs to grant permission first
  END LEGACY VIDEO CODE */

  const formatChatTime = (timestamp: Date) => {
    return timestamp.toLocaleTimeString('nl-NL', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  // Auto-scroll to bottom of chat - Skip initial load to prevent layout shift
  useEffect(() => {
    // Skip scroll on initial load to prevent input from moving up
    if (isInitialChatLoad) {
      if (chatMessages.length > 0) {
        setIsInitialChatLoad(false);
      }
      return;
    }
    
    // Only scroll when there are new messages after initial load
    // Temporarily disabled to test if this causes the layout shift
    // if (chatEndRef.current && chatMessages.length > 0) {
    //   chatEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    // }
  }, [chatMessages, isInitialChatLoad]);

  // Reset initial load state when chat room changes
  useEffect(() => {
    setIsInitialChatLoad(true);
  }, [currentChatRoom?.id]);

  // Real-time message listener
  useEffect(() => {
    if (!currentChatRoom?.id || !user) {
      setChatMessages([]);
      return;
    }


    const messagesQuery = query(
      collection(db, 'chatMessages'),
      where('chatRoomId', '==', currentChatRoom.id),
      orderBy('timestamp', 'asc')
    );

    const unsubscribe = onSnapshot(messagesQuery, (snapshot) => {
      const messagesData = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          timestamp: data.timestamp?.toDate?.() || (data.timestamp ? new Date(data.timestamp) : new Date())
        };
      }) as ChatMessage[];

      setChatMessages(messagesData);
      
      // Check if both participants have sent messages (indicating both are present in chat)
      if (messagesData.length > 0 && currentChatRoom.participants && currentChatRoom.participants.length >= 2) {
        const uniqueSenders = new Set(messagesData.map(m => m.senderId));
        const bothPresent = currentChatRoom.participants.every(participantId => uniqueSenders.has(participantId));
        
        if (bothPresent && currentPhase === 'chat' && connectionFlow) {
          console.log('✅ Both participants have sent messages - both are present in chat');

          // Alleen Firestore updaten als chatStep nog niet actief is – centrale countdown doet de rest
          if (connectionFlow.chatStep?.status !== 'active') {
            // #region agent log
            // #endregion
            const activateChat = async () => {
              try {
                const flowRef = doc(db, 'connectionFlows', connectionFlow.id);
                // #region agent log
                // #endregion
                await updateDoc(flowRef, {
                  'chatStep.status': 'active',
                  'chatStep.startTime': serverTimestamp(),
                  'chatStep.duration': 480,
                  status: 'active',
                  updatedAt: serverTimestamp()
                });
                // #region agent log
                // #endregion
                console.log('✅ Chat activated (both present) - centrale countdown neemt over');
              } catch (error) {
                // #region agent log
                // #endregion
                console.error('❌ Error activating chat:', error);
              }
            };
            activateChat();
          }
        }
      }
    }, (error) => {
      console.error('❌ Error loading messages for room', currentChatRoom.id, ':', error);
      // If index error, show helpful message
      if (error.code === 'failed-precondition') {
        console.error('⚠️ Firestore index missing! Please create index for chatMessages collection with fields: chatRoomId (ascending), timestamp (ascending)');
      }
    });

    return () => {
      unsubscribe();
    };
  }, [currentChatRoom?.id, user, currentPhase, connectionFlow]);

  // Real-time notes listener per connectionFlow
  useEffect(() => {
    if (!connectionFlow?.id || !user?.uid) {
      setNotes([]);
      return;
    }

    try {
      const notesRef = collection(db, 'connectionFlows', connectionFlow.id, 'notes');
      const notesQuery = query(notesRef, orderBy('createdAt', 'asc'));

      const unsubscribe = onSnapshot(
        notesQuery,
        (snapshot) => {
          const notesData: FlowNote[] = snapshot.docs.map((docSnap) => {
            const data = docSnap.data() as any;
            const createdAt =
              (data.createdAt && typeof data.createdAt.toDate === 'function'
                ? data.createdAt.toDate()
                : data.createdAt
              ) || new Date();

            const phase: FlowPhaseId =
              (data.phase as FlowPhaseId) || 'chat';

            return {
              id: docSnap.id,
              text: data.text || '',
              phase,
              createdAt,
              createdBy: data.createdBy || '',
            };
          });

          setNotes(notesData);
        },
        (error) => {
          console.error('❌ Error loading notes for connectionFlow', connectionFlow.id, ':', error);
        }
      );

      return () => {
        unsubscribe();
      };
    } catch (error) {
      console.error('❌ Error setting up notes listener:', error);
      setNotes([]);
    }
  }, [connectionFlow?.id, user?.uid]);

  const handleAddNote = async () => {
    if (!user || !connectionFlow || !newNoteText.trim()) return;

    const text = newNoteText.trim();
    const phase: FlowPhaseId =
      currentPhase === 'call' || currentPhase === 'video' || currentPhase === 'physical'
        ? (currentPhase as FlowPhaseId)
        : 'chat';

    try {
      await addDoc(collection(db, 'connectionFlows', connectionFlow.id, 'notes'), {
        text,
        phase,
        createdAt: serverTimestamp(),
        createdBy: user.uid,
      });

      setNewNoteText('');
    } catch (error) {
      console.error('❌ Error adding note:', error);
    }
  };

  const handleStartMeetupSession = async () => {
    const now = new Date();
    setMeetupStartedAt(now);
    if (selectedConnectionData?.id && user?.uid) {
      try {
        await updateDoc(doc(db, 'connections', selectedConnectionData.id), {
          'physicalMeeting.meetupStartedAt': serverTimestamp(),
          'physicalMeeting.meetupStartedBy': user.uid,
        });
      } catch (err) {
        console.error('[ConnectieFlow] Error writing meetupStartedAt:', err);
      }
    }
  };

  const handleEndMeetupSession = () => {
    if (!meetupStartedAt) {
      setMeetupStartedAt(null);
    } else {
      setShowMeetupReflection(true);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!connectionFlow) return;
    if (!window.confirm('Weet je zeker dat je deze notitie wilt verwijderen?')) return;

    try {
      await deleteDoc(doc(db, 'connectionFlows', connectionFlow.id, 'notes', noteId));
    } catch (error) {
      console.error('❌ Error deleting note:', error);
    }
  };

  const handleEditNote = async (note: FlowNote) => {
    if (!connectionFlow) return;
    const updatedText = window.prompt('Pas de notitie aan:', note.text);
    if (!updatedText || updatedText.trim() === note.text) return;

    try {
      await updateDoc(doc(db, 'connectionFlows', connectionFlow.id, 'notes', note.id), {
        text: updatedText.trim(),
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('❌ Error updating note:', error);
    }
  };

  // AI Relation intro popup is no longer shown on phase entry.
  // The reflection flow is triggered automatically after the meeting ends (stop button).

  // Real-time connectionFlow listener
  useEffect(() => {
    if (!currentChatRoom?.id || !user) return;


    const flowRef = doc(db, 'connectionFlows', currentChatRoom.id);
    const unsubscribe = onSnapshot(flowRef, (snapshot) => {
      if (snapshot.exists()) {
        const flowData = { id: snapshot.id, ...snapshot.data() } as ConnectionFlow;

        // Bootstrap guard: only set connectionFlow here when the primary notification
        // listener (Listener A, keyed by notifListenerFlowIdRef) is not yet running.
        // Once Listener A is active it owns setConnectionFlow, preventing a double
        // state-update (and double render) on every Firestore snapshot.
        if (!notifListenerFlowIdRef.current) {
          setConnectionFlow(flowData);
        }
        // Always sync the Google Meet URL — it is only read here, not in Listener A.
        setGoogleMeetUrl((flowData as any).googleMeetUrl || null);

        // ── Dev-only phase sync diagnostic ─────────────────────────────────
        if (process.env.NODE_ENV === 'development') {
          const firestoreStep = flowData.currentStep ?? '(unset)';
          const uiPhase = currentPhase;
          if (firestoreStep !== uiPhase) {
            console.debug(
              `[phase-sync] 🔍 Firestore step="${firestoreStep}" | UI phase="${uiPhase}" → will reconcile`
            );
          }
        }
        // ──────────────────────────────────────────────────────────────────

        // Map backend step → UI fase key zodat tabs/timer altijd kloppen
        // ✅ FIX: Properly handle completed steps - fall back to chat
        // Priority: active steps > pending steps > currentStep > 'chat' fallback
        let mappedPhase: ConnectionPhase = flowData.currentStep;
        
        if (flowData.callStep?.status === 'active') {
          mappedPhase = 'call';
        } else if (
          flowData.videoStep?.status === 'active' ||
          (
            flowData.videoStep?.status === 'pending' &&
            flowData.videoStep?.proposedBy &&
            flowData.currentStep === 'video'
          )
        ) {
          mappedPhase = 'video';
        } else if (
          flowData.callStep?.status === 'pending' &&
          flowData.callStep?.proposedBy &&
          flowData.currentStep === 'call'
        ) {
          mappedPhase = 'call';
        } else if (
          flowData.physicalStep?.status === 'active' ||
          flowData.physicalStep?.status === 'proposed' ||
          (
            // pending: only initiator or users who already accepted see the physical UI
            flowData.physicalStep?.status === 'pending' &&
            !!flowData.physicalStep?.proposedBy &&
            flowData.currentStep === 'physical' &&
            (
              flowData.physicalStep.proposedBy === user?.uid ||
              flowData.physicalStep.acceptances?.[user?.uid || ''] === true
            )
          )
        ) {
          mappedPhase = 'physical';
        } else if (flowData.currentStep === 'chat') {
          mappedPhase = 'chat';
        } else if (selectedConnectionDataRef.current?.phase === 'completed') {
          // The connection document (connections/{id}) is the authoritative source for
          // the 'completed' phase. Check it BEFORE the currentStep === 'physical' branch
          // because connectionFlows.currentStep is never updated to 'completed' —
          // it stays at 'physical' even after the physical meeting is fully done.
          // Without this guard, a page refresh would always land back on the physical UI.
          mappedPhase = 'completed';
        } else if (flowData.currentStep === 'physical') {
          // Video is completed and currentStep was advanced to 'physical' by endVideoStep.
          // Keep mappedPhase as 'physical' — do NOT fall back to 'chat'.
          mappedPhase = 'physical';
        } else {
          // All active steps completed — fall back to 'chat' to avoid stale currentStep values.
          const allStepsCompleted =
            (flowData.callStep?.status === 'completed' || !flowData.callStep?.status) &&
            (flowData.videoStep?.status === 'completed' || !flowData.videoStep?.status);
          if (allStepsCompleted || flowData.videoStep?.status === 'completed' || flowData.callStep?.status === 'completed') {
            mappedPhase = 'chat';
          }
        }

        setCurrentPhase(mappedPhase);
        if (mappedPhase === 'physical') {
          setActiveCard('physical');
        }
        if (mappedPhase === 'completed') {
          // Ensure all active call/video UIs are dismissed when the flow is done
          setShowCallInterface(false);
          setShowVideoCallInterface(false);
        }

        // Zorg dat beide kanten de juiste interface zien zodra een stap ACTIEF is
        if (flowData.callStep?.status === 'active') {
          // ✅ WebRTC: actieve bel-stap (AUDIO ONLY)

          // Toon alleen onze eigen bel-UI (timer / notities / status)
          setShowCallInterface(true);
          setShowJitsiCall(false);
          // Forceer UI fase call indien nog niet gezet (voorkomt chat/call mismatch)
          setCurrentPhase((prev) => (prev === 'call' ? prev : 'call'));

          // Stop uitgaande beltoon zodra call echt actief is
          if (outgoingToneRef.current) {
            try {
              outgoingToneRef.current.pause();
              outgoingToneRef.current.currentTime = 0;
            } catch {}
          }

          // 🎤 WebRTC audio call setup will be handled by useWebRTCAudioCall hook
          // NO Google Meet for audio calls
        } else if (flowData.callStep?.status === 'completed') {
          // Geen actieve call meer
          setShowJitsiCall(false);
          setShowCallInterface(false);
        }

        // 📹 Check voor actieve OF pending video stap (IFRAME MODE - NO POPUP)
        // ✅ Use mappedPhase (computed above in same sync scope) to avoid stale closure on currentPhase
        const isVideoPendingByMe =
          flowData.videoStep?.status === 'pending' &&
          flowData.videoStep?.proposedBy === user?.uid;
        const shouldShowVideoInterface =
          mappedPhase === 'video' &&
          (flowData.videoStep?.status === 'active' || isVideoPendingByMe);

        if (shouldShowVideoInterface) {

          // ✅ FIX: Toon video-interface voor BEIDE statussen (pending + active)
          // Bij pending: initiator ziet zichzelf terwijl hij wacht op de ander
          // Bij active: beide users zitten in de Jitsi room
          setShowVideoCallInterface(true);
          setShowCallInterface(false);
          setShowJitsiCall(false);

          // Video interface shows automatically when currentPhase === 'video'

        }
        
        // Check voor voltooide video stap
        // ✅ FIX: Verberg ALLEEN bij 'completed', NIET bij 'pending'
        // Bij 'pending' moet de video interface juist getoond worden (zie regel ~5129)
        if (flowData.videoStep?.status === 'completed') {
          // Geen actieve video call meer
          setShowVideoCallInterface(false);
        }

        // Timer wordt uitsluitend door de centrale countdown afgehandeld

        // Check for incoming proposals (other user proposed a step)
        const checkForProposal = (stepData: any, stepName: string, showPopup: (show: boolean) => void) => {
          if (!stepData) {
            return;
          }

          const status = stepData.status;
          const proposedBy = stepData.proposedBy;
          const acceptances = stepData.acceptances || {};

          if (
            (status === 'proposed' || status === 'pending') &&
            proposedBy &&
            proposedBy !== user?.uid &&
            !acceptances[user?.uid || '']
          ) {
            showPopup(true);
          }
        };

        checkForProposal(flowData.callStep, 'call', setShowIncomingCall);
        checkForProposal(flowData.videoStep, 'video', setShowIncomingVideo);
        checkForProposal(flowData.physicalStep, 'physical', setShowIncomingMeeting);
      }
    }, (error) => {
      console.error('❌ Error loading connectionFlow:', error);
    });

    return () => {
      unsubscribe();
    };
  }, [currentChatRoom?.id, user]);

  // Cleanup WebRTC listeners on unmount or when connectionFlow changes
  useEffect(() => {
    return () => {
      if (callAnswerUnsubscribeRef.current) {
        callAnswerUnsubscribeRef.current();
        callAnswerUnsubscribeRef.current = null;
      }
      if (candidateUnsubscribeRef.current) {
        candidateUnsubscribeRef.current();
        candidateUnsubscribeRef.current = null;
      }
    };
  }, [connectionFlow?.id]);

  // Mark component as unmounted so async callbacks can bail out safely
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  // Handle browser close/disconnect - cleanup and notify other user
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Only cleanup if there's an active call or video call
      if (showCallInterface || showVideoCallInterface || currentCall || currentVideoCall) {
        console.log('🛑 Browser closing - cleaning up active call');
        
        // Cleanup local resources
        stopAllAudioAndStreams();
        
        // Update Firestore to notify other user
        if (connectionFlow?.id && user?.uid) {
          try {
            const flowRef = doc(db, 'connectionFlows', connectionFlow.id);
            const updateData: any = {
              status: 'active',
              // Reset currentStep so the other user returns to chat.
              // Skip this when the flow is fully completed — the connection is done.
              ...(currentPhase !== 'completed' ? { currentStep: 'chat' } : {}),
              updatedAt: serverTimestamp()
            };
            
            // Mark current step as completed if active
            if (connectionFlow.currentStep === 'call' && connectionFlow.callStep?.status === 'active') {
              updateData['callStep.status'] = 'completed';
              updateData['callStep.endedBy'] = user.uid;
            } else if (connectionFlow.currentStep === 'video' && connectionFlow.videoStep?.status === 'active') {
              updateData['videoStep.status'] = 'completed';
              updateData['videoStep.endedBy'] = user.uid;
            }
            
            // Use updateDoc synchronously (Firestore will attempt to send)
            // Note: This may not complete if browser closes too quickly
            updateDoc(flowRef, updateData).catch(err => {
              console.error('❌ Error updating Firestore on unload:', err);
            });
          } catch (err) {
            console.error('❌ Error preparing Firestore update on unload:', err);
          }
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [connectionFlow?.id, connectionFlow?.currentStep, connectionFlow?.callStep?.status, connectionFlow?.videoStep?.status, user?.uid, showCallInterface, showVideoCallInterface, currentCall, currentVideoCall]);

  // ✅ FIX: Chat wordt nu direct geactiveerd in acceptChatStep().
  // Deze useEffect is alleen nog een safety-net voor edge cases waar
  // de chat toch nog op 'pending' staat terwijl een user al geaccepteerd heeft.
  useEffect(() => {
    if (!user || !connectionFlow || currentPhase !== 'chat') {
      return;
    }

    const chatStep = connectionFlow.chatStep;
    if (!chatStep || chatStep.status !== 'pending') return;

    // Als de huidige user al geaccepteerd heeft maar status is nog pending,
    // activeer de chat alsnog (safety-net)
    if (chatStep.acceptances?.[user.uid] === true) {
      console.log('🔄 Safety-net: Chat status is pending maar user heeft al geaccepteerd, activeren...');
      const activateChat = async () => {
        try {
          const flowRef = doc(db, 'connectionFlows', connectionFlow.id);
          await updateDoc(flowRef, {
            'chatStep.status': 'active',
            'chatStep.startTime': serverTimestamp(),
            'chatStep.duration': chatStep.duration || 480,
            status: 'active',
            updatedAt: serverTimestamp()
          });
          console.log('✅ Chat geactiveerd via safety-net');
        } catch (error) {
          console.error('❌ Error activating chat step (safety-net):', error);
        }
      };
      activateChat();
    }
  }, [
    user, 
    connectionFlow, 
    connectionFlow?.chatStep?.acceptances,
    connectionFlow?.chatStep?.status,
    currentPhase
  ]);

  // Update selectedConnectionData when selection changes.
  // Guard: only set to null when there is genuinely no selection (selectedConnection is empty).
  // When selectedConnection is set but find() returns undefined, this means the connections
  // array is temporarily being reloaded (e.g. Firestore snapshot from the other user's action).
  // Setting null in that case would collapse the active card and break the physical phase UI.
  useEffect(() => {
    const connectionData = connections.find(c => c.name === selectedConnection);
    if (connectionData) {
      setSelectedConnectionData(connectionData);
    } else if (!selectedConnection) {
      setSelectedConnectionData(null);
    }
    // If selectedConnection is set but not found yet: keep existing data during reload.
  }, [selectedConnection, connections]);

  // Phase shown in the header tabs should reflect the "next step" modal when open,
  // otherwise fall back to the realtime-synced currentPhase.
  // Special-case: when the connection doc is already in physical phase, prefer showing that in the header.
  const phaseForIndicator: ConnectionPhase =
    nextStepChoiceOpen && nextPhaseCandidate && nextPhaseCandidate !== 'chat'
      ? nextPhaseCandidate
      : (selectedConnectionData?.phase === 'physical'
          ? 'physical'
          : currentPhase);

  const currentPhaseInfo =
    phases[phaseForIndicator as keyof typeof phases] ||
    phases[currentPhase as keyof typeof phases] ||
    { name: 'Onbekend', icon: '❓', color: 'gray', duration: null };

  // Check if both persons are online and show start chat popup
  // ONLY for ACCEPTED connections (not pending requests!)
  useEffect(() => {
    if (
      !user?.uid ||
      !selectedConnectionData || 
      selectedConnectionData.status !== 'accepted' || // Only for accepted connections
      connectionStatus !== 'accepted' || 
      pendingAutoAcceptChat ||
      pendingOpen?.mode === 'chat' ||
      isTimerActive || 
      showStartChatPopup ||
      currentPhase !== 'chat' // Only in chat phase
    ) {
      return;
    }

    // BLOCK "Onbekend" users - they haven't completed onboarding
    // Also check if we need to reload the name from the database
    const checkAndReloadName = async () => {
      if (selectedConnectionData.name === 'Onbekend' || !selectedConnectionData.name || selectedConnectionData.name.trim() === '') {
        console.warn('⚠️ Name is "Onbekend" or empty, trying to reload from database...');
        
        // Try to reload the name from the database
        const otherUserId = selectedConnectionData.user1Id === user.uid 
          ? selectedConnectionData.user2Id 
          : selectedConnectionData.user1Id;
        
        if (otherUserId) {
          try {
            // Try userProfiles first
            const profileDoc = await getDoc(doc(db, 'userProfiles', otherUserId));
            if (profileDoc.exists()) {
              const profileData = profileDoc.data();
              const displayName = profileData.displayName || profileData.name || profileData.email?.split('@')[0];
              
              if (displayName && displayName !== 'Onbekend') {
                console.log('✅ Found name in userProfiles:', displayName);
                // Update the selectedConnectionData with the correct name
                setSelectedConnectionData((prev: any) => ({
                  ...prev,
                  name: displayName
                }));
                return true; // Name found, allow popup
              }
            }
            
            // Try users collection as fallback
            const userDoc = await getDoc(doc(db, 'users', otherUserId));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              const displayName = userData.displayName || userData.email?.split('@')[0];
              
              if (displayName && displayName !== 'Onbekend') {
                console.log('✅ Found name in users collection:', displayName);
                setSelectedConnectionData((prev: any) => ({
                  ...prev,
                  name: displayName
                }));
                return true; // Name found, allow popup
              }
            }
          } catch (error) {
            console.error('❌ Error reloading name:', error);
          }
        }
        
        // If we get here, no valid name was found
        console.warn('⚠️ Blocking popup for "Onbekend" user - onboarding not completed');
        return false; // Block popup
      }
      
      return true; // Name is valid, allow popup
    };
    
    // Call async function and handle result
    checkAndReloadName().then((shouldShowPopup) => {
      if (!shouldShowPopup) {
        return;
      }
      
      // Continue with the rest of the logic only if name is valid
      // This will be handled in the checkBothOnline function below
    }).catch((error) => {
      console.error('❌ Error in checkAndReloadName:', error);
    });

    // Get the other user's ID from the connection
    // First check if user1Id and user2Id exist in selectedConnectionData
    if (!selectedConnectionData.user1Id || !selectedConnectionData.user2Id) {
      console.warn('⚠️ Connection data incomplete - missing user IDs');
      return;
    }

    // Verify that current user is actually part of this connection
    const isUser1 = selectedConnectionData.user1Id === user.uid;
    const isUser2 = selectedConnectionData.user2Id === user.uid;
    
    if (!isUser1 && !isUser2) {
      console.warn('⚠️ Current user is not part of this connection');
      return;
    }

    const otherUserId = isUser1 ? selectedConnectionData.user2Id : selectedConnectionData.user1Id;

    if (!otherUserId) {
      console.warn('⚠️ Could not determine other user ID');
      return;
    }

    // Double-check: verify this connection actually exists in Firestore
    const verifyConnection = async () => {
      try {
        const { getDocs, query, collection, where } = await import('firebase/firestore');
        const connectionsQuery = query(
          collection(db, 'connections'),
          where('status', '==', 'accepted')
        );
        const connectionsSnap = await getDocs(connectionsQuery);
        const connectionExists = connectionsSnap.docs.some(doc => {
          const data = doc.data();
          return (data.user1Id === user.uid && data.user2Id === otherUserId) ||
                 (data.user1Id === otherUserId && data.user2Id === user.uid);
        });

        if (!connectionExists) {
          console.warn('⚠️ No accepted connection found in Firestore for these users');
          return false;
        }
        return true;
      } catch (error) {
        console.error('❌ Error verifying connection:', error);
        return false;
      }
    };

    // Check if both users are actually online
    const checkBothOnline = async () => {
      // First verify the connection exists
      const connectionValid = await verifyConnection();
      if (!connectionValid) {
        return;
      }

      try {
        const { getDoc, doc, collection, query, where, getDocs } = await import('firebase/firestore');
        
        // Check current user online status (written to userProfiles by onlineStatusService)
        const currentUserOnlineRef = doc(db, 'userProfiles', user.uid);
        const currentUserOnlineSnap = await getDoc(currentUserOnlineRef);
        const currentUserOnline = currentUserOnlineSnap.exists() && 
          currentUserOnlineSnap.data().isOnline === true;

        // Check other user online status
        const otherUserOnlineRef = doc(db, 'userProfiles', otherUserId);
        const otherUserOnlineSnap = await getDoc(otherUserOnlineRef);
        const otherUserOnline = otherUserOnlineSnap.exists() && 
          otherUserOnlineSnap.data().isOnline === true;

        // Also check lastSeen timestamp (user is online if lastSeen is within last 5 minutes)
        const now = Date.now();
        const fiveMinutesAgo = now - (5 * 60 * 1000);
        
        const currentUserLastSeen = currentUserOnlineSnap.data()?.lastSeen?.toMillis?.() || 0;
        const otherUserLastSeen = otherUserOnlineSnap.data()?.lastSeen?.toMillis?.() || 0;
        
        const currentUserRecentlyActive = currentUserLastSeen > fiveMinutesAgo;
        const otherUserRecentlyActive = otherUserLastSeen > fiveMinutesAgo;

        const bothOnline = (currentUserOnline || currentUserRecentlyActive) && 
                          (otherUserOnline || otherUserRecentlyActive);

        if (bothOnline) {
          if (pendingAutoAcceptChat || pendingOpen?.mode === 'chat' || Date.now() < suppressStartChatPopupUntilRef.current) {
            return;
          }
          // Add this connection to the online connections list
          setOnlineConnectionsForPopup((prev) => {
            const exists = prev.some(conn => 
              (conn.user1Id === user.uid && conn.user2Id === otherUserId) ||
              (conn.user1Id === otherUserId && conn.user2Id === user.uid)
            );
            if (!exists) {
              return [...prev, {
                ...selectedConnectionData,
                user1Id: selectedConnectionData.user1Id,
                user2Id: selectedConnectionData.user2Id,
                otherUserId: otherUserId
              }];
            }
            return prev;
          });
          setShowStartChatPopup(true);
        }
      } catch (error) {
        console.error('❌ Error checking online status:', error);
        // Don't show popup on error - better to miss it than show incorrectly
      }
    };

    checkBothOnline();
  }, [user?.uid, selectedConnectionData, connectionStatus, isTimerActive, showStartChatPopup, currentPhase, pendingAutoAcceptChat, pendingOpen]);

  // Check ALL accepted connections for online status (not just selected one)
  // This runs automatically when connections are loaded or user logs in
  useEffect(() => {
    // Never show the "both online" popup when the flow has already progressed beyond
    // the very first step. Once any step has been active/completed/proposed, the
    // users are already in the flow and should not see this intro popup again.
    const flowHasProgressed =
      connectionFlow?.chatStep?.status === 'active' ||
      connectionFlow?.chatStep?.status === 'completed' ||
      connectionFlow?.callStep?.status === 'active' ||
      connectionFlow?.callStep?.status === 'completed' ||
      connectionFlow?.callStep?.status === 'proposed' ||
      connectionFlow?.videoStep?.status === 'active' ||
      connectionFlow?.videoStep?.status === 'completed' ||
      connectionFlow?.videoStep?.status === 'proposed' ||
      connectionFlow?.physicalStep?.status === 'active' ||
      connectionFlow?.physicalStep?.status === 'proposed';

    if (
      !user?.uid ||
      connections.length === 0 ||
      pendingAutoAcceptChat ||
      pendingOpen?.mode === 'chat' ||
      isTimerActive ||
      showStartChatPopup ||
      currentPhase === 'physical' ||
      flowHasProgressed
    ) {
      return;
    }

    const checkAllOnlineConnections = async () => {
      try {
        const acceptedConnections = connections.filter(conn => conn.status === 'accepted');
        
        if (acceptedConnections.length === 0) {
          return;
        }

        const onlineConnections: any[] = [];

        // Check each accepted connection
        for (const conn of acceptedConnections) {
          // Get the other user ID first
          const otherUserId = conn.user1Id === user.uid ? conn.user2Id : conn.user1Id;
          if (!otherUserId) continue;

          // IMPORTANT: Load the name from database if it's "Onbekend" or empty
          let connectionName = conn.name;
          if (!connectionName || connectionName === 'Onbekend' || connectionName.trim() === '') {
            try {
              // Try userProfiles first
              const profileDoc = await getDoc(doc(db, 'userProfiles', otherUserId));
              if (profileDoc.exists()) {
                const profileData = profileDoc.data();
                connectionName = profileData.displayName || profileData.name || profileData.email?.split('@')[0];
              }
              
              // If still not found, try users collection
              if (!connectionName || connectionName === 'Onbekend') {
                const userDoc = await getDoc(doc(db, 'users', otherUserId));
                if (userDoc.exists()) {
                  const userData = userDoc.data();
                  connectionName = userData.displayName || userData.email?.split('@')[0];
                }
              }
            } catch (error) {
              console.error('❌ Error loading name:', error);
            }
          }

          // Skip "Onbekend" users - they haven't completed onboarding
          if (!connectionName || connectionName === 'Onbekend' || connectionName.trim() === '') {
            console.warn('⚠️ Skipping connection with "Onbekend" name:', otherUserId);
            continue;
          }

          // Check if both users are online (written to userProfiles by onlineStatusService)
          const currentUserOnlineRef = doc(db, 'userProfiles', user.uid);
          const otherUserOnlineRef = doc(db, 'userProfiles', otherUserId);
          
          const [currentUserSnap, otherUserSnap] = await Promise.all([
            getDoc(currentUserOnlineRef),
            getDoc(otherUserOnlineRef)
          ]);

          const currentUserOnline = currentUserSnap.exists() && currentUserSnap.data().isOnline === true;
          const otherUserOnline = otherUserSnap.exists() && otherUserSnap.data().isOnline === true;

          const now = Date.now();
          const fiveMinutesAgo = now - (5 * 60 * 1000);
          
          const currentUserLastSeen = currentUserSnap.data()?.lastSeen?.toMillis?.() || 0;
          const otherUserLastSeen = otherUserSnap.data()?.lastSeen?.toMillis?.() || 0;
          
          const currentUserRecentlyActive = currentUserLastSeen > fiveMinutesAgo;
          const otherUserRecentlyActive = otherUserLastSeen > fiveMinutesAgo;
          
          const bothOnline = (currentUserOnline || currentUserRecentlyActive) && 
                           (otherUserOnline || otherUserRecentlyActive);

          console.log(`🔍 Connection ${connectionName}:`, {
            currentUserOnline: currentUserOnline || currentUserRecentlyActive,
            otherUserOnline: otherUserOnline || otherUserRecentlyActive,
            bothOnline,
            currentUserLastSeen: new Date(currentUserLastSeen).toISOString(),
            otherUserLastSeen: new Date(otherUserLastSeen).toISOString()
          });

          if (bothOnline) {
            onlineConnections.push({
              ...conn,
              name: connectionName, // Use the loaded name
              user1Id: conn.user1Id,
              user2Id: conn.user2Id,
              otherUserId: otherUserId
            });
          }
        }

        // Update the online connections list
        if (onlineConnections.length > 0) {
          console.log('✅ Found', onlineConnections.length, 'online connections:', onlineConnections.map(c => c.name));
          setOnlineConnectionsForPopup(onlineConnections);
          // If we have online connections and popup is not shown, show it
          if (!showStartChatPopup) {
            // Set the first connection as selected if none is selected
            if (!selectedConnectionData && onlineConnections.length > 0) {
              setSelectedConnectionData(onlineConnections[0]);
              setSelectedConnection(onlineConnections[0].name || 'Connectie');
            }
            // Only show popup if the flow has genuinely not started yet
            // (all steps still in their default 'pending' state)
            const flowAlreadyStarted =
              connectionFlow?.chatStep?.status === 'active' ||
              connectionFlow?.chatStep?.status === 'completed' ||
              connectionFlow?.callStep?.status === 'active' ||
              connectionFlow?.callStep?.status === 'completed' ||
              connectionFlow?.callStep?.status === 'proposed' ||
              connectionFlow?.videoStep?.status === 'active' ||
              connectionFlow?.videoStep?.status === 'completed' ||
              connectionFlow?.videoStep?.status === 'proposed' ||
              connectionFlow?.physicalStep?.status === 'active' ||
              connectionFlow?.physicalStep?.status === 'proposed';

            if ((currentPhase === 'chat' || !currentPhase) && !flowAlreadyStarted) {
              if (pendingAutoAcceptChat || pendingOpen?.mode === 'chat' || Date.now() < suppressStartChatPopupUntilRef.current) {
                return;
              }
              setShowStartChatPopup(true);
            } else {
              console.log('⚠️ Not showing popup - flow already in progress:', currentPhase, 'flowStarted:', flowAlreadyStarted);
            }
          } else {
            console.log('⚠️ Popup already shown');
          }
        } else {
          // No online connections, clear the list
          setOnlineConnectionsForPopup([]);
        }
      } catch (error) {
        console.error('❌ Error checking all online connections:', error);
      }
    };

    // Check immediately when connections are loaded
    checkAllOnlineConnections();
    
    // Also check every 5 seconds for online connections (faster than before)
    const interval = setInterval(checkAllOnlineConnections, 5000);

    return () => clearInterval(interval);
  }, [user?.uid, connections, isTimerActive, showStartChatPopup, currentPhase, selectedConnectionData, connectionFlow, pendingAutoAcceptChat, pendingOpen]);

  // Close "both online" popup when flow has already progressed beyond first step,
  // or when entering physical meeting phase (handles timing/race conditions)
  useEffect(() => {
    if (!showStartChatPopup) return;
    const flowAlreadyStarted =
      connectionFlow?.chatStep?.status === 'active' ||
      connectionFlow?.chatStep?.status === 'completed' ||
      connectionFlow?.callStep?.status === 'active' ||
      connectionFlow?.callStep?.status === 'completed' ||
      connectionFlow?.callStep?.status === 'proposed' ||
      connectionFlow?.videoStep?.status === 'active' ||
      connectionFlow?.videoStep?.status === 'completed' ||
      connectionFlow?.videoStep?.status === 'proposed' ||
      connectionFlow?.physicalStep?.status === 'active' ||
      connectionFlow?.physicalStep?.status === 'proposed';
    if (currentPhase === 'physical' || flowAlreadyStarted) {
      setShowStartChatPopup(false);
    }
  }, [currentPhase, showStartChatPopup, connectionFlow]);

  // Real-time listener for online status updates
  useEffect(() => {
    if (!user?.uid || connections.length === 0) return;


    const unsubscribes: (() => void)[] = [];

    connections.forEach((connection) => {
      // Get the other user's ID
      const otherUserId = connection.user1Id === user.uid ? connection.user2Id : connection.user1Id;
      if (!otherUserId) return;

      const onlineUserRef = doc(db, 'userProfiles', otherUserId);
      const unsubscribe = onSnapshot(onlineUserRef, (snap) => {
        if (snap.exists()) {
          const onlineData = snap.data();
          const isOnline = onlineData.isOnline === true || 
            (onlineData.lastSeen && onlineData.lastSeen.toMillis?.() > Date.now() - (5 * 60 * 1000));

          // Update the connection's online status
          setConnections(prev => prev.map(conn => {
            if ((conn.user1Id === user.uid && conn.user2Id === otherUserId) ||
                (conn.user1Id === otherUserId && conn.user2Id === user.uid)) {
              return { ...conn, online: isOnline };
            }
            return conn;
          }));

          // Also update selectedConnectionData if it's this connection
          if (selectedConnectionData && 
              ((selectedConnectionData.user1Id === user.uid && selectedConnectionData.user2Id === otherUserId) ||
               (selectedConnectionData.user1Id === otherUserId && selectedConnectionData.user2Id === user.uid))) {
            setSelectedConnectionData((prev: any) => prev ? { ...prev, online: isOnline } : prev);
          }
        }
      });

      unsubscribes.push(unsubscribe);
    });

    return () => {
      unsubscribes.forEach(unsub => unsub());
    };
  }, [user?.uid, connections.length, selectedConnectionData]);

  // Calculate total online users count
  const totalOnlineCount = connections.filter(conn => conn.online === true).length;

  // Helper: open a connection in the flow (used by list click and global events)
  const openConnectionInFlow = async (connection: any, mode?: 'chat' | 'agenda') => {
    if (!user) {
      console.warn('⚠️ No user, cannot open connection');
      return;
    }

    // Latch the desired mode immediately.
    // Only open the agenda overlay when explicitly requested (mode === 'agenda').
    // For all other cases — including undefined (plain "navigate to connection") —
    // always close the agenda so stale showAgenda state from a prior navigation
    // never leaks into the new view.
    if (mode === 'agenda') {
      console.log('📅 Setting to agenda mode');
      // Explicit agenda navigation: clear suppressor so the agenda renders immediately.
      if (agendaNavSuppressTimerRef.current) clearTimeout(agendaNavSuppressTimerRef.current);
      setAgendaNavSuppressed(false);
      setShowAgenda(true);
      setShowAppointmentForm(false);
    } else {
      // mode === 'chat' or mode === undefined: ensure agenda is closed
      setShowAgenda(false);
      if (mode === 'chat') {
        console.log('💬 Setting to chat mode');
        suppressStartChatPopupUntilRef.current = Date.now() + 6000;
      }
    }

    if (!connection) {
      // If we don't have the connection yet, remember the intent
      return;
    }

    if (connection.name === 'Onbekend') {
      warning('Deze persoon heeft de onboarding nog niet voltooid. Zodra de gegevens compleet zijn, kun je de connectie starten.');
      return;
    }

    if (connection.status !== 'accepted') return;

    console.log('📝 Setting selected connection:', connection.name);
    setSelectedConnection(connection.name);
    
    // Determine phase from connection.phase (source of truth from Firestore connections doc).
    // When mode === 'chat' we force chat so the user lands in the chat view.
    // When mode === 'agenda' we keep the actual connection phase and let the agenda overlay
    // open on top — 'planning' is NOT a valid ConnectionPhase and must not be set here.
    const phaseToSet: ConnectionPhase = mode === 'chat' ? 'chat' : ((connection.phase as ConnectionPhase) || 'chat');
    console.log('🎯 Final phase being set:', phaseToSet);
    setCurrentPhase(phaseToSet);

    setConnectionStatus(connection.status);

    // Pre-populate the prevIdRef BEFORE calling setSelectedConnectionData.
    // The ID-change effect (line ~684) uses prevIdRef to decide whether to reset
    // activeCard. By setting it here, we tell the effect "this connection is already
    // known — don't reset" so our explicit setActiveCard below is not overridden.
    if (connection.id) {
      prevSelectedConnectionIdRef.current = connection.id;
    }
    setSelectedConnectionData(connection);

    // Set the active card AFTER selectedConnectionData (batched with it in the same
    // render cycle). The prevIdRef pre-population above guarantees the reset effect
    // won't clear this value.
    if (phaseToSet === 'physical') {
      setActiveCard('physical');
    }
    console.log('✅ Connection data set successfully');
    if (connection.id) {
      await ensureConnectionJourneyFields(connection.id);
    }

    // Clear 'nieuw' vlag voor deze gebruiker — gebruik arrayRemove zodat
    // gelijktijdige updates van andere gebruikers niet verloren gaan.
    try {
      if (connection.hasNewAcceptance) {
        const connRef = doc(db, 'connections', connection.id);
        await updateDoc(connRef, { newForUserIds: arrayRemove(user.uid) });
      }
    } catch (e) {
      console.warn('⚠️ Kon newForUserIds niet updaten voor connection', connection.id, e);
    }

    // Vind of maak de chatroom
    if (connection.user1Id || connection.user2Id) {
      const otherUserId = connection.user1Id === user.uid ? connection.user2Id : connection.user1Id;
      await findOrCreateChatRoom(otherUserId, connection.name, connection.avatar);
    }

    // ✅ Mark that we should auto-accept chat when ready
    if (mode === 'chat' && phaseToSet === 'chat') {
      console.log('🎯 Setting pendingAutoAcceptChat flag');
      setPendingAutoAcceptChat(true);
      suppressStartChatPopupUntilRef.current = Date.now() + 6000;
    }
  };

  const filteredConnections = connections.filter(connection => {
    // Filter out incomplete connections
    if (!connection.name || connection.name.trim() === '') return false;
    
    // Get the flow for this connection
    const flow = connectionFlowsMap[connection.id];
    const hasActiveFlow = flow && (flow.status === 'active' || flow.status === 'pending');
    const isFlowCompleted = flow && flow.status === 'completed';
    
    // Filter by tab
    if (activeTab === 'pending') {
      // Wachtend: alleen pending connection requests
      if (connection.status !== 'pending') return false;
    } else if (activeTab === 'active') {
      // Verbinden: alleen accepted connecties ZONDER actieve flow
      if (connection.status !== 'accepted') return false;
      if (hasActiveFlow) return false; // Exclude connections with active flows
    } else if (activeTab === 'inprogress') {
      // Lopend: alleen accepted connecties MET actieve flow
      if (connection.status !== 'accepted') return false;
      if (!hasActiveFlow) return false; // Only show connections with active flows
    } else if (activeTab === 'completed') {
      // Afgerond: alleen connecties met completed flow
      if (!isFlowCompleted) return false;
    }
    
    // Filter by search query
    if (searchQuery.trim()) {
      return connection.name.toLowerCase().includes(searchQuery.toLowerCase());
    }
    
    return true;
  });

  // Activate the agenda navigation suppressor as early as possible —
  // when openConnectionFlowForConnection first fires (T=100ms after click).
  // This guarantees the render-level suppressor is active before any Firestore
  // first-snapshot can call setShowAgenda(true).
  useEffect(() => {
    const earlyNavHandler = () => {
      lastNavigationTimeRef.current = Date.now();
      // Activate render suppressor immediately
      setAgendaNavSuppressed(true);
      // Clear after 2500ms — long enough for all first-snapshots to settle
      if (agendaNavSuppressTimerRef.current) clearTimeout(agendaNavSuppressTimerRef.current);
      agendaNavSuppressTimerRef.current = setTimeout(() => setAgendaNavSuppressed(false), 2500);
    };
    window.addEventListener('openConnectionFlowForConnection', earlyNavHandler as any);
    return () => {
      window.removeEventListener('openConnectionFlowForConnection', earlyNavHandler as any);
      if (agendaNavSuppressTimerRef.current) clearTimeout(agendaNavSuppressTimerRef.current);
    };
  }, []);

  // Luister naar global event vanuit App.tsx om een specifieke connectie te openen
  useEffect(() => {
    const handler = (event: any) => {
      try {
        console.log('📥 ConnectieFlow received selectConnectionInFlow event', event.detail);
        const detail = event.detail || {};
        const connectionId = detail.connectionId as string | undefined;
        const mode = detail.mode as 'chat' | 'agenda' | undefined;
        if (!connectionId) {
          console.warn('⚠️ No connectionId in selectConnectionInFlow event');
          return;
        }

        console.log('🎯 Mode received:', mode, 'Setting UI state accordingly');
        if (mode === 'chat') {
          suppressStartChatPopupUntilRef.current = Date.now() + 6000;
        }
        // Latch intent immediately — only when mode is explicitly set
        if (mode === 'agenda') {
          setShowAgenda(true);
          setShowAppointmentForm(false);
        } else if (mode === 'chat') {
          setShowAgenda(false);
        }

        // Mark navigation time so the timer-expired popup is suppressed during
        // the brief window when currentPhase may transiently land on a wrong phase.
        lastNavigationTimeRef.current = Date.now();

        console.log('🔍 Looking for connection in list. Total connections:', connections.length);
        const connection = connections.find((c: any) => c.id === connectionId);
        if (connection) {
          console.log('✅ Found connection, opening in flow:', connection.name);
          openConnectionInFlow(connection, mode);
          // FIX: Do NOT set pendingOpen when connection is already found.
          // Setting it here caused a second openConnectionInFlow call (via the
          // pendingOpen useEffect) with mode defaulting to 'chat', which forced
          // currentPhase → 'chat' and triggered the timer-expired popup.
        } else {
          console.log('⏳ Connection not found in list yet. ConnectionId:', connectionId, '- Waiting for real-time listener to load it...');
          console.log('📋 Available connection IDs:', connections.map((c: any) => c.id));
          // FIX: Only set pendingOpen when we actually need to wait.
          // Use mode as-is (no || 'chat' fallback) so we never accidentally
          // force the chat phase on a connection that is already in physical.
          setPendingOpen({ connectionId, mode });
        }
      } catch (e) {
        console.error('❌ Error handling selectConnectionInFlow event', e);
      }
    };

    // FIX: Only listen to selectConnectionInFlow here.
    // ConnectiesPage already handles openConnectionFlowForConnection and
    // re-dispatches it as selectConnectionInFlow after setting the active tab.
    // Listening to both caused the handler to fire twice per navigation,
    // doubling all side-effects (phase changes, popup triggers, etc.).
    window.addEventListener('selectConnectionInFlow', handler as any);
    
    return () => {
      window.removeEventListener('selectConnectionInFlow', handler as any);
    };
  }, [connections, user?.uid]);

  // Wanneer pendingOpen staat en de connectie later binnenkomt, selecteer hem alsnog
  useEffect(() => {
    if (!pendingOpen) return;
    const connection = connections.find((c: any) => c.id === pendingOpen.connectionId);
    if (!connection) return;
    openConnectionInFlow(connection, pendingOpen.mode);
    setPendingOpen(null);
  }, [pendingOpen, connections]);

  // Auto-select first active connection on mount
  const hasAutoSelected = useRef(false);
  
  useEffect(() => {
    if (!user || loadingConnections || connections.length === 0) return;
    if (hasAutoSelected.current) return;
    if (selectedConnectionData) return;
    // Don't auto-select if we have a pendingOpen (navigation state takes priority)
    if (pendingOpen) return;
    
    // Priority 1: First item in currently active tab list (Verbinden/Open = accepted connections)
    // Priority 2: Newest by updatedAt/createdAt
    const acceptedConnections = connections.filter(c => c.status === 'accepted');
    if (acceptedConnections.length === 0) return;
    
    // Sort by updatedAt or createdAt (newest first)
    const sortedConnections = [...acceptedConnections].sort((a, b) => {
      const aTime = a.updatedAt?.toMillis?.() || a.createdAt?.toMillis?.() || a.createdAt?.getTime?.() || 0;
      const bTime = b.updatedAt?.toMillis?.() || b.createdAt?.toMillis?.() || b.createdAt?.getTime?.() || 0;
      return bTime - aTime; // Newest first
    });
    
    const firstActive = sortedConnections[0];
    if (firstActive) {
      console.log('🎯 Auto-selecting first active connection on mount:', firstActive.name);
      hasAutoSelected.current = true;
      openConnectionInFlow(firstActive, 'chat');
    }
  }, [connections, loadingConnections, selectedConnectionData, user, pendingOpen]);

  // Auto-accept chat when chatroom and connectionFlow are ready
  useEffect(() => {
    if (!pendingAutoAcceptChat) return;
    if (!currentChatRoom || !connectionFlow) {
      console.log('⏳ Waiting for chatroom and connectionFlow...', {
        hasChatRoom: !!currentChatRoom,
        hasConnectionFlow: !!connectionFlow
      });
      return;
    }
    
    console.log('✅ ChatRoom and ConnectionFlow ready - auto-accepting chat step');
    setPendingAutoAcceptChat(false); // Reset flag
    
    // Call acceptChatStep with a small delay to ensure all state is updated
    setTimeout(async () => {
      try {
        await acceptChatStep();
      } catch (error) {
        console.error('❌ Error auto-accepting chat step:', error);
      }
    }, 100);
  }, [pendingAutoAcceptChat, currentChatRoom, connectionFlow]);

  // Safety: ensure journey fields when selection changes
  useEffect(() => {
    if (selectedConnectionData?.id) {
      ensureConnectionJourneyFields(selectedConnectionData.id).catch(err =>
        console.warn('ensureConnectionJourneyFields failed', err)
      );
    }
  }, [selectedConnectionData?.id]);

  // Listen to calendarEvents for the current user to resolve active events
  useEffect(() => {
    if (!user?.uid) return;
    let unsubscribe: (() => void) | undefined;
    (async () => {
      const { collection, query, where, onSnapshot } = await import('firebase/firestore');
      const eventsRef = collection(db, 'calendarEvents');
      const q = query(eventsRef, where('participants', 'array-contains', user.uid));
      unsubscribe = onSnapshot(q, (snapshot) => {
        const items = snapshot.docs.map((doc) => {
          const raw: any = doc.data();
          return {
            id: doc.id,
            ...raw,
            startTime: raw.startTime?.toDate ? raw.startTime.toDate() : new Date(raw.startTime || Date.now()),
            endTime: raw.endTime?.toDate ? raw.endTime.toDate() : new Date(raw.endTime || Date.now()),
            createdAt: raw.createdAt?.toDate ? raw.createdAt.toDate() : new Date(raw.createdAt || Date.now()),
          };
        });
        setFlowCalendarEvents(items);
      }, (err) => {
        console.warn('calendarEvents listener error', err);
      });
    })();
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [user?.uid]);

  // Reset demo timer + phase index whenever we enter a demo detail
  useEffect(() => {
    if (!selectedMobileFlowDemo || mobileFlowView !== 'detail') return;
    setDemoTimerSecs(0);
    const idx = DEMO_PHASES.findIndex(p => p.key === selectedMobileFlowDemo.progress);
    setDemoPhaseIdx(idx >= 0 ? idx : 0);
    setDemoNoteText('');
    setDemoNoteSaved(false);
    setDemoAiEnabled(false);
  }, [selectedMobileFlowDemo, mobileFlowView]);

  // Count-up timer for demo detail
  useEffect(() => {
    if (!selectedMobileFlowDemo || mobileFlowView !== 'detail') return;
    const id = setInterval(() => setDemoTimerSecs(s => s + 1), 1000);
    return () => clearInterval(id);
  }, [selectedMobileFlowDemo, mobileFlowView]);

  // Show onboarding warning if user is not fully onboarded
  if (isFullyOnboarded === false) {
    return (
      <div className="flex h-[calc(100vh-80px)] bg-white items-center justify-center">
        <div className="max-w-md mx-auto p-8 bg-stone-50 border-2 border-stone-200 rounded-lg shadow-lg">
          <div className="flex items-center mb-4">
            <Icon icon={AlertTriangle} size={32} strokeWidth={1.5} color="#d97706" className="mr-3" />
            <h2 className="text-xl font-bold text-[#4A5A3D]">Onboarding niet voltooid</h2>
          </div>
          <p className="text-[#4F6660] mb-4">
            Je kunt deze functie pas gebruiken nadat je alle verplichte gegevens hebt ingevuld.
          </p>
          <div className="mb-4">
            <p className="text-sm font-medium text-[#4A5A3D] mb-2">Ontbrekende gegevens:</p>
            <ul className="list-disc list-inside text-sm text-[#4F6660] space-y-1">
              {missingOnboardingFields.map((field, index) => (
                <li key={index}>{field}</li>
              ))}
            </ul>
          </div>
          <button
            onClick={() => {
              console.log('🔘 Ga naar onboarding button clicked');
              // Dispatch event - App.tsx will handle the navigation and set flags
              window.dispatchEvent(new CustomEvent('navigateToOnboarding'));
            }}
            className="w-full bg-[#7A9488] hover:bg-[#4F6660] text-white font-medium py-2 px-4 rounded transition-colors"
          >
            Ga naar onboarding
          </button>
        </div>
      </div>
    );
  }

  // Check for pending received requests (action required)
  const pendingReceivedRequests = filteredConnections.filter(
    conn => conn.status === 'pending' && conn.requestType === 'received'
  );
  const hasPendingActionRequired = pendingReceivedRequests.length > 0;

  const formatDemoTime = (secs: number) => {
    const m = Math.floor(secs / 60).toString().padStart(2, '0');
    const s = (secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const sendDemoMessage = () => {
    const trimmed = demoChatInput.trim();
    if (!trimmed) return;
    setDemoChatMessages(prev => [...prev, { self: true, text: trimmed }]);
    setDemoChatInput('');
  };

  return (
    <>
      {/* ✅ UX: Toast Notifications - gebruik de geëxporteerde component direct (stabiele referentie, geen re-mount animatie) */}
      <ToastContainer toasts={toasts} onClose={removeToast} />
      
      {/* ✅ UX: Presence Indicator - Show what other user is doing */}
      {otherUserPresence && (
        <div className="fixed top-20 right-4 z-50 animate-fade-in">
          <PresenceIndicator presence={otherUserPresence} />
        </div>
      )}
      
      <div className="flex h-full flex-col md:flex-row md:h-[calc(100vh-80px)] bg-[#F4F1EA] md:bg-white relative w-full overflow-x-hidden mobile-snap-container">
      {/* Prominent Action Card for Pending Requests */}
      {hasPendingActionRequired && (
        <div className="absolute top-0 left-0 right-0 z-50 bg-gradient-to-r from-[#7A9488] to-[#4F6660] text-white p-3 md:p-4 shadow-lg">
          <div className="max-w-5xl mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 md:w-10 md:h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-lg md:text-xl">🔔</span>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-base md:text-lg">
                  {pendingReceivedRequests.length === 1 
                    ? 'Je hebt een nieuw connectieverzoek!' 
                    : `Je hebt ${pendingReceivedRequests.length} nieuwe connectieverzoeken!`}
                </h3>
                <p className="text-xs md:text-sm text-stone-100">
                  {pendingReceivedRequests.length === 1
                    ? `${pendingReceivedRequests[0].name} wil met je connecten. Accepteer of wijs af.`
                    : 'Er wachten connectieverzoeken op jouw actie. Bekijk ze in de lijst.'}
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                setActiveTab('pending');
                if (pendingReceivedRequests.length > 0) {
                  setSelectedConnection(pendingReceivedRequests[0].id || '');
                }
              }}
              className="bg-white text-[#4F6660] px-4 md:px-6 py-2 rounded-lg font-semibold hover:bg-stone-50 transition-colors shadow-md text-sm md:text-base whitespace-nowrap"
            >
              Bekijk verzoeken
            </button>
          </div>
        </div>
      )}

      {/* Left Sidebar - Hidden on mobile, shown on desktop */}
      <div className="hidden md:flex w-72 border-r border-gray-200 flex-col h-full min-h-0 overflow-hidden flex-shrink-0">
        {/* Connection Tabs - Op dezelfde hoogte als fase tabs */}
        <div className={`flex-shrink-0 border-b border-gray-200 bg-white ${hasPendingActionRequired ? 'mt-20' : ''}`}>
          <div className="flex items-center px-2 py-0.5">
            <div className="flex items-center gap-0">
              {tabs.map((tab) => {
                const isActive = activeTab === tab.key;
                // Count items per tab for badges
                const pendingCount = tab.key === 'pending' 
                  ? filteredConnections.filter(c => c.status === 'pending').length 
                  : 0;
                const inprogressCount = tab.key === 'inprogress'
                  ? filteredConnections.filter(c => {
                      const f = connectionFlowsMap[c.id];
                      return c.status === 'accepted' && f && (f.status === 'active' || f.status === 'pending');
                    }).length
                  : 0;
                const badgeCount = pendingCount || inprogressCount;
                
                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`px-1.5 py-0.5 text-[11px] font-medium transition-colors relative leading-tight flex items-center gap-1 ${
                      isActive
                        ? 'text-[#4F6660] border-b-2 border-[#4F6660]'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {tab.label}
                    {badgeCount > 0 && (
                      <span className={`inline-flex items-center justify-center min-w-[16px] h-4 px-1 text-[9px] font-bold rounded-full leading-none ${
                        tab.key === 'pending' 
                          ? 'bg-red-500 text-white' 
                          : 'bg-stone-100 text-[#4F6660]'
                      }`}>
                        {badgeCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        
        {/* Header */}
        <div className="flex-shrink-0 p-2 border-b border-gray-200">
          <div className="flex items-center justify-between mb-2">
            {totalOnlineCount > 0 && (
              <div className="flex items-center space-x-1 text-xs text-green-600">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="font-medium">{totalOnlineCount} online</span>
              </div>
            )}
          </div>
          
          {/* Search */}
          <div className="relative">
            <Icon icon={Search} size={16} strokeWidth={1.5} color="#9ca3af" className="absolute left-3 top-1/2 transform -translate-y-1/2" />
            <input
              type="text"
              placeholder="Zoek op naam..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-[#7A9488] focus:border-transparent"
            />
          </div>

        </div>

        {/* Connections List */}
        <div className="flex-1 overflow-y-auto min-h-0 max-h-full">
          {loadingConnections ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#7A9488]"></div>
              <span className="ml-2 text-gray-600">Laden...</span>
            </div>
          ) : activeTab === 'verbinden-met' ? (
            <div className="p-6">
              <OpenForConnectionsList 
                onConnect={async (userId, userName, userPhoto) => {
                  // ✅ BLOCK: Check phone number BEFORE opening modal
                  if (!currentUserHasPhone) {
                    warning('Telefoonnummer vereist. Je kunt geen connectieverzoek versturen zonder telefoonnummer. Ga naar Instellingen → Profiel om je telefoonnummer toe te voegen.');
                    return;
                  }
                  console.log(`🔵 Opening request modal for ${userName} (${userId})`);
                  setSelectedUserForRequest({ userId, userName, userPhoto });
                  setShowRequestModal(true);
                }}
              />
            </div>
          ) : filteredConnections.length === 0 ? (
            <div className="p-4 text-center">
              {isOpenForConnections ? (
                <>
                  <div className="relative mb-3">
                    <div className="w-3 h-3 bg-green-500 rounded-full mx-auto animate-pulse"></div>
                  </div>
                  <h3 className="text-sm font-medium text-green-700 mb-1">Actief</h3>
                  <p className="text-xs text-gray-600">
                    Je bent zichtbaar voor anderen
                  </p>
                </>
              ) : (
                <>
                  <div className="text-gray-400 text-3xl mb-2">👥</div>
                  <h3 className="text-sm font-medium text-gray-900 mb-1">Nog geen connecties</h3>
                  <p className="text-xs text-gray-600 mb-3">
                    Klik rechts om regio's te selecteren en jezelf open te stellen
                  </p>
                </>
              )}
            </div>
          ) : (
            filteredConnections.map((connection) => (
            <div
              key={connection.id}
              onClick={async () => {
                await openConnectionInFlow(connection, 'chat');
              }}
              className={`flex items-center py-0.5 px-1.5 ${
                connection.name === 'Onbekend' 
                  ? 'opacity-75 cursor-not-allowed' 
                  : connection.status === 'accepted' 
                    ? 'hover:bg-gray-50 cursor-pointer' 
                    : ''
              } ${
                selectedConnection === connection.name ? 'bg-stone-50 border-r-2 border-[#7A9488]' : ''
              }`}
            >
              <div className="relative flex-shrink-0">
                {connection.avatar ? (
                  <img 
                    src={connection.avatar} 
                    alt={connection.name}
                    className="w-8 h-8 rounded-full object-cover"
                    onError={(e) => {
                      const target = e.currentTarget as HTMLImageElement;
                      target.style.display = 'none';
                      const fallback = target.nextElementSibling as HTMLElement;
                      if (fallback) fallback.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div className={`w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center ${connection.avatar ? 'hidden' : ''}`}>
                  <User size={14} strokeWidth={1.5} className="text-gray-500" />
                </div>
                {connection.online && (
                  <div className="absolute bottom-0 right-0 w-1.5 h-1.5 bg-green-500 rounded-full border border-white"></div>
                )}
                {connection.hasNewAcceptance && (
                  <span className="absolute -top-0.5 -right-0.5 bg-[#7A9488] text-white text-[7px] px-0.5 py-0 rounded-full leading-none">
                    nieuw
                  </span>
                )}
              </div>
              
              <div className="ml-1 flex-1 min-w-0">
                <div className="flex items-center justify-between gap-0.5">
                  <div className="flex items-center gap-0.5 min-w-0 flex-1">
                    <p className={`text-[11px] font-medium truncate ${
                      connection.name === 'Onbekend' ? 'text-[#7A9488] italic' : 'text-gray-900'
                    }`}>
                    {connection.name}
                  </p>
                    {connection.name === 'Onbekend' && (
                      <span className="text-[8px] bg-stone-100 text-[#7A9488] px-0.5 py-0 rounded-full whitespace-nowrap flex-shrink-0">
                        ⚠️
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-0.5 flex-shrink-0">
                    <span className={`text-[8px] px-0.5 py-0 rounded-full whitespace-nowrap ${
                      connection.phase === 'pending' ? 'bg-gray-100 text-gray-600' :
                      'bg-stone-100 text-[#4F6660]'
                    }`}>
                      {phases[connection.phase as keyof typeof phases]?.icon}
                    </span>
                    <span className="text-[8px] text-gray-500 whitespace-nowrap">{connection.time}</span>
                  </div>
                </div>
                {connection.lastActivity && connection.name !== 'Onbekend' && (
                  <p className="text-[8px] text-gray-600 truncate mt-0 leading-tight">
                    {connection.lastActivity}
                  </p>
                )}
                
                {/* Action buttons for pending requests */}
                {connection.status === 'pending' && connection.requestType === 'received' && (
                  <div className="flex gap-0.5 mt-0.5 relative z-10" style={{ zIndex: 10 }}>
                    <button
                      type="button"
                      onClick={async (e) => {
                        console.log('🔘 Accepteer button clicked for:', connection.name);
                        e.preventDefault();
                        e.stopPropagation();
                        
                        if (!connection.requestId) {
                          console.error('❌ No requestId found for connection:', connection);
                          showError('Fout: Request ID niet gevonden. Probeer de pagina te verversen.');
                          return;
                        }
                        
                        if (!user?.uid) {
                          showError('Je bent niet ingelogd. Log opnieuw in.');
                          return;
                        }
                        
                        try {
                          console.log('🔄 Accepting connection request:', connection.requestId);
                          const { connectionRequestService } = await import('../services/connectionRequestService');
                          await connectionRequestService.acceptConnectionRequest(connection.requestId);
                          console.log('✅ Connection request accepted in service');
                          
                          // Determine the other user ID (the person who sent the request)
                          const otherUserId = connection.user1Id; // This is the person who sent ME the request
                          if (!otherUserId) {
                            console.error('❌ No otherUserId found');
                            showError('Fout: Gebruiker ID niet gevonden.');
                            return;
                          }
                          
                          const otherUserName = connection.name;
                          const otherUserAvatar = connection.avatar;
                          
                          console.log('🔄 Creating connection document...');
                          // Create connection document (user1Id is always the smaller UID for consistency)
                          const userIds = [user.uid, otherUserId].sort();
                          const knownContact = (connection as any)?.requestData?.knownContact || null;
                          const verificationOnly =
                            knownContact?.verificationOnly === true ||
                            knownContact?.requestedMode === 'verify_call';
                          
                          // Build connection doc — exclude undefined fields (Firestore rejects them)
                          const connectionData: Record<string, any> = {
                            user1Id: userIds[0],
                            user2Id: userIds[1],
                            status: 'accepted',
                            createdAt: new Date(),
                            acceptedAt: new Date(),
                          };
                          if (knownContact !== null) {
                            connectionData.knownContact = knownContact;
                            connectionData.knownContactVerificationOnly = verificationOnly;
                          }
                          
                          const connectionDoc = await addDoc(collection(db, 'connections'), connectionData);
                          
                          console.log('✅ Connection document created:', connectionDoc.id);
                          
                          // Invalidate AI Visibility cache for both users (connection affects score)
                          try {
                            const { invalidateAIVisibilityCache } = await import('../services/aiVisibilityImpactService');
                            await Promise.all([
                              invalidateAIVisibilityCache(user.uid),
                              invalidateAIVisibilityCache(otherUserId)
                            ]);
                          } catch (cacheError) {
                            console.warn('Failed to invalidate cache:', cacheError);
                          }
                          
                          // Create chat room with the other user (not current user!)
                          console.log('🔄 Creating chat room...');
                          await findOrCreateChatRoom(otherUserId, otherUserName, otherUserAvatar);
                          
                          // Create connection object directly (don't wait for reload)
                          const acceptedConnection = {
                            id: connectionDoc.id,
                            name: otherUserName,
                            avatar: otherUserAvatar,
                            online: false,
                            lastActivity: 'Klik om te chatten',
                            time: new Date().toLocaleDateString('nl-NL'),
                            status: 'accepted',
                            phase: 'chat',
                            user1Id: userIds[0],
                            user2Id: userIds[1]
                          };
                          
                          // Automatically select the accepted connection
                          console.log('🔄 Selecting accepted connection...');
                          setSelectedConnection(acceptedConnection.name);
                          setSelectedConnectionData(acceptedConnection);
                          setActiveTab('active'); // Switch to active tab
                          setCurrentPhase('chat'); // Start in chat phase
                          
                          // Reload connections to update the list (after selection)
                          console.log('🔄 Reloading connections...');
                          await loadConnectionsAndRequests();
                          
                          console.log('✅ Connection selected and chat ready:', acceptedConnection.name);
                          success(`Connectie geaccepteerd! Chat is nu beschikbaar met ${connection.name}.`);
                        } catch (error: any) {
                          console.error('❌ Error accepting connection:', error);
                          showError(`Fout bij accepteren: ${error.message || 'Onbekende fout'}. Probeer opnieuw.`);
                        }
                      }}
                      className="flex-1 bg-green-500 hover:bg-green-600 text-white px-1 py-0.5 rounded text-[8px] font-medium transition-colors relative z-10"
                      style={{ zIndex: 10, pointerEvents: 'auto' }}
                    >
                      ✓ Accepteer
                    </button>
                    <button
                      type="button"
                      onClick={async (e) => {
                        console.log('🔘 Afwijzen button clicked for:', connection.name);
                        e.preventDefault();
                        e.stopPropagation();
                        
                        if (!connection.requestId) {
                          console.error('❌ No requestId found for connection:', connection);
                          showError('Fout: Request ID niet gevonden. Probeer de pagina te verversen.');
                          return;
                        }
                        
                        try {
                          const { connectionRequestService } = await import('../services/connectionRequestService');
                          await connectionRequestService.declineConnectionRequest(connection.requestId);
                          console.log('✅ Connection request declined');
                          info('Verzoek afgewezen');
                          // Reload connections to update the list
                          await loadConnectionsAndRequests();
                        } catch (error: any) {
                          console.error('❌ Error declining connection:', error);
                          showError(`Fout bij afwijzen: ${error.message || 'Onbekende fout'}`);
                        }
                      }}
                      className="flex-1 bg-red-500 hover:bg-red-600 text-white px-1 py-0.5 rounded text-[8px] font-medium transition-colors relative z-10"
                      style={{ zIndex: 10, pointerEvents: 'auto' }}
                    >
                      ✗ Afwijzen
                    </button>
                  </div>
                )}
                
                {connection.status === 'pending' && connection.requestType === 'sent' && (
                  <div className="mt-2">
                    <div className="bg-stone-50 border border-stone-200 text-[#7A9488] px-3 py-1.5 rounded text-xs text-center">
                      ⏳ Wacht op acceptatie...
                    </div>
                  </div>
                )}
              </div>
              
              {connection.unread && (
                <div className="w-2 h-2 bg-[#7A9488] rounded-full ml-2"></div>
              )}
            </div>
            ))
          )}
        </div>

      </div>

      {/* Mobile Sidebar - Shown as list view on mobile */}
      <div className={`${(selectedConnectionData || selectedMobileFlowDemo) && mobileFlowView === 'detail' ? 'hidden' : 'flex'} md:hidden w-full flex-1 flex-col bg-[#F4F1EA] overflow-x-hidden mobile-snap-section`}>
        {filteredConnections.length > 0 && (
          <div className={`mx-4 mt-4 flex-shrink-0 rounded-2xl border border-[#E8E4DC] bg-white/90 p-3 shadow-sm ${hasPendingActionRequired ? 'mt-16' : ''}`}>
            <div className="mb-2 flex items-center justify-between">
              <p className="text-[11px] font-medium text-[#4F6660]">ConnectieFlow</p>
              {totalOnlineCount > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-[#F0F7F4] px-2 py-1 text-[11px] font-medium text-[#4F6660]">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#55B982]" />
                  {totalOnlineCount} online
                </span>
              )}
            </div>
            <div className="relative">
              <Icon icon={Search} size={16} strokeWidth={1.5} color="#9ca3af" className="absolute left-3 top-1/2 transform -translate-y-1/2" />
              <input
                type="text"
                placeholder="Zoek op naam..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-xl border border-[#E8E4DC] bg-white py-2.5 pl-9 pr-3 text-sm text-[#2F3A36] placeholder-gray-400 focus:border-[#7A9488] focus:outline-none focus:ring-1 focus:ring-[#7A9488]"
              />
            </div>
          </div>
        )}

        {/* Mobile Connections List */}
        <div className={`${filteredConnections.length > 0 || showMobileFlowDemo ? 'mx-auto mt-3 w-[calc(100%-2rem)] max-w-[430px] rounded-2xl border border-[#E8E4DC] bg-white/90 shadow-sm' : ''} flex-1 overflow-y-auto`}>
          {loadingConnections ? (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#7A9488]"></div>
              <span className="ml-2 text-gray-600 text-sm">Laden...</span>
            </div>
          ) : filteredConnections.length === 0 ? (
            showMobileFlowDemo ? (
              <div className="space-y-3 p-4">
                <div className="mb-1">
                  <p className="text-sm font-semibold text-[#2F3A36]">Voorbeeld ConnectieFlow</p>
                  <p className="mt-1 text-xs leading-relaxed text-[#8A8A84]">
                    Kies een voorbeeldpersoon om de mobiele flow te bekijken.
                  </p>
                </div>

                <div className="flex rounded-full bg-[#F4F1EA] p-1 text-[12px] font-medium text-[#8A8A84]">
                  {([
                    ['open', 'Open'],
                    ['verbonden', 'Verbonden'],
                    ['afgerond', 'Afgerond'],
                  ] as const).map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setMobileFlowDemoFilter(key)}
                      className={`min-h-0 flex-1 rounded-full px-2 py-2 text-center leading-none transition-colors ${
                        mobileFlowDemoFilter === key ? 'bg-white text-[#4F6660] shadow-sm' : 'text-[#8A8A84]'
                      }`}
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                <div>
                  <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-[#8A8A84]">Fase</p>
                  <div
                    className="grid gap-1 rounded-full bg-[#F4F1EA] p-1 text-[11px] font-medium text-[#8A8A84]"
                    style={{ gridTemplateColumns: 'repeat(5, minmax(0, 1fr))' }}
                  >
                    {([
                      ['alle', 'Alle'],
                      ['chat', 'Chat'],
                      ['bellen', 'Bellen'],
                      ['video', 'Video'],
                      ['ontmoeten', 'Ontmoeten'],
                    ] as const).map(([key, label]) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setMobileFlowDemoProgressFilter(key)}
                        className={`min-h-0 rounded-full px-1.5 py-1.5 text-center leading-none transition-colors ${
                          mobileFlowDemoProgressFilter === key
                            ? 'bg-white text-[#4F6660] shadow-sm'
                            : 'text-[#8A8A84]'
                        }`}
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="relative">
                  <Icon icon={Search} size={16} strokeWidth={1.5} color="#9ca3af" className="absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={mobileFlowDemoSearch}
                    onChange={(e) => setMobileFlowDemoSearch(e.target.value)}
                    placeholder="Zoek op naam..."
                    className="w-full rounded-xl border border-[#E8E4DC] bg-white py-2.5 pl-9 pr-3 text-sm text-[#2F3A36] placeholder-gray-400 focus:border-[#7A9488] focus:outline-none focus:ring-1 focus:ring-[#7A9488]"
                  />
                </div>

                {visibleMobileFlowDemoContacts.length === 0 && (
                  <div className="rounded-2xl border border-[#E8E4DC] bg-white px-4 py-6 text-center text-sm text-[#8A8A84]">
                    Geen voorbeeldpersonen gevonden.
                  </div>
                )}

                {visibleMobileFlowDemoContacts.map((contact) => (
                  <button
                    key={contact.id}
                    type="button"
                    onClick={() => {
                      setSelectedMobileFlowDemo(contact);
                      setMobileFlowView('detail');
                    }}
                    className="w-full rounded-2xl border border-[#E8E4DC] bg-white p-3 text-left shadow-sm transition-transform active:scale-[0.99]"
                    style={{
                      WebkitTapHighlightColor: 'transparent',
                      display: 'grid',
                      gridTemplateColumns: '44px minmax(0, 1fr) auto',
                      columnGap: '12px',
                      alignItems: 'center',
                    }}
                  >
                    <div className="relative" style={{ width: 44, height: 44, minWidth: 44 }}>
                      <img
                        src={`https://ui-avatars.com/api/?name=${encodeURIComponent(contact.name)}&background=8DA8A0&color=fff&size=88&bold=true`}
                        alt={contact.name}
                        className="h-11 w-11 rounded-full object-cover"
                        style={{ width: 44, height: 44, display: 'block' }}
                      />
                      {contact.online && (
                        <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-white bg-[#55B982]" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <h3 className="truncate text-sm font-semibold leading-tight text-[#2F3A36]">{contact.name}</h3>
                      <p className="mt-1 truncate text-xs leading-tight text-[#8A8A84]">
                        {contact.online ? 'Online' : 'Offline'} · {contact.phase}
                      </p>
                    </div>
                    <span className="rounded-full bg-[#F0F7F4] px-3 py-1.5 text-[11px] font-semibold text-[#4F6660]">
                      Open
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="flex h-full min-h-[420px] flex-col items-center justify-start px-5 pt-14 text-center">
                <User size={34} strokeWidth={1.5} className="mb-5 text-[#4F6660]/75" />
                <h3 className="text-xl font-semibold leading-tight text-[#2F3A36]">
                  Welkom bij ConnectieFlow
                </h3>
                <p className="mt-2 max-w-[310px] text-sm leading-relaxed text-[#6F746F]">
                  Hier zie je straks je actieve kennismakingen, met chat, bellen, beeldbellen en afspraken op één plek.
                </p>

                <div className="mt-6 w-full max-w-[340px] rounded-2xl border border-[#E8E4DC] bg-white/90 p-4 text-left shadow-sm">
                  <p className="mb-3 text-sm font-semibold text-[#2F3A36]">Zo start je een ConnectieFlow</p>
                  <ol className="space-y-2 text-sm leading-relaxed text-[#5E6462]">
                    <li><span className="font-semibold text-[#4F6660]">1.</span> Ga naar <span className="font-semibold">Mijn Netwerk</span>.</li>
                    <li><span className="font-semibold text-[#4F6660]">2.</span> Zet jezelf open voor netwerken.</li>
                    <li><span className="font-semibold text-[#4F6660]">3.</span> Verstuur of accepteer een verzoek.</li>
                    <li><span className="font-semibold text-[#4F6660]">4.</span> De chatflow verschijnt hier automatisch.</li>
                  </ol>
                </div>

                <button
                  type="button"
                  onClick={() => setShowMobileFlowDemo(true)}
                  className="mt-5 w-full max-w-[340px] rounded-full px-5 py-3 text-sm font-semibold text-white shadow-sm active:scale-[0.98]"
                  style={{ WebkitTapHighlightColor: 'transparent', backgroundColor: '#4F6660', color: '#ffffff' }}
                >
                  Bekijk voorbeeldflow
                </button>
              </div>
            )
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredConnections.slice(0, 5).map((connection) => (
                <button
                  key={connection.id}
                  onClick={async () => {
                    await openConnectionInFlow(connection, 'chat');
                    setMobileFlowView('detail');
                  }}
                  className={`w-full p-3 text-left transition-colors active:bg-[#F0F7F4] ${
                    selectedConnection === connection.name || selectedConnectionData?.id === connection.id ? 'bg-stone-50 border-l-4 border-[#7A9488]' : ''
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    {connection.avatar ? (
                      <img 
                        src={connection.avatar} 
                        alt={connection.name}
                        className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                        onError={(e) => {
                          const target = e.currentTarget as HTMLImageElement;
                          target.style.display = 'none';
                          const fallback = target.nextElementSibling as HTMLElement;
                          if (fallback) fallback.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className={`w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0 ${connection.avatar ? 'hidden' : ''}`}>
                      <User size={20} strokeWidth={1.5} className="text-gray-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h3 className="font-medium text-gray-900 text-sm truncate">{connection.name}</h3>
                        {connection.online && (
                          <div className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0 ml-2"></div>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 truncate">
                        {connection.online ? 'Online' : 'Offline'} · {connection.status === 'pending' ? 'Wachtend' : connection.status === 'accepted' ? 'Actief' : 'Voltooid'}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
              {filteredConnections.length > 5 && (
                <div className="p-3 text-center text-xs text-gray-500">
                  +{filteredConnections.length - 5} meer
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Mobile demo detail - UI only, no database writes */}
      {selectedMobileFlowDemo && mobileFlowView === 'detail' && (
        <div className="flex md:hidden flex-1 min-h-0 flex-col overflow-hidden bg-[#F4F1EA]">
          {/* Header */}
          <div className="flex-shrink-0 border-b border-[#E8E4DC] bg-white px-3 pt-3 pb-2">
            {/* Back + avatar + name row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                type="button"
                onClick={() => { setSelectedMobileFlowDemo(null); setMobileFlowView('list'); }}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, width: 36, height: 36, borderRadius: '50%', WebkitTapHighlightColor: 'transparent', background: 'none', border: 'none', cursor: 'pointer', color: '#4F6660' }}
                aria-label="Terug naar voorbeeldlijst"
              >
                <span style={{ fontSize: 22, lineHeight: 1 }}>‹</span>
              </button>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, width: 36, height: 36, borderRadius: '50%', backgroundColor: '#8DA8A0', color: '#fff', fontSize: 13, fontWeight: 600 }}>
                {selectedMobileFlowDemo.initials}
                {selectedMobileFlowDemo.online && (
                  <span style={{ position: 'absolute', bottom: 0, right: 0, width: 10, height: 10, borderRadius: '50%', border: '2px solid #fff', backgroundColor: '#55B982' }} />
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#2F3A36', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedMobileFlowDemo.name}</p>
                <p style={{ margin: 0, fontSize: 11, color: '#8A8A84' }}>{selectedMobileFlowDemo.online ? 'Online' : 'Offline'}</p>
              </div>
              {/* Action icon buttons */}
              <div style={{ display: 'flex', flexShrink: 0, alignItems: 'center', gap: 2 }}>
                {([{ icon: Calendar, label: 'Afspraak' }, { icon: Phone, label: 'Bellen' }, { icon: Video, label: 'Beeldbellen' }] as const).map(({ icon, label }) => (
                  <button key={label} type="button" aria-label={label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: '50%', background: 'none', border: 'none', cursor: 'pointer', WebkitTapHighlightColor: 'transparent' }}>
                    <Icon icon={icon} size={17} strokeWidth={1.5} color="#6b7280" />
                  </button>
                ))}
              </div>
            </div>

            {/* Phase progress bar */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4, marginTop: 10 }}>
              {DEMO_PHASES.map((phase, i) => {
                const activeIdx = DEMO_PHASES.findIndex(p => p.key === selectedMobileFlowDemo.progress);
                const isCompleted = i < activeIdx;
                const isActive    = i === activeIdx;
                return (
                  <div key={phase.key} style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div style={{ width: '100%', height: 3, borderRadius: 4, backgroundColor: isCompleted ? '#B5C9C3' : isActive ? '#4F6660' : '#E8E4DC' }} />
                    <span style={{ marginTop: 3, fontSize: 10, fontWeight: 500, color: isCompleted ? '#B5C9C3' : isActive ? '#4F6660' : '#C4C0B8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {phase.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Messages */}
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '12px 16px' }}>
            <p style={{ textAlign: 'center', fontSize: 11, color: '#8A8A84', marginBottom: 12 }}>Vandaag</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {demoChatMessages.map((msg, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: msg.self ? 'flex-end' : 'flex-start' }}>
                  <div style={{
                    maxWidth: '72%',
                    borderRadius: msg.self ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                    padding: '8px 12px',
                    fontSize: 13,
                    lineHeight: 1.5,
                    boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                    backgroundColor: msg.self ? '#4F6660' : '#ffffff',
                    color: msg.self ? '#ffffff' : '#2F3A36',
                  }}>
                    {msg.text}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom toolbar */}
          <div style={{ flexShrink: 0, borderTop: '1px solid #E8E4DC', backgroundColor: '#ffffff', padding: '8px 12px 10px', display: 'flex', flexDirection: 'column', gap: 6 }}>

            {/* Row 1: Chat input + send */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="text"
                value={demoChatInput}
                onChange={(e) => setDemoChatInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); sendDemoMessage(); } }}
                placeholder="Typ een bericht..."
                style={{ flex: 1, minWidth: 0, height: 36, borderRadius: 999, border: '1px solid #E8E4DC', backgroundColor: '#FBFAF7', paddingLeft: 14, paddingRight: 14, fontSize: 13, color: '#2F3A36', outline: 'none' }}
              />
              <button
                type="button"
                onClick={sendDemoMessage}
                aria-label="Versturen"
                style={{ flexShrink: 0, width: 36, height: 36, borderRadius: '50%', backgroundColor: '#4F6660', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', WebkitTapHighlightColor: 'transparent' }}
              >
                <Icon icon={Send} size={14} strokeWidth={2} color="#ffffff" />
              </button>
            </div>

            {/* Row 2: Notes + Opslaan */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="text"
                value={demoNoteText}
                onChange={(e) => setDemoNoteText(e.target.value)}
                placeholder="Notitie..."
                style={{ flex: 1, minWidth: 0, height: 32, borderRadius: 8, border: '1px solid #E8E4DC', backgroundColor: '#FBFAF7', paddingLeft: 12, paddingRight: 12, fontSize: 12, color: '#2F3A36', outline: 'none' }}
              />
              <button
                type="button"
                onClick={() => {
                  if (!demoNoteText.trim()) return;
                  setDemoNoteSaved(true);
                  setDemoNoteText('');
                  setTimeout(() => setDemoNoteSaved(false), 2000);
                }}
                style={{ flexShrink: 0, height: 32, borderRadius: 8, backgroundColor: '#7A9488', border: 'none', color: '#fff', fontSize: 11, fontWeight: 600, padding: '0 12px', cursor: 'pointer', whiteSpace: 'nowrap', WebkitTapHighlightColor: 'transparent' }}
              >
                {demoNoteSaved ? 'Opgeslagen ✓' : 'Opslaan'}
              </button>
            </div>

            {/* Row 3: Afbreken | Volgende | timer | +1 min */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <button
                type="button"
                onClick={() => { setSelectedMobileFlowDemo(null); setMobileFlowView('list'); }}
                style={{ display: 'flex', alignItems: 'center', gap: 4, height: 30, borderRadius: 8, border: '1px solid #E8E4DC', backgroundColor: '#fff', color: '#6b7280', fontSize: 11, fontWeight: 500, padding: '0 8px', cursor: 'pointer', flexShrink: 0, WebkitTapHighlightColor: 'transparent' }}
              >
                <Icon icon={X} size={11} strokeWidth={2} color="#9ca3af" />
                Afbreken
              </button>
              <button
                type="button"
                onClick={() => setDemoPhaseIdx(i => Math.min(i + 1, DEMO_PHASES.length - 1))}
                style={{ display: 'flex', alignItems: 'center', gap: 4, height: 30, borderRadius: 8, backgroundColor: '#4F6660', border: 'none', color: '#fff', fontSize: 11, fontWeight: 600, padding: '0 10px', cursor: 'pointer', flexShrink: 0, WebkitTapHighlightColor: 'transparent' }}
              >
                ▶ Volgende
              </button>
              <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 4, overflow: 'hidden' }}>
                <Icon icon={Clock} size={12} strokeWidth={1.5} color="#8A8A84" />
                <span style={{ fontSize: 11, color: '#4F6660', fontWeight: 500, whiteSpace: 'nowrap' }}>{formatDemoTime(demoTimerSecs)}</span>
                <span style={{ fontSize: 11, color: '#8A8A84', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{DEMO_PHASES[demoPhaseIdx]?.label}</span>
              </div>
              <button
                type="button"
                onClick={() => setDemoTimerSecs(s => s + 60)}
                style={{ flexShrink: 0, height: 30, borderRadius: 8, backgroundColor: '#7A9488', border: 'none', color: '#fff', fontSize: 11, fontWeight: 600, padding: '0 8px', cursor: 'pointer', whiteSpace: 'nowrap', WebkitTapHighlightColor: 'transparent' }}
              >
                +1 min
              </button>
            </div>

            {/* Row 4: Agenda | Plannen */}
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, height: 34, borderRadius: 10, border: '1px solid #E8E4DC', backgroundColor: '#fff', fontSize: 12, fontWeight: 500, color: '#4F6660', cursor: 'pointer', WebkitTapHighlightColor: 'transparent' }}
              >
                <Icon icon={Calendar} size={13} strokeWidth={1.5} color="#4F6660" />
                Agenda
              </button>
              <button
                type="button"
                style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, height: 34, borderRadius: 10, border: '1px solid #E8E4DC', backgroundColor: '#fff', fontSize: 12, fontWeight: 500, color: '#4F6660', cursor: 'pointer', WebkitTapHighlightColor: 'transparent' }}
              >
                <Icon icon={Calendar} size={13} strokeWidth={1.5} color="#4F6660" />
                Plannen
              </button>
            </div>

            {/* Row 5: AI Relation Manager toggle */}
            <button
              type="button"
              onClick={() => setDemoAiEnabled(e => !e)}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                height: 36,
                borderRadius: 10,
                border: `1px solid ${demoAiEnabled ? '#7A9488' : '#E8E4DC'}`,
                backgroundColor: demoAiEnabled ? '#F0F7F4' : '#fff',
                fontSize: 12,
                fontWeight: 500,
                color: demoAiEnabled ? '#4F6660' : '#6b7280',
                cursor: 'pointer',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              <Icon icon={Bot} size={14} strokeWidth={1.5} color={demoAiEnabled ? '#4F6660' : '#9ca3af'} />
              AI Relation Manager
              <span style={{ fontSize: 11, fontWeight: 600, color: demoAiEnabled ? '#4F6660' : '#9ca3af' }}>
                {demoAiEnabled ? 'Uitschakelen' : 'Inschakelen'}
              </span>
            </button>

            <p style={{ margin: 0, textAlign: 'center', fontSize: 10, color: '#B0ADA6' }}>
              Voorbeeldmodus · wordt niet opgeslagen
            </p>
          </div>
        </div>
      )}

      {/* Right Content Area - Conditioneel met/zonder regio panel */}
      <div className={`${selectedConnectionData && mobileFlowView === 'detail' && !selectedMobileFlowDemo ? 'flex' : 'hidden'} md:flex flex-1 min-h-0 overflow-hidden w-full overflow-x-hidden mobile-snap-section`}>
        {/* Main Chat Area */}
        <div className={`flex-1 min-h-0 flex flex-col w-full mobile-snap-container ${currentPhase === 'physical' ? 'overflow-y-auto sk-scroll' : 'overflow-hidden'}`}>
          {selectedConnectionData ? (
            <>
              {/* Flow fase tabs - Rechts in main content */}
              <PhaseIndicator
                phases={phases}
                phaseOrder={phaseOrder}
                currentPhase={phaseForIndicator}
              />
              {/* Header - Compact Apple-style - Altijd zichtbaar met alle fases */}
              <div className="flex-shrink-0 px-3 py-3 md:py-0.5 md:px-2 md:mt-2 flex items-center justify-between overflow-x-hidden bg-white border-b border-gray-100 md:border-b-0">
                  <div className="flex items-center min-w-0 flex-1 gap-1.5">
                    <button
                      type="button"
                      onClick={() => setMobileFlowView('list')}
                      className="mr-1 flex h-9 w-9 items-center justify-center rounded-full text-[#4F6660] active:bg-[#F0F7F4] md:hidden"
                      aria-label="Terug naar connecties"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <span className="text-lg leading-none">‹</span>
                    </button>
                    <div className="w-10 h-10 md:w-4 md:h-4 bg-gray-200 rounded-full flex items-center justify-center text-[9px] flex-shrink-0 overflow-hidden">
                      {selectedConnectionData?.avatar ? (
                        <img
                          src={selectedConnectionData.avatar as string}
                          alt={selectedConnectionData?.name || 'Avatar'}
                          className="w-10 h-10 md:w-4 md:h-4 rounded-full object-cover"
                          onError={(e) => {
                            const target = e.currentTarget as HTMLImageElement;
                            target.style.display = 'none';
                          }}
                        />
                      ) : (
                        <User size={18} strokeWidth={1.5} className="text-gray-500 md:w-3 md:h-3" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h2 className="font-semibold md:font-medium text-gray-900 text-sm leading-tight truncate">
                        {selectedConnectionData?.name}
                      </h2>
                      <div className="flex items-center text-xs text-gray-500 gap-1 mt-0.5">
                        <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                        <span className="truncate">
                          {selectedConnectionData?.online ? 'Online' : 'Offline'} • {currentPhaseInfo.name}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Notities per fase knop - In header */}
                  {selectedConnectionData?.status === 'accepted' && (
                    <button
                      onClick={() => setShowPhaseNotesCard(!showPhaseNotesCard)}
                      className="hidden md:flex px-2 py-0.5 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded text-xs font-medium transition-colors items-center gap-1 mr-1"
                    >
                      <span className="text-xs">📝</span>
                      <span className="hidden md:inline">Notities</span>
                      {notes.length > 0 && (
                        <span className="bg-[#7A9488] text-white text-[9px] px-1 py-0.5 rounded-full">
                          {notes.length}
                        </span>
                      )}
                    </button>
                  )}
                  
                    <div className="flex items-center gap-1 flex-shrink-0">
                    {/* Kleine Settings Knop - Alleen zichtbaar als isOpenForConnections */}
                    {isOpenForConnections && (
                      <button
                        onClick={() => setShowSettingsPopup(true)}
                        className="px-1 py-0.5 bg-[#2E5F56] hover:bg-[#2E5F56] text-white rounded text-[9px] font-medium transition-colors flex items-center gap-0.5"
                        title="Regio instellingen"
                      >
                        <span className="text-[10px]">⚙️</span>
                        <span className="hidden md:inline text-[9px]">Settings</span>
                      </button>
                    )}
                    <button 
                      onClick={() => {
                        handlePlanViaAgenda();
                        // ✅ Update presence: user is planning
                        if (connectionFlow?.id && user?.uid) {
                          updatePresence(
                            connectionFlow.id,
                            user.uid,
                            user.displayName || user.email || 'User',
                            'planning'
                          );
                        }
                      }}
                      className="flex h-9 w-9 md:h-auto md:w-auto items-center justify-center rounded-full md:rounded hover:bg-gray-100 active:bg-[#F0F7F4] md:p-0.5"
                      title="Afspraak of locatie plannen"
                      aria-label="Afspraak of locatie plannen"
                    >
                      <Icon icon={Calendar} size={18} strokeWidth={1.5} color="#6b7280" />
                    </button>
                  </div>
                </div>

            {/* Content - Split view: Notities/Timer boven, Chat onderaan met beperkte hoogte */}
            <div className={`flex-1 min-h-0 flex flex-col mobile-snap-section h-full ${currentPhase === 'physical' ? 'overflow-y-auto sk-scroll' : 'overflow-hidden'}`}>
              {/* Phase-specific content (call/video/physical) - Boven */}
              {currentPhase !== 'chat' && (
                <div
                  className={`flex-shrink-0 flex bg-white ${
                    currentPhase === 'video' ? 'h-1/2 p-0 items-stretch overflow-hidden' :
                    currentPhase === 'physical' ? 'h-auto items-start overflow-y-auto' :
                    'h-auto items-center justify-center py-8 overflow-hidden'
                  }`}
                >
                  {/* =============================== */}
                  {/* PHASE:UI:CALL:START */}
                  {/* =============================== */}
                  {currentPhase === 'call' && (
                    <ErrorBoundary fallback={<div className="p-6 text-center text-red-600">Er ging iets mis met de call interface. Probeer de pagina te verversen.</div>}>
                    <CallPhase
                      currentPhase={currentPhase}
                      showCallInterface={showCallInterface}
                      callStatus={callStatus}
                      currentCall={currentCall}
                      CallInterfaceComponent={CallInterface}
                      onEndCall={async () => {
                        // End WebRTC call (hook handles cleanup)
                        webrtcAudioCall.endCall();
                        // ✅ FIX: Mark callStep as completed + reset currentStep for BOTH users
                        await endCallStep();
                        // Laat user kiezen: nu door of plannen
                        await finishAndChooseNext('audio_hangup');
                        // Show summary popup na het gesprek
                        setShowSummaryPopup(true);
                        setConversationSummary(
                          "Dit is een AI gegenereerde samenvatting van het gesprek. De echte AI integratie komt later."
                        );
                      }}
                      onToggleMute={() => webrtcAudioCall.toggleMute()}
                    />
                    </ErrorBoundary>
                  )}
                  {/* =============================== */}
                  {/* PHASE:UI:CALL:END */}
                  {/* =============================== */}
                  {/* =============================== */}
                  {/* PHASE:UI:VIDEO:START */}
                  {/* =============================== */}
                  {currentPhase === 'video' && (
                    <ErrorBoundary fallback={<div className="p-6 text-center text-red-600">Er ging iets mis met de video interface. Probeer de pagina te verversen.</div>}>
                    <VideoPhase
                      currentPhase={currentPhase}
                      participants={connectionFlow?.participants || []}
                      currentUserId={user?.uid || ''}
                      currentUserName={user?.displayName || user?.email || 'Gebruiker'}
                      meetingUrl={connectionFlow?.videoStep?.meetUrl}
                      connectionId={connectionFlow?.id}
                      onEndVideo={async () => {
                        endVideoStep();
                        await finishAndChooseNext('video_end');
                      }}
                    />
                    </ErrorBoundary>
                  )}
                  {/* =============================== */}
                  {/* PHASE:UI:VIDEO:END */}
                  {/* =============================== */}
                  {/* =============================== */}
                  {/* PHASE:UI:PHYSICAL:START */}
                  {/* =============================== */}
                  {currentPhase === 'physical' && selectedConnectionData && (
                    activeCard === 'physical' ? (
                      /* ── EXPANDED: full planning UI ── */
                      <ErrorBoundary fallback={<div className="p-6 text-center text-red-600">Er ging iets mis met de fysieke meeting interface. Probeer de pagina te verversen.</div>}>
                        <PhysicalMeetingPhaseV2
                          connectionId={selectedConnectionData.id}
                          currentUserId={user?.uid || ''}
                          currentUserName={user?.displayName || user?.email || 'Jij'}
                          otherUserId={
                            selectedConnectionData.user1Id === user?.uid
                              ? selectedConnectionData.user2Id
                              : selectedConnectionData.user1Id
                          }
                          otherUserName={
                            selectedConnectionData.name || 'Andere gebruiker'
                          }
                          currentUserProfile={undefined}
                          otherUserProfile={undefined}
                          onOpenAgenda={handlePlanViaAgenda}
                        />
                      </ErrorBoundary>
                    ) : (
                      /* ── OVERVIEW: collapsed card, phase is active but not yet opened ── */
                      <div className="w-full px-4 py-6 flex flex-col items-center gap-3">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">🏢</span>
                          <div>
                            <p className="text-sm font-semibold text-gray-800">Fysieke Afspraak</p>
                            <p className="text-xs text-gray-500">Kies een locatie en plan jullie afspraak in.</p>
                          </div>
                        </div>
                        <button
                          onClick={() => setActiveCard('physical')}
                          className="px-5 py-2.5 bg-[#7A9488] hover:bg-[#4F6660] text-white text-sm font-semibold rounded-xl transition-colors shadow-sm"
                        >
                          Open fysieke planning →
                        </button>
                      </div>
                    )
                  )}
                  {/* =============================== */}
                  {/* PHASE:UI:PHYSICAL:END */}
                  {/* =============================== */}
                  {/* =============================== */}
                  {/* PHASE:UI:COMPLETED:START */}
                  {/* =============================== */}
                  {currentPhase === 'completed' && selectedConnectionData && (
                    <div className="w-full px-4 py-8 flex flex-col items-center gap-5 text-center">
                      {/* SK logo + titel */}
                      <img src="/SuperKonnected_Logo.svg" alt="SuperKonnected" style={{ width: 40, height: 40, objectFit: 'contain', filter: 'brightness(0) saturate(100%) invert(42%) sepia(26%) saturate(450%) hue-rotate(115deg) brightness(92%)' }} />
                      <div>
                        <h2 className="text-lg font-bold text-gray-900 mb-1">
                          Jullie zijn nu SuperKonnected
                        </h2>
                        <p className="text-sm text-gray-500">
                          De volledige ConnectieFlow met{' '}
                          <span className="font-semibold text-gray-700">{selectedConnectionData.name}</span>{' '}
                          is doorlopen.
                        </p>
                      </div>

                      {/* Expertstatus info — in app stijl */}
                      <div className="w-full rounded-xl border border-gray-100 bg-gray-50 p-4 text-left">
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: 'rgba(122,148,136,0.12)' }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7A9488" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/></svg>
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-gray-700 mb-1">Expertstatus update</p>
                            <p className="text-xs text-gray-500 leading-relaxed">
                              Jullie gedeelde ConnectieFlow is verwerkt in het expertprofiel van{' '}
                              <span className="font-medium text-gray-700">{selectedConnectionData.name}</span>.
                              Dit vergroot de zichtbaarheid en vindbaarheid in het netwerk.
                            </p>
                            <div className="flex items-center gap-1.5 mt-2">
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#7A9488" strokeWidth={2.5}><polyline points="20 6 9 17 4 12"/></svg>
                              <span className="text-xs text-[#7A9488] font-medium">Profiel bijgewerkt</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* CTA */}
                      <button
                        onClick={() => {
                          setSelectedConnection('');
                          setSelectedConnectionData(null);
                          setCurrentPhase('chat');
                        }}
                        style={{ background: 'linear-gradient(135deg, #6B8C82 0%, #7A9488 50%, #8DA8A0 100%)' }}
                        className="w-full py-3 text-white text-sm font-semibold rounded-2xl transition-all shadow-sm active:scale-[0.98]"
                      >
                        Terug naar overzicht
                      </button>
                    </div>
                  )}
                  {/* =============================== */}
                  {/* PHASE:UI:COMPLETED:END */}
                  {/* =============================== */}
                </div>
              )}

              {/* Chat Box - Direct onder header zonder lege ruimte */}
              {showChat && (
                <div className={currentPhase === 'chat' ? 'flex-1 min-h-0 flex flex-col overflow-hidden' : 'flex-shrink-0 max-h-[200px] flex flex-col border-t border-gray-200 overflow-hidden'}>
                  <ErrorBoundary fallback={<div className="p-6 text-center text-red-600">Er ging iets mis met de chat interface. Probeer de pagina te verversen.</div>}>
                  <ChatPhase
                    selectedConnectionData={selectedConnectionData}
                    currentPhase={currentPhase}
                    hasChatMessages={hasChatMessages}
                    connectionFlow={connectionFlow}
                    user={user}
                    onAcceptChatStep={acceptChatStep}
                    chatMessages={chatMessages}
                    formatChatTime={formatChatTime}
                    chatEndRef={chatEndRef}
                    newMessage={newMessage}
                    onNewMessageChange={setNewMessage}
                    onSendMessage={sendMessage}
                    onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                    className="flex-1 min-h-0"
                  />
                  </ErrorBoundary>
                </div>
              )}

              {/* Compact Tools Area - Altijd beschikbaar, ongeacht fase */}
              <div className="flex-shrink-0 border-t border-gray-200 bg-white px-2 py-1.5 space-y-1.5">
                {/* Row 1: Note input + save button */}
                <div className="flex gap-1.5">
                  <textarea
                    id="notes-textarea"
                    value={newNoteText}
                    onChange={(e) => setNewNoteText(e.target.value)}
                    onClick={() => {
                      const textarea = document.getElementById('notes-textarea') as HTMLTextAreaElement;
                      if (textarea) textarea.focus();
                    }}
                    placeholder="Notitie..."
                    className="flex-1 resize-none rounded-lg border border-gray-300 px-1.5 py-1 text-[10px] focus:border-[#7A9488] focus:outline-none focus:ring-1 focus:ring-[#7A9488] min-h-[28px] cursor-text"
                    rows={1}
                  />
                  <button
                    type="button"
                    onClick={handleAddNote}
                    disabled={!newNoteText.trim() || !connectionFlow}
                    className="rounded-lg bg-[#7A9488] px-2 py-1 text-[10px] font-medium text-white shadow-sm hover:bg-[#4F6660] disabled:cursor-not-allowed disabled:opacity-40 whitespace-nowrap min-h-[28px] flex items-center justify-center"
                  >
                    Opslaan
                  </button>
                </div>

                {/* Row 2: Action buttons grid (links) + Timer (rechts) + AI knop */}
                {selectedConnectionData?.status === 'accepted' && (
                  <TimerActionButtons
                    currentPhase={currentPhase}
                    onBreakConversation={() => {
                      stopAllAudioAndStreams();
                      handleAbortFlow();
                    }}
                    onNextPhase={async () => {
                      stopAllAudioAndStreams();
                      // ✅ FIX: Mark current step as completed before advancing
                      if (currentPhase === 'call') await endCallStep();
                      if (currentPhase === 'video') await endVideoStep();
                      await finishAndChooseNext('next_button');
                    }}
                    onQuickAction={() => {
                      setShowAgenda(true);
                    }}
                    onScheduleAppointment={() => {
                      handlePlanViaAgenda();
                    }}
                    onPlusOneMinute={handlePlusOneMinute}
                    timer={timer}
                    formatTime={formatTime}
                    isTimerActive={isTimerActive}
                    phaseName={currentPhaseInfo.name}
                    aiEnabled={isAiRelationManagerEnabled}
                    onToggleAi={toggleAiRelationManager}
                    onToggleAiCard={() => setShowAiRelationManagerCard(!showAiRelationManagerCard)}
                    onStartMeetup={handleStartMeetupSession}
                    onEndMeetup={handleEndMeetupSession}
                    meetupStarted={!!meetupStartedAt}
                    isTransitionPending={completingPhase}
                  />
                )}

                {/* Row 3: AI Relation Manager Card - Alleen tonen wanneer uitgeklapt */}
                {selectedConnectionData?.status === 'accepted' && showAiRelationManagerCard && (
                  <div>
                    <AIRelationManagerCard
                      selectedConnectionData={selectedConnectionData}
                      relationManagerText={relationManagerText}
                      isAiRelationManagerEnabled={isAiRelationManagerEnabled}
                      onToggle={toggleAiRelationManager}
                      isExpanded={true}
                    />
                  </div>
                )}
              </div>
            </div>

          {/* Verborgen audio-element voor remote WebRTC-audio */}
          <audio 
            ref={remoteAudioRef} 
            autoPlay 
            playsInline
            muted={false}
            className="hidden" 
          />
          </>
        ) : (
          // Empty State - Wanneer geen connectie geselecteerd
          <div className="flex-1 flex items-center justify-center p-8 bg-gradient-to-br from-gray-50 to-blue-50">
            <div className="text-center max-w-md">
              <div className="mb-6">
                <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-[#4F6660] rounded-full mx-auto flex items-center justify-center mb-4">
                  <span className="text-5xl">💬</span>
                              </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-3">
                  Welkom bij ConnectieFlow
                </h2>
                <p className="text-gray-600 mb-6">
                  {filteredConnections.length === 0 ? (
                    <>
                      Je hebt nog geen actieve connecties. Ga naar het{' '}
                      <strong className="text-[#2E5F56]">Netwerken</strong> tabblad om je open te stellen voor nieuwe connecties!
                    </>
                  ) : (
                    'Selecteer een connectie uit de lijst links om te beginnen met chatten.'
                  )}
                      </p>
                  </div>

              {filteredConnections.length === 0 && (
                <div className="space-y-3 text-left bg-white rounded-lg p-5 shadow-sm border border-blue-100">
                  <h3 className="font-semibold text-gray-800 mb-3 text-center">
                    🚀 Zo maak je connecties:
                  </h3>
                  <div className="space-y-2 text-sm text-gray-700">
                    <div className="flex items-start gap-2">
                      <span className="text-[#3ECBB5] font-bold">1.</span>
                      <span>Ga naar het <strong>Netwerken</strong> tabblad</span>
                      </div>
                    <div className="flex items-start gap-2">
                      <span className="text-[#3ECBB5] font-bold">2.</span>
                      <span>Selecteer je gewenste regio's</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-[#3ECBB5] font-bold">3.</span>
                      <span>Klik op <strong>"Stel me open voor netwerken"</strong></span>
                          </div>
                    <div className="flex items-start gap-2">
                      <span className="text-[#3ECBB5] font-bold">4.</span>
                      <span>Ontdek en connect met ondernemers in jouw regio!</span>
                            </div>
                          </div>
                      </div>
              )}
                            </div>
                          </div>
                        )}
                    </div>
      </div>
      </div>


      {/* Settings Popup - Voor regio instellingen wanneer open for connections actief is */}
      {showSettingsPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-blue-50 to-blue-100">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Regio Instellingen</h3>
                <p className="text-sm text-gray-600 mt-0.5">Pas je regio voorkeuren aan</p>
              </div>
              <button
                onClick={() => setShowSettingsPopup(false)}
                className="p-2 hover:bg-white/50 rounded-lg transition-colors"
              >
                <Icon icon={X} size={20} strokeWidth={1.5} color="#6b7280" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              {/* Regio Selectie - Zelfde als in main panel */}
              <div className="bg-white rounded-md border border-gray-200">
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium text-gray-800">
                      Selecteer je regio's
                    </h4>
                    <span className="text-xs text-gray-600 bg-gray-200 px-2.5 py-0.5 rounded-full">
                      {selectedRegions.length} geselecteerd
                    </span>
                  </div>
                </div>
                
                <div className="divide-y divide-gray-100">
                  {/* Nederland - Accordion */}
                  <div>
                    <button
                      onClick={() => setExpandedRegionSection(
                        expandedRegionSection === 'nederland' ? 'internationaal' : 'nederland'
                      )}
                      className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center space-x-2">
                        <span className="text-lg">🇳🇱</span>
                        <span className="text-sm font-medium text-gray-700">Nederland (Lokaal)</span>
                      </div>
                      <span className="text-gray-400 text-sm">
                        {expandedRegionSection === 'nederland' ? '▼' : '▶'}
                      </span>
                    </button>
                    
                    {expandedRegionSection === 'nederland' && (
                      <div className="px-4 py-3 bg-gray-50 max-h-80 overflow-y-auto">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          {[
                            'Noord-Holland', 'Zuid-Holland', 'Utrecht', 'Flevoland',
                            'Gelderland', 'Overijssel', 'Drenthe', 'Friesland',
                            'Groningen', 'Noord-Brabant', 'Limburg', 'Zeeland'
                          ].map((provincie) => (
                            <label 
                              key={provincie} 
                              className={`flex items-center space-x-2 cursor-pointer px-2.5 py-1.5 rounded-md transition-all ${
                                selectedRegions.includes(provincie)
                                  ? 'bg-white border-2 border-[#E0DDD8]'
                                  : 'bg-white border border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={selectedRegions.includes(provincie)}
                                onChange={() => handleRegionToggle(provincie)}
                                className="form-checkbox h-3.5 w-3.5 text-[#3ECBB5] rounded flex-shrink-0"
                              />
                              <span className="text-xs text-gray-700 leading-tight whitespace-nowrap overflow-hidden text-ellipsis flex-1">
                                {provincie}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Internationaal - Accordion */}
                  <div>
                    <button
                      onClick={() => setExpandedRegionSection(
                        expandedRegionSection === 'internationaal' ? 'nederland' : 'internationaal'
                      )}
                      className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center space-x-2">
                        <span className="text-lg">🌍</span>
                        <span className="text-sm font-medium text-gray-700">Internationale Regio's</span>
                      </div>
                      <span className="text-gray-400 text-sm">
                        {expandedRegionSection === 'internationaal' ? '▼' : '▶'}
                      </span>
                    </button>
                    
                    {expandedRegionSection === 'internationaal' && (
                      <div className="px-4 py-3 bg-gray-50 max-h-80 overflow-y-auto">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          {[
                            'België - Vlaanderen', 'België - Wallonië', 'Duitsland',
                            'Frankrijk', 'Verenigd Koninkrijk', 'Internationaal'
                          ].map((region) => (
                            <label 
                              key={region} 
                              className={`flex items-center space-x-2 cursor-pointer px-2.5 py-1.5 rounded-md transition-all ${
                                selectedRegions.includes(region)
                                  ? 'bg-white border-2 border-[#E0DDD8]'
                                  : 'bg-white border border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={selectedRegions.includes(region)}
                                onChange={() => handleRegionToggle(region)}
                                className="form-checkbox h-3.5 w-3.5 text-[#3ECBB5] rounded flex-shrink-0"
                              />
                              <span className="text-xs text-gray-700 leading-tight whitespace-nowrap overflow-hidden text-ellipsis flex-1">
                                {region}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {selectedRegions.length === 0 && (
                <div className="mt-4 p-3 bg-stone-50 border border-stone-200 rounded-md">
                  <p className="text-sm text-[#7A9488] flex items-center">
                    <span className="mr-2">⚠️</span>
                    <span>Selecteer minimaal één regio</span>
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
              <button
                onClick={() => setShowSettingsPopup(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Annuleren
              </button>
              <div className="flex items-center space-x-2">
                {settingsSaved && (
                  <span className="text-xs bg-green-50 text-green-700 px-3 py-1.5 rounded-md border border-green-200">
                    ✓ Opgeslagen
                  </span>
                )}
                <button
                  onClick={async () => {
                    await saveRegionSettings();
                    setTimeout(() => setShowSettingsPopup(false), 1500);
                  }}
                  disabled={savingSettings || selectedRegions.length === 0}
                  className="px-4 py-2 bg-[#2E5F56] hover:bg-[#2E5F56] text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {savingSettings ? 'Opslaan...' : 'Opslaan'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Meetup Reflection Popup */}
      {showMeetupReflection && connectionFlow && (
        <MeetupReflectionPopup
          phaseName={phases['physical']?.name || 'Fysieke afspraak'}
          onClose={() => setShowMeetupReflection(false)}
          onSave={async ({
            feelingPerson,
            feelingContent,
            talkingBalance,
            takeaways,
            summaryText,
          }) => {
            if (!user) {
              setShowMeetupReflection(false);
              return;
            }

            try {
              const insightsRef = collection(
                db,
                'connectionFlows',
                connectionFlow.id,
                'aiInsights'
              );
              await addDoc(insightsRef, {
                phase: 'physical',
                type: 'reflection',
                feelingPerson,
                feelingContent,
                talkingBalance,
                takeaways,
                summaryText,
                aiEnabled: isAiRelationManagerEnabled,
                startedAt: meetupStartedAt || null,
                createdAt: serverTimestamp(),
                createdBy: user.uid,
              });

              // Ook een korte tekst-notitie aanmaken onder fase Fysieke afspraak
              await addDoc(collection(db, 'connectionFlows', connectionFlow.id, 'notes'), {
                text:
                  'Reflectie fysieke afspraak toegevoegd: ' +
                  (summaryText || takeaways || '[zonder tekst]'),
                phase: 'physical',
                createdAt: serverTimestamp(),
                createdBy: user.uid,
              });
            } catch (error) {
              console.error('❌ Error saving physical reflection:', error);
            } finally {
              setShowMeetupReflection(false);
              setMeetupStartedAt(null);
              const pendingAction = pendingPostReflectionAction;
              setPendingPostReflectionAction(null);
              if (pendingAction) {
                await finishAndChooseNext(pendingAction, true);
              }
            }
          }}
        />
      )}

      {/* AI Relation Manager Intro Popup */}
      {showAiRelationInfo && (
        <AiRelationIntroPopup
          onClose={() => {
            setShowAiRelationInfo(false);
            try {
              if (typeof window !== 'undefined') {
                localStorage.setItem('aiRelationInfoShown', 'true');
              }
            } catch (e) {
              console.warn('Could not persist aiRelationInfoShown flag', e);
            }
          }}
          onEnable={() => {
            toggleAiRelationManager(true);
            setShowAiRelationInfo(false);
            try {
              if (typeof window !== 'undefined') {
                localStorage.setItem('aiRelationInfoShown', 'true');
              }
            } catch (e) {
              console.warn('Could not persist aiRelationInfoShown flag', e);
            }
          }}
        />
      )}

      {/* Google Account Required Popup */}
      {showGoogleAccountRequired && !hasGoogleProvider && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl border border-gray-100 w-full max-w-md mx-4">
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-stone-100 rounded-full flex items-center justify-center">
                    <Icon icon={AlertTriangle} size={20} strokeWidth={1.5} color="#ea580c" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">Google Account Vereist</h3>
                    <p className="text-sm text-gray-500">Voor ConnectieFlow functionaliteit</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowGoogleAccountRequired(false);
                    localStorage.setItem('googleAccountWarningShown', 'true');
                  }}
                  className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Icon icon={X} size={16} strokeWidth={1.5} color="#9ca3af" />
                </button>
              </div>
              
              <div className="mb-6">
                <p className="text-sm text-gray-700 mb-4">
                  Voor de volledige ConnectieFlow functionaliteit (zoals agenda synchronisatie en afspraken plannen) heb je een Google account nodig.
                </p>
                <div className="bg-[#F0F7F4] border border-[#E0DDD8] rounded-lg p-3 mb-4">
                  <p className="text-xs text-[#2E5F56]">
                    <strong>Optie 1:</strong> Koppel je Google account aan je huidige account (aanbevolen)<br/>
                    <strong>Optie 2:</strong> Koppel alleen Google Calendar voor agenda functionaliteit
                  </p>
                </div>
              </div>
              
              <div className="flex flex-col space-y-2">
                <button
                  onClick={handleLinkGoogleAccount}
                  disabled={isLinkingGoogle}
                  className="w-full px-4 py-3 bg-[#4F6660] text-white rounded-lg font-medium hover:bg-[#4F6660] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  {isLinkingGoogle ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Bezig...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                      </svg>
                      <span>Google Account Koppelen</span>
                    </>
                  )}
                </button>
                
                <button
                  onClick={handleConnectGoogleCalendar}
                  disabled={isLinkingGoogle}
                  className="w-full px-4 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  {isLinkingGoogle ? (
                    <>
                      <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                      <span>Bezig...</span>
                    </>
                  ) : (
                    <>
                      <Icon icon={Calendar} size={20} strokeWidth={1.5} />
                      <span>Alleen Google Calendar Koppelen</span>
                    </>
                  )}
                </button>
                
                <button
                  onClick={() => {
                    setShowGoogleAccountRequired(false);
                    localStorage.setItem('googleAccountWarningShown', 'true');
                  }}
                  className="w-full px-4 py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                >
                  Later doen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Extended Agenda Popup */}
      {/* agendaNavSuppressed blocks rendering during navigation so Firestore
          first-snapshots can never flash the agenda even for a single frame. */}
      {showAgenda && !agendaNavSuppressed && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[90] p-4" style={{ zIndex: 90 }}>
          <div className="w-full max-w-7xl max-h-[95vh] overflow-hidden">
            <div className="relative">
              {/* Close button — vraagt bevestiging als er een actieve planning is */}
              <button
                onClick={() => setShowAgendaCloseConfirm(true)}
                className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-lg hover:bg-gray-50 transition-colors"
              >
                <Icon icon={X} size={20} strokeWidth={1.5} color="#6b7280" />
              </button>

              {/* Sluit-bevestiging overlay */}
              {showAgendaCloseConfirm && (
                <div className="absolute inset-0 z-[100] flex items-center justify-center bg-black/40 rounded-2xl">
                  <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-xs w-full mx-4 text-center">
                    <div className="text-3xl mb-3">📅</div>
                    <h3 className="text-base font-semibold text-gray-900 mb-1">Planning verlaten?</h3>
                    <p className="text-sm text-gray-500 mb-4">
                      Jouw geselecteerde blokken worden bewaard. Je ontvangt een herinnering om verder te plannen.
                    </p>
                    <div className="flex flex-col gap-2">
                      <button
                        onClick={async () => {
                          // Maak planning_pending inbox item aan
                          if (selectedConnectionData?.id && user?.uid) {
                            try {
                              await addDoc(collection(db, 'inboxItems'), {
                                userId: user.uid,
                                type: 'planning_pending',
                                category: 'planning',
                                title: 'Rond je planning af',
                                message: `Je was bezig met plannen met ${selectedConnectionData.name || 'je connectie'}. Ga verder waar je gebleven was.`,
                                data: {
                                  connectionId: selectedConnectionData.id,
                                  otherUserName: selectedConnectionData.name || 'Connectie',
                                  planningOwnerId: user.uid,
                                },
                                isRead: false,
                                createdAt: serverTimestamp(),
                              });
                            } catch (e) {
                              console.warn('[ConnectieFlow] Could not save planning_pending item', e);
                            }
                          }
                          setShowAgendaCloseConfirm(false);
                          setShowAgenda(false);
                          setPlanningLockResult(null);
                          // Directe bevestiging tonen (3s, kort en bondig)
                          info(`📅 Planning bewaard — je vindt de herinnering in je inbox onder "Planning"`, 3000);
                        }}
                        className="w-full px-4 py-2.5 bg-[#2E5F56] text-white text-sm font-semibold rounded-xl hover:bg-[#265049] transition-colors"
                      >
                        Bewaar & sluit — ik plan later verder
                      </button>
                      <button
                        onClick={() => {
                          setShowAgendaCloseConfirm(false);
                          setShowAgenda(false);
                          setPlanningLockResult(null);
                        }}
                        className="w-full px-4 py-2.5 bg-gray-100 text-gray-600 text-sm font-medium rounded-xl hover:bg-gray-200 transition-colors"
                      >
                        Sluiten zonder te bewaren
                      </button>
                      <button
                        onClick={() => setShowAgendaCloseConfirm(false)}
                        className="text-xs text-gray-400 hover:text-gray-600 mt-1"
                      >
                        Terug naar planning
                      </button>
                    </div>
                  </div>
                </div>
              )}
              <div className="bg-white rounded-t-2xl px-4 sm:px-6 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 shadow-lg">
                <div>
                  <div className="text-xs text-gray-500">Plannen voor</div>
                  <div className="text-base font-semibold text-gray-900">
                    {selectedConnectionData?.name || 'Connectie'}
            </div>
                  {selectedConnectionData && (
                    <div className="text-xs text-gray-500">
                      Andere gebruiker: {selectedConnectionData?.name || 'Connectie'}
          </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => {
                      setAutoplanOpen(true);
                      setAutoplanError(null);
                      runAutoplan();
                    }}
                    disabled={autoplanLoading}
                    className={`px-3 py-2 text-xs font-semibold rounded-lg shadow transition-colors flex items-center gap-2 ${
                      autoplanLoading
                        ? 'bg-[#2E5F56] text-white opacity-80 cursor-not-allowed'
                        : 'bg-[#2E5F56] text-white hover:bg-[#265049]'
                    }`}
                  >
                    {autoplanLoading && (
                      <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    )}
                    Autoplan
                  </button>
                  <button
                    onClick={() => {
                      setAutoplanOpen(true);
                      if (hasActiveScheduled(selectedConnectionData)) {
                        setAutoplanError('Er staat al een afspraak gepland. Open planning om te verplaatsen.');
                        return;
                      }
                      openProposalComposer();
                    }}
                    className="px-3 py-2 bg-white border border-gray-300 text-xs font-semibold text-gray-700 rounded-lg shadow hover:bg-gray-50 transition-colors"
                  >
                    Voorstel doen
                  </button>
                </div>
              </div>
              <ExtendedAgenda
                selectedConnectionData={{
                  id: selectedConnectionData?.id,
                  name: selectedConnectionData?.name,
                  photoURL: selectedConnectionData?.photoURL,
                  user1Id: selectedConnectionData?.user1Id,
                  user2Id: selectedConnectionData?.user2Id,
                  availabilitySuggestions: selectedConnectionData?.availabilitySuggestions,
                }}
                connectionId={selectedConnectionData?.id}
                planningLockResult={planningLockResult}
                currentUserId={user?.uid}
                confirmedLocation={confirmedMeetingLocation}
                onClosePlanning={() => {
                  setShowAgenda(false);
                  setPlanningLockResult(null);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Oude Agenda Popup - Uitgeschakeld */}
      {false && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl border border-gray-100 w-full max-w-2xl max-h-[90vh] m-4 flex flex-col">
            
            {/* Header */}
            <div className="p-5 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-gray-800">Agenda</h3>
                  <p className="text-sm text-gray-500">Gesynchroniseerd met al je kalenders</p>
                </div>
                <button
                  onClick={() => setShowAgenda(false)}
                  className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Icon icon={X} size={16} strokeWidth={1.5} color="#9ca3af" />
                </button>
              </div>
            </div>

            {/* Calendar Navigation */}
            <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => {
                    const newDate = new Date(selectedCalendarDate);
                    newDate.setDate(newDate.getDate() - 7);
                    setSelectedCalendarDate(newDate);
                  }}
                  className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <span className="text-gray-600">←</span>
                </button>
                <h4 className="font-medium text-gray-800">
                  {selectedCalendarDate.toLocaleDateString('nl-NL', { 
                    month: 'long', 
                    year: 'numeric' 
                  })}
                </h4>
                <button
                  onClick={() => {
                    const newDate = new Date(selectedCalendarDate);
                    newDate.setDate(newDate.getDate() + 7);
                    setSelectedCalendarDate(newDate);
                  }}
                  className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <span className="text-gray-600">→</span>
                </button>
              </div>
              <button
                onClick={() => setSelectedCalendarDate(new Date())}
                className="text-xs bg-stone-100 text-[#4F6660] px-3 py-1.5 rounded-lg hover:bg-stone-200 transition-colors"
              >
                Vandaag
              </button>
            </div>

            {/* Calendar Content */}
            <div className="flex-1 overflow-hidden">
              
              {/* Week View */}
              {calendarView === 'week' && (
                <>
                  {/* Week Header */}
                  <div className="grid grid-cols-7 border-b border-gray-100">
                    {getWeekDays(selectedCalendarDate).map((day, index) => {
                      const isToday = day.toDateString() === new Date().toDateString();
                      const hasApps = hasAppointments(day);
                      
                      return (
                        <button
                          key={index}
                          onClick={() => handleDateClick(day)}
                          className={`p-3 text-center border-r border-gray-100 last:border-r-0 hover:bg-gray-50 transition-colors ${
                            hasApps ? 'bg-stone-50' : ''
                          }`}
                        >
                          <div className="text-xs text-gray-500 mb-1">
                            {day.toLocaleDateString('nl-NL', { weekday: 'short' })}
                          </div>
                          <div className={`text-sm font-medium ${
                            isToday ? 'text-[#4F6660]' : hasApps ? 'text-[#7A9488]' : 'text-gray-800'
                          }`}>
                            {day.getDate()}
                          </div>
                          {hasApps && (
                            <div className="w-2 h-2 bg-[#7A9488] rounded-full mx-auto mt-1"></div>
                          )}
                        </button>
                      );
                    })}
                  </div>

                  {/* Week Content */}
                  <div className="flex-1 overflow-y-auto">
                    <div className="grid grid-cols-7 min-h-96">
                      {getWeekDays(selectedCalendarDate).map((day, index) => {
                        const dayEvents = getEventsForDate(day);
                        return (
                          <div key={index} className="border-r border-gray-100 last:border-r-0 p-2">
                            <div className="space-y-1">
                              {dayEvents.map((event: any) => (
                                <div
                                  key={event.id}
                                  onClick={(e) => handleEventClick(event, e)}
                                  className={`p-2 rounded-lg text-xs cursor-pointer hover:opacity-80 transition-opacity ${
                                    event.type === 'connectflow' ? 'bg-stone-100 text-[#4F6660] border border-stone-200' :
                                    event.type === 'work' ? 'bg-[rgba(46,95,86,0.08)] text-[#2E5F56] border border-[#E0DDD8]' :
                                    event.type === 'custom' ? 'bg-stone-200 text-[#4A5A3D] border border-stone-300' :
                                    'bg-green-100 text-green-700 border border-green-200'
                                  }`}
                                >
                                  <div className="font-medium truncate">{event.title}</div>
                                  <div className="text-xs opacity-75">
                                    {event.start || event.time} {event.end ? `- ${event.end}` : ''}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}

              {/* Month View */}
              {calendarView === 'month' && (
                <>
                  {/* Month Header - Days of Week */}
                  <div className="grid grid-cols-7 border-b border-gray-100 bg-gray-50">
                    {['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'].map((day, index) => (
                      <div key={index} className="p-2 text-center border-r border-gray-100 last:border-r-0">
                        <div className="text-xs font-medium text-gray-600">{day}</div>
                      </div>
                    ))}
                  </div>

                  {/* Month Content */}
                  <div className="flex-1 overflow-y-auto">
                    <div className="grid grid-cols-7 auto-rows-fr min-h-96">
                      {getMonthDays(selectedCalendarDate).map((day, index) => {
                        const isToday = day.toDateString() === new Date().toDateString();
                        const hasApps = hasAppointments(day);
                        const isCurrentMonth = day.getMonth() === selectedCalendarDate.getMonth();
                        const dayEvents = getEventsForDate(day);
                        
                        return (
                          <button
                            key={index}
                            onClick={() => handleDateClick(day)}
                            className={`p-2 border-r border-b border-gray-100 hover:bg-gray-50 transition-colors text-left min-h-24 ${
                              !isCurrentMonth ? 'bg-gray-50 text-gray-400' : 
                              hasApps ? 'bg-stone-50' : 'bg-white'
                            }`}
                          >
                            <div className={`text-sm font-medium mb-1 ${
                              isToday ? 'text-[#4F6660] bg-stone-100 rounded-full w-6 h-6 flex items-center justify-center' : 
                              hasApps && isCurrentMonth ? 'text-[#7A9488]' : 
                              isCurrentMonth ? 'text-gray-800' : 'text-gray-400'
                            }`}>
                              {day.getDate()}
                            </div>
                            
                            {/* Events in month view */}
                            <div className="space-y-1">
                              {dayEvents.slice(0, 2).map((event: any) => (
                                <div
                                  key={event.id}
                                  onClick={(e) => handleEventClick(event, e)}
                                  className={`text-xs px-1 py-0.5 rounded truncate cursor-pointer hover:opacity-80 ${
                                    event.type === 'connectflow' ? 'bg-stone-200 text-[#4A5A3D]' :
                                    event.type === 'work' ? 'bg-blue-200 text-[#2E5F56]' :
                                    event.type === 'custom' ? 'bg-stone-300 text-[#4A5A3D]' :
                                    'bg-green-200 text-green-800'
                                  }`}
                                >
                                  {event.title}
                                </div>
                              ))}
                              {dayEvents.length > 2 && (
                                <div className="text-xs text-gray-500">
                                  +{dayEvents.length - 2} meer
                                </div>
                              )}
                            </div>
                            
                            {hasApps && dayEvents.length === 0 && (
                              <div className="w-2 h-2 bg-[#7A9488] rounded-full mt-1"></div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Calendar Sources */}
            <div className="p-4 border-t border-gray-100 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-[#7A9488] rounded"></div>
                    <span className="text-xs text-gray-600">ConnectFlow</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-stone-200 rounded"></div>
                    <span className="text-xs text-gray-600">Mijn Afspraken</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-[#2E5F56] rounded"></div>
                    <span className="text-xs text-gray-600">Google Calendar</span>
                  </div>
                </div>
                <div className="text-xs text-gray-500">
                  Laatste sync: {new Date().toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Appointment Form Popup */}
      {showAppointmentForm && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl border border-gray-100 w-full max-w-md mx-4">
            <div className="p-5 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-gray-800">Nieuw agendapunt</h3>
                  <p className="text-sm text-gray-500">
                    {selectedAppointmentDate && new Date(selectedAppointmentDate + 'T00:00:00').toLocaleDateString('nl-NL', { 
                      weekday: 'long', 
                      day: 'numeric', 
                      month: 'long' 
                    })}
                  </p>
                </div>
                <button
                  onClick={() => setShowAppointmentForm(false)}
                  className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Icon icon={X} size={16} strokeWidth={1.5} color="#9ca3af" />
                </button>
              </div>
            </div>

            <div className="p-5 space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Onderwerp</label>
                <input
                  type="text"
                  value={appointmentForm.title}
                  onChange={(e) => setAppointmentForm({...appointmentForm, title: e.target.value})}
                  className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A9488] focus:border-[#7A9488] text-sm"
                  placeholder="Typ je afspraak..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Time */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tijd</label>
                  <input
                    type="time"
                    value={appointmentForm.time}
                    onChange={(e) => setAppointmentForm({...appointmentForm, time: e.target.value})}
                    className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A9488] focus:border-[#7A9488] text-sm"
                  />
                </div>

                {/* Alarm */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Herinnering</label>
                  <select
                    value={appointmentForm.alarm}
                    onChange={(e) => setAppointmentForm({...appointmentForm, alarm: e.target.value})}
                    className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A9488] focus:border-[#7A9488] text-sm"
                  >
                    <option value="0">Geen</option>
                    <option value="5">5 min</option>
                    <option value="15">15 min</option>
                    <option value="30">30 min</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="p-5 border-t border-gray-100 flex space-x-3">
              <button
                onClick={saveAppointment}
                disabled={!appointmentForm.title.trim()}
                className="flex-1 bg-[#7A9488] text-white py-2.5 rounded-lg hover:bg-[#4F6660] disabled:opacity-40 transition-colors text-sm font-medium"
              >
                Opslaan
              </button>
              <button
                onClick={() => setShowAppointmentForm(false)}
                className="flex-1 bg-gray-100 text-gray-600 py-2.5 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
              >
                Afbreken
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Appointment List Popup */}
      {showAppointmentList && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl border border-gray-100 w-full max-w-md mx-4">
            <div className="p-5 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-gray-800">Afspraken</h3>
                  <p className="text-sm text-gray-500">
                    {selectedAppointmentDate && new Date(selectedAppointmentDate + 'T00:00:00').toLocaleDateString('nl-NL', { 
                      weekday: 'long', 
                      day: 'numeric', 
                      month: 'long' 
                    })}
                  </p>
                </div>
                <button
                  onClick={() => setShowAppointmentList(false)}
                  className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Icon icon={X} size={16} strokeWidth={1.5} color="#9ca3af" />
                </button>
              </div>
            </div>

            <div className="p-5 space-y-3 max-h-96 overflow-y-auto">
              {appointments.filter((app: any) => app.date === selectedAppointmentDate).map((appointment: any) => (
                <div key={appointment.id} className="bg-stone-50 border border-stone-200 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-800 mb-1">{appointment.title}</h4>
                      <div className="text-sm text-gray-600">
                        🕒 {appointment.time}
                        {appointment.alarm !== '0' && ` • 🔔 ${appointment.alarm} min`}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex space-x-2 mt-3">
                    <button
                      onClick={() => editAppointment(appointment)}
                      className="flex-1 bg-[#7A9488] text-white py-1.5 rounded text-xs hover:bg-[#4F6660] transition-colors"
                    >
                      Wijzigen
                    </button>
                    <button
                      onClick={() => deleteAppointment(appointment.id)}
                      className="flex-1 bg-stone-300 text-white py-1.5 rounded text-xs hover:bg-[#7A9488] transition-colors"
                    >
                      Verwijderen
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Custom Time Proposal Popup */}
      {showCustomTimePopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[90]" style={{ zIndex: 90 }}>
          <div className="bg-white rounded-xl shadow-2xl border border-gray-100 w-full max-w-md mx-4">
            <div className="p-5 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-gray-800">Eigen Tijd Voorstellen</h3>
                  <p className="text-sm text-gray-500">Stel jouw voorkeurstijd voor</p>
                </div>
                <button
                  onClick={() => setShowCustomTimePopup(false)}
                  className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Icon icon={X} size={16} strokeWidth={1.5} color="#9ca3af" />
                </button>
              </div>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Datum</label>
                <input
                  type="date"
                  value={customTimeForm.date}
                  onChange={(e) => setCustomTimeForm({...customTimeForm, date: e.target.value})}
                  className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A9488] focus:border-[#7A9488] text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tijd</label>
                <input
                  type="time"
                  value={customTimeForm.time}
                  onChange={(e) => setCustomTimeForm({...customTimeForm, time: e.target.value})}
                  className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A9488] focus:border-[#7A9488] text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Bericht (optioneel)</label>
                <textarea
                  value={customTimeForm.message}
                  onChange={(e) => setCustomTimeForm({...customTimeForm, message: e.target.value})}
                  className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A9488] focus:border-[#7A9488] text-sm resize-none"
                  rows={3}
                  placeholder="Laat een bericht achter..."
                />
              </div>
            </div>

            <div className="p-5 border-t border-gray-100 flex space-x-3">
              <button
                onClick={() => {
                  console.log('Eigen tijd voorgesteld:', customTimeForm);
                  setShowCustomTimePopup(false);
                  setCustomTimeForm({ date: '', time: '', message: '' });
                  setResyncAttempts(0);
                }}
                disabled={!customTimeForm.date || !customTimeForm.time}
                className="flex-1 bg-[#7A9488] text-white py-2.5 rounded-lg hover:bg-[#4F6660] disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-sm font-medium"
              >
                Voorstel Versturen
              </button>
              <button
                onClick={() => setShowCustomTimePopup(false)}
                className="flex-1 bg-gray-100 text-gray-600 py-2.5 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
              >
                Annuleren
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ConnectFlow Phase Selector */}
      {showPhaseSelector && selectedEvent && (
        <PhaseProgressPopup
          selectedEvent={selectedEvent}
          phases={phases}
          onClose={() => setShowPhaseSelector(false)}
          onSchedulePhase={(phaseKey) => {
            console.log('Plannen fase:', phaseKey);
          }}
          onSkipPhase={(phaseKey) => {
            console.log('Overslaan fase:', phaseKey);
          }}
          onSyncReschedule={() => {
            console.log('Nieuwe synchronisatie tijd zoeken voor beide personen');
          }}
          onManualReschedule={() => {
            console.log('Eigen tijd voorstellen');
          }}
          onModify={() => {
            console.log('Afspraak wijzigen');
          }}
        />
      )}

      {/* Timer Expired Popup - Only show if no other phase popups are active and NOT in physical phase */}
      {showTimerExpiredPopup && !showIncomingCall && !showIncomingVideo && !showIncomingMeeting && currentPhase !== 'physical' && (
        <TimerExpiredPopup
          currentPhase={timerExpiredPhase}
          phases={phases}
          onClose={() => {
            // ✅ FIX: Markeer deze stap als dismissed zodat de popup NIET herhaald opent
            const activeStartMs = timerExpiredPhase === 'chat' ? chatStartTimeMs
              : timerExpiredPhase === 'call' ? callStartTimeMs
              : timerExpiredPhase === 'video' ? videoStartTimeMs : 0;
            timerExpiredDismissedKeyRef.current = `${timerExpiredPhase}_${activeStartMs}`;
            setShowTimerExpiredPopup(false);
          }}
          onNextPhase={async (nextPhase?: string) => {
            stopAllAudioAndStreams();
            // ✅ FIX: Gebruik timerExpiredPhase (opgeslagen bij expiry) i.p.v. currentPhase
            const expiredPhase = timerExpiredPhase;
            const activeStartMs = expiredPhase === 'chat' ? chatStartTimeMs
              : expiredPhase === 'call' ? callStartTimeMs
              : expiredPhase === 'video' ? videoStartTimeMs : 0;
            timerExpiredDismissedKeyRef.current = `${expiredPhase}_${activeStartMs}`;
            setShowTimerExpiredPopup(false);

            // ─── PHYSICAL FAST PATH ───────────────────────────────────────────
            // handleStartNow in TimerExpiredPopup already wrote physicalStep=pending
            // to connectionFlows. We must NOT call finishAndChooseNext here because
            // that opens the NextStepModal → user clicks again → second Firestore
            // write → duplicate event → knipperloop.
            //
            // Instead: mark the current video/call step as completed directly,
            // then call handleDirectCall('physical') which:
            //   1. writes physicalStep (idempotent)
            //   2. sends physical_phase_started notification
            //   3. sets currentPhase('physical') + activeCard('physical') + closes video UI
            if (nextPhase === 'physical') {
              // ── CRITICAL: switch phase IMMEDIATELY, before any await ──────────
              // Firestore snapshots from the writes below arrive asynchronously.
              // If currentPhase is still 'video' when they arrive, the phase-sync
              // guard (isInitiatorWithPendingPhysical && currentPhase==='physical')
              // won't fire and the sync resets to 'chat' → flicker.
              // Setting currentPhase here first ensures the guard is already active.
              setShowVideoCallInterface(false);
              setShowCallInterface(false);
              setShowJitsiCall(false);
              setCurrentPhase('physical');
              setActiveCard('physical');
              // ─────────────────────────────────────────────────────────────────

              // Complete current step in Firestore WITHOUT touching currentStep.
              // endVideoStep() writes currentStep='chat' which conflicts — so we
              // do a targeted update instead.
              if (connectionFlow?.id && user?.uid) {
                try {
                  if (expiredPhase === 'video') {
                    await updateDoc(doc(db, 'connectionFlows', connectionFlow.id), {
                      'videoStep.status': 'completed',
                      'videoStep.endedBy': user.uid,
                      'videoStep.endTime': serverTimestamp(),
                      updatedAt: serverTimestamp(),
                      // NOTE: currentStep intentionally NOT set to 'chat' here —
                      // handleDirectCall('physical') will set it to 'physical'.
                    });
                  } else if (expiredPhase === 'call') {
                    await endCallStep();
                  }
                } catch (e) {
                  console.warn('[onNextPhase→physical] Could not complete current step:', e);
                }
              }
              await handleDirectCall('physical');
              return; // ← skip finishAndChooseNext entirely
            }
            // ─────────────────────────────────────────────────────────────────

            // Mark current step as completed before advancing (non-physical path)
            if (expiredPhase === 'call') await endCallStep();
            if (expiredPhase === 'video') await endVideoStep();
            await finishAndChooseNext('timer_expired');
          }}
          onOpenAgenda={async () => {
            const activeStartMs = timerExpiredPhase === 'chat' ? chatStartTimeMs
              : timerExpiredPhase === 'call' ? callStartTimeMs
              : timerExpiredPhase === 'video' ? videoStartTimeMs : 0;
            timerExpiredDismissedKeyRef.current = `${timerExpiredPhase}_${activeStartMs}`;
            setShowTimerExpiredPopup(false);
            await handlePlanViaAgenda();
          }}
          onCompleteFlow={() => {
            const activeStartMs = timerExpiredPhase === 'chat' ? chatStartTimeMs
              : timerExpiredPhase === 'call' ? callStartTimeMs
              : timerExpiredPhase === 'video' ? videoStartTimeMs : 0;
            timerExpiredDismissedKeyRef.current = `${timerExpiredPhase}_${activeStartMs}`;
            setShowTimerExpiredPopup(false);
            if (selectedConnectionData && user) {
              updateConnectionStatus(selectedConnectionData.id, 'completed');
            }
          }}
          showIncomingCall={setShowIncomingCall}
          showIncomingVideo={setShowIncomingVideo}
          showIncomingMeeting={setShowIncomingMeeting}
          onOpenCustomTime={() => {
            const activeStartMs = timerExpiredPhase === 'chat' ? chatStartTimeMs
              : timerExpiredPhase === 'call' ? callStartTimeMs
              : timerExpiredPhase === 'video' ? videoStartTimeMs : 0;
            timerExpiredDismissedKeyRef.current = `${timerExpiredPhase}_${activeStartMs}`;
            setShowTimerExpiredPopup(false);
            setShowCustomTimePopup(true);
          }}
          connectionId={selectedConnectionData?.id}
          currentUserId={user?.uid}
          otherUserId={
            selectedConnectionData?.user1Id === user?.uid
              ? selectedConnectionData?.user2Id
              : selectedConnectionData?.user1Id
          }
          otherUserName={selectedConnectionData?.name}
          nextPhaseCandidate={nextPhaseCandidate}
          otherUserEnded={otherUserEnded}
          onEndConnection={handleEndConnection}
        />
      )}

      {/* Incoming Call Popup */}
      {showIncomingCall && (
        <IncomingCallPopup
          selectedConnection={selectedConnectionData}
          onAccept={() => {
            // acceptStep handles everything: Firestore update + Meet window opening
            suppressIncomingPopupUntilRef.current.call = Date.now() + 6000;
            setShowIncomingCall(false);
            acceptStep('call');
          }}
          onSchedule={() => {
            suppressIncomingPopupUntilRef.current.call = Date.now() + 10000;
            setShowIncomingCall(false);
            stopAllAudioAndStreams();
            // Open volledige agenda met context + Autoplan starten
            openFullAgendaPage();
            setShowAgenda(true);
            setAutoplanOpen(true);
            setAutoplanError(null);
            runAutoplan(true);
            setToastMessage('Planning geopend voor deze connectie');
          }}
          onDecline={async () => {
            suppressIncomingPopupUntilRef.current.call = Date.now() + 10000;
            setShowIncomingCall(false);
            stopAllAudioAndStreams();
            setShowCallInterface(false);
            setCurrentPhase('chat');

            if (!connectionFlow?.id || !user?.uid) return;

            try {
              const flowRef = doc(db, 'connectionFlows', connectionFlow.id);
              await updateDoc(flowRef, {
                'callStep.status': 'completed',
                'callStep.declinedBy': user.uid,
                'callStep.declinedAt': serverTimestamp(),
                currentStep: 'chat',
                status: 'active',
                updatedAt: serverTimestamp()
              });
            } catch (err) {
              console.error('❌ Error declining incoming call:', err);
            }
          }}
        />
      )}

      {/* Incoming Video Call Popup */}
      {showIncomingVideo && (
        <IncomingVideoCallPopup
          selectedConnection={selectedConnectionData}
          onAccept={() => {
            // acceptStep handles everything: Firestore update + Meet window opening
            suppressIncomingPopupUntilRef.current.video = Date.now() + 6000;
            setShowIncomingVideo(false);
            acceptStep('video');
          }}
          onSchedule={() => {
            suppressIncomingPopupUntilRef.current.video = Date.now() + 10000;
            setShowIncomingVideo(false);
            setShowAgenda(true);
          }}
          onDecline={async () => {
            suppressIncomingPopupUntilRef.current.video = Date.now() + 10000;
            setShowIncomingVideo(false);
            setShowVideoCallInterface(false);

            if (!connectionFlow?.id || !user?.uid) return;

            try {
              const flowRef = doc(db, 'connectionFlows', connectionFlow.id);
              await updateDoc(flowRef, {
                'videoStep.status': 'completed',
                'videoStep.declinedBy': user.uid,
                'videoStep.declinedAt': serverTimestamp(),
                currentStep: 'chat',
                status: 'active',
                updatedAt: serverTimestamp()
              });
            } catch (err) {
              console.error('❌ Error declining incoming video call:', err);
            }
          }}
        />
      )}

      {/* Incoming Meeting Popup */}
      {showIncomingMeeting && (
        <IncomingMeetingPopup
          selectedConnection={selectedConnectionData}
          onAccept={() => {
            acceptStep('physical');
          }}
          onSchedule={() => {
            suppressIncomingPopupUntilRef.current.physical = Date.now() + 10000;
            setShowIncomingMeeting(false);
            setShowAgenda(true);
          }}
          onDecline={async () => {
            suppressIncomingPopupUntilRef.current.physical = Date.now() + 10000;
            setShowIncomingMeeting(false);
            setCurrentPhase('chat');

            if (!connectionFlow?.id || !user?.uid) return;

            try {
              const flowRef = doc(db, 'connectionFlows', connectionFlow.id);
              await updateDoc(flowRef, {
                'physicalStep.status': 'completed',
                'physicalStep.declinedBy': user.uid,
                'physicalStep.declinedAt': serverTimestamp(),
                currentStep: 'chat',
                status: 'active',
                updatedAt: serverTimestamp()
              });
            } catch (err) {
              console.error('❌ Error declining incoming meeting:', err);
            }
          }}
        />
      )}

      {/* Break Conversation Popup */}
      {showBreakPopup && (
        <BreakConversationPopup
          onClose={() => setShowBreakPopup(false)}
          onSubmit={async (data) => {
            console.log('Gesprek afgebroken:', data);
            stopAllAudioAndStreams();
            setShowBreakPopup(false);
            
            // ✅ FIX: Call handleAbortFlow to properly terminate the flow for both users
            await handleAbortFlow();
            
            // Handle the break conversation data
            if (data.reportToAdmin || data.sendChatLog) {
              console.log('Admin melding:', {
                reason: data.reason,
                reportToAdmin: data.reportToAdmin,
                sendChatLog: data.sendChatLog,
                chatMessages: data.sendChatLog ? chatMessages : null,
                connection: selectedConnectionData
              });
              // TODO: Send to backend/admin system
            }
          }}
        />
      )}

      {/* Schedule Appointment Popup */}
      {showSchedulePopup && (
        <ScheduleAppointmentPopup
          onClose={() => setShowSchedulePopup(false)}
          onSyncAgenda={() => {
            console.log('Synchronisatie geaccepteerd');
            setShowSchedulePopup(false);
          }}
          onCustomTime={() => {
            console.log('Eigen tijd voorstellen');
            setShowSchedulePopup(false);
          }}
          currentPhase={currentPhase}
          phases={phases}
        />
      )}

      {/* Start Chat Popup */}
      {showStartChatPopup && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[100]"
          onClick={(e) => {
            // Close popup when clicking outside
            if (e.target === e.currentTarget) {
              setShowStartChatPopup(false);
              setOnlineConnectionsForPopup([]); // Clear list when closing
            }
          }}
          style={{ zIndex: 100 }}
        >
          <div 
            className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 relative z-[101]"
            onClick={(e) => {
              e.stopPropagation();
            }}
            style={{ zIndex: 101 }}
          >
            <div className="p-6">
              <div className="text-center mb-4">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 text-3xl animate-pulse">
                  💬
                </div>
                
                <h3 className="text-lg font-medium text-gray-800 mb-2">
                  {onlineConnectionsForPopup.length > 1 
                    ? `${onlineConnectionsForPopup.length} personen zijn nu online!`
                    : 'Jullie zijn nu beide online!'}
                </h3>
                {onlineConnectionsForPopup.length === 1 && (
                  <p className="text-gray-600 mb-1">
                    {(() => {
                      const name = selectedConnectionData?.name;
                      // Block "Onbekend" - if it's still "Onbekend", don't show the popup
                      if (!name || name === 'Onbekend' || name.trim() === '') {
                        console.warn('⚠️ Blocking popup - name is still "Onbekend"');
                        return 'Connectie';
                      }
                      return name;
                    })()}
                  </p>
                )}
                <p className="text-sm text-gray-500 mb-6">
                  {onlineConnectionsForPopup.length > 1 
                    ? 'Kies met wie je wilt chatten:'
                    : 'Wat wil je doen?'}
                </p>
              </div>
              
              {/* Show list of online connections if multiple */}
              {onlineConnectionsForPopup.length > 1 && (
                <div className="mb-6 max-h-60 overflow-y-auto space-y-2">
                  {onlineConnectionsForPopup.map((conn, index) => {
                    const isSelected = selectedConnectionData && 
                      ((selectedConnectionData.user1Id === conn.user1Id && selectedConnectionData.user2Id === conn.user2Id) ||
                       (selectedConnectionData.user1Id === conn.user2Id && selectedConnectionData.user2Id === conn.user1Id));
                    
                    return (
                      <div
                        key={`${conn.user1Id}-${conn.user2Id}-${index}`}
                        onClick={() => {
                          setSelectedConnectionData(conn);
                          setSelectedConnection(conn.name || 'Connectie');
                        }}
                        className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          isSelected
                            ? 'border-[#7A9488] bg-stone-50'
                            : 'border-gray-200 hover:border-stone-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center text-sm">
                              {conn.avatar || '👤'}
                            </div>
                            <div>
                              <p className="font-medium text-gray-800 text-sm">
                                {conn.name && conn.name !== 'Onbekend' ? conn.name : 'Connectie'}
                              </p>
                              <p className="text-xs text-gray-500">Online</p>
                            </div>
                          </div>
                          {isSelected && (
                            <span className="text-[#7A9488] text-lg">✓</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              
              <div className="space-y-3">
                <button
                  type="button"
                  onClick={async (e) => {
                    console.log('🔘 Accepteren button clicked');
                    e.preventDefault();
                    e.stopPropagation();
                    const desiredAction = postAcceptAction === 'none' ? 'chat' : postAcceptAction;
                    // Default intent: chat, unless previously set to agenda
                    if (postAcceptAction === 'none') {
                      setPostAcceptAction('chat');
                    }
                    
                    if (!user) {
                      showError('Je bent niet ingelogd.');
                      return;
                    }
                    
                    if (!selectedConnectionData) {
                      showError('Geen connectie geselecteerd.');
                      return;
                    }
                    
                    try {
                      console.log('🔄 Closing popup and processing accept...');
                      
                      // Get the other user ID
                      const otherUserId = selectedConnectionData.user1Id === user.uid 
                        ? selectedConnectionData.user2Id 
                        : selectedConnectionData.user1Id;
                      
                      if (!otherUserId) {
                        showError('Gebruiker ID niet gevonden.');
                        setShowStartChatPopup(false);
                        return;
                      }
                      
                      // Close popup first
                      setShowStartChatPopup(false);
                      setOnlineConnectionsForPopup([]); // Clear list
                      // Suppress reopening while we transition into chat
                      suppressStartChatPopupUntilRef.current = Date.now() + 30 * 60 * 1000;
                      
                      // Ensure we have a chat room first
                      let chatRoom = currentChatRoom;
                      if (!chatRoom) {
                        console.log('🔄 Creating chat room first...');
                        await findOrCreateChatRoom(otherUserId, selectedConnectionData.name || 'Connectie', selectedConnectionData.avatar);
                        // Wait a moment for state to update
                        await new Promise(resolve => setTimeout(resolve, 1000));
                        // Re-check currentChatRoom after state update
                        chatRoom = currentChatRoom;
                      }
                      
                      if (!chatRoom) {
                        console.error('❌ Chat room still not available after creation attempt');
                        showError('Kon chat room niet aanmaken. Probeer het opnieuw.');
                        return;
                      }
                      
                      // Ensure connection flow exists
                      let flow = connectionFlow;
                      if (!flow) {
                        console.log('🔄 Creating connection flow...');
                        await findOrCreateConnectionFlow(chatRoom.id, chatRoom.participants);
                        // Wait a bit for state to update
                        await new Promise(resolve => setTimeout(resolve, 1000));
                        // Re-check connectionFlow after state update
                        flow = connectionFlow;
                      }
                      
                      // Accept chat step
                      if (flow) {
                        console.log('✅ ConnectionFlow exists, accepting chat step...');
                        await acceptChatStep();
                        // Reload connectionFlow after accepting to update UI
                        const flowRef = doc(db, 'connectionFlows', flow.id);
                        const flowSnap = await getDoc(flowRef);
                        if (flowSnap.exists()) {
                          const flowData = { id: flowSnap.id, ...flowSnap.data() } as ConnectionFlow;
                          setConnectionFlow(flowData);
                        }
                      } else {
                        console.warn('⚠️ ConnectionFlow not available, retrying...');
                        await new Promise(resolve => setTimeout(resolve, 1000));
                        await acceptChatStep();
                        // Reload connectionFlow after accepting
                        if (chatRoom) {
                          const flowRef = doc(db, 'connectionFlows', chatRoom.id);
                          const flowSnap = await getDoc(flowRef);
                          if (flowSnap.exists()) {
                            const flowData = { id: flowSnap.id, ...flowSnap.data() } as ConnectionFlow;
                            setConnectionFlow(flowData);
                          }
                        }
                      }
                      
                      if (desiredAction === 'agenda') {
                        setShowAgenda(true);
                      } else if (desiredAction === 'chat') {
                        setShowAgenda(false);
                      }
                      setPostAcceptAction('none');
                      // Directly select the connection and show chat
                      console.log('🔄 Selecting connection and opening chat...');
                      
                      // Ensure we have the latest connection data with correct name
                      const finalConnectionName = selectedConnectionData.name || 'Connectie';
                      
                      // Update all states to show chat
                      setSelectedConnection(finalConnectionName);
                      setSelectedConnectionData({
                        ...selectedConnectionData,
                        name: finalConnectionName
                      });
                      setActiveTab('active'); // Switch to active tab
                      setCurrentPhase('chat'); // Start in chat phase
                      
                      // Force a small delay to ensure state updates are processed
                      await new Promise(resolve => setTimeout(resolve, 300));
                      
                      // Scroll to chat if needed (the chat interface should now be visible)
                      if (chatEndRef.current) {
                        chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
                      }
                      
                      console.log('✅ Chat sessie geaccepteerd en geopend!');
                      console.log('📋 Current state:', {
                        selectedConnection: finalConnectionName,
                        currentPhase: 'chat',
                        activeTab: 'active',
                        hasChatRoom: !!chatRoom,
                        hasConnectionFlow: !!flow
                      });
                    } catch (error: any) {
                      console.error('❌ Error accepting chat:', error);
                      showError(`Er is een fout opgetreden: ${error.message || 'Onbekende fout'}`);
                      setShowStartChatPopup(false);
                    }
                  }}
                  data-testid="accept-connection"
                  className="w-full bg-[#7A9488] text-white py-3 rounded-lg hover:bg-[#4F6660] transition-colors text-sm font-semibold flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed relative z-[102]"
                  style={{ zIndex: 102, pointerEvents: 'auto' }}
                  disabled={!user}
                >
                  <span>✅</span>
                  <span className="text-white">Start chat</span>
                </button>
                
                <button
                  type="button"
                  onClick={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    // Close popup immediately (otherwise it can stick around)
                    setShowStartChatPopup(false);
                    setOnlineConnectionsForPopup([]); // Clear list
                    // Suppress reopening while user is planning
                    suppressStartChatPopupUntilRef.current = Date.now() + 30 * 60 * 1000;
                    if (selectedConnectionData?.id) {
                      if ((selectedConnectionData?.phase ?? 'chat') === 'chat') {
                        await setConnectionPhase(selectedConnectionData.id, 'call', 'not_scheduled').catch(console.warn);
                      }
                    }
                    setAutoplanOpen(true);
                    handleConnectionCTA('agenda');
                  }}
                  className="w-full bg-[#2E5F56] text-white py-3 rounded-lg hover:bg-[#265049] transition-colors text-sm font-semibold flex items-center justify-center space-x-2 relative z-[102]"
                  style={{ zIndex: 102, pointerEvents: 'auto' }}
                >
                  <span>🗓️</span>
                  <span className="text-white">Sync planning</span>
                </button>

                {String(primaryCta?.label || '').trim().toLowerCase() !== 'start chat' && (
                  <button
                    type="button"
                    onClick={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      // Close popup immediately (otherwise it can stick around)
                      setShowStartChatPopup(false);
                      setOnlineConnectionsForPopup([]); // Clear list
                      suppressStartChatPopupUntilRef.current = Date.now() + 30 * 60 * 1000;
                      await handlePrimaryCta();
                    }}
                    className="w-full bg-[#2E5F56] text-white py-3 rounded-lg hover:bg-[#2E5F56] transition-colors text-sm font-semibold flex items-center justify-center space-x-2 relative z-[102]"
                    style={{ zIndex: 102, pointerEvents: 'auto' }}
                  >
                    <span>🔄</span>
                    <span className="text-white">{primaryCta.label}</span>
                  </button>
                )}

                {hasMismatchedActiveEvent && (
                  <div className="mt-2 text-xs text-[#4F6660] bg-stone-50 border border-stone-200 rounded-lg p-2">
                    Deze afspraak lijkt niet bij deze connectie te horen.
                    <div className="mt-2">
                      <button
                        onClick={() => handleConnectionCTA('agenda')}
                        className="px-2 py-1 bg-[#7A9488] text-white rounded text-xs hover:bg-[#4F6660]"
                      >
                        Open planning
                      </button>
                    </div>
                  </div>
                )}

                {canCompletePhase && (
                  <button
                    type="button"
                    onClick={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      await finishAndChooseNext('complete_button');
                    }}
                    disabled={completingPhase}
                    className={`w-full bg-green-600 text-white py-3 rounded-lg transition-colors text-sm font-medium flex items-center justify-center space-x-2 mt-2 relative z-[102] ${
                      completingPhase ? 'opacity-70 cursor-not-allowed' : 'hover:bg-green-700'
                    }`}
                    style={{ zIndex: 102, pointerEvents: 'auto' }}
                  >
                    <span>✅</span>
                    <span>
                      {completingPhase
                        ? 'Afronden...'
                        : selectedConnectionData?.phase === 'video'
                        ? 'Videogesprek afronden'
                        : selectedConnectionData?.phase === 'physical'
                        ? 'Ontmoeting afronden'
                        : 'Belafspraak afronden'}
                    </span>
                  </button>
                )}

                {canCancelAppointment && (
                  <button
                    type="button"
                    onClick={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      await handleCancelAppointment();
                    }}
                    disabled={cancelling}
                    className={`w-full bg-[#4F6660] text-white py-3 rounded-lg transition-colors text-sm font-medium flex items-center justify-center space-x-2 mt-2 relative z-[102] ${
                      cancelling ? 'opacity-70 cursor-not-allowed' : 'hover:bg-[#4F6660]'
                    }`}
                    style={{ zIndex: 102, pointerEvents: 'auto' }}
                  >
                    <span>✖️</span>
                    <span>{cancelling ? 'Annuleren...' : 'Afspraak annuleren'}</span>
                  </button>
                )}

                {completionNotice && (
                  <div className="mt-2 text-xs text-green-800 bg-green-50 border border-green-200 rounded-lg p-2">
                    {completionNotice}
                  </div>
                )}

                {cancelNotice && (
                  <div className="mt-2 text-xs text-[#4A5A3D] bg-stone-50 border border-stone-200 rounded-lg p-2">
                    {cancelNotice}
                  </div>
                )}

                {selectedConnectionData &&
                  ['call', 'video', 'physical'].includes(selectedConnectionData?.phase ?? 'chat') &&
                  !['done', 'completed'].includes(selectedConnectionData?.phaseStatus ?? 'not_scheduled') && (
                    <PlanningCard
                      selectedConnectionData={selectedConnectionData}
                      activeEvent={activeEvent}
                      incomingProposals={incomingProposals}
                      onPlanManual={() => {
                        handleConnectionCTA('agenda');
                        setAutoplanOpen(false);
                      }}
                      onAutoplan={() => {
                        setAutoplanOpen(true);
                        runAutoplan();
                      }}
                      onProposal={() => {
                        setAutoplanOpen(true);
                        openProposalComposer();
                      }}
                    />
                  )}

                {selectedConnectionData && (
                  <div className="mt-2 text-right">
                    <button
                      onClick={openFullAgendaPage}
                      className="text-xs text-[#2E5F56] hover:text-[#2E5F56] underline"
                    >
                      Open volledige AgendaPage met deze connectie
                    </button>
                  </div>
                )}

                {SHOW_DEV_JOURNEY_CONTROLS && selectedConnectionData?.id && (
                  <div className="mt-2 space-y-1 text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-2">
                    <div className="flex items-center justify-between">
                      <span>Phase: {selectedConnectionData?.phase ?? 'chat'}</span>
                      <span>Status: {selectedConnectionData?.phaseStatus ?? 'not_scheduled'}</span>
                    </div>
                    <div>ActiveEvent: {selectedConnectionData?.activeEventId || '—'}</div>
                    <button
                      onClick={() => advanceJourney(selectedConnectionData)}
                      className="w-full mt-1 px-2 py-1 bg-[rgba(46,95,86,0.1)] text-[#2E5F56] rounded hover:bg-[rgba(46,95,86,0.1)]"
                    >
                      Dev: Mark phase done → next
                    </button>
                  </div>
                )}

                {/* Autoplan */}
                <AutoplanCard
                  autoplanLoading={autoplanLoading}
                  autoplanError={autoplanError}
                  autoplanResults={autoplanResults}
                  selectedSlot={selectedSlot}
                  selectedConnectionData={selectedConnectionData}
                  proposalSlots={proposalSlots}
                  proposalMessage={proposalMessage}
                  onRunAutoplan={runAutoplan}
                  onSetProposalSlots={setProposalSlots}
                  onSetProposalMessage={setProposalMessage}
                  onSubmitProposal={submitProposal}
                  onCreateEventFromSlot={createEventFromSlot}
                  onHandleConnectionCTA={handleConnectionCTA}
                  hasActiveScheduled={hasActiveScheduled}
                />

                {/* Incoming proposals */}
                <IncomingProposalsCard
                  incomingProposals={incomingProposals}
                  user={user}
                  selectedConnectionData={selectedConnectionData}
                  onSetAutoplanError={setAutoplanError}
                  onSetProposalOpen={setProposalOpen}
                  onHandleConnectionCTA={handleConnectionCTA}
                  hasActiveScheduled={hasActiveScheduled}
                  onAcceptProposal={async (proposal: MeetingProposal, slot: { startTime: Date; endTime: Date }) => {
                    if (!user || !selectedConnection) return;
                    const participants = [user.uid, proposal.createdBy];
                    const participantNames = [user.displayName || 'Onbekend', selectedConnectionData?.name || 'Onbekend'];
                    await acceptProposal(selectedConnection, proposal, slot, participants, participantNames);
                  }}
                  onRejectProposal={async (proposalId: string) => {
                    if (!selectedConnection) return;
                    await rejectProposal(selectedConnection, proposalId);
                    openInboxTab('afspraken');
                  }}
                />

                <button
                  type="button"
                  onClick={(e) => {
                    console.log('🔘 Voorstel Doen button clicked');
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('🔄 Closing popup and opening custom time popup...');
                    setShowStartChatPopup(false);
                    setOnlineConnectionsForPopup([]); // Clear list
                    // Small delay to ensure popup closes first
                    setTimeout(() => {
                      setShowCustomTimePopup(true);
                      console.log('✅ Voorstel doen - custom time popup');
                    }, 100);
                  }}
                  className="w-full bg-green-500 text-white py-3 rounded-lg hover:bg-green-600 transition-colors text-sm font-semibold flex items-center justify-center space-x-2 relative z-[102]"
                  style={{ zIndex: 102, pointerEvents: 'auto' }}
                >
                  <span>📅</span>
                  <span className="text-white">Voorstel Doen</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowStartChatPopup(false);
                    setOnlineConnectionsForPopup([]);
                    openInboxTab('afspraken');
                  }}
                  className="w-full border border-gray-200 text-gray-600 py-2 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium flex items-center justify-center space-x-2"
                >
                  <span>🗓️</span>
                  <span>Bekijk Afspraken</span>
                </button>
              </div>
              
              <button
                onClick={() => {
                  setShowStartChatPopup(false);
                  setOnlineConnectionsForPopup([]); // Clear list
                  // User explicitly ignored: don't re-open this popup for a while
                  suppressStartChatPopupUntilRef.current = Date.now() + 30 * 60 * 1000;
                }}
                className="mt-4 text-gray-400 text-sm hover:text-gray-600 transition-colors"
              >
                Negeren
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Call Popup verwijderd */}

      {/* Conversation Summary Popup */}
      {showSummaryPopup && (
        <ConversationSummary
          onClose={() => setShowSummaryPopup(false)}
          onDownloadPDF={() => {
            console.log('PDF download gestart...');
            // TODO: Implement PDF generation
          }}
          summary={conversationSummary}
          tips={[
            "Probeer meer open vragen te stellen om diepere gesprekken te voeren.",
            "Luister actief en herhaal belangrijke punten om begrip te tonen.",
            "Gebruik concrete voorbeelden om abstracte concepten uit te leggen."
          ]}
          suggestions={[
            "Plan een follow-up gesprek over de besproken onderwerpen.",
            "Deel relevante resources of documenten na het gesprek.",
            "Vraag om feedback over het gesprek om te verbeteren."
          ]}
          userName={currentCall?.userName || 'Onbekend'}
          callDuration={
            currentCall && currentCall.startTime
              ? Math.floor(
                  (Date.now() - currentCall.startTime.getTime()) / 1000
                )
              : 0
          }
        />
      )}

      {/* Connection Request Modal */}
      {showRequestModal && selectedUserForRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Connectie verzoek naar {selectedUserForRequest.userName}
            </h3>
            
            {/* ✅ Phone number warning in modal */}
            {!currentUserHasPhone && (
              <div className="bg-stone-50 border border-stone-300 rounded-lg p-3 mb-4">
                <div className="flex items-start gap-2">
                  <span className="text-[#7A9488] text-sm flex-shrink-0">⚠️</span>
                  <div>
                    <p className="text-sm font-semibold text-[#4A5A3D] mb-1">Telefoonnummer ontbreekt</p>
                    <p className="text-xs text-[#4F6660]">
                      Je kunt wel een verzoek versturen, maar zonder telefoonnummer ben je niet zichtbaar in "Open voor connecties". 
                      Ga naar <strong>Instellingen → Profiel</strong> om je nummer toe te voegen.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <textarea
              value={requestMessage}
              onChange={(e) => setRequestMessage(e.target.value)}
              placeholder="Schrijf een persoonlijk bericht (optioneel)..."
              className="w-full p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-[#3ECBB5] focus:border-transparent mb-4"
              rows={3}
            />
            
            <div className="flex items-center mb-4">
              <input
                type="checkbox"
                id="knownContactConnectieFlow"
                checked={isKnownContact}
                onChange={(e) => setIsKnownContact(e.target.checked)}
                className="mr-2 h-4 w-4 text-[#7A9488] focus:ring-[#7A9488] border-gray-300 rounded accent-[#7A9488]"
              />
              <label htmlFor="knownContactConnectieFlow" className="text-sm text-gray-700">
                👋 We kennen elkaar al
              </label>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowRequestModal(false);
                  setSelectedUserForRequest(null);
                  setRequestMessage('');
                  setIsKnownContact(false);
                }}
                disabled={sendingRequest}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Annuleren
              </button>
              <button
                onClick={() => {
                  if (selectedUserForRequest) {
                    sendConnectionRequest(selectedUserForRequest.userId);
                  }
                }}
                disabled={sendingRequest}
                className="flex-1 px-4 py-2 bg-[#2E5F56] text-white rounded-lg hover:bg-[#265049] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sendingRequest ? 'Verzenden...' : 'Verzoek Versturen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Next Step Choice Modal */}
          <NextStepModal
            isOpen={nextStepChoiceOpen}
        onClose={() => setNextStepChoiceOpen(false)}
            nextPhaseCandidate={nextPhaseCandidate as any}
            connectionName={selectedConnectionData?.name || 'deze persoon'}
            onDirectCall={handleDirectCall}
            onAbortFlow={handleAbortFlow}
            onPlanAgenda={async () => {
              // Physical phase must start with location selection inside PhysicalMeetingPhase,
              // not by jumping directly to agenda.
              if (nextPhaseCandidate === 'physical') {
                await handleDirectCall('physical');
                return;
              }
              await handlePlanViaAgenda();
            }}
        onEndWithoutPhysical={(reason) => {
          handleEndConnection(reason);
          setNextStepChoiceOpen(false);
        }}
      />

      {/* Toast — z-[9999] zodat het boven agenda modal en alle overlays verschijnt */}
      {toastMessage && (
        <div className="fixed bottom-6 right-6 z-[9999]">
          <div className="bg-gray-900 text-white text-sm px-4 py-3 rounded-xl shadow-2xl shadow-black/40 max-w-sm">
            {toastMessage}
          </div>
        </div>
      )}

      {/* Planning banner — user 2 notification when user 1 picks a location */}
      {planningBanner && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 10000,
            display: 'flex',
            justifyContent: 'center',
            padding: '0',
            pointerEvents: 'none',
          }}
        >
          <div
            style={{
              background: 'linear-gradient(135deg, #1a3a2a 0%, #0f2318 100%)',
              color: '#fff',
              borderRadius: '0 0 20px 20px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
              padding: '18px 24px 20px',
              maxWidth: 420,
              width: '100%',
              pointerEvents: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
              animation: 'sk-banner-in 0.4s cubic-bezier(0.34,1.56,0.64,1)',
            }}
          >
            <style>{`@keyframes sk-banner-in { from { transform: translateY(-100%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}</style>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ width: 40, height: 40, borderRadius: 12, background: 'rgba(112,130,89,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8DC26F" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/><path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ fontWeight: 700, fontSize: 14, margin: 0, color: '#e8f4e8' }}>
                  {planningBanner.userName} heeft een locatie gekozen
                </p>
                <p style={{ fontSize: 13, margin: '4px 0 0', color: '#a8c4a0', lineHeight: 1.4 }}>
                  <span style={{ color: '#8DC26F', fontWeight: 600 }}>"{planningBanner.locationName}"</span> is de ontmoetingsplek. Plan nu samen de datum!
                </p>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => { setPlanningBanner(null); setShowAgenda(true); }}
                style={{
                  flex: 1,
                  background: 'linear-gradient(135deg, #7A9488, #4F6660)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 10,
                  padding: '10px 16px',
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2}><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                Datum plannen
              </button>
              <button
                onClick={() => setPlanningBanner(null)}
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  color: '#c8d8c0',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: 10,
                  padding: '10px 14px',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Later
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Autoplan banner — user LEFT gets notified when user RIGHT finds/doesn't find a match */}
      {autoplanBanner && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 10001, display: 'flex', justifyContent: 'center', pointerEvents: 'none' }}>
          <div
            style={{
              background: autoplanBanner.type === 'match'
                ? 'linear-gradient(135deg, #1a3a2a 0%, #0f2318 100%)'
                : 'linear-gradient(135deg, #3a1a0a 0%, #2a1008 100%)',
              color: '#fff',
              borderRadius: '0 0 20px 20px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
              padding: '16px 20px 18px',
              maxWidth: 420,
              width: '100%',
              pointerEvents: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              animation: 'sk-banner-in 0.4s cubic-bezier(0.34,1.56,0.64,1)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ width: 38, height: 38, borderRadius: 12, background: autoplanBanner.type === 'match' ? 'rgba(22,163,74,0.25)' : 'rgba(249,115,22,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {autoplanBanner.type === 'match'
                  ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth={2.5}><polyline points="20 6 9 17 4 12"/></svg>
                  : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fb923c" strokeWidth={2.5}><line x1="12" y1="8" x2="12" y2="12"/><circle cx="12" cy="12" r="10"/></svg>
                }
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ fontWeight: 700, fontSize: 14, margin: 0, color: autoplanBanner.type === 'match' ? '#e8f4e8' : '#F0F7F4' }}>
                  {autoplanBanner.type === 'match'
                    ? `${autoplanBanner.userName} heeft ${autoplanBanner.matchCount} match${(autoplanBanner.matchCount ?? 1) > 1 ? 'es' : ''} gevonden!`
                    : `${autoplanBanner.userName} heeft Autoplan geprobeerd`}
                </p>
                <p style={{ fontSize: 12, margin: '4px 0 0', color: autoplanBanner.type === 'match' ? '#a8c4a0' : '#8DA8A0', lineHeight: 1.4 }}>
                  {autoplanBanner.type === 'match'
                    ? autoplanBanner.slotLabel
                      ? <>Mogelijk moment: <span style={{ color: '#86efac', fontWeight: 600 }}>{autoplanBanner.slotLabel}</span> — afspraak wordt binnenkort bevestigd</>
                      : 'Er is een overlappend moment gevonden. De afspraak wordt binnenkort bevestigd.'
                    : 'Er is nog geen overlappende beschikbaarheid. Voeg meer "Vrij voor netwerken" blokken toe.'}
                </p>
              </div>
            </div>
            <button
              onClick={() => setAutoplanBanner(null)}
              style={{ alignSelf: 'flex-end', background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 600, color: '#e0e0e0', cursor: 'pointer' }}
            >
              Begrepen
            </button>
          </div>
        </div>
      )}

      {/* ============================================================
          📅 AFSPRAAK BEVESTIGINGSKAART
          Verschijnt voor BEIDE users nadat een afspraak gepland is
          ============================================================ */}
      {appointmentConfirmCard && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 10002,
            background: 'rgba(10,20,15,0.72)',
            backdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 24,
            animation: 'sk-confirm-bg 0.3s ease',
          }}
          onClick={() => {
            setAppointmentConfirmCard(null);
            // Navigate to inbox planning tab so user sees the appointment immediately
            openInboxTab('afspraken');
          }}
        >
          <style>{`
            @keyframes sk-confirm-bg { from { opacity: 0; } to { opacity: 1; } }
            @keyframes sk-confirm-card { from { transform: scale(0.88) translateY(24px); opacity: 0; } to { transform: scale(1) translateY(0); opacity: 1; } }
            @keyframes sk-confirm-check { 0% { stroke-dashoffset: 60; } 100% { stroke-dashoffset: 0; } }
          `}</style>
          <div
            className="sk-confirm-modal"
            style={{
              background: '#ffffff',
              borderRadius: 24,
              padding: '0 0 28px',
              maxWidth: 400,
              width: '100%',
              boxShadow: '0 32px 80px rgba(0,0,0,0.25), 0 0 0 1px rgba(125,168,160,0.15)',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0,
              animation: 'sk-confirm-card 0.45s cubic-bezier(0.34,1.56,0.64,1)',
              overflow: 'hidden',
            }}
            onClick={e => e.stopPropagation()}
          >
            {/* Sage green header */}
            <div style={{
              width: '100%',
              background: 'linear-gradient(135deg, #6B8C82 0%, #7A9488 50%, #8DA8A0 100%)',
              padding: '32px 32px 28px',
              display: 'flex', flexDirection: 'column', alignItems: 'center',
            }}>
              {/* Animated check circle */}
              <div style={{ position: 'relative', marginBottom: 16 }}>
                <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', border: '2px solid rgba(255,255,255,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5"/>
                    <polyline
                      points="6 12 10 16 18 8"
                      stroke="#ffffff"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeDasharray="60"
                      strokeDashoffset="60"
                      style={{ animation: 'sk-confirm-check 0.6s ease 0.2s forwards' }}
                    />
                  </svg>
                </div>
                <div style={{ position: 'absolute', inset: -8, borderRadius: '50%', border: '1.5px solid rgba(255,255,255,0.2)', animation: 'sk-cp-wave 1.8s ease-out 0.3s infinite' }} />
              </div>
              <p style={{ margin: '0 0 4px', fontSize: 22, fontWeight: 800, color: '#ffffff', letterSpacing: '-0.01em', textAlign: 'center' }}>
                Afspraak bevestigd!
              </p>
              <p style={{ margin: 0, fontSize: 13, color: 'rgba(255,255,255,0.85)', textAlign: 'center' }}>
                Jullie afspraak staat in de agenda
              </p>
            </div>

            {/* Body */}
            <div style={{ width: '100%', padding: '20px 24px 0' }}>
              {/* Date & time card */}
              <div style={{ width: '100%', background: 'rgba(122,148,136,0.08)', border: '1px solid rgba(122,148,136,0.2)', borderRadius: 16, padding: '16px 20px', marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg, #7A9488, #8DA8A0)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth={2}><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                  </div>
                  <div>
                    <p style={{ margin: 0, fontSize: 11, color: '#7A9488', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Datum &amp; tijd</p>
                    <p style={{ margin: '2px 0 0', fontSize: 15, fontWeight: 700, color: '#3C4F49' }}>
                      {appointmentConfirmCard.start.toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                    </p>
                    <p style={{ margin: '1px 0 0', fontSize: 13, color: '#5a7268' }}>
                      {appointmentConfirmCard.start.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
                      {' – '}
                      {appointmentConfirmCard.end.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })} uur
                    </p>
                  </div>
                </div>

                {/* Location row */}
                {appointmentConfirmCard.locationName && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 10, borderTop: '1px solid rgba(122,148,136,0.2)' }}>
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg, #7A9488, #8DA8A0)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/><path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
                    </div>
                    <div>
                      <p style={{ margin: 0, fontSize: 11, color: '#7A9488', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Locatie</p>
                      <p style={{ margin: '2px 0 0', fontSize: 14, fontWeight: 600, color: '#3C4F49' }}>{appointmentConfirmCard.locationName}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Participants */}
              <div style={{ width: '100%', background: 'rgba(122,148,136,0.08)', border: '1px solid rgba(122,148,136,0.2)', borderRadius: 14, padding: '12px 16px', marginBottom: 20 }}>
                <p style={{ margin: '0 0 10px', fontSize: 11, color: '#7A9488', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Deelnemers</p>
                <div style={{ display: 'flex', gap: 10 }}>
                  {[appointmentConfirmCard.myName, appointmentConfirmCard.otherUserName].map((name, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, background: 'rgba(122,148,136,0.12)', borderRadius: 10, padding: '8px 10px' }}>
                      <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'linear-gradient(135deg, #7A9488, #8DA8A0)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: '#ffffff' }}>
                          {name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#3C4F49', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Close + navigate to inbox */}
              <button
                onClick={() => {
                  setAppointmentConfirmCard(null);
                  // Close any open panels/overlays
                  setShowAgenda(false);
                  // Navigate to inbox afspraken tab — the appointment is waiting there
                  openInboxTab('afspraken');
                }}
                style={{
                  width: '100%',
                  padding: '13px',
                  background: 'linear-gradient(135deg, #6B8C82 0%, #7A9488 50%, #8DA8A0 100%)',
                  border: 'none',
                  borderRadius: 14,
                  fontSize: 14,
                  fontWeight: 700,
                  color: '#ffffff',
                  cursor: 'pointer',
                  letterSpacing: '0.01em',
                  transition: 'opacity 0.15s ease',
                }}
                onMouseEnter={e => (e.currentTarget.style.opacity = '0.88')}
                onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
              >
                Geweldig, tot snel! 🎉
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Working Call Popup Test verwijderd */}

      {/* Video Call Popup Test verwijderd */}


      {/* ==========================================
          ✅ VIDEO CALL INTERFACE
          ==========================================
          Video calls worden nu afgehandeld door Google Meet (externe popup)
          Audio calls door WebRTC (embedded, via useWebRTCAudioCall hook)
          ========================================== */}

      {/* Debug Call Interface sectie verwijderd */}

      {/* Conversation Analysis Trigger - NIEUW */}
      {showAnalysisTrigger && completedConnectionId && completedPhase && user && (
        <ConversationAnalysisTrigger
          userId={user.uid}
          connectionId={completedConnectionId}
          phase={completedPhase}
          onClose={() => {
            setShowAnalysisTrigger(false);
            setCompletedConnectionId(null);
            setCompletedPhase(null);
          }}
          onSuccess={() => {
            setShowAnalysisTrigger(false);
            setCompletedConnectionId(null);
            setCompletedPhase(null);
            // Optional: Show success toast
          }}
        />
      )}
    </>
  );
};

// Timer Expired Popup Component
const TimerExpiredPopup = ({ 
  currentPhase, 
  phases, 
  onClose, 
  onNextPhase, 
  onOpenAgenda, 
  onCompleteFlow,
  showIncomingCall, 
  showIncomingVideo, 
  showIncomingMeeting,
  onOpenCustomTime,
  connectionId,
  currentUserId,
  otherUserId,
  otherUserName,
  nextPhaseCandidate,
  otherUserEnded,
  onEndConnection
}: {
  currentPhase: string;
  phases: any;
  onClose: () => void;
  onNextPhase: (phase: string) => void;
  onOpenAgenda: () => void;
  onCompleteFlow: () => void;
  showIncomingCall: (show: boolean) => void;
  showIncomingVideo: (show: boolean) => void;
  showIncomingMeeting: (show: boolean) => void;
  onOpenCustomTime: () => void;
  connectionId?: string;
  currentUserId?: string;
  otherUserId?: string;
  otherUserName?: string;
  nextPhaseCandidate?: ConnectionPhase | null;
  otherUserEnded?: boolean;
  onEndConnection?: (reason?: string) => Promise<void>;
}) => {
  // State voor planning lock status
  const [planningLock, setPlanningLock] = React.useState<{
    ownerId: string | null;
    ownerName: string | null;
    phase: string | null;
  } | null>(null);
  
  // State voor reden textbox wanneer andere user heeft beëindigd
  const [endReason, setEndReason] = React.useState('');

  // Real-time listener voor planning lock
  React.useEffect(() => {
    if (!connectionId) return;

    const unsubscribe = onSnapshot(
      doc(db, 'connections', connectionId),
      (snapshot) => {
        if (!snapshot.exists()) return;
        
        const data = snapshot.data();
        const lockData = {
          ownerId: data.planningOwnerId || null,
          ownerName: data.planningOwnerName || null,
          phase: data.planningOwnerPhase || null,
        };
        
        setPlanningLock(lockData);
        console.log('[timer-popup] Planning lock updated:', lockData);
      },
      (error) => {
        console.error('[timer-popup] Planning lock listener error:', error);
      }
    );

    return () => unsubscribe();
  }, [connectionId]);

  // Check of current user observer is
  const isObserver = !!(planningLock?.ownerId && planningLock.ownerId !== currentUserId);
  const isPlanningOwner = planningLock?.ownerId === currentUserId;

  // Handle "Afsluiten" - maak notificatie aan
  const handleCloseWithNotification = async () => {
    if (!connectionId || !currentUserId || !otherUserId || !planningLock?.ownerName) {
      onClose();
      return;
    }

    try {
      // Maak inbox item aan voor current user
      await addDoc(collection(db, 'inboxItems'), {
        userId: currentUserId,
        type: 'planning_pending',
        category: 'planning',
        title: 'Planning nog niet afgerond',
        message: `${planningLock.ownerName} is bezig met planning. Je kunt later verder plannen.`,
        connectionId: connectionId,
        planningOwnerId: planningLock.ownerId,
        idempotencyKey: `planning_pending:${connectionId}:${planningLock.ownerId}:${currentUserId}`,
        isRead: false,
        createdAt: serverTimestamp(),
      });

      // Ook voor de andere gebruiker (planning owner)
      await addDoc(collection(db, 'inboxItems'), {
        userId: otherUserId,
        type: 'planning_pending',
        category: 'planning',
        title: 'Planning nog niet afgerond',
        message: `${otherUserName || 'De andere gebruiker'} heeft de planning popup gesloten. Je kunt verder plannen.`,
        connectionId: connectionId,
        planningOwnerId: planningLock.ownerId,
        idempotencyKey: `planning_pending:${connectionId}:${planningLock.ownerId}:other`,
        isRead: false,
        createdAt: serverTimestamp(),
      });

      console.log('[timer-popup] Planning pending notifications created');
    } catch (error) {
      console.error('[timer-popup] Failed to create notifications:', error);
    }

    onClose();
  };

  // 🔔 Zachte melding als de fase klaar is
  React.useEffect(() => {
    let audio: HTMLAudioElement | null = null;
    try {
      audio = new Audio('/sounds/phase-ended.mp3');
      const playPromise = audio.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch((err) => {
          console.warn('Phase-end sound blocked by browser:', err);
        });
      }
    } catch (err) {
      console.warn('Kon phase-end geluid niet afspelen:', err);
    }

    return () => {
      if (audio) {
        try {
          audio.pause();
          audio.currentTime = 0;
          // Setting src to '' releases the WebMediaPlayer immediately,
          // preventing the "too many WebMediaPlayers" browser error when
          // this component mounts/unmounts repeatedly.
          audio.src = '';
          audio.load();
        } catch {}
      }
    };
  }, []);

  // Bepaal volgende fase op basis van huidige
  const getNextPhaseLocal = () => {
    if (currentPhase === 'chat') return 'call';
    if (currentPhase === 'call') return 'video';
    if (currentPhase === 'video') return 'physical';
    return 'completed';
  };

  const nextPhase = getNextPhaseLocal();
  const isCallNext = nextPhase === 'call';
  const isVideoNext = nextPhase === 'video';
  const isMeetupNext = nextPhase === 'physical';

  const handleStartNow = async () => {
    // ✅ FIX: Update Firestore instead of showing local popup
    // This way, User 2 gets an incoming popup automatically via real-time listener
    
    if (!connectionId || !currentUserId || !otherUserId) {
      console.error('❌ Missing required data for handleStartNow');
      onClose();
      return;
    }

    try {
      console.log(`🚀 [TimerExpired] Starting ${nextPhase} phase via Firestore update`);
      
      // Get the connectionFlow reference
      const flowRef = doc(db, 'connectionFlows', connectionId);
      
      // Determine step key
      const stepKey =
        nextPhase === 'call' ? 'callStep' :
        nextPhase === 'video' ? 'videoStep' :
        nextPhase === 'physical' ? 'physicalStep' :
        null;
      
      if (!stepKey) {
        console.error('❌ Invalid phase for direct start:', nextPhase);
        onClose();
        return;
      }
      
      // Create acceptances object (only current user accepted)
      const acceptances: { [key: string]: boolean } = {};
      acceptances[currentUserId] = true;
      
      // Generate Jitsi URL for video calls
      let videoMeetUrl: string | null = null;
      if (nextPhase === 'video') {
        try {
          const { meetService } = await import('../services/meetService');
          const auth = (await import('../firebase')).auth;
          const user = auth.currentUser;
          
          videoMeetUrl = await meetService.createMeetUrlForFlow(
            connectionId,
            'video',
            user?.email || undefined,
            user?.uid,
            user?.displayName || user?.email || 'Gebruiker'
          );
          console.log('✅ [TimerExpired] Jitsi URL generated:', videoMeetUrl);
        } catch (err) {
          console.error('❌ [TimerExpired] Failed to generate video URL:', err);
        }
      }
      
      // Build update payload
      // NOTE: status must always be 'pending' here — 'meeting' would conflict with
      // handleDirectCall which writes 'pending', causing a snapshot mismatch that
      // confuses the phase-sync useEffect.
      const updatePayload: any = {
        [`${stepKey}.status`]: 'pending',
        [`${stepKey}.proposedBy`]: currentUserId,
        [`${stepKey}.acceptances`]: acceptances,
        [`${stepKey}.duration`]: 480, // 8 minutes
        currentStep: nextPhase,
        status: 'pending',
        updatedAt: serverTimestamp()
      };
      
      // Include meetUrl for video calls
      if (nextPhase === 'video' && videoMeetUrl) {
        updatePayload[`${stepKey}.meetUrl`] = videoMeetUrl;
      }
      
      // Update Firestore
      await updateDoc(flowRef, updatePayload);
      
      console.log(`✅ [TimerExpired] Firestore updated - ${stepKey} set to pending`);
      console.log(`📢 [TimerExpired] User 2 will receive incoming ${nextPhase} popup automatically`);
      
      // Close timer popup (User 1 will see the phase interface)
      onClose();
      
      // Call onNextPhase to trigger any additional local UI updates
      onNextPhase(nextPhase);
      
    } catch (error) {
      console.error('❌ [TimerExpired] Failed to start next phase:', error);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl border border-gray-100 w-full max-w-md mx-4">
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Tijd voorbij voor deze fase</h3>
              <p className="text-sm text-gray-500 mt-1">
                De {phases[currentPhase as keyof typeof phases]?.name?.toLowerCase?.() || 'huidige'} fase is afgerond.
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-full hover:bg-gray-100 text-gray-400"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Planning lock status indicator */}
          {isObserver && planningLock?.ownerName && (
            <div className="mb-4 bg-stone-50 border-2 border-[#7A9488] rounded-lg p-3">
              <div className="flex items-center gap-2">
                <User size={16} className="text-[#4F6660]" />
                <p className="text-sm font-medium text-[#4A5A3D]">
                  {planningLock.ownerName} is bezig met planning...
                </p>
              </div>
              <p className="text-xs text-[#4F6660] mt-1">
                Wacht even of sluit af om later verder te plannen.
              </p>
            </div>
          )}

          <div className="flex flex-col items-center mb-5">
            <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center text-2xl mb-3">
              {phases[currentPhase as keyof typeof phases]?.icon}
            </div>
            <p className="text-sm text-gray-600 text-center">
              Kies hoe je de <span className="font-medium">volgende stap</span> wilt doen.
            </p>
          </div>

          {/* Melding wanneer andere user heeft beëindigd */}
          {otherUserEnded && (
            <div className="mb-4 bg-red-50 border-2 border-red-400 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <span className="text-red-600">⚠️</span>
                <p className="text-sm font-medium text-red-900">
                  {otherUserName || 'De andere gebruiker'} heeft de connectie beëindigd.
                </p>
              </div>
              <p className="text-xs text-red-700 mt-1">
                Je kunt een reden opgeven voordat je beëindigt.
              </p>
            </div>
          )}

          <div className="space-y-3">
            {/* Knop 1: Direct beginnen met volgende fase (dynamisch) */}
            {(isCallNext || isVideoNext || isMeetupNext) && !isObserver && (
              <button
                onClick={handleStartNow}
                className="w-full bg-green-500 text-white py-3 rounded-lg hover:bg-green-600 transition-colors text-sm font-medium flex items-center justify-center space-x-2"
              >
                <span>▶️</span>
                <span>
                  Direct beginnen met {phases[nextPhase as keyof typeof phases]?.name || 'volgende fase'}
                </span>
              </button>
            )}

            {/* Knop 2: Plannen */}
            <button
              onClick={onOpenAgenda}
              disabled={!!isObserver}
              className={`w-full bg-[#7A9488] text-white py-3 rounded-lg hover:bg-[#4F6660] transition-colors text-sm font-medium flex items-center justify-center space-x-2 ${
                isObserver ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <Calendar className="w-4 h-4" />
              <span>
                {isObserver 
                  ? `${planningLock?.ownerName} plant nu...` 
                  : 'Plannen'}
              </span>
            </button>

            {/* Afsluiten knop voor observer */}
            {isObserver && (
              <button
                onClick={handleCloseWithNotification}
                className="w-full bg-gray-600 text-white py-3 rounded-lg hover:bg-gray-700 transition-colors text-sm font-medium"
              >
                Afsluiten en later verder plannen
              </button>
            )}

            {/* Knop 3: Beëindigen */}
            {otherUserEnded && (
              <div className="space-y-2">
                <textarea
                  value={endReason}
                  onChange={(e) => setEndReason(e.target.value)}
                  placeholder="Geef een reden op voor het beëindigen (optioneel)..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#7A9488] focus:border-[#7A9488] resize-none"
                  rows={3}
                />
              </div>
            )}
            <button
              onClick={async () => {
                if (onEndConnection) {
                  await onEndConnection(endReason || undefined);
                } else {
                  onCompleteFlow();
                }
              }}
              className="w-full bg-gray-100 text-gray-700 py-3 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
            >
              Beëindigen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Meetup Reflection Popup Component
const MeetupReflectionPopup = ({
  onClose,
  onSave,
  phaseName,
}: {
  onClose: () => void;
  onSave: (data: {
    feelingPerson: string;
    feelingContent: string;
    talkingBalance: 'meer_gesproken' | 'meer_geluisterd' | 'in_balans';
    takeaways: string;
    summaryText: string;
  }) => void;
  phaseName: string;
}) => {
  const [feelingPerson, setFeelingPerson] = React.useState('');
  const [feelingContent, setFeelingContent] = React.useState('');
  const [talkingBalance, setTalkingBalance] =
    React.useState<'meer_gesproken' | 'meer_geluisterd' | 'in_balans'>('in_balans');
  const [takeaways, setTakeaways] = React.useState('');
  const [summaryText, setSummaryText] = React.useState('');
  const [isRecording, setIsRecording] = React.useState(false);
  const recognitionRef = React.useRef<any | null>(null);

  const handleSave = () => {
    onSave({ feelingPerson, feelingContent, talkingBalance, takeaways, summaryText });
  };

  const handleToggleRecording = () => {
    const SpeechRecognition =
      (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

    if (!SpeechRecognition) {
      alert('Spraakherkenning wordt niet ondersteund door deze browser. Probeer Google Chrome.');
      return;
    }

    // Stop opnemen
    if (isRecording && recognitionRef.current) {
      recognitionRef.current.stop();
      setIsRecording(false);
      return;
    }

    // Start opnemen
    try {
      const recognition = new SpeechRecognition();
      recognition.lang = 'nl-NL';
      recognition.continuous = false;
      recognition.interimResults = true;

      recognition.onresult = (event: any) => {
        let transcript = '';
        for (let i = 0; i < event.results.length; i++) {
          transcript += event.results[i][0].transcript + ' ';
        }
        setSummaryText((prev) => (prev ? prev + ' ' + transcript.trim() : transcript.trim()));
      };

      recognition.onend = () => {
        setIsRecording(false);
      };

      recognition.onerror = () => {
        setIsRecording(false);
      };

      recognitionRef.current = recognition;
      recognition.start();
      setIsRecording(true);
    } catch (error) {
      console.error('❌ Error starting speech recognition:', error);
      setIsRecording(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl border border-gray-100 w-full max-w-lg mx-4">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Reflectie fysieke afspraak
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {`Kort nagesprek met AI om je relatiebeheer in fase "${phaseName}" te verbeteren.`}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-gray-100 text-gray-500"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              1. Hoe voel je je over de persoon na deze afspraak?
            </label>
            <textarea
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#7A9488] focus:outline-none focus:ring-1 focus:ring-[#7A9488]"
              rows={2}
              value={feelingPerson}
              onChange={(e) => setFeelingPerson(e.target.value)}
              placeholder="Bijvoorbeeld: ik voel me enthousiast / nog wat twijfelend / ik kreeg een goed gevoel over de samenwerking..."
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              2. Hoe voel je je over de inhoud van het gesprek?
            </label>
            <textarea
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#7A9488] focus:outline-none focus:ring-1 focus:ring-[#7A9488]"
              rows={2}
              value={feelingContent}
              onChange={(e) => setFeelingContent(e.target.value)}
              placeholder="Bijvoorbeeld: de inhoud was helder / chaotisch / inspirerend / te oppervlakkig..."
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-2">
              3. Hoe was de balans tussen praten en luisteren?
            </label>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <button
                type="button"
                onClick={() => setTalkingBalance('meer_gesproken')}
                className={`px-2 py-2 rounded-lg border ${
                  talkingBalance === 'meer_gesproken'
                    ? 'bg-[#7A9488] text-white border-[#7A9488]'
                    : 'bg-white text-gray-700 border-gray-200'
                }`}
              >
                Ik heb meer gesproken
              </button>
              <button
                type="button"
                onClick={() => setTalkingBalance('in_balans')}
                className={`px-2 py-2 rounded-lg border ${
                  talkingBalance === 'in_balans'
                    ? 'bg-[#7A9488] text-white border-[#7A9488]'
                    : 'bg-white text-gray-700 border-gray-200'
                }`}
              >
                In balans
              </button>
              <button
                type="button"
                onClick={() => setTalkingBalance('meer_geluisterd')}
                className={`px-2 py-2 rounded-lg border ${
                  talkingBalance === 'meer_geluisterd'
                    ? 'bg-[#7A9488] text-white border-[#7A9488]'
                    : 'bg-white text-gray-700 border-gray-200'
                }`}
              >
                Ik heb meer geluisterd
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              4. Wat zou je volgende keer anders willen doen in je communicatie?
            </label>
            <textarea
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#7A9488] focus:outline-none focus:ring-1 focus:ring-[#7A9488]"
              rows={2}
              value={takeaways}
              onChange={(e) => setTakeaways(e.target.value)}
              placeholder="Bijvoorbeeld: meer doorvragen, vaker samenvatten, meer stiltes laten vallen..."
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              5. Korte samenvatting in je eigen woorden
            </label>
            <div className="flex items-start gap-2">
              <textarea
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#7A9488] focus:outline-none focus:ring-1 focus:ring-[#7A9488]"
                rows={3}
                value={summaryText}
                onChange={(e) => setSummaryText(e.target.value)}
                placeholder="Vat in 3–5 zinnen samen wat er is besproken en wat voor jou belangrijk was, of spreek je samenvatting in."
              />
              <button
                type="button"
                onClick={handleToggleRecording}
                className={`mt-1 flex h-9 items-center rounded-full px-3 text-xs font-medium text-white ${
                  isRecording ? 'bg-red-500 hover:bg-red-600' : 'bg-[#7A9488] hover:bg-[#4F6660]'
                }`}
              >
                {isRecording ? 'Stop opname' : '🎙 Inspreken'}
              </button>
            </div>
            <p className="mt-1 text-[11px] text-gray-500">
              Spraak‑naar‑tekst gebruikt de microfoon van je browser. Controleer of je toegang
              hebt gegeven. Je kunt de tekst achteraf gewoon aanpassen.
            </p>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="px-4 py-2 text-sm rounded-lg bg-[#7A9488] text-white font-medium hover:bg-[#4F6660]"
          >
            Reflectie opslaan
          </button>
        </div>
      </div>
    </div>
  );
};

// AI Relation Manager Intro Popup
const AiRelationIntroPopup = ({
  onClose,
  onEnable,
}: {
  onClose: () => void;
  onEnable: () => void;
}) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl border border-gray-100 w-full max-w-md mx-4">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">AI Relation Manager</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Gebruik AI om elke fase van je gesprek te analyseren en je relatiebeheer
              naar een hoger niveau te tillen.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-gray-100 text-gray-500"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-3 text-sm text-gray-700">
          <p>
            In de fase <strong>Fysieke afspraak</strong> kan de AI Relation Manager je
            helpen om:
          </p>
          <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
            <li>je gevoel over de persoon en het gesprek te verhelderen,</li>
            <li>beter inzicht te krijgen in jouw balans tussen praten en luisteren,</li>
            <li>concrete verbeterpunten en voorbeeldvragen te ontvangen.</li>
          </ul>
          <p className="text-sm text-gray-700">
            <strong>Zo gebruik je het:</strong> zet de schakelaar{' '}
            <span className="font-semibold">AI Relation Manager</span> <em>onder de timer</em>{' '}
            op <span className="font-semibold text-green-600">aan</span>. Na je fysieke
            afspraak krijg je automatisch een korte reflectie en AI‑tips.
          </p>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Later
          </button>
          <button
            type="button"
            onClick={onEnable}
            className="px-4 py-2 text-sm rounded-lg bg-[#7A9488] text-white font-medium hover:bg-[#4F6660]"
          >
            Zet AI Relation Manager aan
          </button>
        </div>
      </div>
    </div>
  );
};

// Incoming Call Popup Component
const IncomingCallPopup = ({ 
  selectedConnection, 
  onAccept, 
  onSchedule,
  onDecline 
}: {
  selectedConnection: any;
  onAccept: () => void;
  onSchedule: () => void;
  onDecline: () => void;
}) => {
  // 🔔 Simple ringtone on incoming call - gebruik ref om audio bij te houden
  const audioRef = React.useRef<HTMLAudioElement | null>(null);

  // Stop audio functie
  const stopAudio = React.useCallback(() => {
    if (audioRef.current) {
      try {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current = null;
        console.log('🔇 Incoming call audio gestopt');
      } catch (err) {
        console.warn('Kon audio niet stoppen:', err);
      }
    }
  }, []);

  React.useEffect(() => {
    try {
      const audio = new Audio('/sounds/incoming-call.mp3');
      audio.loop = true;
      audioRef.current = audio;
      
      // play() kan soms een Promise teruggeven; fout gewoon loggen
      const playPromise = audio.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch((err) => {
          console.warn('Ringtone playback blocked by browser:', err);
        });
      }
    } catch (err) {
      console.warn('Kon beltoon niet afspelen:', err);
    }

    return () => {
      stopAudio();
    };
  }, [stopAudio]);

  // Wrapped handlers die audio stoppen
  const handleAccept = React.useCallback(() => {
    stopAudio();
    onAccept();
  }, [stopAudio, onAccept]);

  const handleSchedule = React.useCallback(() => {
    stopAudio();
    onSchedule();
  }, [stopAudio, onSchedule]);

  const handleDecline = React.useCallback(() => {
    stopAudio();
    onDecline();
  }, [stopAudio, onDecline]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm mx-4">
        <div className="p-6 text-center">
          <div className="w-20 h-20 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-4 text-3xl animate-pulse">
            📞
          </div>
          
          <h3 className="text-lg font-medium text-gray-800 mb-2">Inkomende Oproep</h3>
          <p className="text-gray-600 mb-1">{selectedConnection?.name}</p>
          <p className="text-sm text-gray-500 mb-6">
            Wil je nu bellen? De call start direct in de app. Laat dit scherm open voor timer en notities.
          </p>
          
          <div className="flex space-x-3">
            <button
              data-testid="accept-call"
              onClick={handleAccept}
              className="flex-1 bg-[#7A9488] text-white py-3 rounded-lg hover:bg-[#4F6660] transition-colors text-sm font-medium flex items-center justify-center space-x-2"
            >
              <Icon icon={Phone} size={16} strokeWidth={1.5} />
              <span>Accepteren</span>
            </button>
            
            <button
              onClick={handleSchedule}
              className="flex-1 bg-stone-300 text-white py-3 rounded-lg hover:bg-[#7A9488] transition-colors text-sm font-medium flex items-center justify-center space-x-2"
            >
              <Calendar className="w-4 h-4" />
              <span>Plannen</span>
            </button>
          </div>
          
          <button
            onClick={handleDecline}
            className="mt-3 text-gray-400 text-sm hover:text-gray-600 transition-colors"
          >
            Negeren
          </button>
        </div>
      </div>
    </div>
  );
};

// Incoming Video Call Popup Component
const IncomingVideoCallPopup = ({ 
  selectedConnection, 
  onAccept, 
  onSchedule,
  onDecline 
}: {
  selectedConnection: any;
  onAccept: () => void;
  onSchedule: () => void;
  onDecline: () => void;
}) => {
  // 🔔 Zelfde beltoon ook voor video-oproep - gebruik ref om audio bij te houden
  const audioRef = React.useRef<HTMLAudioElement | null>(null);

  // Stop audio functie
  const stopAudio = React.useCallback(() => {
    if (audioRef.current) {
      try {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current = null;
        console.log('🔇 Incoming video call audio gestopt');
      } catch (err) {
        console.warn('Kon audio niet stoppen:', err);
      }
    }
  }, []);

  React.useEffect(() => {
    try {
      const audio = new Audio('/sounds/incoming-call.mp3');
      audio.loop = true;
      audioRef.current = audio;
      
      const playPromise = audio.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch((err) => {
          console.warn('Video ringtone playback blocked by browser:', err);
        });
      }
    } catch (err) {
      console.warn('Kon video-belttoon niet afspelen:', err);
    }

    return () => {
      stopAudio();
    };
  }, [stopAudio]);

  // Wrapped handlers die audio stoppen
  const handleAccept = React.useCallback(() => {
    stopAudio();
    onAccept();
  }, [stopAudio, onAccept]);

  const handleSchedule = React.useCallback(() => {
    stopAudio();
    onSchedule();
  }, [stopAudio, onSchedule]);

  const handleDecline = React.useCallback(() => {
    stopAudio();
    onDecline();
  }, [stopAudio, onDecline]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl border border-gray-100 w-full max-w-md mx-4">
        <div className="p-6 text-center">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Inkomende Video Oproep</h3>
          <p className="text-gray-600 mb-6">{selectedConnection?.name} wil video bellen</p>
          
          <div className="flex space-x-3">
            <button
              data-testid="accept-video"
              onClick={handleAccept}
              className="flex-1 bg-green-500 text-white py-3 rounded-lg hover:bg-green-600 transition-colors text-sm font-medium"
            >
              Accepteren
            </button>
            <button
              onClick={handleDecline}
              className="flex-1 bg-red-500 text-white py-3 rounded-lg hover:bg-red-600 transition-colors text-sm font-medium"
            >
              Weigeren
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Incoming Meeting Popup Component
const IncomingMeetingPopup = ({ 
  selectedConnection, 
  onAccept, 
  onSchedule,
  onDecline 
}: {
  selectedConnection: any;
  onAccept: () => void;
  onSchedule: () => void;
  onDecline: () => void;
}) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl border border-gray-100 w-full max-w-md mx-4">
        <div className="p-6 text-center">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Ontmoeting Voorstel</h3>
          <p className="text-gray-600 mb-6">{selectedConnection?.name} wil een ontmoeting plannen</p>
          
          <div className="flex space-x-3">
            <button
              onClick={onAccept}
              className="flex-1 bg-green-500 text-white py-3 rounded-lg hover:bg-green-600 transition-colors text-sm font-medium"
            >
              Accepteren
            </button>
            <button
              onClick={onDecline}
              className="flex-1 bg-red-500 text-white py-3 rounded-lg hover:bg-red-600 transition-colors text-sm font-medium"
            >
              Weigeren
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Break Conversation Popup Component
const BreakConversationPopup = ({ 
  onClose, 
  onSubmit 
}: {
  onClose: () => void;
  onSubmit: (data: any) => void;
}) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl border border-gray-100 w-full max-w-md mx-4">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Gesprek Beëindigen</h3>
          <p className="text-gray-600 mb-6">Weet je zeker dat je het gesprek wilt beëindigen?</p>
          
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="flex-1 bg-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-400 transition-colors text-sm font-medium"
            >
              Annuleren
            </button>
            <button
              onClick={() => onSubmit({})}
              className="flex-1 bg-red-500 text-white py-3 rounded-lg hover:bg-red-600 transition-colors text-sm font-medium"
            >
              Beëindigen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Schedule Appointment Popup Component
const ScheduleAppointmentPopup = ({ 
  onClose, 
  onSyncAgenda,
  onCustomTime,
  currentPhase,
  phases
}: {
  onClose: () => void;
  onSyncAgenda: () => void;
  onCustomTime: () => void;
  currentPhase: string;
  phases: any;
}) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl border border-gray-100 w-full max-w-md mx-4">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Afspraak Plannen</h3>
          <p className="text-gray-600 mb-6">Wil je een afspraak plannen?</p>
          
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="flex-1 bg-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-400 transition-colors text-sm font-medium"
            >
              Annuleren
            </button>
            <button
              onClick={onSyncAgenda}
              className="flex-1 bg-[#7A9488] text-white py-3 rounded-lg hover:bg-[#4F6660] transition-colors text-sm font-medium"
            >
              Plannen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Soft BLE ring for outgoing call (initiating state) ────────────────────
function createOutgoingRing(ctx: AudioContext): () => void {
  let stopped = false;
  let loopId: ReturnType<typeof setTimeout>;
  const tone = (freq: number, t0: number, dur: number) => {
    if (stopped) return;
    const osc = ctx.createOscillator(); const gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    osc.type = 'sine'; osc.frequency.setValueAtTime(freq, t0);
    gain.gain.setValueAtTime(0, t0);
    gain.gain.linearRampToValueAtTime(0.28, t0 + 0.025);
    gain.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
    osc.start(t0); osc.stop(t0 + dur);
  };
  const ring = () => {
    if (stopped) return;
    const now = ctx.currentTime;
    tone(880, now, 0.18); tone(1046, now + 0.22, 0.18); tone(1318, now + 0.44, 0.24);
    loopId = setTimeout(ring, 2600);
  };
  ring();
  return () => { stopped = true; clearTimeout(loopId); };
}

// Call Interface Component - Smartphone style in-call UI
const CallInterface = ({ 
  onEndCall, 
  onMute,
  userName,
  callDuration,
  callStatus,
}: {
  onEndCall: () => void;
  onMute: (muted: boolean) => void;
  userName: string;
  callDuration: number;
  callStatus?: string;
}) => {
  const [localMuted, setLocalMuted] = React.useState(false);
  const [micLevel, setMicLevel] = React.useState(0);
  const audioContextRef = React.useRef<AudioContext | null>(null);
  const analyserRef = React.useRef<AnalyserNode | null>(null);
  const localStreamRef = React.useRef<MediaStream | null>(null);
  const animationFrameRef = React.useRef<number | null>(null);
  const ringCtxRef = React.useRef<AudioContext | null>(null);
  const stopRingRef = React.useRef<(() => void) | null>(null);

  // BLE ring during initiating state
  React.useEffect(() => {
    if (callStatus === 'initiating') {
      try {
        const AudioCtx = (window as any).AudioContext || (window as any).webkitAudioContext;
        if (!AudioCtx) return;
        const ringCtx = new AudioCtx() as AudioContext;
        ringCtxRef.current = ringCtx;
        if (ringCtx.state === 'suspended') ringCtx.resume();
        stopRingRef.current = createOutgoingRing(ringCtx);
      } catch {}
    } else {
      // Stop ring function (sets stopped=true, clears timeout)
      stopRingRef.current?.();
      stopRingRef.current = null;
      // Close AudioContext to fully silence any scheduled oscillator tones
      ringCtxRef.current?.close().catch(() => {});
      ringCtxRef.current = null;
    }
    return () => {
      stopRingRef.current?.();
      stopRingRef.current = null;
      ringCtxRef.current?.close().catch(() => {});
      ringCtxRef.current = null;
    };
  }, [callStatus]);

  const initials = (userName || '')
    .split(' ').filter(Boolean).slice(0, 2)
    .map(p => p.charAt(0).toUpperCase()).join('') || 'SK';

  const formatDuration = (s: number) =>
    `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  // Mic-level meter — reuses the existing WebRTC call stream to avoid a second
  // getUserMedia() that interferes with echo cancellation and causes audio feedback
  React.useEffect(() => {
    let ownedStream = false; // true only if we created this stream ourselves
    const setup = async () => {
      try {
        // Prefer the already-active WebRTC stream; only fall back to getUserMedia
        // if no call stream exists (e.g. component used outside of an active call)
        let stream: MediaStream | null = webrtcService.getLocalStream();
        if (!stream) {
          stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
          ownedStream = true;
          localStreamRef.current = stream;
        }
        const AudioContextClass = (window as any).AudioContext || (window as any).webkitAudioContext;
        if (!AudioContextClass) return;
        const audioContext = new AudioContextClass();
        audioContextRef.current = audioContext;
        // createMediaStreamSource only reads the stream — it does NOT play it back
        const source = audioContext.createMediaStreamSource(stream);
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 256; analyser.smoothingTimeConstant = 0.8;
        source.connect(analyser); analyserRef.current = analyser;
        const dataArray = new Uint8Array(analyser.frequencyBinCount);
        const tick = () => {
          if (!analyserRef.current) return;
          analyserRef.current.getByteFrequencyData(dataArray);
          const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
          setMicLevel(Math.min(1, avg / 80));
          animationFrameRef.current = requestAnimationFrame(tick);
        };
        tick();
      } catch {}
    };
    setup();
    return () => {
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      // Only stop tracks if we created the stream ourselves
      if (ownedStream) localStreamRef.current?.getTracks().forEach(t => t.stop());
      audioContextRef.current?.close().catch(() => {});
      analyserRef.current = null;
    };
  }, []);

  const handleToggleMute = () => {
    const next = !localMuted;
    setLocalMuted(next);
    onMute(next);
  };

  return (
    <div className="flex h-full w-full items-center justify-center" style={{ background: 'linear-gradient(160deg, #0f172a 0%, #0d2318 100%)', borderRadius: '16px' }}>
      <div className="relative flex w-full max-w-sm flex-col items-center px-6 pt-10 pb-8 text-center">
        {/* Status + duur */}
        <div className="mb-1 text-xs font-semibold tracking-wide uppercase" style={{ color: callStatus === 'initiating' ? '#86efac' : '#4ade80' }}>
          {callStatus === 'initiating' ? 'Verbinden…' : 'Gesprek actief'}
        </div>
        <div className="mb-6 text-sm font-mono" style={{ color: '#d1fae5' }}>
          {callStatus !== 'initiating' && formatDuration(callDuration)}
          {callStatus === 'initiating' && 'Aan het verbinden…'}
        </div>


        {/* Avatar + pulsing rings */}
        <div className="relative mb-5" style={{ width: "112px", height: "112px" }}>
          {[148, 128, 112].map((size, i) => (
            <span key={i} style={{
              position: "absolute", top: "50%", left: "50%",
              width: `${size}px`, height: `${size}px`, borderRadius: "50%",
              border: "2px solid #16A34A",
              transform: "translate(-50%,-50%) scale(0)", opacity: 0,
              animation: `sk-ci-ring 1.8s ease-out infinite ${i * 0.45}s`,
            }} />
          ))}
          <div className="relative flex items-center justify-center rounded-full" style={{ zIndex: 2, width: "112px", height: "112px", background: "linear-gradient(135deg, #16A34A, #059669)", boxShadow: "0 0 0 3px rgba(22,163,74,0.3), 0 8px 32px rgba(22,163,74,0.4)" }}>
            <span className="text-3xl font-semibold text-white">{initials}</span>
          </div>
        </div>

        {/* Naam + status */}
        <div className="mb-1 text-xl font-semibold" style={{ color: '#f9fafb' }}>
          {userName || 'Gesprekspartner'}
        </div>
        <div className="mb-6 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium" style={{ background: 'rgba(22,163,74,0.15)', color: '#86efac' }}>
          <span className="h-2 w-2 rounded-full bg-green-400 animate-pulse" />
          <span>Gesprek actief</span>
        </div>

        {/* Eenvoudige audio-level indicatie mbv micLevel */}
        <div className="mb-8 flex h-6 items-end justify-center gap-1">
          {[1, 2, 3, 4, 5].map((bar, index) => {
            const base = 4 + index * 3;
            const extra = micLevel * 20;
            return (
              <div
                key={bar}
                className="w-1 rounded-full bg-emerald-400/80"
                style={{ height: `${base + extra}px` }}
              />
            );
          })}
        </div>

        {/* Call controls */}
        <div className="mb-4 flex items-center justify-center gap-10">
          {callStatus !== "initiating" && (
            <button onClick={handleToggleMute} style={{
              width: "56px", height: "56px", borderRadius: "50%", border: "none",
              background: localMuted ? "rgba(239,68,68,0.2)" : "rgba(255,255,255,0.1)",
              color: localMuted ? "#f87171" : "#d1fae5",
              boxShadow: localMuted ? "0 0 0 2px #f87171" : "none",
              cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
              transition: "all 0.18s",
            }} aria-label={localMuted ? "Microfoon aanzetten" : "Microfoon dempen"}>
              {localMuted ? (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="1" y1="1" x2="23" y2="23"/>
                  <path d="M9 9v3a3 3 0 005.12 2.12M15 9.34V4a3 3 0 00-5.94-.6"/>
                  <path d="M17 16.95A7 7 0 015 12v-2m14 0v2a7 7 0 01-.11 1.23"/>
                  <line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/>
                </svg>
              ) : (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z"/>
                  <path d="M19 10v2a7 7 0 01-14 0v-2"/>
                  <line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/>
                </svg>
              )}
            </button>
          )}

          {/* Ophangen */}
          <button
            onClick={onEndCall}
            style={{ width: '68px', height: '68px', borderRadius: '50%', border: 'none', background: '#EF4444', boxShadow: '0 6px 24px rgba(239,68,68,0.5)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'transform 0.12s' }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1.07)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)'; }}
            aria-label="Ophangen"
          >
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: 'rotate(135deg)' }}>
              <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.72 19.79 19.79 0 01.12 1.14 2 2 0 012.11 0h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L6.91 7.91a16 16 0 006.08 6.08l1.27-.44a2 2 0 012.11.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/>
            </svg>
          </button>
        </div>

        <style>{`
          @keyframes sk-ci-ring {
            0%   { transform: translate(-50%,-50%) scale(0.55); opacity: 0.55; }
            100% { transform: translate(-50%,-50%) scale(1);    opacity: 0;    }
          }
        `}</style>
      </div>
    </div>
  );
};

// Conversation Summary Component
const ConversationSummary = ({ 
  onClose, 
  onDownloadPDF,
  summary,
  tips,
  suggestions,
  userName,
  callDuration
}: {
  onClose: () => void;
  onDownloadPDF: () => void;
  summary: string;
  tips: string[];
  suggestions: string[];
  userName: string;
  callDuration: number;
}) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl border border-gray-100 w-full max-w-md mx-4">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Gesprek Samenvatting</h3>
          <p className="text-gray-600 mb-6">Hier is de samenvatting van je gesprek</p>
          
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="flex-1 bg-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-400 transition-colors text-sm font-medium"
            >
              Sluiten
            </button>
            <button
              onClick={onDownloadPDF}
              className="flex-1 bg-[#2E5F56] text-white py-3 rounded-lg hover:bg-[#2E5F56] transition-colors text-sm font-medium"
            >
              Download PDF
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ✅ REFACTORED: Oude VideoCallInterface component verwijderd - gebruik nu src/components/VideoCallInterface.tsx

// Call Popup Component - Luxury incoming-call card style
const CallPopup = ({ 
  onClose, 
  onAccept, 
  onDecline,
  userName 
}: {
  onClose: () => void;
  onAccept: () => void;
  onDecline: () => void;
  userName: string;
}) => {
  const cpInitials = (userName || "").split(" ").filter(Boolean).slice(0, 2).map((p: string) => p.charAt(0).toUpperCase()).join("") || "SK";
  return (
    <>
      <div style={{ position:"fixed", inset:0, zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center", padding:"20px", background:"rgba(0,0,0,0.52)", backdropFilter:"blur(6px)" }}>
        <div style={{ background:"#fff", borderRadius:"28px", width:"100%", maxWidth:"300px", overflow:"hidden", boxShadow:"0 32px 80px rgba(0,0,0,0.28),0 0 0 1px rgba(0,0,0,0.06)", animation:"sk-cp-in 0.32s cubic-bezier(0.34,1.52,0.64,1) forwards" }}>
          <div style={{ height:"3px", background:"#16A34A", animation:"sk-cp-blink 1.1s ease-in-out infinite" }} />
          <div style={{ display:"flex", flexDirection:"column", alignItems:"center", textAlign:"center", padding:"28px 24px 16px" }}>
            <div style={{ position:"relative", width:"80px", height:"80px", marginBottom:"16px" }}>
              {[136,116,80].map((sz,i) => (
                <span key={i} style={{ position:"absolute", top:"50%", left:"50%", width:`${sz}px`, height:`${sz}px`, borderRadius:"50%", border:"2px solid #16A34A", transform:"translate(-50%,-50%) scale(0)", opacity:0, animation:`sk-cp-wave 1.8s ease-out infinite ${i*0.45}s` }} />
              ))}
              <div style={{ position:"relative", zIndex:2, width:"80px", height:"80px", borderRadius:"50%", background:"linear-gradient(135deg,#16A34A,#059669)", display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 4px 20px rgba(0,0,0,0.14), 0 0 0 3px rgba(22,163,74,0.3)" }}>
                <span style={{ fontSize:"26px", fontWeight:700, color:"#fff" }}>{cpInitials}</span>
              </div>
            </div>
            <p style={{ fontSize:"10px", fontWeight:700, letterSpacing:".1em", textTransform:"uppercase", color:"#16A34A", marginBottom:"4px" }}>Telefonisch gesprek</p>
            <h2 style={{ fontSize:"20px", fontWeight:700, color:"#111827", margin:"0 0 4px" }}>{userName}</h2>
            <p style={{ fontSize:"13px", color:"#9CA3AF", margin:0 }}>wil je spreken</p>
          </div>
          <div style={{ display:"flex", justifyContent:"center", gap:"40px", padding:"0 24px 28px" }}>
            <button onClick={onDecline} style={{ background:"none", border:"none", padding:0, cursor:"pointer", display:"flex", flexDirection:"column", alignItems:"center", gap:"8px" }} aria-label="Later">
              <span style={{ width:"60px", height:"60px", borderRadius:"50%", background:"#EF4444", boxShadow:"0 4px 16px rgba(239,68,68,0.4)", display:"flex", alignItems:"center", justifyContent:"center" }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform:"rotate(135deg)" }}><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.72 19.79 19.79 0 01.12 1.14 2 2 0 012.11 0h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L6.91 7.91a16 16 0 006.08 6.08l1.27-.44a2 2 0 012.11.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/></svg>
              </span>
              <span style={{ fontSize:"12px", fontWeight:600, color:"#EF4444" }}>Later</span>
            </button>
            <button onClick={onAccept} style={{ background:"none", border:"none", padding:0, cursor:"pointer", display:"flex", flexDirection:"column", alignItems:"center", gap:"8px" }} aria-label="Opnemen">
              <span style={{ width:"60px", height:"60px", borderRadius:"50%", background:"#16A34A", boxShadow:"0 4px 16px rgba(22,163,74,0.4)", display:"flex", alignItems:"center", justifyContent:"center" }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.72 19.79 19.79 0 01.12 1.14 2 2 0 012.11 0h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L6.91 7.91a16 16 0 006.08 6.08l1.27-.44a2 2 0 012.11.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/></svg>
              </span>
              <span style={{ fontSize:"12px", fontWeight:600, color:"#16A34A" }}>Opnemen</span>
            </button>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes sk-cp-in { from { transform:scale(0.82) translateY(24px); opacity:0; } to { transform:scale(1) translateY(0); opacity:1; } }
        @keyframes sk-cp-blink { 0%,100% { opacity:1; } 50% { opacity:0.3; } }
        @keyframes sk-cp-wave { 0% { transform:translate(-50%,-50%) scale(0.6); opacity:0.7; } 100% { transform:translate(-50%,-50%) scale(1); opacity:0; } }
      `}</style>
    </>
  );
};

// Video Call Popup Component - Luxury card style
const VideoCallPopup = ({ 
  onClose, 
  onAccept, 
  onDecline,
  userName = ""
}: {
  onClose: () => void;
  onAccept: () => void;
  onDecline: () => void;
  userName?: string;
}) => {
  const vcInitials = (userName || "").split(" ").filter(Boolean).slice(0, 2).map((p: string) => p.charAt(0).toUpperCase()).join("") || "SK";
  return (
    <>
      <div style={{ position:"fixed", inset:0, zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center", padding:"20px", background:"rgba(0,0,0,0.52)", backdropFilter:"blur(6px)" }}>
        <div style={{ background:"#fff", borderRadius:"28px", width:"100%", maxWidth:"300px", overflow:"hidden", boxShadow:"0 32px 80px rgba(0,0,0,0.28),0 0 0 1px rgba(0,0,0,0.06)", animation:"sk-cp-in 0.32s cubic-bezier(0.34,1.52,0.64,1) forwards" }}>
          <div style={{ height:"3px", background:"#2E5F56", animation:"sk-cp-blink 1.1s ease-in-out infinite" }} />
          <div style={{ display:"flex", flexDirection:"column", alignItems:"center", textAlign:"center", padding:"28px 24px 16px" }}>
            <div style={{ position:"relative", width:"80px", height:"80px", marginBottom:"16px" }}>
              {[136,116,80].map((sz,i) => (
                <span key={i} style={{ position:"absolute", top:"50%", left:"50%", width:`${sz}px`, height:`${sz}px`, borderRadius:"50%", border:"2px solid #2563EB", transform:"translate(-50%,-50%) scale(0)", opacity:0, animation:`sk-cp-wave 1.8s ease-out infinite ${i*0.45}s` }} />
              ))}
              <div style={{ position:"relative", zIndex:2, width:"80px", height:"80px", borderRadius:"50%", background:"linear-gradient(135deg,#2E5F56,#265049)", display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 4px 20px rgba(0,0,0,0.14), 0 0 0 3px rgba(46,95,86,0.3)" }}>
                <span style={{ fontSize:"26px", fontWeight:700, color:"#fff" }}>{vcInitials}</span>
              </div>
            </div>
            <p style={{ fontSize:"10px", fontWeight:700, letterSpacing:".1em", textTransform:"uppercase", color:"#2E5F56", marginBottom:"4px" }}>Videogesprek</p>
            <h2 style={{ fontSize:"20px", fontWeight:700, color:"#111827", margin:"0 0 4px" }}>{userName}</h2>
            <p style={{ fontSize:"13px", color:"#9CA3AF", margin:0 }}>wil je video bellen</p>
          </div>
          <div style={{ display:"flex", justifyContent:"center", gap:"40px", padding:"0 24px 28px" }}>
            <button onClick={onDecline} style={{ background:"none", border:"none", padding:0, cursor:"pointer", display:"flex", flexDirection:"column", alignItems:"center", gap:"8px" }} aria-label="Later">
              <span style={{ width:"60px", height:"60px", borderRadius:"50%", background:"#EF4444", boxShadow:"0 4px 16px rgba(239,68,68,0.4)", display:"flex", alignItems:"center", justifyContent:"center" }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform:"rotate(135deg)" }}><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.72 19.79 19.79 0 01.12 1.14 2 2 0 012.11 0h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L6.91 7.91a16 16 0 006.08 6.08l1.27-.44a2 2 0 012.11.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/></svg>
              </span>
              <span style={{ fontSize:"12px", fontWeight:600, color:"#EF4444" }}>Later</span>
            </button>
            <button onClick={onAccept} style={{ background:"none", border:"none", padding:0, cursor:"pointer", display:"flex", flexDirection:"column", alignItems:"center", gap:"8px" }} aria-label="Video bellen">
              <span style={{ width:"60px", height:"60px", borderRadius:"50%", background:"#2E5F56", boxShadow:"0 4px 16px rgba(46,95,86,0.4)", display:"flex", alignItems:"center", justifyContent:"center" }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>
              </span>
              <span style={{ fontSize:"12px", fontWeight:600, color:"#2E5F56" }}>Opnemen</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

// ✅ Helper functie voor getUserMedia testing vanuit browser console
async function testGetUserMediaVideoAndAudio() {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
    console.log("testGetUserMediaVideoAndAudio OK", stream);
    // we hangen het even op window zodat we de stream niet verliezen
    (window as any).__testStream = stream;
    return stream;
  } catch (err: any) {
    console.error("testGetUserMediaVideoAndAudio ERROR", {
      error: err,
      name: err?.name,
      message: err?.message,
      stack: err?.stack,
    });
    throw err;
  }
}

// Maak de functie beschikbaar in de console
if (typeof window !== "undefined") {
  (window as any).testGetUserMediaVideoAndAudio = testGetUserMediaVideoAndAudio;
}

export default ConnectieFlow;
